import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase, setReadOnly } from "./supabaseClient";
import * as db from "./db";
import { buildCompliancePdf } from "./compliancePdf";
import { PolicyRichEditor, PolicyBody, printPolicy } from "./policyDocs";
import {
  LayoutDashboard, Users, ClipboardCheck, GraduationCap, Calendar,
  ChevronRight, ChevronUp, ChevronDown, Check, X, Clock, AlertTriangle, Plus, Search,
  Award, TrendingUp, FileQuestion, ArrowRight, ArrowLeft, CircleDot, Trophy,
  CalendarClock, UserPlus, Send, RotateCcw, Pencil, Trash2,
  BookOpen, PenLine, LogOut, Bell, Eye, Lock, User, ShieldCheck, AlertCircle, HelpCircle,
  Upload, Download, Copy, Folder, Play, Pause, Volume2, Maximize, FileText, Menu
} from "lucide-react";

/* ============================================================
   DOMAIN MODEL

   Two ride groups with different level ladders:
   - Kiddie Land (Slide, Tornado, Dragon, Kiddie): T -> L2 -> L3
       L2 operates independently, L3 can train.
   - Tracks (Sprint, Road, Euro):                  T -> L2 -> L3 -> L4
       L3 & L4 operate independently, L4 is the trainer.
   ============================================================ */
const RIDES = [
  { key: "slide", label: "Slide", group: "kiddieLand", ladder: ["T", "L2", "L3"], trainer: "L3" },
  { key: "tornado", label: "Tornado", group: "kiddieLand", ladder: ["T", "L2", "L3"], trainer: "L3" },
  { key: "dragon", label: "Dragon", group: "kiddieLand", ladder: ["T", "L2", "L3"], trainer: "L3" },
  { key: "kiddieKart", label: "Kiddie Kart", group: "kiddieLand", ladder: ["T", "L2", "L3"], trainer: "L3" },
  { key: "sprint", label: "Sprint", group: "tracks", ladder: ["T", "L2", "L3", "L4"], trainer: "L4" },
  { key: "road", label: "Road", group: "tracks", ladder: ["T", "L2", "L3", "L4"], trainer: "L4" },
  { key: "euro", label: "Euro Certified", group: "tracks", ladder: ["T", "L2", "L3", "L4"], trainer: "L4" },
];
const RIDE_MAP = Object.fromEntries(RIDES.map((r) => [r.key, r]));

// Progression chain
const AMUSEMENT_RIDES = ["slide", "tornado", "dragon", "kiddieKart"]; // must all reach L2 to unlock Road
const TRACK_ORDER = ["road", "sprint", "euro"]; // each L2 unlocks the next track's L2
const nextTrack = (track) => { const i = TRACK_ORDER.indexOf(track); return i >= 0 && i < TRACK_ORDER.length - 1 ? TRACK_ORDER[i + 1] : null; };
const lvlRank = (lv) => LEVELS[lv]?.rank ?? 0;
const hasReached = (emp, ride, lv) => lvlRank(emp.levels[ride]) >= lvlRank(lv);

/* Ensure the employee is at minimum 'T' on a ride. Used when issuing a quiz
   so the trainee is properly enrolled before they take the exam — prevents
   the bug where a quiz pass couldn't trigger T->L2 progression because the
   employee was still at level 0. Mutates emp.levels and appends a history
   entry only if a level change actually occurred. */
function ensureAtLeastT(emp, ride, byName) {
  if (!emp.levels) emp.levels = {};
  const current = emp.levels[ride] || "0";
  if (lvlRank(current) < 1) {
    emp.levels[ride] = "T";
    emp.history = emp.history || [];
    emp.history.unshift({
      date: todayISO(),
      text: `${RIDE_MAP[ride].label}: started training (T)${byName ? " — " + byName : ""}`,
    });
    return true;
  }
  return false;
}

// Level meanings
const LEVELS = {
  "0": { label: "—", desc: "Not in training", color: "var(--l0)", rank: 0 },
  "T": { label: "T", desc: "Trainee — shadow / observed hourly", color: "var(--lT)", rank: 1 },
  "L2": { label: "L2", desc: "Operates independently", color: "var(--l2)", rank: 2 },
  "L3": { label: "L3", desc: "Independent (trainer in Kiddie Land)", color: "var(--l3)", rank: 3 },
  "L4": { label: "L4", desc: "Certified trainer", color: "var(--l4)", rank: 4 },
};

// L2+ operates independently in both groups
function isIndependent(level) {
  return level !== "0" && level !== "T";
}
function isTrainer(level, ride) {
  return level === RIDE_MAP[ride].trainer;
}

function nextLevel(current, ride) {
  const ladder = ["0", ...RIDE_MAP[ride].ladder];
  // Nobody is "advanced to T". Starting training on a ride makes them a
  // trainee automatically, so from 0 the next target is L2, same as from T.
  if ((current || "0") === "0" && ladder.includes("L2")) return "L2";
  const idx = ladder.indexOf(current);
  if (idx === -1 || idx >= ladder.length - 1) return null;
  return ladder[idx + 1];
}

// What action advances TO each target level
// What KIND of requirement advances TO a target level on a ride.
// Returns one of: "shadow" | "quiz" | "observations" | "ttt_sessions" | "ttt_observation"
function stepRequirement(ride, targetLevel) {
  const r = RIDE_MAP[ride];
  if (!r) return "quiz";
  if (targetLevel === "T") return "shadow";
  if (targetLevel === "L2") return "quiz"; // T -> L2 is the certification quiz (after training sign-off)
  if (targetLevel === "L3") {
    // L2 -> L3 is OBSERVATIONS ONLY. Kiddie uses 3 Train-the-Trainer sessions instead.
    return ride === "kiddieKart" ? "ttt_sessions" : "observations";
  }
  if (targetLevel === "L4") return "ttt_observation"; // L3 -> L4 (Tracks) is a Train-the-Trainer observation
  return "quiz";
}

function stepAction(targetLevel, ride) {
  const req = stepRequirement(ride, targetLevel);
  switch (req) {
    case "shadow": return "Begin shadow training";
    case "quiz": return "Training sign-off → certification quiz";
    case "observations": return "4 observations (≥2 distinct observers)";
    case "ttt_sessions": return "3 Train-the-Trainer sessions";
    case "ttt_observation": return "Train-the-Trainer observation";
    default: return "—";
  }
}

const todayISO = () => new Date().toISOString().slice(0, 10);
const PASS_SCORE = 80;

// L2 -> L3 observation plan for standard rides (each slot must be a DIFFERENT person)
const OBS_PLAN = [
  { slot: "trainer", label: "Trainer observation", roles: ["trainer"] },
  { slot: "manager", label: "Manager observation", roles: ["manager"] },
  { slot: "agm_gm", label: "AGM/GM observation", roles: ["agm_gm"] },
  { slot: "volume", label: "Mgr/AGM/GM during volume", roles: ["manager", "agm_gm"] },
];

// Kiddie L2->L3: 3 Train-the-Trainer sessions; >=1 with/observed by AGM+
const TTT_SESSIONS_REQUIRED = 3;

// Standard 4-observation completion: needs all 4 slot passes + at least 2 distinct observers.
// (Was: all 4 must be different people. Relaxed to 2 distinct minimum because parks
// don't always have 4 AGMs available — some redundancy is preserved without making
// completion impossible.)
function obsComplete(list) {
  if (!list || !list.length) return false;
  const passes = list.filter((o) => o.result === "pass");
  const distinctNames = new Set(passes.map((p) => (p.observer?.name || "").toLowerCase()).filter(Boolean));
  const slotsPassed = new Set(passes.map((p) => p.slot));
  const baseSlotsOk = OBS_PLAN.every((p) => slotsPassed.has(p.slot));
  const fails = list.filter((o) => o.result === "fail").length;
  const extrasPassedBeyondBase = passes.length - OBS_PLAN.length;
  const extrasOutstanding = fails - Math.max(0, extrasPassedBeyondBase);
  return baseSlotsOk && distinctNames.size >= 2 && extrasOutstanding <= 0;
}

function obsProgress(list) {
  const passes = (list || []).filter((o) => o.result === "pass");
  const slotsPassed = new Set(passes.map((p) => p.slot));
  const baseDone = OBS_PLAN.filter((p) => slotsPassed.has(p.slot)).length;
  const fails = (list || []).filter((o) => o.result === "fail").length;
  const extrasPassed = Math.max(0, passes.length - OBS_PLAN.length);
  const extrasOutstanding = Math.max(0, fails - extrasPassed);
  return { baseDone, baseTotal: OBS_PLAN.length, extrasOutstanding };
}

// Train-the-Trainer sessions complete? (Kiddie L2->L3, and the single TtT obs for L3->L4)
function tttComplete(list, requiredCount) {
  const passes = (list || []).filter((o) => o.result === "pass");
  if (passes.length < requiredCount) return false;
  // at least one of the passing sessions must be with or observed by AGM+
  const hasAgmPlus = passes.some((p) => roleRank(p.observer?.role) >= roleRank("agm_gm") || p.agmObserved);
  return hasAgmPlus;
}

// RT (Retrain) status: needs 2 CONSECUTIVE passes within 30 days; a fail resets and
// requires a re-observation by a DIFFERENT AGM+.
function withinDays(iso, days) {
  if (!iso) return false;
  const d = new Date(iso + "T00:00:00"); const now = new Date(todayISO() + "T00:00:00");
  return (now - d) / 86400000 <= days;
}
function rtStatus(rt) {
  // rt = { level, ride, observations:[{result, observer, date}] } or null
  if (!rt) return null;
  const obs = rt.observations || [];
  // consider only the trailing streak of passes that are within 30 days of each other
  let consecutivePasses = 0; let lastFailAgm = null;
  for (let i = obs.length - 1; i >= 0; i--) {
    if (obs[i].result === "pass") consecutivePasses++;
    else { lastFailAgm = obs[i].observer; break; }
  }
  const recentPasses = obs.slice(-2).filter((o) => o.result === "pass" && withinDays(o.date, 30)).length;
  const cleared = consecutivePasses >= 2 && recentPasses >= 2;
  return { consecutivePasses, cleared, lastFailAgm };
}

// Human label for a sign-off task kind
function signoffKindLabel(so) {
  switch (so.kind) {
    case "train_gate": return "Training sign-off → unlocks quiz";
    case "advance_full": return "Post-test sign-off (100%) — AGM/GM";
    case "advance_gaps": return "Post-test sign-off (80–99%) — employee + manager";
    case "advance_obs": return `Post-observation sign-off — AGM+${so.note ? " · " + so.note : ""}`;
    default: return "Training sign-off";
  }
}

/* ============================================================
   TASK-GROUPING ENGINE (per employee, per section)
   Sections: "quiz" | "signoff" | "observation"
   Each task: { id, section, ride, label, action, canDo(user), reason }
   ============================================================ */
// Is this user a trainer for this specific ride? (Kiddie Land trainer=L3, Tracks=L4)
function isTrainerForRide(user, emp, ride) {
  // A "trainer" role user must themselves be at trainer level on that ride to train it.
  // We approximate using their own role; managers+ can always act.
  return roleRank(user.role) >= roleRank("manager");
}

function quizTasksFor(state, emp) {
  return state.quizzes
    .filter((q) => q.empId === emp.id && (q.status === "sent" || q.status === "failed"))
    .map((q) => ({
      id: q.id, section: "quiz", ride: q.ride,
      label: `${RIDE_MAP[q.ride].label} → ${q.targetLevel} · ${q.status === "failed" ? "Retake quiz (failed)" : "Certification quiz"}`,
      status: q.status, quiz: q,
      // Only the trainee themselves can take their own quiz. Audit trail integrity.
      canDo: (u) => u && u.employeeId === emp.id,
    }));
}

function signoffTasksFor(state, emp) {
  const tasks = state.signoffs
    .filter((so) => so.empId === emp.id && so.status !== "complete")
    .map((so) => {
      const need = so.requiredRoles.filter((r) => !so.signatures.some((s) => s.role === r));
      return {
        id: so.id, section: "signoff", ride: so.ride,
        label: `${RIDE_MAP[so.ride].label} → ${so.targetLevel} · ${signoffKindLabel(so)}`,
        sub: `${so.signatures.length}/${so.requiredRoles.length} signed${need.length ? " · needs " + need.map((r) => r === "agm_gm" ? "AGM+" : r === "manager" ? "Manager" : r === "employee" ? "Employee" : "Trainer/Mgr").join(", ") : ""}`,
        signoff: so,
        canDo: (u) => need.some((r) => canActAs(u, r)),
      };
    });
  return tasks;
}

function observationTasksFor(state, emp) {
  const tasks = [];

  // T→L2 observations needed: trainee passed quiz (passed_full/passed_gaps) for a ride
  // but doesn't yet have a passing T→L2 observation. These live in the new l2_observations
  // table (NOT the legacy emp.observations system).
  (state.quizzes || [])
    .filter((q) => q.empId === emp.id && (q.status === "passed_full" || q.status === "passed_gaps"))
    // Already at L2+ on this ride (e.g. advanced another way) — nothing to observe.
    .filter((q) => !hasReached(emp, q.ride, "L2"))
    .forEach((q) => {
      const obs = (state.l2_observations || []).filter((o) =>
        o.employee_id === emp.id && o.ride === q.ride && (o.progression || "t_to_l2") === "t_to_l2"
      );
      const hasPass = obs.some((o) => o.outcome === "pass");
      if (hasPass) return;
      const needsWorkCount = obs.filter((o) => o.outcome === "needs_work").length;
      tasks.push({
        id: `l2obs-${q.id}`, section: "observation", ride: q.ride, l2Obs: true, quizRef: q, progression: "t_to_l2",
        label: `${RIDE_MAP[q.ride].label} → L2 · T→L2 observation (Manager+)`,
        sub: needsWorkCount > 0
          ? `Re-observation needed (${needsWorkCount} prior 'needs work')`
          : `Quiz passed ${q.completedDate ? fmtDate(q.completedDate) : ""}`,
        canDo: (u) => roleRank(u.role) >= roleRank("manager"),
      });
    });

  // L2→L3 observations needed: employee is L2 on a ride (except kiddieKart, which uses
  // Train-the-Trainer below) and doesn't yet have 4 passing L2→L3 observations from
  // at least 2 different observers.
  RIDES.forEach((r) => {
    if (r.key === "kiddieKart") return; // kiddieKart uses TtT path below
    if (emp.levels?.[r.key] !== "L2") return;
    const obs = (state.l2_observations || []).filter((o) =>
      o.employee_id === emp.id && o.ride === r.key && (o.progression || "t_to_l2") === "l2_to_l3"
    );
    const passes = obs.filter((o) => o.outcome === "pass");
    if (passes.length >= 4) {
      const observers = new Set(passes.map((o) => o.observer_id));
      if (observers.size >= 2) return; // already qualified; advancement should have happened
    }
    const needsWorkCount = obs.filter((o) => o.outcome === "needs_work").length;
    const observers = new Set(passes.map((o) => o.observer_id));
    tasks.push({
      id: `l2to3-${emp.id}-${r.key}`, section: "observation", ride: r.key, l2Obs: true, progression: "l2_to_l3",
      label: `${RIDE_MAP[r.key].label} → L3 · L2→L3 observation (Manager+)`,
      sub: `${passes.length}/4 passes · ${observers.size} of 2 observers${needsWorkCount > 0 ? ` · ${needsWorkCount} prior 'needs work'` : ""}`,
      canDo: (u) => roleRank(u.role) >= roleRank("manager"),
    });
  });

  // Legacy observation flows: retrain, kiddieKart Train-the-Trainer, L3→L4 TtT, L4 readiness.
  // These use the emp.observations / emp.retrain / l4Checks structures, NOT l2_observations.
  RIDES.forEach((r) => {
    const cur = emp.levels[r.key];
    const rt = emp.retrain?.[r.key];
    const obsList = emp.observations?.[r.key] || [];
    if (rt) {
      const st = rtStatus(rt);
      tasks.push({ id: `rt-${r.key}`, section: "observation", ride: r.key,
        label: `${RIDE_MAP[r.key].label} · RETRAIN (${rt.level})`,
        sub: `${st.consecutivePasses}/2 consecutive passes${rt.needDifferentAgm ? " · needs different AGM+" : ""}`,
        canDo: (u) => can.observe(u) && roleRank(u.role) >= roleRank("agm_gm") });
    } else if (cur === "L2" && r.key === "kiddieKart") {
      const passes = obsList.filter((o) => o.kind === "ttt" && o.result === "pass").length;
      tasks.push({ id: `ttt-${r.key}`, section: "observation", ride: r.key,
        label: `${RIDE_MAP[r.key].label} L2→L3 · Train-the-Trainer`, sub: `${passes}/${TTT_SESSIONS_REQUIRED} sessions (≥1 AGM+)`,
        canDo: (u) => can.observe(u) });
    } else if (cur === "L3" && RIDE_MAP[r.key].group === "tracks" && hasScheduled(emp, r.key, "L4")) {
      const l4 = obsList.filter((o) => o.kind === "ttt" && o.forL4 && o.result === "pass").length;
      if (l4 < 1) tasks.push({ id: `tttl4-${r.key}`, section: "observation", ride: r.key,
        label: `${RIDE_MAP[r.key].label} L3→L4 · Train-the-Trainer obs`, sub: "1 passing observation",
        canDo: (u) => can.observe(u) });
    }
  });
  // L4-readiness flags (due) become observation tasks too
  l4Flags(emp).forEach((f) => {
    if (f.due) tasks.push({ id: `l4flag-${f.ride}`, section: "observation", ride: f.ride, l4flag: true,
      label: `${RIDE_MAP[f.ride].label} · L4 readiness check`, sub: f.lastCheck ? `last checked ${fmtDate(f.lastCheck)}` : "never checked",
      canDo: (u) => can.observe(u) });
  });
  return tasks;
}

// Build grouped rows for a section. Trainer role: only rides they train + tasks they can do.
function groupedForSection(state, user, section) {
  const rows = [];
  state.employees.filter((e) => !e.archived).forEach((e) => {
    let tasks = section === "quiz" ? quizTasksFor(state, e)
      : section === "signoff" ? signoffTasksFor(state, e)
      : observationTasksFor(state, e);
    if (user.role === "trainer") tasks = tasks.filter((t) => t.canDo(user)); // trainers only see actionable
    if (tasks.length) rows.push({ emp: e, tasks });
  });
  return rows;
}

// Days since start date
function daysSince(iso) {
  if (!iso) return 9999;
  const d = new Date(iso + "T00:00:00"); const now = new Date(todayISO() + "T00:00:00");
  return Math.floor((now - d) / 86400000);
}
// L4-readiness check cadence: every 14 days for first 90 days, then every 30
function l4CheckIntervalDays(startDate) {
  return daysSince(startDate) <= 90 ? 14 : 30;
}

// Does this employee have an open (incomplete) schedule entry for ride+target?
function hasScheduled(emp, ride, target) {
  return (emp.schedule || []).some((sc) => sc.ride === ride && sc.targetLevel === target);
}
// Is there an active quiz/observation already in flight for this ride+target?
function progressionInFlight(state, emp, ride) {
  const q = state.quizzes.some((x) => x.empId === emp.id && x.ride === ride && (x.status === "sent" || x.status === "failed"));
  return q;
}

// Auto-create the next training steps based on the unlock chain (up to L3).
// Mutates emp.schedule on the draft state. Returns list of created step descriptions.
function syncProgression(state, emp) {
  const created = [];
  // Non-Attractions employees don't get auto-created next steps.
  // Once they have any ride at L2+, treat them as Attractions-equivalent for that
  // progression — the existing chain just reads current levels, so this gate only
  // applies when the person has NO ride training at all.
  const dept = (emp.department || "Attractions");
  const hasAnyRideProgress = (emp.levels && Object.values(emp.levels).some((v) => v && v !== "0"));
  if (dept !== "Attractions" && !hasAnyRideProgress) return created;

  const add = (ride, target, note) => {
    if (hasReached(emp, ride, target)) return;
    if (hasScheduled(emp, ride, target)) return;
    if (emp.retrain && emp.retrain[ride]) return;
    emp.schedule.push({ id: `sch-${Date.now()}-${ride}-${target}-${Math.random().toString(36).slice(2, 6)}`, ride, targetLevel: target, date: "", note });
    created.push(`${RIDE_MAP[ride].label} → ${target}`);
  };

  // Step 1: L2 on ALL amusement rides unlocks Road L2
  const allRidesL2 = AMUSEMENT_RIDES.every((r) => hasReached(emp, r, "L2"));
  if (allRidesL2) add("road", "L2", "Unlocked: L2 on all rides");

  // Step 2+: each track at L2 unlocks L3-same-track AND L2-next-track
  TRACK_ORDER.forEach((track) => {
    if (hasReached(emp, track, "L2")) {
      add(track, "L3", "Unlocked: track L2 → push to L3"); // same-track L3
      const nt = nextTrack(track);
      if (nt) add(nt, "L2", `Unlocked by ${RIDE_MAP[track].label} L2`); // next-track L2
    }
  });
  return created;
}

// Build L4-readiness flags: applies once L2 on all rides AND L3 on a track.
// Returns [{ride, dueDays}] for tracks at L3 (not yet L4) whose check is due.
function l4Flags(emp) {
  const flags = [];
  const ridesL2 = AMUSEMENT_RIDES.every((r) => hasReached(emp, r, "L2"));
  if (!ridesL2) return flags;
  const interval = l4CheckIntervalDays(emp.startDate);
  TRACK_ORDER.forEach((track) => {
    if (emp.levels[track] === "L3") {
      const checks = (emp.l4Checks && emp.l4Checks[track]) || [];
      const last = checks.length ? checks[checks.length - 1].date : null;
      const due = !last || daysSince(last) >= interval;
      flags.push({ ride: track, due, interval, lastCheck: last });
    }
  });
  return flags;
}

/* ============================================================
   TRAINING CONTENT (inlined) — modules + quiz banks
   ============================================================ */
/* ============================================================
   TRAINING CONTENT — modules + quiz banks
   Grounded in the Las Vegas Mini Grand Prix Amusements
   Training Program (Chapters 2–7).
   ============================================================ */

/* ----------------------------------------------------------
   TRAINING MODULES
   Each module = ordered sections a trainer reviews with the
   trainee. `key` matches the topic tags used by quiz questions
   so "missed topics" can be mapped back to module sections.
   ---------------------------------------------------------- */

const MODULES = {
  _general: {
    title: "General Amusements Training (review first)",
    intro:
      "Every operator reviews this before any ride-specific module. It covers the safety mindset, universal guest rules, incident reporting, and guest-management standards that apply across all attractions.",
    sections: [
      {
        key: "safety-mindset", heading: "Safety mindset & zero tolerance",
        points: [
          "Employee safety is the top priority — you are the number one defense against accidents and injuries.",
          "Zero tolerance for violation of safety rules and policies — for employees and guests alike.",
          "Review the Job Hazard Analysis (JHA) for any task; if none exists, build one with a supervisor.",
          "Never 'mess around' on or with equipment; when driving karts for any reason, follow the same rules guests follow.",
        ],
        detail: [
          { type: "prose", text: "Amusement rides have inherent risks and failure to enforce safety policies and precautions can result in catastrophic injury or death. Ensuring the well-being of guests and staff is a fundamental responsibility that directly impacts the park's reputation, legal compliance, and overall success." },
          { type: "prose", text: "Guests are much more likely to comply with rules and policies when we explain the reasoning behind them. The 'Talking Point Toolkit' in each section gives you language to use. Sometimes you'll have the difficult job of telling a guest they cannot participate for safety reasons — it is far harder to hold their hand while they wait for medical help, or to explain to their loved ones that they were hurt because we didn't enforce a rule." },
          { type: "steps", title: "Employee safety basics", items: [
            "Review the JHA for the job/task; if none exists, work with your supervisor to create one.",
            "Stay alert and always pay attention to your surroundings.",
            "Know and always follow the rules for the equipment you are assigned to.",
          ]},
        ],
      },
      {
        key: "universal-guest-rules", heading: "Universal guest rules (all attractions)",
        points: [
          "No guest may ride ANY attraction without a confirmed waiver wristband.",
          "Hair shoulder-length or longer must be secured above the shoulders before riding.",
          "Loose clothing (scarves, purses, bags, drawstrings) must be removed.",
          "Closed shoes required; no flip-flops; any backstrap must be worn.",
          "No riding after alcohol; pregnant guests and those with back/neck or heart conditions should not ride.",
        ],
        detail: [
          { type: "prose", text: "Go karts and rides have moving parts in which hair and loose clothing can become entangled, resulting in catastrophic injury or death. Guests with hair longer than waist-length are not permitted to ride certain attractions. If a guest's hair is secured so you can't judge its length, ask them to unsecure it to show it is not waist-length or longer." },
          { type: "toolkit", lines: [
            "Your/your child's hair is beautiful, and we want it to stay that way. Go karts and rides have moving parts in which long hair can become entangled, resulting in catastrophic injury. For this reason it is extremely important that hair is secured above the shoulders whenever on an amusement ride.",
            "Unfortunately it is not safe to ride our high-speed go karts with hair longer than waist-length; even when secured, hair can come loose during the ride and be caught in moving parts. With your hair secured above your shoulders, you're welcome to enjoy our other rides and arcade!",
            "I'll need you to remove any loose clothing — scarves, bags, purses, etc. — before riding.",
            "You won't be able to ride with your head scarf — if it comes loose it can be caught in the moving parts. If you wear it for religious purposes, we have safe loaner head coverings; let me get a manager to bring one out.",
          ]},
          { type: "steps", title: "The full universal rule set", items: [
            "All guests must sign a waiver and wear the confirmed waiver wristband — NO wristband, NO ride.",
            "Secure shoulder-length-or-longer hair above the shoulders with a restraint before riding.",
            "Remove all loose clothing; offer safe loaner head coverings (via a manager) for religious coverings with loose ends.",
            "Closed shoes only; no flip-flops; backstraps must be worn. Loaner shoes available for a small fee.",
            "No riding after consuming alcohol — remove the wristband and notify a supervisor if impairment is suspected.",
            "Individuals with health conditions (back/neck, heart) should not ride.",
            "Pregnant women should not ride.",
            "It is YOUR responsibility to ensure guests are aware of and comply with all rules at all times.",
          ]},
          { type: "prose", text: "Note to trainer: provide articles/videos of hair and loose-clothing injuries to bring this to life for employees." },
        ],
      },
      {
        key: "incident-reporting", heading: "Incident management & reporting",
        points: [
          "Assess the scene and remove or isolate hazards first.",
          "Call a supervisor/manager immediately; call 911 for serious or life-threatening injury.",
          "Only administer first aid if trained and comfortable.",
          "Document every guest incident even if the guest declines; report near-misses.",
        ],
        detail: [
          { type: "steps", title: "Employee incident & injury procedure", items: [
            "Assess the situation: ensure immediate safety of everyone; identify and eliminate/isolate ongoing hazards.",
            "Call for a supervisor or manager immediately.",
            "If NOT serious: report before end of shift, render basic first aid only if trained (band-aid, ice), identify witnesses, follow the Accident/Incident Investigation procedure; the AGM on duty provides documentation if the employee seeks medical care.",
            "If serious/life-threatening: call 911 immediately; give clear location, nature of injury, and hazards; provide first aid only if trained; secure the scene; follow the Incident Investigation & Reporting procedures.",
          ]},
          { type: "steps", title: "Guest incident & injury procedure", items: [
            "Assess the situation and isolate hazards.",
            "Call for a supervisor or manager immediately.",
            "Follow the Guest Incident Investigation & Reporting procedures.",
            "If the guest declines to document, do not force them — but still document your perspective and the facts.",
            "If serious/life-threatening: call 911; give location/nature/hazards; first aid only if trained; secure the scene.",
          ]},
          { type: "prose", text: "Near miss: an action or situation that could result in incident or injury but did not. Immediately correct it and complete a near-miss report." },
        ],
      },
      {
        key: "guest-service", heading: "Professionalism & guest management",
        points: [
          "Be clearly identifiable: clean uniform, name tag, alert posture, no phone use.",
          "Use the megaphone so the safety briefing is heard; project confidence and make eye contact.",
          "De-escalate upset guests: stay calm, listen, empathize, inform, involve a manager.",
        ],
        detail: [
          { type: "prose", text: "Good customer service and guest management make for a safer park. Engagement and effective communication keep patrons, riders, and operators alert and aware. Guest management begins with professionalism — you are in control of the ride, and your professionalism shapes how guests respond to your direction." },
          { type: "checklist", title: "Ride-operator professionalism", items: [
            "Clearly identifiable as the operator: clean, neat uniform and name tag.",
            "Good posture; stand alert and ready to respond.",
            "Attentive and focused; not distracted or goofing off.",
            "Minimize distractions — no personal conversations or phone use.",
            "Project confidence; make eye contact; use appropriate tone and pitch; use the megaphone so you're heard.",
            "Keep your work area free of clutter and debris.",
          ]},
          { type: "steps", title: "When a guest becomes upset", items: [
            "Stay calm — keep your emotions in check to de-escalate.",
            "Listen actively — let them express concerns without interrupting.",
            "Empathize — 'I understand how you feel' / 'I'm sorry to hear you're upset.'",
            "Provide accurate information — explain the safety reasoning behind the rule.",
            "Stay professional — avoid arguing or getting defensive; focus on solutions.",
            "Involve a supervisor or manager if it escalates or you can't resolve it.",
          ]},
        ],
      },
    ],
  },

  road: {
    title: "Road (Go Kart Road / Family Track)",
    intro:
      "Family track with single and 2-seater karts, riders as small as 33\". Requires a MINIMUM of two operators (3–4 when busy). One operator must always have eyes on the track and be within arm's reach of the Kartrol controls while karts race. The track shall NOT operate if Kartrol is not working properly.",
    sections: [
      {
        key: "staffing", heading: "Staffing & race-position roles",
        points: [
          "Minimum two operators; three to four during busy times.",
          "RA1 stays at the podium observing; RA2 directs/repositions karts; RA3/RA4 take field positions.",
          "One operator always within arm's reach of Kartrol with eyes on the track during racing.",
        ],
        detail: [
          { type: "steps", title: "During-race positions", items: [
            "Ride Attendant 1: remain at the podium observing the race.",
            "Ride Attendant 2: direct karts to track and reposition karts in the pit.",
            "With 4 operators: RA stays in pit, body angled toward pit entrance/guests, reads the safety briefing to the next group, and corrects safety deficiencies in line.",
            "With 2–3 operators: use the remote to slow nearby karts so you can safely cross to field position 1 to observe and direct drivers.",
            "Ride Attendant 3: before karts release, move to field position 2 to observe and direct.",
            "Ride Attendant 4: before karts release, move to field position 1 to observe and direct.",
          ]},
        ],
      },
      {
        key: "rider-requirements", heading: "Rider requirements",
        points: [
          "Drivers alone: 54\"+. Passengers: 33\"+ and able to follow directions. Under 33\" may not ride at all.",
          "Drivers carrying a passenger must be 18+ or the passenger's guardian.",
          "Seatbelts tightly fastened (no extenders); hands and arms inside at all times.",
        ],
        detail: [
          { type: "checklist", title: "Full rider rule set", items: [
            "Waiver wristband confirmed.",
            "Driver alone: 54 inches or taller.",
            "Passenger: at least 33 inches and capable of following directions; under 33\" may not ride in any capacity under any circumstances.",
            "Driver with a passenger: at least 18 years old or the passenger's legal guardian.",
            "Driver can control the kart while seated and properly restrained in the intended position.",
            "Seatbelts tightly fastened; no extenders — if a rider can't properly belt, they can't ride.",
            "Hands and arms inside at all times — reaching for ground/bumper = immediate ejection without refund; hands outside = one warning, then ejection.",
            "No bumping or erratic driving — one warning, then ejection.",
            "No phones or hearing-obstructing devices while riding.",
            "Everyone exits after each ride.",
          ]},
          { type: "prose", text: "If guests do not meet all safety requirements, or are unwilling to confirm understanding and willingness to follow the rules, they cannot ride. Explain why, escort them from the ride, and contact a manager if needed." },
        ],
      },
      {
        key: "opening", heading: "Opening the track & starting karts",
        points: [
          "Confirm the Daily Equipment & Track inspections are done and the AGM on duty has signed off.",
          "Stage fire extinguishers; gather tools; clean and organize the track and surrounding area.",
        ],
        detail: [
          { type: "steps", title: "Opening the track", items: [
            "Confirm Daily Equipment & Track Pre-Operation Inspections are complete and AGM-signed.",
            "Check and fuel karts as needed.",
            "Place fire extinguishers in designated spots around the track and pit.",
            "Gather/verify tools in place: radio, megaphone, Kartrol remote, j-hook, first aid kit, spill kit, guest vests, hair ties.",
            "Ensure the track and surrounding area are clean and organized.",
          ]},
          { type: "steps", title: "Starting karts (karts off when no guests waiting)", items: [
            "Locate the red on/off switch on the back of the kart; switch it on.",
            "Locate the choke (small white switch) and pull it toward you — don't bump the gas lever directly below it.",
            "Locate the pull string above the on/off switch; pull once (like a lawnmower) to start.",
            "Turn the choke off.",
            "If it doesn't start, pull again; repeat as needed.",
            "If it still won't start, jack up the rear (helpful in cold weather when clutch oil is thick): place the jack under the back and push down using leg strength.",
            "Once started, return the pull string to its starting position and move the choke as far forward as possible for full speed.",
          ]},
        ],
      },
      {
        key: "loading", heading: "Loading, screening & pre-ride checks",
        points: [
          "Read the safety script word-for-word; screen every rider; get verbal 'yes' + thumbs-up.",
          "Per-kart pre-ride checks; lock both gates before stepping away.",
        ],
        detail: [
          { type: "steps", title: "Loading sequence", items: [
            "Use the megaphone to read the safety script word for word, pausing for eye contact.",
            "Open and stand in front of the gate to keep control while letting guests on.",
            "Confirm the waiver wristband.",
            "Check height (Driver 54\", Passenger 33\"–53\").",
            "If carrying a passenger, ask the driver 'is this your child?'; if not, confirm they're 18+ or the guardian.",
            "Check all safety requirements (height, age, hair, shoes, clothing, demeanor, abilities).",
            "Take/scan the wristband or tear tickets into the ticket box immediately — never hold tickets or pocket them.",
            "Get a verbal 'yes' AND thumbs-up from each rider confirming understanding and agreement.",
            "Instruct each rider to a specific kart, starting at position A1.",
            "Once all riders are in, close and lock the gate.",
          ]},
          { type: "script", title: "Say to the waiting line", lines: [
            "Each race is approximately 5 minutes; while you're waiting, please familiarize yourself with the rules and instructions on our signs, and listen to the safety briefing playing overhead.",
          ]},
          { type: "checklist", title: "Per-kart pre-ride checks", items: [
            "Driver seatbelt tight and secure (green tabs tighten, orange loosen).",
            "Driver can reach the pedals with bent legs.",
            "Gas cap secure.",
            "Tires not flat.",
            "Bumpers and d-rubbers intact.",
          ]},
          { type: "script", title: "Say while checking", lines: [
            "To driver: keep your hands in the kart at all times and observe all safety instructions; when I call the race in, enter the pit slowly, do not bump, and stay seated with your seatbelt fastened until I tell you to remove it and stand up.",
            "To passenger: keep your hands inside the kart at all times; never place your hand on the bumper (show the bumper).",
            "To driver of a passenger: please make sure your passenger keeps hands and arms inside the kart at all times for their safety.",
          ]},
          { type: "script", title: "Final briefing at the front of karts", lines: [
            "Please remember to keep hands inside the kart at all times.",
            "Absolutely no bumping — if you bump I'll call the race in and you'll lose your racing privileges without refund.",
            "When I call the race in, drive into the pit slowly and cautiously, do not bump, and stay seated and belted until I tell you to stand up.",
            "Observe all instructions while racing. Let's go racing!",
          ]},
          { type: "warn", text: "Ensure BOTH the exit and entrance gates are locked before stepping away." },
        ],
      },
      {
        key: "kartrol", heading: "Kartrol (safety speed-control system)",
        points: [
          "Five controls: Pit On, Pit Off, Slow, Medium, Fast. Press and hold 2–3 seconds; never repeatedly tap.",
          "Slow MUST be engaged for any incident and BEFORE stepping on the track; confirm karts actually slowed.",
          "Never run without Kartrol; never enter unless on Slow; never turn your back to moving karts.",
        ],
        detail: [
          { type: "prose", text: "Kartrol ('podium' or 'remote') remotely reduces kart speed. We use it to slow karts in their final lap into the pit at a safe speed, to prevent accidents and spinouts, and to slow karts before an attendant enters the track. On the Road track the controls are on the podium; remotes are shorter-range and slow specific karts/areas." },
          { type: "steps", title: "The five controls", items: [
            "Pit On: engages the pit loop — karts auto-slow passing it (on the north side by the back patio fence). Used to bring the race in.",
            "Pit Off: turns the pit loop off so karts won't slow passing it — it does NOT speed karts back up.",
            "Slow: slows karts. Engage for any incident and before any attendant enters the track; confirm karts slowed first.",
            "Medium: partially slows for giving instructions from a safe zone — never enter the track on Medium.",
            "Fast: returns to full speed — only after the situation is resolved and all attendants are in safe zones.",
          ]},
          { type: "steps", title: "Engaging Kartrol", items: [
            "Press and hold the proper button 2–3 seconds; do not repeatedly push.",
            "After releasing, observe that karts respond at the selected speed.",
            "If a single kart won't respond: whistle/hand-signal it in safely and pull it for repair until fixed and tested.",
            "If NONE respond: call a supervisor; from a safe zone use whistles/hand signals to slowly bring all karts in; close the track until Kartrol is fixed and tested.",
          ]},
          { type: "warn", text: "NEVER run the track if Kartrol isn't working. NEVER enter the track unless Kartrol is engaged on SLOW. ALWAYS stand in a safe zone with eyes on the track when Kartrol isn't engaged; never turn your back to moving karts." },
        ],
      },
      {
        key: "during-race", heading: "Monitoring the race & spinouts",
        points: [
          "Watch for fast/slow karts, mechanical issues, bumping, hands outside, hair/clothing coming loose.",
          "For spinouts: Kartrol Slow, confirm karts slowed, then fix with the J-hook (legs not back).",
        ],
        detail: [
          { type: "checklist", title: "Watch for during the race", items: [
            "Any kart obviously faster or slower than the others — tag and remove from operation.",
            "Other signs of mechanical issues — tag and remove.",
            "Bumping or erratic driving — whistle warning, call in on second warning.",
            "Hands outside the kart — whistle; if reaching for the ground, call in immediately; otherwise call in on second warning.",
            "Spinout risk: a kart turning wide while another turns into its back bumper — whistle and warn, call in on second warning.",
            "Hair or clothing coming unsecured — whistle to immediately signal, stop, and correct.",
          ]},
          { type: "steps", title: "If there's a spinout or accident", items: [
            "Use the Kartrol podium or remote to Slow karts before entering the track.",
            "Observe to confirm karts have slowed before stepping on.",
            "Fix the spinout with the J-hook — use legs not back; never swing the j-hook.",
            "Only be on the track during a race for a spinout or to retrieve a lost item, with Kartrol on Slow.",
          ]},
        ],
      },
      {
        key: "bringing-in", heading: "Bringing the race in & closing",
        points: [
          "RA1 engages Pit on the final-lap red light, plays the pit message, signals the team.",
          "Direct 2-seaters to lane A, singles to lane B; riders stay belted until told to exit.",
          "Closing: turn karts off first; bring in the ticket box; cover podiums in rain.",
        ],
        detail: [
          { type: "steps", title: "Bringing the race in", items: [
            "RA1: when the podium red light turns on, press Pit to slow karts as they enter the final lap.",
            "RA1: turn on the pit message, then signal the team (long whistle, finger up, circular motion).",
            "RA2: return to the pit, stand in the safe zone, direct 2-seaters to lane A and singles to lane B.",
            "RA3 & RA4: direct karts back, confirm they've slowed, help park safely.",
            "Once all karts are stopped, direct drivers to remove seatbelts, stand, and exit.",
            "After the race, clear debris from the track (one attendant walks the track while another preps the next group).",
          ]},
          { type: "steps", title: "Closing the track", items: [
            "Always turn karts off first — never leave the track unattended with karts running.",
            "Bring in the ticket box.",
            "If closing for rain, cover both podiums with black trash bags.",
          ]},
        ],
      },
      {
        key: "inspection", heading: "Daily inspections (equipment & track)",
        points: [
          "Pre-op visual + test drive on every kart; never 'pencil whip' a checklist.",
          "Between races, watch for fast/slow karts, brake issues, and Kartrol failures.",
        ],
        detail: [
          { type: "prose", text: "A pre-operation inspection must be completed on each kart before use each day. Your trainer demonstrates each check and observes you performing it. Never put a kart on the track you suspect isn't in proper working order." },
          { type: "checklist", title: "Daily equipment — visual inspection", items: [
            "Record kart hours.",
            "Fuel system: no leaks from tank/cap/filter/vents/fittings; remove cap and inspect the gasket for an even filler-neck imprint and damage.",
            "Covers & guards: steering cover, axle cover, pulley guard, fenders, body properly installed.",
            "Body mounts: no looseness or improper fastening.",
            "Seat belts: no frayed webbing or loose stitching; buckle and adjustment work.",
            "Pads: seat, seat back, seat belt, roll bar, steering wheel and post pads undamaged.",
            "Tires & wheels: wear lines visible, pressure 40–50 PSI, lug nuts/studs tight.",
            "All nuts & bolts: none loose or missing.",
            "Steering: front wheels aligned (same direction).",
            "Starter rope: no fraying; air-intake screen clear of foreign matter.",
            "Roll bar: no looseness.",
            "D-rubbers & bumper system: no damage; no loose/hanging bolts.",
            "Warning decals: all present and legible.",
          ]},
          { type: "checklist", title: "Daily equipment — test drive", items: [
            "On/off switch secured and works to turn the kart on and off.",
            "Accelerator: smooth operation and full return when released.",
            "Brakes: check pedal travel and proper action during the test drive.",
            "Steering: smooth, precise full-left to full-right; no jerkiness or abnormal sounds.",
            "Remote shut-off: pit loop slows the kart past the transponder; remote slowdown slows it; remote stop stops it.",
          ]},
          { type: "checklist", title: "Daily track walk", items: [
            "Surface: no cracks, uneven areas, debris, oil, or slippery substances.",
            "Barriers: integrity of barriers/guardrails; securely fastened and aligned; replace tires that can't absorb impact.",
            "Signage: all safety/directional/instructional signs present, visible, in good condition.",
            "Emergency exits: clearly marked, accessible, unobstructed, open easily.",
          ]},
          { type: "checklist", title: "Operational equipment & surrounding area", items: [
            "Kartrol: test on every kart (Slow, then pit loop); pull any that don't respond; if none work, the track does not operate.",
            "Overhead speaker: safety message plays and is audible in the pit and waiting areas.",
            "Megaphone: working.",
            "Cleanliness: line/waiting area and pit clear of debris and garbage.",
            "Signage present: Height, Rules of the Road, Rotating Parts Warning, Amusement Risk.",
            "Fire extinguishers available, placed, charge indicator in the green.",
            "First aid kit stocked: bandages, gauze, tape, antiseptic, scissors, gloves, eye wash, hand sanitizer.",
          ]},
        ],
      },
      {
        key: "surrounding", heading: "Surrounding area",
        points: [
          "Includes the track, safe zone, pit, and line/waiting area — inspect before and throughout the day.",
        ],
        detail: [
          { type: "prose", text: "The Track: inspect for cracks, loose objects, debris, dented/broken railing, and railing tires needing replacement — before opening, at intervals, and after any kart-railing collision. Only be on the track during a race for a spinout or lost item, with Kartrol on Slow." },
          { type: "prose", text: "The Safe Zone: any area surrounded by railing — where you stand during races, protected from karts while able to grab riders' attention. Inspect for trip/fall hazards and debris." },
          { type: "prose", text: "The Pit: four lanes where karts stage when not running (entrance south, exit north). Out-of-service karts stage at the back. Inspect for trip/fall hazards, signage, egress, debris, fire extinguishers, and working communication equipment." },
          { type: "prose", text: "The Line: where guests wait to enter the pit. Inspect for trip/fall hazards, sharp edges/catch points on fence/railing, signage, and egress." },
        ],
      },
      {
        key: "fueling", heading: "Fueling",
        points: [
          "Goggles + gloves + long sleeves; karts off; no smoking; fill to ~1\" from top; use a funnel; double-check caps.",
          "Spill: contact a supervisor and absorb with kitty litter.",
        ],
        detail: [
          { type: "steps", title: "Fueling procedure", items: [
            "Be familiar with the Gasoline Safety Data Sheet, clean-up procedures, first-aid steps, and required PPE.",
            "Stage karts in the correct fueling lanes (Road: lanes A & B), leaving a walkway for you and the gas caddy; turn karts off.",
            "Get the gas caddy from the approved storage area.",
            "Wear PPE: goggles and long nitrile gloves; use a headlamp at night.",
            "Confirm fire extinguisher, eye wash, first aid kit, and spill kit are in the area.",
            "Remove the gas cap.",
            "Insert the caddy spout; fuel to ~1 inch from the top to allow for expansion; release the trigger and let drips fall into the tank before removing.",
            "Replace and screw the gas cap back on.",
            "Double-check that all caps are secure (second person verifies if two are fueling).",
            "Return the caddy/equipment/PPE to storage; alert a manager if the caddy needs a refill.",
          ]},
          { type: "warn", text: "Gasoline is extremely flammable — absolutely no smoking in the fueling area (guests and employees). Turn karts off before fueling. Only use approved fuel and never add anything to it. On a spill, contact a supervisor and absorb with kitty litter." },
        ],
      },
      {
        key: "signals", heading: "Whistles & hand signals",
        points: [
          "Whistles get attention to inform or stop unsafe behavior and to call the race in.",
        ],
        detail: [
          { type: "checklist", title: "Hand signals", items: [
            "Pointer finger up — speed karts up.",
            "Pointer finger down — slow karts down.",
            "A 'zero' with your hand — ride is full / zero karts available.",
            "Pointer finger up + circular motion — bring the race in.",
            "Pump one or both hands, palms down — signal guests to slow down.",
          ]},
        ],
      },
    ],
  },

  sprint: {
    title: "Sprint",
    intro:
      "Our most exhilarating wheel-to-wheel track with 16 two-seater karts, for adults and older children. Minimum one operator (two when busy). One operator must always have eyes on the track. The track shall NOT operate if Kartrol is not working properly.",
    sections: [
      {
        key: "staffing", heading: "Staffing",
        points: ["Minimum one operator; two when busy.", "With two: one observes from the podium; the second stages empty karts and briefs the line."],
        detail: [
          { type: "steps", title: "With two attendants", items: [
            "One attendant remains at the podium observing the race.",
            "The second moves empty karts forward so on-track karts can stage properly when called in.",
            "When there are lines, the second briefs guests waiting in line during the race.",
          ]},
        ],
      },
      {
        key: "rider-requirements", heading: "Rider requirements",
        points: [
          "Ride alone: 58\"+. Passenger: 40\"+ and able to follow directions; under 40\" may not ride at all.",
          "Driver with a passenger: 18+ or the passenger's guardian.",
          "Hair shoulder-to-waist: secure above shoulders AND wear a vest for visibility.",
        ],
        detail: [
          { type: "checklist", title: "Full rider rule set", items: [
            "Waiver wristband confirmed.",
            "Ride alone: 58 inches or taller (measure within/above the red line).",
            "Passenger: at least 40 inches and able to follow directions; under 40\" may not ride in any capacity.",
            "Driver with passenger: 18+ or the passenger's legal guardian.",
            "Seatbelts tight (green tightens, orange loosens); no extenders.",
            "Hands and arms inside at all times — reaching for ground/bumper = immediate ejection; hands out = one warning.",
            "No bumping/erratic driving — one warning then ejection.",
            "No phones or hearing-obstructing devices.",
            "Provide a vest to riders with hair between shoulder and waist length.",
          ]},
          { type: "toolkit", lines: [
            "Thank you for wearing this vest while you ride — it lets us easily spot riders with longer hair so we can watch for hair coming loose during the race.",
          ]},
        ],
      },
      {
        key: "starting", heading: "Opening & starting karts",
        points: ["Confirm inspections + AGM sign-off; fuel as needed; stage tools.", "Start karts with the red switch, choke, and pull string."],
        detail: [
          { type: "steps", title: "Starting karts", items: [
            "Red on/off switch to on.",
            "Pull the choke (small white switch) toward you.",
            "Pull the start string (like a lawnmower).",
            "If it doesn't start, pull the choke all the way back and pull the string again.",
            "If it still won't start, jack up the rear (see jack instructions) using leg strength.",
            "Once started, return the string to start position and move the choke fully forward for full speed.",
          ]},
        ],
      },
      {
        key: "loading", heading: "Loading, staging & checks",
        points: ["Read the safety script; stage A1 → all A's → B1; get verbal yes + thumbs-up; per-kart checks; lock both gates."],
        detail: [
          { type: "steps", title: "Loading sequence", items: [
            "Megaphone safety script word-for-word, pausing for eye contact.",
            "Stand at the gate to keep control while loading.",
            "Confirm wristband; check height (Driver 58\", Passenger 40\").",
            "If carrying a passenger, confirm the driver is 18+ or guardian.",
            "Check all safety requirements; provide vests for shoulder-to-waist hair.",
            "Tear tickets into the box immediately — never pocket tickets.",
            "Get verbal 'yes' + thumbs-up from each rider.",
            "Assign karts starting A1, work all A's, then B1 onward.",
            "Close and lock the gate.",
          ]},
          { type: "checklist", title: "Per-kart pre-ride checks", items: [
            "Driver seatbelt tight (green tightens, orange loosens).",
            "Driver reaches pedals with bent legs.",
            "Gas cap secure.",
            "Tires not flat.",
            "Bumpers and d-rubbers intact.",
          ]},
          { type: "script", title: "Final briefing", lines: [
            "Keep hands inside the kart at all times.",
            "Absolutely no bumping — if you bump I'll call the race in and you'll lose your racing privileges without refund.",
            "When I call the race in, drive into the pit slowly, do not bump, and stay belted until I tell you to stand up.",
            "As you enter the track, test your brake so you know where it is. Let's go racing!",
          ]},
          { type: "warn", text: "Lock both gates before stepping away." },
        ],
      },
      {
        key: "kartrol", heading: "Kartrol",
        points: [
          "Five controls; press and hold 2–3 sec. Slow before entering; confirm karts slowed. Never enter on Medium.",
          "Never run without Kartrol; pre-op test every kart.",
        ],
        detail: [
          { type: "steps", title: "The five controls", items: [
            "Pit On: pit loop auto-slows karts passing it — used to bring the race in.",
            "Pit Off: turns the pit loop off; does NOT speed karts up.",
            "Slow: engage for incidents and before entering; confirm karts slowed.",
            "Medium: partial slow for instructions from a safe zone — never enter on Medium.",
            "Fast: full speed only after resolution with all attendants in safe zones.",
          ]},
          { type: "steps", title: "Pre-op Kartrol test", items: [
            "Test every single kart on Slow.",
            "Return to fast, engage the pit loop, and confirm each kart slows passing it.",
            "Pull any kart that doesn't respond; if none respond, the track does not operate.",
          ]},
          { type: "warn", text: "Never run the track if Kartrol isn't working. Never enter unless on Slow. Never turn your back to moving karts." },
        ],
      },
      {
        key: "bringing-in", heading: "Bringing the race in & spinouts",
        points: ["Final-lap red light → Pit; play pit message; whistle in; alternate lanes A/B.", "Spinouts: slow, confirm, fix with J-hook."],
        detail: [
          { type: "steps", title: "Bringing in", items: [
            "On the podium red light, press Pit to slow karts into the final lap.",
            "Turn on the pit message; whistle drivers in.",
            "From the safe zone, alternate directing karts to lane A and lane B.",
            "Once all karts stop, direct drivers to unbelt, stand, and exit.",
            "Clear track debris after the race.",
          ]},
          { type: "steps", title: "Spinout / accident", items: [
            "Use the podium or remote to slow karts before entering.",
            "Confirm karts slowed.",
            "Fix spinouts with J-hooks (legs not back).",
          ]},
        ],
      },
      {
        key: "fueling", heading: "Fueling",
        points: ["Goggles + gloves; karts off; ~1\" from top; funnel; double-check caps. Sprint gas caps are at the back of the kart."],
        detail: [
          { type: "warn", text: "No smoking in the fueling area; turn karts off; use only approved fuel; never overfill. On a spill, contact a supervisor and absorb with kitty litter." },
        ],
      },
    ],
  },

  euro: {
    title: "Euro Track (Euro Certified)",
    intro:
      "High-speed track requiring helmets and head socks. Minimum one operator (two when busy). One operator must always have eyes on the track. The track shall NOT operate if Kartrol is not working properly. Stricter hair rules apply.",
    sections: [
      {
        key: "hair-rules", heading: "Hair rules (stricter on Euro)",
        points: [
          "Hair LONGER than waist-length may NOT ride Euro at all — never make an exception.",
          "Hair shoulder-to-waist: secure above shoulders AND wear a safety vest.",
          "If hair is secured and length is unclear, ask the guest to unsecure it to verify.",
        ],
        detail: [
          { type: "prose", text: "This is a controversial policy; guests are often upset and sometimes try to sneak by. It is your responsibility to never allow a guest with hair waist-length or longer on the ride. Telling guests no is never easy — but it is far harder to tell their family they've been catastrophically injured." },
          { type: "toolkit", lines: [
            "Your/your child's hair is beautiful and we want it to stay that way. Unfortunately it's not safe to ride with hair of that length — even when secured, hair can come loose during the ride and be caught in moving parts.",
            "We ask guests with hair between shoulder and waist length to wear a vest so we can identify them on the track and catch loose hair quickly to stop and re-secure it.",
          ]},
        ],
      },
      {
        key: "rider-requirements", heading: "Rider requirements",
        points: ["60\"+ to ride alone; at least 14 years old (proof of age if asked).", "No seatbelt extenders; hands and arms inside at all times."],
        detail: [
          { type: "checklist", title: "Full rider rule set", items: [
            "Waiver wristband confirmed.",
            "60 inches or taller to ride alone.",
            "At least 14 years old; able to provide proof of age if asked.",
            "Can control the kart while properly restrained.",
            "No seatbelt extenders — if they can't belt properly, they can't ride.",
            "Hands and arms inside at all times.",
          ]},
        ],
      },
      {
        key: "helmets", heading: "Helmets & head socks",
        points: ["Bring helmets to the track and confirm they work.", "Give each rider a clean head sock; set up a dirty head-sock bin."],
        detail: [
          { type: "steps", title: "Setup", items: [
            "Bring helmets to the track; ensure they're in working order.",
            "Set up the clean/dirty head-sock station.",
            "Provide a clean head sock to each rider; used socks go in the dirty bin.",
          ]},
        ],
      },
      {
        key: "kartrol", heading: "Kartrol / remote slow-shutoff",
        points: ["Same five controls; Slow before entering; confirm karts slowed; never enter on Medium.", "Tires sit against the inner line; reset after any impact."],
        detail: [
          { type: "steps", title: "Controls & rules", items: [
            "Pit On / Pit Off / Slow / Medium / Fast — press and hold 2–3 seconds.",
            "Slow before entering for any incident; confirm karts slowed.",
            "Never enter on Medium; Fast only after resolution with attendants in safe zones.",
            "If a kart won't respond, pull it; if none respond, close the track and call a supervisor.",
          ]},
          { type: "prose", text: "Track tires should always sit against the inner line of the track (your supervisor will show you). Inspect after any kart-tire impact and reset any moved tires." },
          { type: "warn", text: "Never run without Kartrol; never enter unless on Slow; never turn your back to moving karts." },
        ],
      },
      {
        key: "timing", heading: "Timing board",
        points: ["Euro is NOT head-to-head; a working timing board helps prevent guests racing each other.", "Test the timing board during pre-op."],
        detail: [
          { type: "prose", text: "Because Euro is not a head-to-head race, a working timing board showing kart numbers and times helps prevent guests from treating it as one. Confirm it displays correctly during pre-op." },
        ],
      },
      {
        key: "loading", heading: "Loading & checks",
        points: ["Verify wristband, height, age, hair rules, footwear; helmet + clean head sock on.", "Per-kart checks; lock the entrance gate."],
        detail: [
          { type: "checklist", title: "Per-kart pre-ride checks", items: [
            "Seatbelt tight (green tightens, orange loosens).",
            "Reaches pedals with bent legs.",
            "Gas cap secure.",
            "Tires not flat; bumpers/d-rubbers intact.",
          ]},
          { type: "script", title: "While checking seatbelts", lines: [
            "All riders must keep hands and arms inside the kart at all times.",
          ]},
          { type: "warn", text: "Ensure the entrance gate is locked before stepping away." },
        ],
      },
      {
        key: "fueling", heading: "Fueling",
        points: ["Goggles + gloves; karts off; no smoking; ~1\" from top; funnel; double-check caps."],
        detail: [
          { type: "warn", text: "Use only approved fuel; never overfill. On a spill, contact a supervisor and absorb with kitty litter." },
        ],
      },
    ],
  },

  kiddieKart: {
    title: "Kiddie Kart",
    intro:
      "Kiddie Land go-kart track for our smallest guests (38\"–53\"... riders 33\"–under 54\" per the rule set). Most riders cannot read and have never driven — extra vigilance required. Requires one operator who must always have eyes on the track from a safe zone, ready to act with urgency.",
    sections: [
      {
        key: "staffing", heading: "Staffing & attention",
        points: ["One operator, always eyes on the track from a safe zone.", "Watch for children exiting karts unexpectedly; the drivers are little kids who may not react as expected."],
        detail: [
          { type: "prose", text: "Most Kiddie Track guests are unable to read and have never driven anything. Be aware of your audience and take extra precautions. Use your whistle, use your voice, stay in the safe zone, pay attention, and be ready to move out of the way." },
        ],
      },
      {
        key: "height-age", heading: "Rider requirements",
        points: [
          "Riders 33\"–under 54\", measured within/above the red line.",
          "Must be able to follow the instruction to buckle their seatbelt — a test of following directions.",
          "Seatbelt tight (no extenders); hands and arms inside at all times.",
        ],
        detail: [
          { type: "checklist", title: "Full rider rule set", items: [
            "Confirmed waiver wristband.",
            "Hair shoulder-length or longer secured above the shoulders.",
            "Loose clothing removed; proper footwear.",
            "Minimum 33\" and less than 54\", within/above the red line.",
            "Capable of controlling the kart while seated and restrained.",
            "Able to follow the instruction to buckle the seatbelt — if not, they cannot ride.",
            "Seatbelt tightly fastened; no extenders.",
            "Hands and arms inside at all times.",
            "No phones/hearing-obstructing devices (hearing aids permitted).",
            "Everyone exits after each ride.",
          ]},
        ],
      },
      {
        key: "starting", heading: "Opening & starting karts",
        points: ["Confirm inspections + AGM sign-off; if not trained in fueling, hand it off.", "Start with red switch, choke, pull string."],
        detail: [
          { type: "steps", title: "Opening", items: [
            "Confirm Daily Equipment & Track inspections and AGM sign-off.",
            "Check/fuel karts (must be fueling-trained; otherwise have a trained operator do it).",
            "Place fire extinguishers; gather tools (radio, megaphone, j-hook, first aid kit, spill kit, hair ties).",
            "Ensure the track and surrounding area are clean and organized.",
          ]},
          { type: "steps", title: "Starting karts", items: [
            "Red on/off switch on.",
            "Pull the choke toward you (don't bump the gas lever below).",
            "Pull the start string once.",
            "Turn the choke off; repeat the pull if needed.",
            "If still not starting, jack up the rear with leg strength.",
            "Return the string to start position; choke fully forward for full speed.",
          ]},
        ],
      },
      {
        key: "operating", heading: "Operating & bringing in",
        points: ["Read the script; get a thumbs-up; direct one driver at a time into the pit with a cone behind each kart."],
        detail: [
          { type: "script", title: "Per-kart instruction", lines: [
            "If you hear me blow the whistle, take your foot off the gas and look to me for instructions.",
            "Keep your hands on the wheel at all times; when the race ends, enter the pit slowly, do not bump, and stay belted until I tell you to stand up.",
          ]},
          { type: "script", title: "Confirm understanding", lines: [
            "Can you give me a thumbs up to show you understand and can follow the rules I just went over? (Get a thumbs up from each rider.)",
          ]},
          { type: "steps", title: "Bringing the race in", items: [
            "When the timer signals the end, whistle and signal the final lap.",
            "From the safe zone, direct one driver at a time into the pit, placing a cone behind each kart to prevent impacts.",
            "Once stopped, place accelerator covers from the last kart forward, then direct drivers to unbelt and exit.",
          ]},
          { type: "steps", title: "Spinout / accident", items: [
            "Use the whistle to instruct drivers.",
            "Guide drivers past the spinout and have them slow or stop.",
            "Fix with the j-hook, staying in the safe zone where possible — never with your back to oncoming karts.",
          ]},
        ],
      },
      {
        key: "fueling", heading: "Fueling",
        points: ["Must be fueling-trained; goggles + gloves; karts off; no smoking; never overfill; use a funnel."],
        detail: [
          { type: "warn", text: "No smoking in the fueling area; turn karts off; only approved fuel; never overfill. On a spill, contact a supervisor and absorb with kitty litter." },
        ],
      },
    ],
  },

  // Kiddie Land rides without separate detailed chapters — grounded in general rules.
  slide: {
    title: "Slide",
    intro: "Kiddie Land attraction. Review with a trainer before the certification quiz. Apply all universal guest rules.",
    sections: [
      { key: "dispatch", heading: "Dispatch & spacing",
        points: ["Maintain proper spacing between dispatches.", "Never send a rider until the lane/landing below is clear."],
        detail: [{ type: "steps", title: "Dispatch", items: [
          "Confirm the previous rider has cleared the bottom lane/landing.",
          "Confirm the rider is in the correct riding position.",
          "Release with proper spacing — never bunch dispatches even when busy.",
        ]}],
      },
      { key: "guest-rules", heading: "Guest screening",
        points: ["Waiver wristband, hair secured, loose clothing removed, proper footwear, posted height/position."],
        detail: [{ type: "prose", text: "Apply the full universal guest rule set from the General module. Enforce posted height and riding-position requirements." }],
      },
      { key: "emergency", heading: "Stoppage & emergencies",
        points: ["Stop dispatching on any safety risk; address the hazard before resuming; call a supervisor for incidents."],
        detail: [],
      },
    ],
  },

  tornado: {
    title: "Tornado",
    intro: "Kiddie Land attraction. Review with a trainer before the certification quiz. Apply all universal guest rules.",
    sections: [
      { key: "restraints", heading: "Restraint checks",
        points: ["Check restraints for every rider, every dispatch — never skip when busy.", "Never exceed posted vehicle capacity."],
        detail: [{ type: "steps", title: "Per-dispatch", items: [
          "Verify each restraint is secured and locked for every rider.",
          "Confirm rider count is within posted capacity.",
          "Resolve any restraint that won't lock before dispatching.",
        ]}],
      },
      { key: "guest-rules", heading: "Guest screening",
        points: ["Waiver wristband, hair, loose clothing, footwear, height — all verified before boarding."],
        detail: [{ type: "prose", text: "Apply the full universal guest rule set from the General module." }],
      },
      { key: "emergency", heading: "Stoppage & emergencies",
        points: ["Use the e-stop for any rider-safety risk; resume only after the hazard is cleared."],
        detail: [],
      },
    ],
  },

  dragon: {
    title: "Dragon",
    intro: "Kiddie Land attraction. Review with a trainer before the certification quiz. Apply all universal guest rules.",
    sections: [
      { key: "restraints", heading: "Lap bars & restraints",
        points: ["Lap bars down and locked, verified by the operator for each rider.", "If a vehicle fails to lock, remove it from service and report it."],
        detail: [{ type: "steps", title: "Per-dispatch", items: [
          "Lower and verify each lap bar is locked for every rider.",
          "Tug-test the restraint before dispatch.",
          "Remove any vehicle that fails to lock; never send it.",
        ]}],
      },
      { key: "guest-rules", heading: "Guest screening",
        points: ["Waiver wristband, hair secured, loose clothing removed, footwear, posted height."],
        detail: [{ type: "prose", text: "Apply the full universal guest rule set from the General module." }],
      },
      { key: "emergency", heading: "Stoppage & emergencies",
        points: ["Stop the ride for any safety risk and address before resuming."],
        detail: [],
      },
    ],
  },
};

/* ----------------------------------------------------------
   QUIZ BANKS
   Each question: { id, topic, q, choices:[...], correct:index }
   `topic` maps to a module section key (for "missed topics").
   Quizzes pull a randomized subset and shuffle answer order
   on every attempt, so no two attempts are identical.
   ---------------------------------------------------------- */

// General questions mixed into every ride quiz
const GENERAL_Q = [
  { id: "g1", topic: "universal-guest-rules", q: "A guest arrives without a waiver wristband but insists they signed online. What do you do?", choices: ["Let them ride this once", "Do not allow them to ride until a confirmed wristband is on", "Ask another guest to vouch", "Allow them if the line is short"], correct: 1 },
  { id: "g2", topic: "universal-guest-rules", q: "A guest has shoulder-length hair that is loose. Before riding they must:", choices: ["Tuck it into their shirt", "Secure it above the shoulders with a restraint", "Nothing, shoulder-length is fine", "Wear sunglasses"], correct: 1 },
  { id: "g3", topic: "universal-guest-rules", q: "Which footwear is acceptable to ride?", choices: ["Flip-flops", "Crocs with no backstrap", "Closed shoes, or sandals with the backstrap worn", "Bare feet"], correct: 2 },
  { id: "g4", topic: "universal-guest-rules", q: "A guest wears a religious head covering with long loose ends. You should:", choices: ["Allow it, it's religious", "Refuse and offer nothing", "Offer a safe loaner head covering via a manager", "Tuck the ends in for them"], correct: 2 },
  { id: "g5", topic: "universal-guest-rules", q: "You see a guest finish an alcoholic drink, then get in line. You should:", choices: ["Let them ride, they seem fine", "Remove their wristband and notify a supervisor", "Give one warning", "Ask them to wait 10 minutes"], correct: 1 },
  { id: "g6", topic: "incident-reporting", q: "A guest bumps their knee but says they're fine and don't want a report. You should:", choices: ["Do nothing since they declined", "Document your perspective and the facts anyway", "Only tell a coworker", "Make them sign a form"], correct: 1 },
  { id: "g7", topic: "incident-reporting", q: "What is the FIRST step when an incident occurs?", choices: ["Call 911", "Assess the scene and remove/isolate ongoing hazards", "Fill out paperwork", "Restart the ride"], correct: 1 },
  { id: "g8", topic: "incident-reporting", q: "A serious, possibly life-threatening injury occurs. You should:", choices: ["Drive them to a hospital yourself", "Call 911 immediately and give location/nature/hazards", "Wait for a manager before acting", "Administer first aid even if untrained"], correct: 1 },
  { id: "g9", topic: "safety-mindset", q: "The number one defense against accidents and injuries is:", choices: ["The equipment", "The warning signs", "You, the operator", "The waiver"], correct: 2 },
  { id: "g10", topic: "incident-reporting", q: "A situation almost caused an injury but didn't. You should:", choices: ["Ignore it since nothing happened", "Correct it and file a near-miss report", "Only mention it if asked", "Wait to see if it happens again"], correct: 1 },
  { id: "g11", topic: "guest-service", q: "An upset guest is denied a ride for long hair. Best first response:", choices: ["Argue the policy", "Stay calm, listen, empathize, explain the safety reason", "Call security", "Walk away"], correct: 1 },
  { id: "g12", topic: "universal-guest-rules", q: "Which guest should NOT ride?", choices: ["A guest wearing sunscreen", "A pregnant guest", "A guest with a name tag", "A guest with a water bottle in the car"], correct: 1 },
];

const QUIZ_BANKS = {
  road: [
    { id: "r1", topic: "staffing", q: "What is the MINIMUM number of operators required to run the Road track?", choices: ["One", "Two", "Three", "Four"], correct: 1 },
    { id: "r2", topic: "staffing", q: "During busy times, the Road track should be staffed with:", choices: ["One operator", "Two operators", "Three to four operators", "As many as want to help"], correct: 2 },
    { id: "r3", topic: "kartrol", q: "The Road track shall NOT operate if:", choices: ["It is cloudy", "The Kartrol controller is not working properly", "Fewer than 10 guests are in line", "The megaphone battery is low"], correct: 1 },
    { id: "r4", topic: "kartrol", q: "Before any attendant steps onto the track during racing, you must:", choices: ["Blow the whistle twice", "Engage Kartrol Slow and confirm karts actually slowed", "Turn the karts off", "Put on sunglasses"], correct: 1 },
    { id: "r5", topic: "kartrol", q: "What does the 'Pit Off' button do?", choices: ["Speeds karts back up", "Turns off the pit loop so karts won't slow passing it; it does NOT speed them up", "Stops all karts", "Ends the race"], correct: 1 },
    { id: "r6", topic: "kartrol", q: "How do you properly engage a Kartrol button?", choices: ["Tap it repeatedly", "Press and hold 2–3 seconds, then observe karts respond", "Hold it the entire race", "Press it once quickly"], correct: 1 },
    { id: "r7", topic: "kartrol", q: "You may enter the track when karts are slowed to MEDIUM.", choices: ["True", "False — never enter on medium", "Only if you're a trainer", "Only during volume"], correct: 1 },
    { id: "r8", topic: "kartrol", q: "When is it appropriate to engage FAST?", choices: ["Whenever the race is boring", "Only after the situation is resolved and all attendants are in safe zones", "As soon as a spinout starts", "When a guest asks to go faster"], correct: 1 },
    { id: "r9", topic: "kartrol", q: "If NONE of the karts respond to Kartrol, you should:", choices: ["Keep racing and watch closely", "Call a supervisor, safely bring all karts in from a safe zone, and close the track", "Switch to the remote and continue", "Speed them up to finish faster"], correct: 1 },
    { id: "r10", topic: "rider-requirements", q: "The minimum height to DRIVE alone on the Road track is:", choices: ["33 inches", "40 inches", "54 inches", "58 inches"], correct: 2 },
    { id: "r11", topic: "rider-requirements", q: "The minimum height for a PASSENGER on the Road track is:", choices: ["33 inches", "40 inches", "54 inches", "No minimum"], correct: 0 },
    { id: "r12", topic: "rider-requirements", q: "A 30-inch child wants to ride as a passenger. You should:", choices: ["Allow it with a guardian", "Not allow them to ride in any capacity", "Allow it if belted tightly", "Allow it on a slow setting"], correct: 1 },
    { id: "r13", topic: "rider-requirements", q: "An adult wants to carry a passenger who is not their child. The driver must be:", choices: ["Any age", "At least 16", "At least 18 or the passenger's legal guardian", "A season pass holder"], correct: 2 },
    { id: "r14", topic: "loading", q: "On the seatbelt harness, which tab tightens the belt?", choices: ["Orange", "Green", "Red", "Either one"], correct: 1 },
    { id: "r15", topic: "loading", q: "Before stepping away from the loaded karts, you must:", choices: ["Start the timer", "Lock both the entrance and exit gates", "Announce the lap count", "Refuel the lead kart"], correct: 1 },
    { id: "r16", topic: "loading", q: "What confirmation must you get from EVERY rider before racing?", choices: ["A signed form", "A verbal 'yes' and a thumbs-up that they understand and will comply", "Payment", "A photo"], correct: 1 },
    { id: "r17", topic: "spinouts", q: "The correct way to use a J-hook to fix a spinout is:", choices: ["Swing it for momentum", "Use your back to pull hard", "Use your legs, not your back; never swing it", "Have a guest help you"], correct: 2 },
    { id: "r18", topic: "spinouts", q: "When may an attendant be on the track during a race?", choices: ["Anytime to tidy up", "Only for a spinout or to retrieve a lost item, with Kartrol on Slow", "To chat with drivers", "Never, under any circumstance"], correct: 1 },
    { id: "r19", topic: "inspection", q: "Correct go-kart tire pressure is:", choices: ["20–30 PSI", "40–50 PSI", "60–70 PSI", "As high as possible"], correct: 1 },
    { id: "r20", topic: "fueling", q: "When fueling, you should fill the tank to:", choices: ["The very top", "About 1 inch from the top to allow for expansion", "Half full", "Until it overflows slightly"], correct: 1 },
    { id: "r21", topic: "fueling", q: "Required PPE for fueling includes:", choices: ["Just gloves", "Goggles, gloves, and long sleeves", "A helmet", "Nothing if you're quick"], correct: 1 },
    { id: "r22", topic: "fueling", q: "If gasoline spills, you should:", choices: ["Hose it down with water", "Contact a supervisor and absorb it with kitty litter", "Sweep it into a drain", "Ignore small spills"], correct: 1 },
    { id: "r23", topic: "signals", q: "An attendant points one finger up and makes a circular motion. This means:", choices: ["Speed the karts up", "Bring the race in", "The ride is full", "Slow down"], correct: 1 },
    { id: "r24", topic: "signals", q: "Making a 'zero' with your hand signals:", choices: ["Everything is okay", "The ride is full / zero karts available", "Stop immediately", "Speed up"], correct: 1 },
    { id: "r25", topic: "kartrol", q: "A single kart fails to slow when Kartrol is engaged. You should:", choices: ["Ignore it if it's just one", "Whistle/hand-signal it in safely and pull it for repair", "Speed everyone else up", "Tell the driver to brake harder next time"], correct: 1 },
    { id: "r26", topic: "rider-requirements", q: "Seatbelt extenders on the Road track are:", choices: ["Available on request", "Not offered — if a rider can't properly belt, they can't ride", "Only for adults", "Kept at the podium"], correct: 1 },
  ],

  sprint: [
    { id: "s1", topic: "staffing", q: "The minimum number of operators to run the Sprint track is:", choices: ["One", "Two", "Three", "Four"], correct: 0 },
    { id: "s2", topic: "rider-requirements", q: "Minimum height to ride ALONE on Sprint is:", choices: ["54 inches", "58 inches", "60 inches", "40 inches"], correct: 1 },
    { id: "s3", topic: "rider-requirements", q: "Minimum height for a Sprint PASSENGER is:", choices: ["33 inches", "40 inches", "54 inches", "58 inches"], correct: 1 },
    { id: "s4", topic: "rider-requirements", q: "A guest is 38 inches tall and wants to ride as a passenger. You:", choices: ["Allow with a guardian", "Do not allow them to ride at all", "Allow on the slow setting", "Allow if belted tightly"], correct: 1 },
    { id: "s5", topic: "kartrol", q: "Sprint shall not operate if:", choices: ["The timing board is off", "The Kartrol controller is not working properly", "There are fewer than 8 karts", "It's after dark"], correct: 1 },
    { id: "s6", topic: "kartrol", q: "During pre-op, how do you test Kartrol?", choices: ["Test only the lead kart", "Test every single kart on Slow and on the pit loop; pull any that don't respond", "Assume it works if it worked yesterday", "Test only when busy"], correct: 1 },
    { id: "s7", topic: "kartrol", q: "Before entering the track for an incident you must:", choices: ["Engage Slow and confirm karts slowed", "Engage Fast", "Engage Medium", "Just be quick"], correct: 0 },
    { id: "s8", topic: "loading", q: "Sprint karts are staged starting with:", choices: ["B1, then A1", "Position A1, working through all A's, then B1", "Whichever is closest", "The middle outward"], correct: 1 },
    { id: "s9", topic: "loading", q: "As drivers enter the track, you instruct them to:", choices: ["Accelerate fully", "Test the brake so they know where it is", "Honk", "Wave"], correct: 1 },
    { id: "s10", topic: "bringing-in", q: "When the podium red light comes on, you:", choices: ["End the race instantly with Slow", "Press Pit to slow karts as they enter the final lap", "Turn the karts off", "Engage Fast"], correct: 1 },
    { id: "s11", topic: "bringing-in", q: "When directing karts into the pit you should:", choices: ["Send them all to lane A", "Alternate lanes A and B", "Let drivers choose", "Stop them on the track"], correct: 1 },
    { id: "s12", topic: "rider-requirements", q: "On the seatbelt, the orange tab is used to:", choices: ["Tighten the belt", "Loosen the belt", "Release the kart", "Sound the horn"], correct: 1 },
    { id: "s13", topic: "rider-requirements", q: "Guests with hair between shoulder and waist length must:", choices: ["Cut their hair", "Secure it above the shoulders and wear a vest for visibility", "Ride only as passengers", "Wear a helmet"], correct: 1 },
    { id: "s14", topic: "spinouts", q: "A common spinout cause to watch for is:", choices: ["A kart braking too early", "A kart turning wide while another turns into its back bumper", "Two karts in the pit", "A slow kart in front"], correct: 1 },
    { id: "s15", topic: "spinouts", q: "Repeated bumping or erratic driving warrants:", choices: ["Immediate ejection, no warning", "A warning, then call the driver in on the second", "Nothing, it's a race", "Speeding the kart up"], correct: 1 },
    { id: "s16", topic: "loading", q: "A guest reaches a hand toward the ground while riding. You:", choices: ["Give two warnings", "Call them in immediately", "Ignore unless they fall", "Speed up the race"], correct: 1 },
    { id: "s17", topic: "fueling", q: "Sprint kart gas caps are located:", choices: ["Under the seat", "In the back of the kart", "On the steering column", "There are none"], correct: 1 },
    { id: "s18", topic: "fueling", q: "Before fueling any kart you must:", choices: ["Leave it idling", "Turn the kart off", "Rev the engine", "Remove the seat"], correct: 1 },
    { id: "s19", topic: "kartrol", q: "You may NOT enter the track when karts are on:", choices: ["Slow", "Medium", "Off", "Stopped"], correct: 1 },
    { id: "s20", topic: "staffing", q: "With two Sprint operators during a line, the second operator should:", choices: ["Take a break", "Stage empty karts and brief guests in line", "Stand at the exit", "Drive a kart"], correct: 1 },
    { id: "s21", topic: "loading", q: "Per-kart pre-ride checks include all EXCEPT:", choices: ["Seatbelt tight and reaches pedals", "Gas cap secure, tires not flat", "Bumpers/d-rubbers intact", "Guest's ticket price"], correct: 3 },
    { id: "s22", topic: "rider-requirements", q: "Seatbelt extenders on Sprint are:", choices: ["Offered on request", "Not offered; if a rider can't belt properly they can't ride", "Only for passengers", "At the podium"], correct: 1 },
  ],

  euro: [
    { id: "e1", topic: "hair-rules", q: "A guest's hair is LONGER than waist length. On Euro they:", choices: ["May ride with it secured", "May NOT ride Euro at all", "May ride as a passenger only", "May ride with a vest"], correct: 1 },
    { id: "e2", topic: "hair-rules", q: "Hair between shoulder and waist length on Euro requires:", choices: ["Nothing extra", "Securing above the shoulders AND a safety vest", "A helmet only", "Riding alone"], correct: 1 },
    { id: "e3", topic: "hair-rules", q: "A guest's hair is secured and you can't tell its length. You:", choices: ["Assume it's fine", "Ask them to unsecure it so you can verify length", "Deny them outright", "Let a guest decide"], correct: 1 },
    { id: "e4", topic: "rider-requirements", q: "Minimum height to ride alone on Euro is:", choices: ["54 inches", "58 inches", "60 inches", "48 inches"], correct: 2 },
    { id: "e5", topic: "rider-requirements", q: "Minimum age to ride Euro is:", choices: ["10", "12", "14", "16"], correct: 2 },
    { id: "e6", topic: "rider-requirements", q: "A guest claims to be old enough but looks young. You may:", choices: ["Take their word", "Ask for proof of age", "Deny everyone who looks young", "Let a manager guess"], correct: 1 },
    { id: "e7", topic: "helmets", q: "Each Euro rider must be provided:", choices: ["Sunglasses", "A clean head sock under a helmet", "Gloves", "A vest only"], correct: 1 },
    { id: "e8", topic: "helmets", q: "Used head socks should be:", choices: ["Reused on the next rider", "Placed in a dirty head-sock bin", "Thrown on the ground", "Washed at the podium"], correct: 1 },
    { id: "e9", topic: "timing", q: "Why is a working timing board important on Euro?", choices: ["It's decorative", "Euro is not head-to-head; the board helps prevent guests racing each other", "It controls kart speed", "It opens the gate"], correct: 1 },
    { id: "e10", topic: "kartrol", q: "Euro track tires should sit:", choices: ["Loosely anywhere", "Against the inner line of the track", "In the pit", "Stacked at corners"], correct: 1 },
    { id: "e11", topic: "kartrol", q: "After a kart-tire impact you should:", choices: ["Keep racing", "Inspect and reset any moved tires", "Speed up the karts", "Ignore it if no one fell"], correct: 1 },
    { id: "e12", topic: "kartrol", q: "Before entering the Euro track during an incident you must:", choices: ["Engage Slow and confirm karts slowed", "Engage Fast", "Engage Medium", "Just hurry"], correct: 0 },
    { id: "e13", topic: "kartrol", q: "Euro shall not operate if:", choices: ["The timing board flickers", "Kartrol is not working properly", "Fewer than 6 karts run", "It's windy"], correct: 1 },
    { id: "e14", topic: "rider-requirements", q: "Seatbelt extenders on Euro are:", choices: ["Provided", "Not offered; a rider who can't belt properly can't ride", "Only for tall guests", "Optional"], correct: 1 },
    { id: "e15", topic: "loading", q: "On the seatbelt, green tightens and orange:", choices: ["Tightens too", "Loosens", "Releases the kart", "Sounds the horn"], correct: 1 },
    { id: "e16", topic: "loading", q: "Before stepping away from loaded Euro karts you must:", choices: ["Lock the entrance gate", "Start the timer only", "Announce lap count", "Refuel"], correct: 0 },
    { id: "e17", topic: "staffing", q: "The minimum number of operators for Euro is:", choices: ["One", "Two", "Three", "Four"], correct: 0 },
    { id: "e18", topic: "helmets", q: "Part of Euro pre-op setup includes:", choices: ["Only checking fuel", "Bringing helmets to the track and setting up the head-sock station", "Removing the timing board", "Locking the pit"], correct: 1 },
    { id: "e19", topic: "kartrol", q: "You may enter the Euro track when karts are on Medium.", choices: ["True", "False — never enter on medium", "Only as a trainer", "Only during volume"], correct: 1 },
    { id: "e20", topic: "fueling", q: "When fueling Euro karts, fill to:", choices: ["The top", "About 1 inch from the top", "Half", "Until overflow"], correct: 1 },
    { id: "e21", topic: "rider-requirements", q: "All Euro riders must keep:", choices: ["Their visor up", "Hands and arms inside the kart at all times", "One hand waving", "Feet off the pedals on turns"], correct: 1 },
  ],
};

// Build a randomized quiz for a ride: shuffle the bank + general pool,
// take `count` questions, and shuffle each question's answer choices.
// Build a randomized quiz for a ride. If `bank` is provided (DB-backed), uses that.
// Otherwise falls back to hardcoded QUIZ_BANKS (legacy).
//
// Rules:
//   - All MANDATORY ride-specific questions are always included.
//   - Remaining slots are filled first with ride-specific (random),
//     then with universal questions (tagged to all 7 rides) capped at 3.
//   - If mandatory > count, all mandatory still go in (quiz slightly exceeds cap).
function generateQuiz(rideKey, count = 15, bank = null) {
  // Database-backed path
  if (bank && Array.isArray(bank)) {
    const allRides = ["slide", "tornado", "dragon", "kiddieKart", "road", "sprint", "euro"];
    const isUniversal = (q) => allRides.every((r) => (q.tags || []).includes(r));
    const matchesRide = (q) => (q.tags || []).includes(rideKey);

    const rideSpecific = bank.filter((q) => matchesRide(q) && !isUniversal(q));
    const universal = bank.filter(isUniversal);

    // Separate mandatory (ride-specific only — universal mandatory would apply to all rides which is rare)
    const mandatoryRide = rideSpecific.filter((q) => q.mandatory);
    const optionalRide = rideSpecific.filter((q) => !q.mandatory);

    // Shuffle the optional pools
    const shuffledOptional = shuffle(optionalRide);
    const shuffledUniv = shuffle(universal).slice(0, 3); // cap universal at 3

    // Fill order: mandatory first, then optional ride-specific, then universal
    // If mandatory >= count, we still include all of them (quiz may exceed `count`)
    const remaining = Math.max(0, count - mandatoryRide.length);
    const optionalSlots = Math.max(0, remaining - shuffledUniv.length);
    const picked = [
      ...mandatoryRide,
      ...shuffledOptional.slice(0, optionalSlots),
      ...shuffledUniv.slice(0, remaining - optionalSlots),
    ];

    // Final shuffle so mandatory questions aren't always at the top
    const finalPicked = shuffle(picked);
    return finalPicked.map((item) => {
      const order = shuffle(item.choices.map((_, i) => i));
      const choices = order.map((i) => item.choices[i]);
      const correct = order.indexOf(item.correct);
      return { id: item.id, topic: "", q: item.text, choices, correct };
    });
  }
  // Legacy hardcoded path
  const oldBank = QUIZ_BANKS[rideKey] || [];
  const pool = [...oldBank, ...GENERAL_Q];
  const shuffled = shuffle(pool);
  const picked = shuffled.slice(0, Math.min(count, shuffled.length));
  return picked.map((item) => {
    const order = shuffle(item.choices.map((_, i) => i));
    const choices = order.map((i) => item.choices[i]);
    const correct = order.indexOf(item.correct);
    return { id: item.id, topic: item.topic, q: item.q, choices, correct };
  });
}

function topicLabel(rideKey, topicKey) {
  const mod = MODULES[rideKey] || MODULES._general;
  const sec = (mod.sections || []).find((s) => s.key === topicKey)
    || (MODULES._general.sections || []).find((s) => s.key === topicKey);
  return sec ? sec.heading : topicKey;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}


/* ============================================================
   SEED DATA  (transcribed from the training board photo)
   Levels use board values; "T" preserved, blanks => "0"
   ============================================================ */
const SEED_EMPLOYEES = [
  ["Andy", "", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"3",sprint:"3",euro:"3" }],
  ["Armand", "", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"3",sprint:"3",euro:"0" }],
  ["Asia", "2025-04-05", { slide:"2",tornado:"2",dragon:"2",kiddieKart:"2",goKart:"T",sprint:"0",euro:"0" }],
  ["BK", "", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"2",sprint:"3",euro:"2" }],
  ["Dallas", "", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"3",sprint:"3",euro:"3" }],
  ["Dillon", "", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"3",sprint:"3",euro:"3" }],
  ["Eldon", "", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"2",sprint:"2",euro:"0" }],
  ["Erick", "", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"2",sprint:"0",euro:"0" }],
  ["Jacob", "", { slide:"2",tornado:"2",dragon:"2",kiddieKart:"2",goKart:"3",sprint:"3",euro:"3" }],
  ["James", "", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"3",sprint:"3",euro:"3" }],
  ["Kyle", "2025-04-05", { slide:"2",tornado:"2",dragon:"2",kiddieKart:"2",goKart:"T",sprint:"0",euro:"0" }],
  ["Lander", "2025-01-05", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"T",goKart:"0",sprint:"0",euro:"0" }],
  ["Leilah", "2025-01-05", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"T",goKart:"0",sprint:"0",euro:"0" }],
  ["Marlene", "", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"3",sprint:"3",euro:"0" }],
  ["Neal", "2025-04-05", { slide:"2",tornado:"2",dragon:"2",kiddieKart:"2",goKart:"T",sprint:"0",euro:"0" }],
  ["Raeann", "", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"2",sprint:"2",euro:"0" }],
  ["Sebastian G.", "", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"3",sprint:"T",euro:"0" }],
  ["Sebastian M.", "2025-01-05", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"3",sprint:"T",euro:"0" }],
  ["Tristen", "", { slide:"3",tornado:"3",dragon:"3",kiddieKart:"3",goKart:"3",sprint:"3",euro:"3" }],
  ["Xavier", "2025-04-05", { slide:"2",tornado:"2",dragon:"2",kiddieKart:"2",goKart:"T",sprint:"0",euro:"0" }],
  ["David", "2025-05-07", { slide:"2",tornado:"2",dragon:"2",kiddieKart:"2",goKart:"0",sprint:"0",euro:"0" }],
  ["Zyrahia", "2025-05-13", { slide:"T",tornado:"T",dragon:"T",kiddieKart:"T",goKart:"0",sprint:"0",euro:"0" }],
  ["Akeem", "2025-05-13", { slide:"T",tornado:"T",dragon:"T",kiddieKart:"T",goKart:"0",sprint:"0",euro:"0" }],
  ["Alexis", "2025-05-20", { slide:"T",tornado:"T",dragon:"T",kiddieKart:"0",goKart:"0",sprint:"0",euro:"0" }],
  ["Mouh", "2025-05-20", { slide:"T",tornado:"T",dragon:"T",kiddieKart:"T",goKart:"0",sprint:"0",euro:"0" }],
  ["Axl", "2025-05-26", { slide:"T",tornado:"T",dragon:"0",kiddieKart:"0",goKart:"0",sprint:"0",euro:"0" }],
  ["Kingston", "2025-05-27", { slide:"0",tornado:"0",dragon:"0",kiddieKart:"0",goKart:"0",sprint:"0",euro:"0" }],
  ["Mateo", "2025-05-27", { slide:"0",tornado:"0",dragon:"0",kiddieKart:"0",goKart:"0",sprint:"0",euro:"0" }],
  ["Natalia", "2025-05-27", { slide:"0",tornado:"0",dragon:"0",kiddieKart:"0",goKart:"0",sprint:"0",euro:"0" }],
];

function makeSeed() {
  // Migrate the photo-board values (numeric) into the new two-group scheme.
  // goKart -> road ; Kiddie Land: 1->T,2->L2,3->L3 ; Tracks: 1->T,2->L2,3->L4
  const mapKiddieLand = { "0": "0", "T": "T", "1": "T", "2": "L2", "3": "L3" };
  const mapTracks = { "0": "0", "T": "T", "1": "T", "2": "L2", "3": "L4" };
  const migrate = (old) => {
    const out = {};
    RIDES.forEach((r) => {
      const sourceKey = r.key === "road" ? "goKart" : r.key;
      const raw = old[sourceKey] ?? "0";
      out[r.key] = (r.group === "kiddieLand" ? mapKiddieLand : mapTracks)[raw] ?? "0";
    });
    return out;
  };
  return SEED_EMPLOYEES.map(([name, start, levels], i) => ({
    id: `emp-${i}-${name.replace(/\W/g, "")}`,
    name,
    startDate: start || "",
    levels: migrate(levels),
    retrain: {},
    observations: {},
    l4Checks: {},
    archived: false,
    schedule: [],
    history: [],
  }));
}

// Demo quizzes so the trainee dashboard (Asia → emp-2-Asia) shows content
function makeSeedQuizzes() {
  const asia = "emp-2-Asia";
  return [
    // a pending quiz she can take (training gate already signed)
    { id: "quiz-seed-1", empId: asia, empName: "Asia", ride: "kiddieKart", targetLevel: "L2",
      supervisor: "Dallas Cole", sentDate: addDays(todayISO(), -2), status: "sent",
      questions: generateQuiz("kiddieKart", 15), score: null, attempts: 0, missedTopics: [] },
    // a completed quiz with a couple missed topics
    { id: "quiz-seed-2", empId: asia, empName: "Asia", ride: "slide", targetLevel: "L2",
      supervisor: "Tristen Park", sentDate: addDays(todayISO(), -10), completedDate: addDays(todayISO(), -9),
      status: "passed_gaps", score: 87, attempts: 1,
      questions: [], missedTopics: ["guest-rules", "emergency"] },
  ];
}

/* ============================================================
   ROLES & USERS (prototype auth — NOT real security)
   ============================================================ */
const ROLES = {
  trainee:  { label: "Trainee", rank: 0 },
  trainer:  { label: "Trainer", rank: 1 },
  manager:  { label: "Manager", rank: 2 },
  agm_gm:   { label: "AGM / GM", rank: 3 },
  admin:    { label: "Admin", rank: 4 },
};
const roleRank = (r) => (typeof ROLES !== "undefined" ? (ROLES[r]?.rank ?? -1) : -1);

// Which roles can satisfy each required signer role on a sign-off
const ROLE_SATISFIES = {
  trainer_manager: ["trainer", "manager", "agm_gm", "admin"], // training gate
  employee: ["trainee", "trainer", "manager", "agm_gm", "admin"], // the employee signs as themselves
  manager: ["manager", "agm_gm", "admin"],
  agm_gm: ["agm_gm", "admin"],
  admin: ["admin"],
};

const SEED_USERS = [
  { id: "u-admin", username: "admin", password: "admin123", name: "Pat Quinn", title: "System Admin", role: "admin" },
  { id: "u-gm", username: "gm", password: "gm123", name: "Jordan Vega", title: "General Manager", role: "agm_gm" },
  { id: "u-agm", username: "agm", password: "agm123", name: "Sam Rivera", title: "Assistant GM", role: "agm_gm" },
  { id: "u-mgr", username: "manager", password: "mgr123", name: "Dallas Cole", title: "Manager", role: "manager" },
  { id: "u-trainer", username: "trainer", password: "train123", name: "Tristen Park", title: "Lead Trainer", role: "trainer" },
  { id: "u-emp", username: "trainee", password: "emp123", name: "Asia", title: "Ride Operator", role: "trainee", employeeId: "emp-2-Asia" },
];

const AUTH_KEY = "trainingAuth_v1";

function canActAs(user, requiredRole) {
  if (!user) return false;
  return (ROLE_SATISFIES[requiredRole] || []).includes(user.role);
}
function canSignAnything(user) {
  return user && roleRank(user.role) >= roleRank("trainer");
}
// Capability gates
const can = {
  addEmployee: (u) => u && roleRank(u.role) >= roleRank("manager"),       // Manager+
  archive:     (u) => u && roleRank(u.role) >= roleRank("agm_gm"),        // AGM+
  changeLevel: (u) => u && roleRank(u.role) >= roleRank("agm_gm"),        // AGM+ (up to L4)
  startTraining: (u) => u && roleRank(u.role) >= roleRank("manager"),     // Manager+ (0 -> T only)
  setLeadership: (u) => u && roleRank(u.role) >= roleRank("admin"),       // Admin only (assign AGM/GM)
  deleteRecord: (u) => u && roleRank(u.role) >= roleRank("admin"),        // Admin only
  observe:     (u) => u && roleRank(u.role) >= roleRank("trainer"),       // Trainer+ records observations
  setRetrain:  (u) => u && roleRank(u.role) >= roleRank("manager"),       // Manager+ can place in RT
  viewDocuments: (u) => u && roleRank(u.role) >= roleRank("manager"),     // Manager+ can see employee docs
};



/* ============================================================
   DATE HELPERS for milestones
   ============================================================ */
function addDays(iso, days) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  const now = new Date(todayISO() + "T00:00:00");
  return Math.round((d - now) / 86400000);
}
function fmtDate(iso) {
  if (!iso) return "—";
  // Accept either a plain date string ("2026-06-04") or a full timestamp ("2026-06-04T11:23:45.123+00:00")
  let d;
  if (typeof iso === "string" && iso.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    d = new Date(iso + "T00:00:00");
  } else {
    d = new Date(iso);
  }
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* Full date format including year, used in compliance/legal reports */
function fmtDateFull(iso) {
  if (!iso) return "—";
  let d;
  if (typeof iso === "string" && iso.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    d = new Date(iso + "T00:00:00");
  } else {
    d = new Date(iso);
  }
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

/* ============================================================
   APP
   ============================================================ */
export default function App() {
  const [state, setState] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("dashboard");
  const [quiz, setQuiz] = useState(null); // active quiz modal
  const [signoff, setSignoff] = useState(null); // active sign-off modal
  const [schedModal, setSchedModal] = useState(null);
  const [addEmp, setAddEmp] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [rideDetail, setRideDetail] = useState(null);
  const [authUser, setUser] = useState(null);      // logged-in user (the real account)
  // "View as employee": while set, the whole app renders as that employee
  // and the database client is read-only (see supabaseClient.js).
  const [viewAs, setViewAs] = useState(null);       // { emp, user, returnTab, returnEmp }
  const user = viewAs ? viewAs.user : authUser;     // everything below renders as this user
  // Trainings / policies / cards for the manager Home, Team and employee pages.
  const [teamExtras, reloadTeamExtras] = useTeamExtras(!!user && user.role !== "trainee");
  // Badge: how many published policies still need this user's signature.
  const [policiesDue, setPoliciesDue] = useState(0);
  // Bumped when someone signs or assignments change, so the badge refreshes.
  const [policyTick, setPolicyTick] = useState(0);
  useEffect(() => {
    const bump = () => setPolicyTick((t) => t + 1);
    window.addEventListener("lvmgp:policies-changed", bump);
    return () => window.removeEventListener("lvmgp:policies-changed", bump);
  }, []);
  useEffect(() => {
    if (!state || !user) { setPoliciesDue(0); return; }
    let cancelled = false;
    (async () => {
      const me = findMyEmployee(state, user);
      if (!me) { if (!cancelled) setPoliciesDue(0); return; }
      const [pRes, aRes, gRes] = await Promise.all([
        db.loadPolicies(),
        db.loadPolicyAcknowledgments(me.id),
        db.loadEmployeeGroups(),
      ]);
      if (cancelled || !pRes.ok || !aRes.ok) return;
      const gi = buildGroupIndex(gRes.ok ? gRes.groups : []);
      const signed = {};
      (aRes.acknowledgments || []).forEach((a) => {
        signed[a.policy_id] = Math.max(signed[a.policy_id] || 0, a.version);
      });
      const due = (pRes.policies || []).filter(
        (p) =>
          policyVisibleTo(p, me, gi) &&
          policyRequiresSignature(p, me, gi) &&
          (signed[p.id] || 0) < p.version
      ).length;
      setPoliciesDue(due);
    })();
    return () => { cancelled = true; };
  }, [state, user, policyTick]);
  const [authChecked, setAuthChecked] = useState(false);
  const [signoffTask, setSignoffTask] = useState(null); // a pending signoff being completed
  const [manageEmp, setManageEmp] = useState(null);     // employee management modal
  const [observeTask, setObserveTask] = useState(null); // {empId, ride?} observation entry
  const [l2ObsTask, setL2ObsTask] = useState(null); // {empId, ride, quizRef} L2 observation entry
  const [adhocPicker, setAdhocPicker] = useState(false); // shows the adhoc observation employee picker
  const [adhocPickerEmp, setAdhocPickerEmp] = useState(null); // adhoc obs with employee pre-selected (skip step 1)
  const [trainers, setTrainers] = useState([]);      // list of users assignable as trainer
  // Full-screen employee page (managers): { empId, tab }
  const [profile, setProfile] = useState(null);
  // Training practical / verification opened from Home or an employee page
  const [moduleAction, setModuleAction] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]); // question bank from DB
  const [obsChecklists, setObsChecklists] = useState([]); // L2 obs checklist bank from DB
  const [l2Observations, setL2Observations] = useState([]); // completed L2 observations
  const [trainingModules, setTrainingModules] = useState([]); // DB-backed training modules

  // Load assignable trainers when user is set
  useEffect(() => {
    if (!user) { setTrainers([]); return; }
    let alive = true;
    db.loadAssignableTrainers().then((res) => {
      if (alive && res.ok) setTrainers(res.trainers);
    });
    return () => { alive = false; };
  }, [user]);

  // Load quiz question bank when user is set
  const reloadQuizBank = React.useCallback(async () => {
    const res = await db.loadQuizQuestions();
    if (res.ok) setQuizQuestions(res.questions);
  }, []);
  useEffect(() => {
    if (!user) { setQuizQuestions([]); return; }
    reloadQuizBank();
  }, [user, reloadQuizBank]);

  // Load L2 observation checklist bank when user is set
  const reloadObsChecklists = React.useCallback(async () => {
    const res = await db.loadObsChecklists();
    if (res.ok) setObsChecklists(res.items);
  }, []);
  useEffect(() => {
    if (!user) { setObsChecklists([]); return; }
    reloadObsChecklists();
  }, [user, reloadObsChecklists]);

  // Load training modules from DB
  const reloadTrainingModules = React.useCallback(async () => {
    const res = await db.loadTrainingModules();
    if (res.ok) setTrainingModules(res.modules);
  }, []);
  useEffect(() => {
    if (!user) { setTrainingModules([]); return; }
    reloadTrainingModules();
  }, [user, reloadTrainingModules]);

  // Load all completed L2 observations for the team (used to determine 'awaiting L2 obs' state)
  const reloadL2Observations = React.useCallback(async () => {
    if (!user) { setL2Observations([]); return; }
    // Pull all observations; small table, fine to fetch.
    const { data, error } = await supabase
      .from("l2_observations").select("*")
      .order("created_at", { ascending: false });
    if (!error) setL2Observations(data || []);
  }, [user]);
  useEffect(() => {
    if (!user) { setL2Observations([]); return; }
    reloadL2Observations();
  }, [user, reloadL2Observations]);

  // Merge L2 observations into state so child components / tasksFor helpers can read it.
  // IMPORTANT: This hook must be declared before any early returns (loading/login/forcePassword)
  // to comply with React's rules of hooks. state may be null on first render — guard with || {}.
  const stateWithObs = React.useMemo(
    () => ({ ...(state || {}), l2_observations: l2Observations }),
    [state, l2Observations]
  );

  // Keep trainees on their allowed tabs
  useEffect(() => {
    if (user && user.role === "trainee" && !["dashboard", "modules", "myquizzes", "myrecord", "policies"].includes(tab)) {
      setTab("dashboard");
    }
  }, [user, tab]);

  // Reset any removed tabs (quizzes, signoffs, observations were removed from the sidebar)
  useEffect(() => {
    if (["quizzes", "signoffs", "observations"].includes(tab)) {
      setTab("dashboard");
    }
  }, [tab]);

  // load app data from Supabase
  const refresh = useCallback(async () => {
    try {
      const s = await db.loadAll();
      setState(s);
    } catch (e) { console.error("loadAll failed", e); }
    setLoaded(true);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Supabase auth session -> user profile
  useEffect(() => {
    let active = true;
    const loadProfile = async (uid) => {
      if (!uid) { if (active) { setUser(null); setAuthChecked(true); } return; }
      const { data } = await supabase.from("profiles").select("id, full_name, title, role, employee_id, username, is_username_only, force_password_change").eq("id", uid).single();
      if (active) {
        setUser(data ? {
          id: data.id, name: data.full_name, title: data.title, role: data.role, employeeId: data.employee_id,
          username: data.username || null,
          isUsernameOnly: !!data.is_username_only,
          forcePasswordChange: !!data.force_password_change,
        } : null);
        setAuthChecked(true);
      }
    };
    supabase.auth.getSession().then(({ data }) => loadProfile(data.session?.user?.id));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => { loadProfile(s?.user?.id); refresh(); });
    return () => { active = false; sub.subscription.unsubscribe(); };
  }, [refresh]);

  // login now takes {email, password}; returns {ok,message}
  const login = async ({ email, password }) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) return { ok: true };
    // Translate cryptic Supabase ban error to a friendly message
    const msg = (error.message || "").toLowerCase();
    if (msg.includes("ban") || msg.includes("disabled")) {
      return { ok: false, message: "This account is deactivated. Contact a manager to be reactivated." };
    }
    return { ok: false, message: error.message };
  };
  const logout = async () => {
    if (viewAs) { setReadOnly(false); setViewAs(null); }
    await supabase.auth.signOut(); setUser(null);
  };

  const canViewAs = !!authUser && roleRank(authUser.role) >= roleRank("manager");
  const startViewAs = async (emp) => {
    if (!canViewAs || !emp) return;
    // Use the employee's real login role so they see exactly their own menus.
    let role = "trainee", profileId = null, fullName = emp.name, title = emp.jobTitle || "";
    const { data } = await supabase
      .from("profiles").select("id, role, full_name, title").eq("employee_id", emp.id).limit(1);
    if (data && data[0]) {
      role = data[0].role || "trainee";
      profileId = data[0].id;
      fullName = data[0].full_name || emp.name;
      title = data[0].title || title;
    }
    setReadOnly(true);
    setViewAs({
      emp,
      hasLogin: !!(data && data[0]),
      user: { id: profileId, name: fullName, title, role, employeeId: emp.id, preview: true },
      returnTab: tab,
      returnEmp: selectedEmp,
      returnProfile: profile,
    });
    setProfile(null);
    setTab("dashboard");
  };
  const exitViewAs = () => {
    const back = viewAs;
    setReadOnly(false);
    setViewAs(null);
    if (back) { setTab(back.returnTab || "roster"); setSelectedEmp(back.returnEmp || null); setProfile(back.returnProfile || null); }
    // Anything clicked during the preview only changed the screen, never the
    // database — reload so the screen matches the database again.
    refresh();
    window.dispatchEvent(new Event("lvmgp:policies-changed"));
  };

  // Polyfill structuredClone for older browsers (Edge < 98, Safari < 15.4)
  // Deep-clone via JSON. State doesn't contain Date/Map/Set/etc, so this is fine.
  const safeClone = useCallback((obj) => {
    if (typeof structuredClone === "function") return structuredClone(obj);
    return JSON.parse(JSON.stringify(obj));
  }, []);

  // Write-through update: apply the change locally (instant UI), then sync the
  // changed entities to Supabase by diffing old vs new state.
  // Wrapped defensively: if anything throws synchronously during the diff
  // setup, we still apply the local state change (so the UI doesn't desync)
  // and refresh from the server to recover.
  const update = useCallback((fn) => {
    setState((prev) => {
      let next;
      try {
        next = fn(safeClone(prev));
      } catch (err) {
        console.error("update callback threw", err);
        return prev; // no-op state
      }
      // Persist changes, then reload from DB so any client-generated IDs are
      // replaced with the real database UUIDs.
      try {
        const p = db.syncDiff(prev, next);
        if (p && typeof p.then === "function") {
          p.then(() => { if (typeof refresh === "function") refresh(); })
           .catch((e) => {
             console.error("sync failed", e);
             if (typeof refresh === "function") refresh();
           });
        }
      } catch (err) {
        console.error("syncDiff threw synchronously", err);
        if (typeof refresh === "function") refresh();
      }
      return next;
    });
  }, [refresh, safeClone]);

  // legacy no-ops (local persistence replaced by Supabase)
  const _unusedSave = null;
  // load saved auth session (handled by onAuthStateChange above)
  useEffect(() => { setAuthChecked(true); }, []);


  /* ---- Actions ---- */

  // Start a progression step. Creates the appropriate pending sign-off OR observation set.
  // - target L2 (or L4 trainer cert): trainer/manager training-gate sign-off -> unlocks quiz
  // - target L3: observation-based (handled separately via observations)
  const startProgression = (empId, ride, targetLevel, signature, notes) => {
    update((s) => {
      const emp = s.employees.find((e) => e.id === empId);
      // A training sign-off requires the employee to be at least T (Trainee).
      // If they're at 0, auto-promote first so the sign-off chain is consistent
      // and the resulting quiz pass can trigger T->L2 advancement correctly.
      ensureAtLeastT(emp, ride, signature.name);
      // record the training-gate sign-off and immediately unlock the quiz
      s.signoffs.unshift({
        id: `so-${Date.now()}`, kind: "train_gate", empId: emp.id, empName: emp.name,
        ride, targetLevel, date: todayISO(), requiredRoles: ["trainer_manager"],
        signatures: [signature], status: "complete", missedTopics: [],
        note: notes || "",
      });
      if (emp.retrain) delete emp.retrain[ride];
      // create the quiz — but only if one doesn't already exist for this ride/level
      const existingQuiz = s.quizzes.find((q) => q.empId === emp.id && q.ride === ride && q.targetLevel === targetLevel && (q.status === "sent" || q.status === "failed"));
      if (!existingQuiz) {
        s.quizzes.unshift({
          id: `quiz-${Date.now()}`, empId: emp.id, empName: emp.name, ride, targetLevel,
          supervisor: signature.name, sentDate: todayISO(), status: "sent",
          questions: generateQuiz(ride, 15, quizQuestions), score: null, attempts: 0, missedTopics: [],
        });
      }
      const noteSuffix = notes ? ` · Notes: ${notes}` : "";
      emp.history.unshift({ date: todayISO(), text: `${RIDE_MAP[ride].label}: training signed off by ${signature.name} (${signature.title}) → quiz unlocked (target ${targetLevel})${noteSuffix}` });
      s.log.unshift({ date: todayISO(), text: `${emp.name}: ${RIDE_MAP[ride].label} training signed off → quiz unlocked`, type: "quiz" });
      return s;
    });
  };

  // Employee submits quiz.
  // `attestation` is the signed certification object captured by
  // QuizAttestationGate immediately before submit. It is stored ONCE per quiz —
  // retakes pass null and reuse the attestation already on the row.
  const submitQuiz = (quizId, answers, attestation) => {
    let outcome = { resultPass: false, scoreVal: 0, kind: "fail", missed: [] };
    update((s) => {
      const qz = s.quizzes.find((q) => q.id === quizId);
      // Record the attestation before grading, and only if one isn't already
      // stored — the signature covers the attempt, not the score.
      const newlyAttested = !!attestation && !qz.attestation;
      if (newlyAttested) {
        qz.attestation = attestation;
        qz.attestedAt = attestation.signed_at;
      }
      const correctCount = qz.questions.reduce((n, q) => n + (answers[q.id] === q.correct ? 1 : 0), 0);
      const missedTopics = [...new Set(qz.questions.filter((q) => answers[q.id] !== q.correct).map((q) => q.topic))];
      const scoreVal = Math.round((correctCount / qz.questions.length) * 100);
      qz.score = scoreVal;
      qz.attempts += 1;
      qz.missedTopics = missedTopics;
      // Keep the answers and a self-contained record of this attempt (the
      // questions are re-drawn on retake, so the misses are stored as text).
      qz.answers = { ...answers };
      qz.attemptHistory = [...(qz.attemptHistory || []), {
        at: new Date().toISOString(),
        attempt: qz.attempts,
        score: scoreVal,
        missedTopics,
        missed: qz.questions.filter((q) => answers[q.id] !== q.correct).map((q) => ({
          q: q.q, topic: q.topic,
          chosen: q.choices?.[answers[q.id]] ?? null,
          correct: q.choices?.[q.correct] ?? null,
        })),
      }];
      if (scoreVal >= PASS_SCORE) qz.completedDate = todayISO();
      const emp = s.employees.find((e) => e.id === qz.empId);
      const rideLabel = RIDE_MAP[qz.ride].label;

      if (newlyAttested) {
        emp.history.unshift({
          date: todayISO(),
          text: `${rideLabel}: training certification signed by ${attestation.signer_name}`,
        });
      }

      if (scoreVal < PASS_SCORE) {
        // FAIL -> Retrain (RT1)
        qz.status = "failed";
        emp.retrain = emp.retrain || {};
        emp.retrain[qz.ride] = true;
        outcome = { resultPass: false, scoreVal, kind: "fail", missed: missedTopics };
        emp.history.unshift({ date: todayISO(), text: `${rideLabel}: FAILED quiz (${scoreVal}%) → Retrain (RT1)` });
        s.log.unshift({ date: todayISO(), text: `⚠ ${emp.name} FAILED ${rideLabel} (${scoreVal}%) → RETRAIN. Manager notified.`, type: "fail" });
        s.notifications.unshift({ id: `n-${Date.now()}`, date: todayISO(), read: false,
          text: `${emp.name} failed ${rideLabel} (${scoreVal}%) and is in Retrain. Re-training required before another attempt.` });
      } else if (scoreVal === 100) {
        // 100% -> awaiting Manager+ L2 observation (next day or later)
        qz.status = "passed_full";
        outcome = { resultPass: true, scoreVal, kind: "full", missed: [] };
        emp.history.unshift({ date: todayISO(), text: `${rideLabel}: PASSED quiz 100% → awaiting L2 observation by Manager+` });
        s.log.unshift({ date: todayISO(), text: `${emp.name} PASSED ${rideLabel} 100% → awaiting L2 observation`, type: "pass" });
        s.notifications.unshift({ id: `n-${Date.now()}`, date: todayISO(), read: false,
          text: `${emp.name} scored 100% on ${rideLabel}. Schedule an L2 observation (Manager+, can be next day) to complete advancement to ${qz.targetLevel}.` });
      } else {
        // 80-99% -> retrain missed topics + still requires L2 observation
        qz.status = "passed_gaps";
        outcome = { resultPass: true, scoreVal, kind: "gaps", missed: missedTopics };
        emp.history.unshift({ date: todayISO(), text: `${rideLabel}: PASSED quiz (${scoreVal}%) → awaiting L2 observation (review missed topics)` });
        s.log.unshift({ date: todayISO(), text: `${emp.name} passed ${rideLabel} (${scoreVal}%) → L2 observation needed`, type: "quiz" });
        s.notifications.unshift({ id: `n-${Date.now()}`, date: todayISO(), read: false,
          text: `${emp.name} passed ${rideLabel} at ${scoreVal}%. Schedule the L2 observation and go over the missed topics (${missedTopics.map((t) => topicLabel(qz.ride, t)).join(", ")}) during it.` });
      }
      return s;
    });
    return outcome;
  };

  // Complete a pending sign-off with captured signatures; advance if satisfied
  const completeSignoff = (signoffId, signature) => {
    update((s) => {
      const so = s.signoffs.find((x) => x.id === signoffId);
      if (!so) return s;
      so.signatures.push(signature); // {role, name, title, dataUrl, date}
      const haveRoles = new Set(so.signatures.map((x) => x.role));
      const done = so.requiredRoles.every((r) => haveRoles.has(r));
      if (done) {
        so.status = "complete";
        const emp = s.employees.find((e) => e.id === so.empId);
        if (so.kind === "advance_full" || so.kind === "advance_gaps" || so.kind === "advance_obs") {
          emp.levels[so.ride] = so.targetLevel;
          if (emp.retrain) delete emp.retrain[so.ride];
          emp.history.unshift({ date: todayISO(), text: `${RIDE_MAP[so.ride].label}: sign-off complete → advanced to ${so.targetLevel}` });
          s.log.unshift({ date: todayISO(), text: `${emp.name} advanced to ${so.targetLevel} on ${RIDE_MAP[so.ride].label} (sign-off complete)`, type: "level" });
          const made = syncProgression(s, emp);
          if (made.length) s.log.unshift({ date: todayISO(), text: `Auto-unlocked for ${emp.name}: ${made.join(", ")}`, type: "sched" });
        } else if (so.kind === "train_gate") {
          // A training sign-off requires the employee to be at least T (Trainee).
          // If they're at 0, auto-promote first so the resulting quiz pass can
          // trigger T->L2 advancement correctly.
          ensureAtLeastT(emp, so.ride, signature.name);
          emp.history.unshift({ date: todayISO(), text: `${RIDE_MAP[so.ride].label}: training sign-off complete → quiz unlocked` });
          s.log.unshift({ date: todayISO(), text: `${emp.name}: ${RIDE_MAP[so.ride].label} training signed off → quiz unlocked`, type: "quiz" });
          // create the quiz now — but only if one doesn't already exist for this ride/level
          const existingQuiz = s.quizzes.find((q) => q.empId === emp.id && q.ride === so.ride && q.targetLevel === so.targetLevel && (q.status === "sent" || q.status === "failed"));
          if (!existingQuiz) {
            s.quizzes.unshift({
              id: `quiz-${Date.now()}`, empId: emp.id, empName: emp.name, ride: so.ride,
              targetLevel: so.targetLevel, supervisor: signature.name, sentDate: todayISO(),
              status: "sent", questions: generateQuiz(so.ride, 15, quizQuestions), score: null, attempts: 0, missedTopics: [],
            });
          }
        }
      }
      return s;
    });
  };

  const retakeQuiz = (quizId) => update((s) => {
    const qz = s.quizzes.find((q) => q.id === quizId);
    qz.status = "sent"; qz.score = null; qz.missedTopics = [];
    qz.questions = generateQuiz(qz.ride, 15, quizQuestions); // fresh randomized set
    const emp = s.employees.find((e) => e.id === qz.empId);
    if (emp && emp.retrain) delete emp.retrain[qz.ride]; // retraining done, allow attempt
    return s;
  });

  // ---- L2 Observation (NEW T->L2 flow) ----
  // Records an L2 observation. Manager+ only.
  // Outcome: 'pass' => advance to L2; 'needs_work' => stay at T, retrain flagged items.
  const completeL2Observation = async (empId, ride, payload) => {
    // payload: { results, notes, observer_sig, outcome, progression }
    const emp = state.employees.find((e) => e.id === empId);
    if (!emp) return { ok: false, message: "Employee not found." };
    const prog = payload.progression || "t_to_l2";
    const isL2toL3 = prog === "l2_to_l3";

    const obsRow = {
      employee_id: empId,
      ride,
      observer_id: user.id,
      observer_name: user.name,
      observer_title: user.title || (ROLES[user.role]?.label || ""),
      observer_sig: payload.observer_sig || "",
      obs_date: todayISO(),
      results: payload.results || [],
      notes: payload.notes || "",
      outcome: payload.outcome,
      progression: prog,
    };
    const res = await db.createL2Observation(obsRow);
    if (!res.ok) return res;
    reloadL2Observations();

    const rideLabel = RIDE_MAP[ride].label;

    if (payload.outcome === "pass") {
      if (isL2toL3) {
        // L2→L3: count passes across ALL prior L2→L3 obs for this emp/ride, including this new one
        const allPasses = [
          ...(state.l2_observations || []).filter((o) =>
            o.employee_id === empId && o.ride === ride && (o.progression || "t_to_l2") === "l2_to_l3" && o.outcome === "pass"
          ),
          { observer_id: user.id }, // include the one we just inserted
        ];
        const observers = new Set(allPasses.map((o) => o.observer_id));
        const qualified = allPasses.length >= 4 && observers.size >= 2;

        update((s) => {
          const e = s.employees.find((x) => x.id === empId);
          if (!e) return s;
          if (qualified) {
            e.levels[ride] = "L3";
            if (e.retrain) delete e.retrain[ride];
            e.history.unshift({ date: todayISO(), text: `${rideLabel}: L2→L3 observation completed by ${user.name} (${allPasses.length}/4 passes, ${observers.size} observers) → advanced to L3` });
            s.log.unshift({ date: todayISO(), text: `${e.name}: ${rideLabel} L2→L3 obs PASSED → advanced to L3`, type: "pass" });
          } else {
            e.history.unshift({ date: todayISO(), text: `${rideLabel}: L2→L3 observation passed by ${user.name} (${allPasses.length}/4 passes, ${observers.size}/2 observers)` });
            s.log.unshift({ date: todayISO(), text: `${e.name}: ${rideLabel} L2→L3 pass #${allPasses.length} by ${user.name}`, type: "pass" });
          }
          return s;
        });
      } else {
        // T→L2
        update((s) => {
          const e = s.employees.find((x) => x.id === empId);
          if (!e) return s;
          e.levels[ride] = "L2";
          if (e.retrain) delete e.retrain[ride];
          e.history.unshift({ date: todayISO(), text: `${rideLabel}: T→L2 observation completed by ${user.name} → advanced to L2` });
          s.log.unshift({ date: todayISO(), text: `${e.name}: ${rideLabel} T→L2 observation PASSED → advanced to L2`, type: "pass" });
          return s;
        });
      }
    } else {
      // needs_work: doesn't advance level, log flagged items
      const flagged = (payload.results || []).filter((r) => r.result === "needs_work").map((r) => r.item_text);
      const progLabel = isL2toL3 ? "L2→L3" : "T→L2";
      update((s) => {
        const e = s.employees.find((x) => x.id === empId);
        if (!e) return s;
        e.history.unshift({ date: todayISO(), text: `${rideLabel}: ${progLabel} observation by ${user.name} — needs work on ${flagged.length} item${flagged.length === 1 ? "" : "s"}: ${flagged.slice(0, 3).join("; ")}${flagged.length > 3 ? "…" : ""}` });
        s.log.unshift({ date: todayISO(), text: `${e.name}: ${rideLabel} ${progLabel} obs needs work (${flagged.length} item${flagged.length === 1 ? "" : "s"} flagged)`, type: "fail" });
        s.notifications.unshift({ id: `n-${Date.now()}`, date: todayISO(), read: false,
          text: `${e.name} needs additional training on ${rideLabel} (${progLabel}): ${flagged.length} checklist item${flagged.length === 1 ? "" : "s"} flagged. Re-observation needed after retraining.` });
        return s;
      });
    }
    return { ok: true };
  };

  // Record an observation. context: "obs" (L2->L3 standard), "ttt" (Train-the-Trainer),
  // "rt" (retrain), or "adhoc" (any employee/ride, no progression effect).
  const recordObservation = (empId, ride, opts) => {
    const { context = "adhoc", slot, result, observer, traineeTrained, agmObserved, note } = opts;
    update((s) => {
      const emp = s.employees.find((e) => e.id === empId);
      emp.observations = emp.observations || {};
      const rideLabel = RIDE_MAP[ride].label;

      if (context === "rt") {
        // Retrain observation stream
        emp.retrain = emp.retrain || {};
        const rt = emp.retrain[ride];
        if (!rt) return s;
        rt.observations = rt.observations || [];
        if (result === "fail") {
          // a fail resets the streak and requires a re-observation by a DIFFERENT AGM+
          rt.observations.push({ id: `rt-${Date.now()}`, result: "fail", observer, date: todayISO(), note });
          rt.needDifferentAgm = observer; // the next required obs must be a different AGM+
          emp.history.unshift({ date: todayISO(), text: `${rideLabel}: RT (${rt.level}) observation FAILED (${observer.name}) — re-observation by a different AGM+ required` });
          s.log.unshift({ date: todayISO(), text: `${emp.name} failed RT observation on ${rideLabel} — different AGM+ must re-observe`, type: "fail" });
          s.notifications.unshift({ id: `n-${Date.now()}`, date: todayISO(), read: false,
            text: `${emp.name} failed a retrain observation on ${rideLabel}. A DIFFERENT AGM+ must perform the re-observation.` });
        } else {
          rt.observations.push({ id: `rt-${Date.now()}`, result: "pass", observer, date: todayISO(), note });
          rt.needDifferentAgm = null;
          emp.history.unshift({ date: todayISO(), text: `${rideLabel}: RT (${rt.level}) observation PASSED (${observer.name})` });
          s.log.unshift({ date: todayISO(), text: `${emp.name} passed RT observation on ${rideLabel}`, type: "pass" });
        }
        const st = rtStatus(rt);
        if (st.cleared) {
          emp.levels[ride] = rt.level;          // restore to the level they were retraining at
          delete emp.retrain[ride];
          syncProgression(s, emp);
          emp.history.unshift({ date: todayISO(), text: `${rideLabel}: RETRAIN cleared (2 consecutive passes in 30 days) → restored to ${rt.level}` });
          s.log.unshift({ date: todayISO(), text: `${emp.name} cleared retrain on ${rideLabel} → ${rt.level}`, type: "level" });
          s.notifications.unshift({ id: `n-${Date.now()}`, date: todayISO(), read: false,
            text: `${emp.name} cleared retrain on ${rideLabel} and is restored to ${rt.level}.` });
        }
        return s;
      }

      // Non-RT: store in observations[ride]
      const list = emp.observations[ride] = emp.observations[ride] || [];

      if (context === "ttt") {
        list.push({ id: `ttt-${Date.now()}`, kind: "ttt", result, observer, traineeTrained, agmObserved: !!agmObserved, date: todayISO(), note });
        emp.history.unshift({ date: todayISO(), text: `${rideLabel}: Train-the-Trainer session ${result === "pass" ? "PASSED" : "FAILED"} (supervised by ${observer.name}${agmObserved ? ", AGM+ observed" : ""})` });
        s.log.unshift({ date: todayISO(), text: `${emp.name} TtT session ${result} on ${rideLabel}`, type: result === "pass" ? "pass" : "fail" });
        // Kiddie L2->L3 needs 3 passing TtT (>=1 AGM+); Tracks L3->L4 needs 1 TtT obs
        const isKiddie = ride === "kiddieKart";
        const cur = emp.levels[ride];
        const tttList = list.filter((o) => o.kind === "ttt");
        if (cur === "L2" && isKiddie && tttComplete(tttList, TTT_SESSIONS_REQUIRED)) {
          if (!s.signoffs.some((so) => so.empId === emp.id && so.ride === ride && so.kind === "advance_obs" && so.status !== "complete")) {
            s.signoffs.unshift({
              id: `so-${Date.now()}`, kind: "advance_obs", empId: emp.id, empName: emp.name,
              ride, targetLevel: "L3", date: todayISO(), requiredRoles: ["agm_gm"], signatures: [], missedTopics: [],
              note: "3 Train-the-Trainer sessions complete",
            });
            emp.history.unshift({ date: todayISO(), text: `${rideLabel}: 3 Train-the-Trainer sessions complete → awaiting AGM+ sign-off to advance to L3` });
            s.log.unshift({ date: todayISO(), text: `${emp.name} completed TtT on ${rideLabel} → AGM+ sign-off needed for L3`, type: "quiz" });
            s.notifications.unshift({ id: `n-${Date.now()}`, date: todayISO(), read: false,
              text: `${emp.name} completed 3 Train-the-Trainer sessions on ${rideLabel}. AGM+ sign-off required to advance to L3.` });
          }
        } else if (cur === "L3" && tttComplete(tttList.filter((o)=>o.forL4), 1)) {
          // handled below for L4 via forL4 flag
        }
        return s;
      }

      if (context === "ttt_l4") {
        list.push({ id: `tttl4-${Date.now()}`, kind: "ttt", forL4: true, result, observer, traineeTrained, agmObserved: !!agmObserved, date: todayISO(), note });
        emp.history.unshift({ date: todayISO(), text: `${rideLabel}: L3→L4 Train-the-Trainer observation ${result === "pass" ? "PASSED" : "FAILED"} (${observer.name})` });
        s.log.unshift({ date: todayISO(), text: `${emp.name} L4 TtT obs ${result} on ${rideLabel}`, type: result === "pass" ? "pass" : "fail" });
        const l4list = list.filter((o) => o.kind === "ttt" && o.forL4);
        if (emp.levels[ride] === "L3" && tttComplete(l4list, 1)) {
          if (!s.signoffs.some((so) => so.empId === emp.id && so.ride === ride && so.kind === "advance_obs" && so.status !== "complete")) {
            s.signoffs.unshift({
              id: `so-${Date.now()}`, kind: "advance_obs", empId: emp.id, empName: emp.name,
              ride, targetLevel: "L4", date: todayISO(), requiredRoles: ["agm_gm"], signatures: [], missedTopics: [],
              note: "Train-the-Trainer observation complete",
            });
            emp.history.unshift({ date: todayISO(), text: `${rideLabel}: Train-the-Trainer observation complete → awaiting AGM+ sign-off to advance to L4` });
            s.log.unshift({ date: todayISO(), text: `${emp.name} completed L4 TtT obs on ${rideLabel} → AGM+ sign-off needed`, type: "quiz" });
            s.notifications.unshift({ id: `n-${Date.now()}`, date: todayISO(), read: false,
              text: `${emp.name} completed the Train-the-Trainer observation on ${rideLabel}. AGM+ sign-off required to advance to L4.` });
          }
        }
        return s;
      }

      // Standard L2->L3 observation, OR ad-hoc (any employee/ride)
      list.push({ id: `obs-${Date.now()}`, slot, result, observer, date: todayISO(), note });
      const planItem = OBS_PLAN.find((p) => p.slot === slot);
      const slotLabel = planItem ? planItem.label : "Observation";
      if (result === "fail") {
        if (planItem) list.push({ id: `obs-extra-${Date.now()}`, slot, result: "required-extra", observer: "", date: "", extra: true });
        emp.history.unshift({ date: todayISO(), text: `${rideLabel}: ${slotLabel} FAILED (${observer.name})${planItem ? " — extra observation added" : ""}` });
        s.log.unshift({ date: todayISO(), text: `${emp.name} failed ${slotLabel} on ${rideLabel}`, type: "fail" });
      } else {
        emp.history.unshift({ date: todayISO(), text: `${rideLabel}: ${slotLabel} PASSED (${observer.name})` });
        s.log.unshift({ date: todayISO(), text: `${emp.name} passed ${slotLabel} on ${rideLabel}`, type: "pass" });
      }
      // progression: when 4 observations complete, create an AGM+ post-observation sign-off (no auto-advance)
      if (emp.levels[ride] === "L2" && ride !== "kiddieKart" && obsComplete(list.filter((o) => !o.kind))) {
        if (!s.signoffs.some((so) => so.empId === emp.id && so.ride === ride && so.kind === "advance_obs" && so.status !== "complete")) {
          s.signoffs.unshift({
            id: `so-${Date.now()}`, kind: "advance_obs", empId: emp.id, empName: emp.name,
            ride, targetLevel: "L3", date: todayISO(), requiredRoles: ["agm_gm"], signatures: [], missedTopics: [],
            note: "All 4 observations complete",
          });
          emp.history.unshift({ date: todayISO(), text: `${rideLabel}: 4 observations complete → awaiting AGM+ sign-off to advance to L3` });
          s.log.unshift({ date: todayISO(), text: `${emp.name} completed observations on ${rideLabel} → AGM+ sign-off needed for L3`, type: "quiz" });
          s.notifications.unshift({ id: `n-${Date.now()}`, date: todayISO(), read: false,
            text: `${emp.name} completed all 4 observations on ${rideLabel}. AGM+ sign-off required to advance to L3.` });
        }
      }
      return s;
    });
  };

  // Record a periodic L4-readiness check on a track at L3. ready=true starts L4 training.
  const recordL4Check = (empId, ride, ready, actor, note) => update((s) => {
    const emp = s.employees.find((e) => e.id === empId);
    emp.l4Checks = emp.l4Checks || {};
    (emp.l4Checks[ride] = emp.l4Checks[ride] || []).push({ date: todayISO(), ready, by: actor.name, note });
    if (ready) {
      // begin L4 path: a Train-the-Trainer observation
      emp.schedule = emp.schedule || [];
      if (!hasScheduled(emp, ride, "L4")) emp.schedule.push({ id: `sch-${Date.now()}`, ride, targetLevel: "L4", date: "", note: "L4 readiness confirmed — TtT observation" });
      emp.history.unshift({ date: todayISO(), text: `${RIDE_MAP[ride].label}: deemed READY for L4 by ${actor.name} → Train-the-Trainer observation scheduled` });
      s.log.unshift({ date: todayISO(), text: `${emp.name} ready for L4 on ${RIDE_MAP[ride].label} (${actor.name})`, type: "level" });
    } else {
      emp.history.unshift({ date: todayISO(), text: `${RIDE_MAP[ride].label}: L4 readiness check — not yet ready (${actor.name})` });
      s.log.unshift({ date: todayISO(), text: `${emp.name} L4 readiness check on ${RIDE_MAP[ride].label}: not yet`, type: "sched" });
    }
    return s;
  });

  // Place an employee into Retrain (RT) at a given level on a ride. Manager+.
  const setRetrain = (empId, ride, level, actor) => update((s) => {
    const emp = s.employees.find((e) => e.id === empId);
    emp.retrain = emp.retrain || {};
    emp.retrain[ride] = { level, ride, observations: [], openedBy: actor.name, openedDate: todayISO(), needDifferentAgm: null };
    emp.history.unshift({ date: todayISO(), text: `${RIDE_MAP[ride].label}: placed in RETRAIN at ${level} by ${actor.name}` });
    s.log.unshift({ date: todayISO(), text: `${emp.name} placed in RETRAIN (${level}) on ${RIDE_MAP[ride].label} by ${actor.name}`, type: "fail" });
    s.notifications.unshift({ id: `n-${Date.now()}`, date: todayISO(), read: false,
      text: `${emp.name} placed in retrain (${level}) on ${RIDE_MAP[ride].label}. Needs 2 consecutive passing observations within 30 days to clear.` });
    return s;
  });

  // AGM+ change a ride level directly (audit-logged). Admin required to grant leadership is handled in user mgmt.
  const changeLevel = (empId, ride, newLevel, actor) => update((s) => {
    const emp = s.employees.find((e) => e.id === empId);
    const old = emp.levels[ride];
    emp.levels[ride] = newLevel;
    emp.history.unshift({ date: todayISO(), text: `${RIDE_MAP[ride].label}: level changed ${old} → ${newLevel} by ${actor.name} (${ROLES[actor.role].label})` });
    s.log.unshift({ date: todayISO(), text: `${emp.name}: ${RIDE_MAP[ride].label} ${old}→${newLevel} (manual change by ${actor.name})`, type: "level" });
    const made = syncProgression(s, emp);
    if (made.length) s.log.unshift({ date: todayISO(), text: `Auto-unlocked for ${emp.name}: ${made.join(", ")}`, type: "sched" });
    return s;
  });

  // Override level — for migrating existing-state data (e.g., employee was already L2 on Road
  // before this system existed; the standard progression chain would have auto-created a T-to-L2
  // training step that's not actually needed).
  //
  // Behavior:
  //   1. Sets the level directly on the named ride
  //   2. Records a clear history entry indicating this is a migration / override entry
  //   3. Cancels (deletes) any PENDING training steps for that ride at or below the new level:
  //        - schedule entries
  //        - sign-offs with status !== "complete"
  //        - quizzes with status in (sent, failed)
  //      Completed records are NEVER touched — they are audit history.
  //   4. Skips syncProgression so it doesn't immediately auto-create new T steps for the next ride
  //      in the chain (caller should run overrideLevel for each ride that needs migration entry).
  const overrideLevel = (empId, ride, newLevel, note, actor) => update((s) => {
    const emp = s.employees.find((e) => e.id === empId);
    if (!emp) return s;
    const oldLevel = emp.levels[ride];
    emp.levels[ride] = newLevel;
    const newRank = lvlRank(newLevel);

    // 1. Cancel pending schedule entries for this ride at or below the new level
    const beforeSchedule = emp.schedule.length;
    emp.schedule = emp.schedule.filter((sc) => {
      if (sc.ride !== ride) return true;
      const tgtRank = lvlRank(sc.targetLevel);
      return tgtRank > newRank; // keep only steps targeting a HIGHER level than what we set
    });
    const removedSchedule = beforeSchedule - emp.schedule.length;

    // 2. Cancel pending sign-offs for this ride at or below the new level
    const removedSignoffIds = [];
    s.signoffs = s.signoffs.filter((so) => {
      if (so.empId !== empId) return true;
      if (so.ride !== ride) return true;
      if (so.status === "complete") return true; // never delete completed records
      const tgtRank = lvlRank(so.targetLevel);
      if (tgtRank > newRank) return true;
      removedSignoffIds.push(so.id);
      return false;
    });

    // 3. Cancel pending quizzes for this ride at or below the new level
    const removedQuizIds = [];
    s.quizzes = s.quizzes.filter((q) => {
      if (q.empId !== empId) return true;
      if (q.ride !== ride) return true;
      // Only cancel quizzes that aren't yet completed/passed
      if (q.status === "passed_full" || q.status === "passed_gaps" || q.status === "complete") return true;
      const tgtRank = lvlRank(q.targetLevel);
      if (tgtRank > newRank) return true;
      removedQuizIds.push(q.id);
      return false;
    });

    // 4. Add history entry with full context
    const counts = [];
    if (removedSchedule > 0) counts.push(`${removedSchedule} scheduled step${removedSchedule > 1 ? "s" : ""}`);
    if (removedSignoffIds.length > 0) counts.push(`${removedSignoffIds.length} pending sign-off${removedSignoffIds.length > 1 ? "s" : ""}`);
    if (removedQuizIds.length > 0) counts.push(`${removedQuizIds.length} pending quiz${removedQuizIds.length > 1 ? "zes" : ""}`);
    const cancelledNote = counts.length > 0 ? ` Auto-cancelled: ${counts.join(", ")}.` : "";
    const noteText = note ? ` — ${note}` : "";
    emp.history.unshift({
      date: todayISO(),
      text: `${RIDE_MAP[ride].label}: level set to ${newLevel} (migration override, prior level: ${oldLevel}) by ${actor.name} (${ROLES[actor.role].label})${noteText}.${cancelledNote}`,
    });
    s.log.unshift({
      date: todayISO(),
      text: `${emp.name}: ${RIDE_MAP[ride].label} → ${newLevel} (migration override by ${actor.name}).${cancelledNote}`,
      type: "level",
    });

    return s;
  });

  // Archive / restore (AGM+). Records are retained, never deleted.
  const setArchived = async (empId, archived, actor) => {
    // First, find any auth user(s) linked to this employee and toggle their login
    try {
      const { data: linkedProfiles } = await supabase
        .from("profiles").select("id").eq("employee_id", empId);
      if (linkedProfiles && linkedProfiles.length > 0) {
        for (const p of linkedProfiles) {
          // Skip self (would lock you out)
          if (p.id === actor?.id) continue;
          await db.setUserActive(p.id, !archived);
        }
      }
    } catch (e) {
      console.warn("Could not toggle login for archive:", e);
    }
    // Then update the roster record
    update((s) => {
      const emp = s.employees.find((e) => e.id === empId);
      emp.archived = archived;
      const label = archived ? "Deactivated" : "Reactivated";
      emp.history.unshift({ date: todayISO(), text: `${label} by ${actor.name} (${ROLES[actor.role].label})` });
      s.log.unshift({ date: todayISO(), text: `${emp.name} ${archived ? "deactivated (login disabled)" : "reactivated (login restored)"} by ${actor.name}`, type: "new" });
      return s;
    });
  };

  // Admin-only hard delete (the ONLY way records leave the system).
  const deleteEmployee = (empId, actor) => update((s) => {
    const emp = s.employees.find((e) => e.id === empId);
    if (!emp) return s;
    s.log.unshift({ date: todayISO(), text: `RECORD DELETED: ${emp.name} permanently removed by ${actor.name} (Admin)`, type: "fail" });
    s.employees = s.employees.filter((e) => e.id !== empId);
    return s;
  });

  const dismissNotification = (id) => update((s) => {
    const n = s.notifications.find((x) => x.id === id); if (n) n.read = true; return s;
  });

  const addScheduleEntry = (empId, ride, targetLevel, date, note, assignedTo) => update((s) => {
    const emp = s.employees.find((e) => e.id === empId);
    emp.schedule.push({ id: `sch-${Date.now()}`, ride, targetLevel, date, note, assignedTo: assignedTo || null });
    const trainerName = assignedTo ? (trainers.find((t) => t.id === assignedTo)?.full_name || "trainer") : null;
    s.log.unshift({ date: todayISO(), text: `Scheduled ${emp.name}: ${RIDE_MAP[ride].label} → ${targetLevel}${date ? " on " + fmtDate(date) : ""}${trainerName ? " · trainer: " + trainerName : ""}`, type: "sched" });
    return s;
  });

  const removeScheduleEntry = (empId, schedId) => update((s) => {
    const emp = s.employees.find((e) => e.id === empId);
    emp.schedule = emp.schedule.filter((x) => x.id !== schedId);
    return s;
  });

  const assignTrainer = (empId, schedId, trainerId, actor) => update((s) => {
    const emp = s.employees.find((e) => e.id === empId);
    const sc = emp && emp.schedule.find((x) => x.id === schedId);
    if (!sc) return s;
    const oldId = sc.assignedTo || null;
    sc.assignedTo = trainerId || null;
    const trainerName = trainerId ? (trainers.find((t) => t.id === trainerId)?.full_name || "user") : "unassigned";
    emp.history = emp.history || [];
    emp.history.unshift({
      id: `h-${Date.now()}`, date: todayISO(),
      text: `${RIDE_MAP[sc.ride].label} → ${sc.targetLevel} ${oldId ? "reassigned" : "assigned"} to ${trainerName}`,
      by: actor?.name || "",
    });
    s.log.unshift({ date: todayISO(), text: `${emp.name}: ${RIDE_MAP[sc.ride].label} → ${sc.targetLevel} assigned to ${trainerName}`, type: "sched" });
    return s;
  });

  const addEmployee = (name, startDate, department, actor) => update((s) => {
    const levels = Object.fromEntries(RIDES.map((r) => [r.key, "0"]));
    s.employees.push({
      id: `emp-${Date.now()}`, name, startDate, department: department || "Attractions",
      levels, retrain: {}, observations: {}, l4Checks: {}, schedule: [], history: [],
      archived: false, createdBy: actor?.name || "",
    });
    s.log.unshift({ date: todayISO(), text: `Added employee: ${name} (${department || "Attractions"})${actor ? " by " + actor.name : ""}`, type: "new" });
    return s;
  });

  const resetAll = () => { refresh(); };

  if (!loaded || !state || !authChecked) {
    return <div className="loading"><Style />Loading training board…</div>;
  }

  if (!user) {
    return <LoginScreen onLogin={login} />;
  }
  if (user.forcePasswordChange) {
    return <ChangePasswordScreen user={user} onDone={async () => {
      // Optimistic: clear the flag in local state immediately so the screen disappears
      // even if the database update is slow or fails. We refetch in the background.
      const optimisticUser = { ...user, forcePasswordChange: false };
      setUser(optimisticUser);
      // Clear the flag in the DB
      const { error: updErr } = await supabase.from("profiles").update({ force_password_change: false }).eq("id", user.id);
      if (updErr) {
        console.warn("Could not clear force_password_change flag:", updErr.message);
        // The user is still moved forward (optimistic). An admin can manually clear it later if needed.
        return;
      }
      // Refetch for consistency
      const { data } = await supabase.from("profiles").select("id, full_name, title, role, employee_id, username, is_username_only, force_password_change").eq("id", user.id).single();
      if (data) setUser({
        id: data.id, name: data.full_name, title: data.title, role: data.role, employeeId: data.employee_id,
        username: data.username || null,
        isUsernameOnly: !!data.is_username_only,
        forcePasswordChange: !!data.force_password_change,
      });
    }} onLogout={logout} />;
  }

  const pendingQuizzes = state.quizzes.filter((q) => q.status === "sent" || q.status === "failed");
  const openSignoffs = state.signoffs.filter((x) => x.status !== "complete");
  const unread = state.notifications.filter((n) => !n.read);
  // Observations needing attention: in-progress L2->L3 sets, RT streaks, Kiddie TtT, or due L4 flags
  const obsAttention = state.employees.filter((e) => !e.archived).reduce((acc, e) => {
    let n = 0;
    RIDES.forEach((r) => {
      const cur = e.levels[r.key];
      if (e.retrain?.[r.key]) n++;
      else if (cur === "L2" && r.key !== "kiddieKart" && !obsComplete((e.observations?.[r.key] || []).filter((o) => !o.kind))) n++;
      else if (cur === "L2" && r.key === "kiddieKart") n++;
    });
    n += l4Flags(e).filter((f) => f.due).length;
    return acc + (n > 0 ? 1 : 0);
  }, 0);

  // Observation routing shared by Home, Team and the employee page.
  const handleObserve = (emp, ride) => {
    const liveEmp = state.employees.find((e) => e.id === emp.id) || emp;
    const inRT = liveEmp.retrain && liveEmp.retrain[ride];
    const cur = liveEmp.levels?.[ride] || "0";
    const isKiddieTtT = cur === "L2" && ride === "kiddieKart";
    const isL3TtT = cur === "L3" && RIDE_MAP[ride]?.group === "tracks";
    if (inRT || isKiddieTtT || isL3TtT) {
      setObserveTask({ empId: emp.id, ride });
    } else {
      const progression = (cur === "L2" || cur === "L3" || cur === "L4") ? "l2_to_l3" : "t_to_l2";
      setL2ObsTask({ empId: emp.id, ride, quizRef: null, progression });
    }
  };
  const handleL2Obs = (emp, ride, taskOrQuizRef) => {
    const progression = taskOrQuizRef?.progression || "t_to_l2";
    const quizRef = progression === "t_to_l2" ? (taskOrQuizRef?.quizRef || taskOrQuizRef) : null;
    setL2ObsTask({ empId: emp.id, ride, quizRef, progression });
  };
  const openEmployee = (emp, empTab) => {
    setProfile({ empId: emp.id, tab: empTab || "overview" });
    reloadTeamExtras();
  };
  const h = {
    openEmployee,
    onCompleteSignoff: (so) => setSignoffTask(so),
    onObserve: handleObserve,
    onL2Obs: handleL2Obs,
    onModuleAction: (x) => setModuleAction(x),
    onStartRide: (empId, ride) => update((st) => { const e = st.employees.find((z) => z.id === empId); if (e && ensureAtLeastT(e, ride, user?.name)) st.log.unshift({ date: todayISO(), text: `${e.name}: started ${RIDE_MAP[ride].label} training (T)`, type: "sched" }); }),
  };
  const goTab = (t) => { setProfile(null); setTab(t); };

  const isTrainee = user.role === "trainee";
  const traineeTabs = ["dashboard", "modules", "myquizzes", "myrecord", "policies"];
  const myEmp = isTrainee ? findMyEmployee(state, user) : null;
  const sidebarPending = isTrainee && myEmp
    ? state.quizzes.filter((q) => q.empId === myEmp.id && (q.status === "sent" || q.status === "failed")).length
    : pendingQuizzes.length;

  // Merge L2 observations into state so child components / tasksFor helpers can read it.
  // (Note: actual useMemo for this is declared earlier, before any early returns,
  // to comply with React's rules of hooks.)

  return (
    <div className="app">
      <Style />
      <Sidebar tab={profile && !isTrainee ? "roster" : tab} setTab={goTab} pendingCount={sidebarPending}
        signoffCount={openSignoffs.length} obsCount={obsAttention} unreadCount={unread.length}
        policyCount={policiesDue}
        onReset={resetAll} user={user} onLogout={logout} />

      <main className="main">
        {viewAs && (
          <div className="viewas-bar">
            <Eye size={15} />
            <span>
              Viewing as <strong>{viewAs.user.name}</strong>
              {" "}({ROLES[viewAs.user.role]?.label || viewAs.user.role}
              {viewAs.emp.department ? ` · ${viewAs.emp.department}` : ""})
              {" "}— read only. Nothing you click is saved.
              {!viewAs.hasLogin && " This employee has no login yet, so they can't see this until one is created."}
            </span>
            <button className="viewas-exit" onClick={exitViewAs}><X size={14} /> Exit preview</button>
          </div>
        )}
        <Header tab={tab} search={search} setSearch={setSearch} onAdd={() => setAddEmp(true)} user={user} />

        <div className={`content ${profile && !isTrainee ? "profile-open" : ""}`}>
          {isTrainee ? (
            <>
              {tab === "dashboard" && <TraineeDashboard state={state} user={user} onOpenQuiz={(q) => setQuiz(q)} />}
              {tab === "modules" && <ModulesView trainingModules={trainingModules} user={user} employees={state.employees} onChanged={reloadTrainingModules} />}
              {tab === "myquizzes" && <MyQuizzesView state={state} user={user} />}
              {tab === "myrecord" && <MyRecordView state={state} user={user} />}
              {tab === "policies" && <PoliciesView state={state} user={user} />}
            </>
          ) : (
          <>
          {profile && (
            <EmployeePage
              empId={profile.empId}
              initialTab={profile.tab}
              state={stateWithObs} user={user} trainers={trainers}
              extras={teamExtras} reloadExtras={reloadTeamExtras} h={h}
              onBack={() => setProfile(null)}
              onViewAs={canViewAs && !viewAs ? startViewAs : null}
              onAssign={(empId, schedId, trainerId) => assignTrainer(empId, schedId, trainerId, user)}
              onSignoff={(emp) => setSignoff(emp)}
              onSchedule={(emp) => setSchedModal(emp)}
              onManage={(emp) => setManageEmp(emp)}
              onObserveEmp={(emp) => setAdhocPickerEmp(emp)}
            />
          )}
          {!profile && tab === "dashboard" && (
            <ManagerHome
              state={stateWithObs} user={user} extras={teamExtras} h={h}
              onOpenRide={(rideKey) => setRideDetail(rideKey)}
              onDismiss={dismissNotification}
              onAdhocObserve={() => setAdhocPicker(true)}
            />
          )}
          {!profile && tab === "roster" && (
            <TeamView state={stateWithObs} user={user} extras={teamExtras} onOpenEmp={(e) => openEmployee(e)} />
          )}
          {tab === "modules" && <ModulesView trainingModules={trainingModules} user={user} employees={state.employees} onChanged={reloadTrainingModules} />}
          {tab === "schedule" && (
            <ScheduleView state={state} user={user} trainers={trainers} onAdd={(emp) => setSchedModal(emp)} onRemove={removeScheduleEntry} onAssign={(empId, schedId, trainerId) => assignTrainer(empId, schedId, trainerId, user)} />
          )}
          {tab === "users" && roleRank(user.role) >= roleRank("manager") && (
            <UsersView user={user} employees={state.employees} />
          )}
          {tab === "quizedit" && roleRank(user.role) >= roleRank("agm_gm") && (
            <QuizEditorView user={user} questions={quizQuestions} onChanged={reloadQuizBank} />
          )}
          {tab === "checklisteditor" && roleRank(user.role) >= roleRank("agm_gm") && (
            <ChecklistEditorView user={user} items={obsChecklists} onChanged={reloadObsChecklists} />
          )}
          {tab === "reports" && roleRank(user.role) >= roleRank("manager") && (
            <ReportsView
              state={state}
              user={user}
              onChangeJobTitle={(empId, title) => update((s) => {
                const emp = s.employees.find((e) => e.id === empId);
                if (emp) {
                  const old = emp.jobTitle || "Ride Attendant";
                  if (old !== title) {
                    emp.jobTitle = title;
                    emp.history = emp.history || [];
                    emp.history.push({ id: `h-${Date.now()}`, date: todayISO(), text: `Job title: ${old} → ${title}`, by: user?.name || "" });
                  }
                }
                return s;
              })}
            />
          )}
          {tab === "mytraining" && (
            <MyTrainingView state={state} user={user} onOpenQuiz={(q) => setQuiz(q)} />
          )}
          {tab === "policies" && <PoliciesView state={state} user={user} />}
          {tab === "goals" && roleRank(user.role) >= roleRank("manager") && <GoalsAdminView state={state} user={user} />}
          </>
          )}
        </div>
      </main>

      <RideCertificationQueue key={user?.employeeId || "self"} state={state} user={user} paused={!!quiz} />
      {moduleAction?.kind === "practical" && (
        <ModulePracticalModal
          assignment={moduleAction.assignment} emp={moduleAction.emp} training={moduleAction.training} user={user}
          onClose={() => setModuleAction(null)}
          onComplete={async () => { setModuleAction(null); await reloadTeamExtras(); }}
        />
      )}
      {moduleAction?.kind === "open" && (
        <ModulePlayerModal
          assignment={moduleAction.assignment} training={moduleAction.training} emp={moduleAction.emp} reviewOnly={!!moduleAction.reviewOnly}
          onClose={async () => { setModuleAction(null); await reloadTeamExtras(); }}
        />
      )}
      {moduleAction?.kind === "verify" && (
        <ModuleReviewModal
          assignment={moduleAction.assignment} emp={moduleAction.emp} training={moduleAction.training} user={user}
          onClose={() => setModuleAction(null)}
          onComplete={async () => { setModuleAction(null); await reloadTeamExtras(); }}
        />
      )}

      {signoff && (
        <StartProgressionModal
          emp={signoff} user={user} onClose={() => setSignoff(null)}
          onConfirm={(ride, target, signature, notes) => { startProgression(signoff.id, ride, target, signature, notes); setSignoff(null); }}
        />
      )}
      {signoffTask && (
        <CompleteSignoffModal
          task={signoffTask} user={user} onClose={() => setSignoffTask(null)}
          onSign={(signature) => { completeSignoff(signoffTask.id, signature); setSignoffTask(null); }}
        />
      )}
      {quiz && (
        <QuizModal
          quiz={quiz} user={user} onClose={() => setQuiz(null)}
          onSubmit={(answers, attestation) => submitQuiz(quiz.id, answers, attestation)}
        />
      )}
      {schedModal && (
        <ScheduleModal
          emp={schedModal} trainers={trainers} onClose={() => setSchedModal(null)}
          onAdd={(ride, target, date, note, assignedTo) => { addScheduleEntry(schedModal.id, ride, target, date, note, assignedTo); setSchedModal(null); }}
        />
      )}
      {addEmp && (
        <AddEmployeeModal onClose={() => setAddEmp(false)} onAdd={(n, d, dept) => { addEmployee(n, d, dept, user); setAddEmp(false); }} />
      )}
      {manageEmp && (
        <ManageEmployeeModal
          emp={state.employees.find((e) => e.id === manageEmp.id)} user={user} onClose={() => setManageEmp(null)}
          onChangeLevel={(ride, lvl) => changeLevel(manageEmp.id, ride, lvl, user)}
          onOverrideLevel={(ride, lvl, note) => overrideLevel(manageEmp.id, ride, lvl, note, user)}
          onChangeName={(newName) => update((s) => {
            const emp = s.employees.find((e) => e.id === manageEmp.id);
            if (emp && newName && newName !== emp.name) {
              const old = emp.name;
              emp.name = newName;
              emp.history = emp.history || [];
              emp.history.push({ id: `h-${Date.now()}`, date: todayISO(), text: `Name corrected: ${old} → ${newName}`, by: user?.name || "" });
              // Update any cached emp_name on sign-offs (so they display the corrected name)
              (s.signoffs || []).forEach((so) => { if (so.empId === manageEmp.id) so.empName = newName; });
            }
            return s;
          })}
          onChangeDept={(dept) => update((s) => {
            const emp = s.employees.find((e) => e.id === manageEmp.id);
            if (emp) { const old = emp.department || "Attractions"; emp.department = dept; emp.history = emp.history || []; emp.history.push({ id: `h-${Date.now()}`, date: todayISO(), text: `Department: ${old} → ${dept}`, by: user?.name || "" }); }
            return s;
          })}
          onChangeJobTitle={(title) => update((s) => {
            const emp = s.employees.find((e) => e.id === manageEmp.id);
            if (emp) {
              const old = emp.jobTitle || "Ride Attendant";
              emp.jobTitle = title;
              emp.history = emp.history || [];
              emp.history.push({ id: `h-${Date.now()}`, date: todayISO(), text: `Job title: ${old} → ${title}`, by: user?.name || "" });
            }
            return s;
          })}
          onChangeStartDate={(newDate) => update((s) => {
            const emp = s.employees.find((e) => e.id === manageEmp.id);
            if (emp && newDate !== emp.startDate) {
              const old = emp.startDate || "—";
              emp.startDate = newDate;
              emp.history = emp.history || [];
              emp.history.push({ id: `h-${Date.now()}`, date: todayISO(), text: `Start date: ${old} → ${newDate || "—"}`, by: user?.name || "" });
            }
            return s;
          })}
          onArchive={(arch) => { setArchived(manageEmp.id, arch, user); setManageEmp(null); }}
          onDelete={() => { if (confirm("ADMIN: permanently delete this employee and ALL their records? This is the only irreversible action.")) { deleteEmployee(manageEmp.id, user); setManageEmp(null); } }}
          onRetrain={(ride, lvl) => { setRetrain(manageEmp.id, ride, lvl, user); }}
        />
      )}
      {observeTask && (
        <ObservationModal
          emp={state.employees.find((e) => e.id === observeTask.empId)} user={user} preRide={observeTask.ride}
          onClose={() => setObserveTask(null)}
          onRecord={(ride, opts) => { recordObservation(observeTask.empId, ride, opts); setObserveTask(null); }}
        />
      )}
      {l2ObsTask && (
        <L2ObservationModal
          emp={state.employees.find((e) => e.id === l2ObsTask.empId)}
          ride={l2ObsTask.ride}
          progression={l2ObsTask.progression || "t_to_l2"}
          quizRef={l2ObsTask.quizRef}
          checklists={obsChecklists}
          user={user}
          state={stateWithObs}
          onClose={() => setL2ObsTask(null)}
          onSubmit={async (payload) => {
            const res = await completeL2Observation(l2ObsTask.empId, l2ObsTask.ride, payload);
            if (!res.ok) { alert(`Could not save: ${res.message}`); return; }
            setL2ObsTask(null);
          }}
        />
      )}
      {adhocPicker && (
        <AdhocObservationPicker
          employees={state.employees}
          onClose={() => setAdhocPicker(false)}
          onPick={(emp, ride, progression) => {
            setAdhocPicker(false);
            setL2ObsTask({ empId: emp.id, ride, quizRef: null, progression });
          }}
        />
      )}
      {adhocPickerEmp && (
        <AdhocObservationPicker
          employees={state.employees}
          preselectedEmp={adhocPickerEmp}
          onClose={() => setAdhocPickerEmp(null)}
          onPick={(emp, ride, progression) => {
            setAdhocPickerEmp(null);
            setL2ObsTask({ empId: emp.id, ride, quizRef: null, progression });
          }}
        />
      )}
      {rideDetail && (
        <RideDetailModal
          rideKey={rideDetail} employees={state.employees}
          onClose={() => setRideDetail(null)}
          onSignoff={(emp) => { setRideDetail(null); setSignoff(emp); }}
          onStart={(emp) => {
            // Promote employee to T on this ride, then open the schedule modal so the user
            // can optionally pick a training date. No sign-off required to start training.
            const ride = rideDetail;
            changeLevel(emp.id, ride, "T", user);
            setRideDetail(null);
            setSchedModal(emp);
          }}
        />
      )}
    </div>
  );
}

/* ============================================================
   SIDEBAR + HEADER
   ============================================================ */
function Sidebar({ tab, setTab, pendingCount, signoffCount, obsCount, unreadCount, policyCount, onReset, user, onLogout }) {
  const isTrainee = user.role === "trainee";
  const items = isTrainee ? [
    { key: "dashboard", label: "My Training", icon: LayoutDashboard },
    { key: "modules", label: "Training Docs", icon: BookOpen },
    { key: "myquizzes", label: "My Quizzes", icon: FileQuestion, badge: pendingCount },
    { key: "myrecord", label: "My Record", icon: Users },
    { key: "policies", label: "Policies", icon: FileText, badge: policyCount },
  ] : [
    { key: "dashboard", label: "Home", icon: LayoutDashboard, badge: unreadCount },
    { key: "roster", label: "Team", icon: Users },
    { key: "modules", label: "Modules", icon: BookOpen },
    { key: "schedule", label: "Schedule", icon: Calendar },
    { key: "policies", label: "Policies", icon: FileText, badge: policyCount },
  ];
  // Non-trainees also get a "My Training" view if they have an employee record linked
  if (!isTrainee && user.employeeId) items.push({ key: "mytraining", label: "My Training", icon: GraduationCap });
  if (roleRank(user.role) >= roleRank("manager")) items.push({ key: "reports", label: "Reports", icon: TrendingUp });
  if (roleRank(user.role) >= roleRank("manager")) items.push({ key: "goals", label: "Goals", icon: Trophy });
  if (roleRank(user.role) >= roleRank("manager")) items.push({ key: "users", label: "Users", icon: ShieldCheck });
  if (roleRank(user.role) >= roleRank("agm_gm")) items.push({ key: "quizedit", label: "Quiz Editor", icon: FileQuestion });
  if (roleRank(user.role) >= roleRank("agm_gm")) items.push({ key: "checklisteditor", label: "L2 Checklists", icon: Eye });

  // Phone layout: a bottom tab bar with the four main screens + "More".
  const [more, setMore] = useState(false);
  const primaryKeys = isTrainee ? ["dashboard", "myquizzes", "policies", "modules"] : ["dashboard", "roster", "policies", "schedule"];
  const primary = primaryKeys.map((k) => items.find((i) => i.key === k)).filter(Boolean);
  const rest = items.filter((i) => !primaryKeys.includes(i.key));
  const restBadge = rest.reduce((n, i) => n + (i.badge || 0), 0);
  const go = (k) => { setMore(false); setTab(k); };
  const mobileNav = (
    <>
      <nav className="mobile-nav">
        {primary.map((it) => {
          const Icon = it.icon;
          return (
            <button key={it.key} className={`mn-item ${tab === it.key ? "active" : ""}`} onClick={() => go(it.key)}>
              <span className="mn-ic"><Icon size={20} />{it.badge ? <span className="mn-badge">{it.badge}</span> : null}</span>
              <span className="mn-label">{it.label}</span>
            </button>
          );
        })}
        <button className={`mn-item ${more || rest.some((i) => i.key === tab) ? "active" : ""}`} onClick={() => setMore((v) => !v)}>
          <span className="mn-ic"><Menu size={20} />{restBadge ? <span className="mn-badge">{restBadge}</span> : null}</span>
          <span className="mn-label">More</span>
        </button>
      </nav>
      {more && (
        <div className="mn-sheet-overlay" onClick={() => setMore(false)}>
          <div className="mn-sheet" onClick={(e) => e.stopPropagation()}>
            {rest.map((it) => {
              const Icon = it.icon;
              return (
                <button key={it.key} className={`mn-sheet-item ${tab === it.key ? "active" : ""}`} onClick={() => go(it.key)}>
                  <Icon size={18} /><span>{it.label}</span>{it.badge ? <span className="nav-badge">{it.badge}</span> : null}
                </button>
              );
            })}
            <div className="mn-sheet-foot">
              <div className="mn-user"><strong>{user.name}</strong><span className="dim">{ROLES[user.role].label}</span></div>
              <button className="btn-ghost sm" onClick={() => { setMore(false); onReset(); }}><RotateCcw size={13} /> Refresh</button>
              <button className="btn-ghost sm" onClick={onLogout}><LogOut size={13} /> Log out</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
  return (
    <>
    {mobileNav}
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-mark"><Trophy size={20} /></div>
        <div>
          <div className="brand-name">RIDE OPS</div>
          <div className="brand-sub">Training Board</div>
        </div>
      </div>
      <nav>
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <button key={it.key} className={`nav-item ${tab === it.key ? "active" : ""}`} onClick={() => setTab(it.key)}>
              <Icon size={18} />
              <span>{it.label}</span>
              {it.badge ? <span className="nav-badge">{it.badge}</span> : null}
            </button>
          );
        })}
      </nav>
      <div className="sidebar-foot">
        <div className="user-card">
          <div className="user-avatar">{user.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}</div>
          <div className="user-meta">
            <div className="user-name">{user.name}</div>
            <div className="user-role">{ROLES[user.role].label}</div>
          </div>
          <button className="icon-btn" title="Log out" onClick={onLogout}><LogOut size={15} /></button>
        </div>
        <button className="reset-btn" onClick={onReset}><RotateCcw size={14} /> Refresh data</button>
      </div>
    </aside>
    </>
  );
}

function Header({ tab, search, setSearch, onAdd, user }) {
  const titles = {
    dashboard: user.role === "trainee" ? "My Training" : "Home",
    roster: "Team",
    modules: user.role === "trainee" ? "Training Documents" : "Training Modules",
    quizzes: "Quiz Center",
    myquizzes: "My Quizzes",
    myrecord: "My Training Record",
    signoffs: "Training Sign-off Queue",
    observations: "Observations",
    schedule: "Training Schedule",
    policies: "Policies",
    goals: "Goals",
  };
  return (
    <header className="header">
      <h1>{titles[tab]}</h1>
      <div className="header-actions">

      </div>
    </header>
  );
}

/* ============================================================
   TEAM · HOME · EMPLOYEE PAGE  (managers)

   Home       — what needs a manager today: count tiles + one prioritized
                "Needs attention" list. Every row names the employee (opens
                their page) and, where possible, has the action button.
   Team       — everyone at a glance, filter by department, search; list
                or skills matrix. Clicking a person opens their page.
   Employee   — a full screen per person with tabs: Overview, Rides,
                Trainings, Quizzes, Policies, Goals, Documents, History.
   ============================================================ */

const DEPARTMENTS = ["Attractions", "Restaurant", "Kart & Ride Service Tech"];
const deptOf = (e) => e.department || "Attractions";
const initialsOf = (name) => (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

/* Everything beyond `state` that the manager screens need, in one load. */
async function loadTeamExtras() {
  const [mRes, aRes, mqRes, pRes, ackRes, gRes, dRes] = await Promise.all([
    db.loadModules({ includeArchived: true }),
    db.loadModuleAssignments({}),
    db.loadModuleQuizzes(),
    db.loadPolicies(),
    db.loadPolicyAcknowledgments(),
    db.loadEmployeeGroups(),
    db.loadAllEmployeeDocuments(),
  ]);
  const modules = mRes.ok ? mRes.modules : [];
  const modulesById = {};
  modules.forEach((m) => { modulesById[m.id] = m; });
  return {
    modules,
    modulesById,
    assignments: aRes.ok ? aRes.assignments : [],
    moduleQuizzes: mqRes.ok ? mqRes.quizzes : [],
    policies: pRes.ok ? pRes.policies : [],
    acks: ackRes.ok ? ackRes.acknowledgments : [],
    groupIdx: buildGroupIndex(gRes.ok ? gRes.groups : []),
    documents: dRes.ok ? dRes.documents : [],
  };
}

function useTeamExtras(enabled) {
  const [extras, setExtras] = useState(null);
  const reload = useCallback(async () => {
    if (!enabled) return;
    setExtras(await loadTeamExtras());
  }, [enabled]);
  useEffect(() => { reload(); }, [reload]);
  return [extras, reload];
}

/* One training's status for one person, for the colored squares.
   done | progress | problem | none (not assigned) */
function trainingCellStatus(a, t) {
  if (!a) return { key: "none", label: "Not assigned" };
  if (a.completed_at) return { key: "done", label: `Done ${fmtDate(String(a.completed_at).slice(0, 10))}` };
  const st = moduleAssignmentStatus(a, t);
  if (st.cls === "tag-fail") return { key: "problem", label: st.text };
  return { key: "progress", label: st.text };
}
const CELL_COLORS = { done: "var(--l2)", progress: "var(--lT)", problem: "#e5624a", none: "var(--l0)" };

/* Squares for every training in one area (e.g. Restaurant), done / total. */
function AreaTrainingSquares({ emp, extras, section }) {
  if (!extras) return <span className="tr-cap dim">…</span>;
  const mods = extras.modules.filter((m) => m.section === section && !m.archived).sort((a, b) => a.title.localeCompare(b.title));
  if (!mods.length) return <span className="tr-cap dim">—</span>;
  const byMod = {};
  extras.assignments.filter((a) => a.employee_id === emp.id).forEach((a) => {
    const cur = byMod[a.module_id];
    if (!cur || (a.completed_at && !cur.completed_at)) byMod[a.module_id] = a;
  });
  const cells = mods.map((m) => ({ m, st: trainingCellStatus(byMod[m.id], m) }));
  const done = cells.filter((c) => c.st.key === "done").length;
  return (
    <span className="tr-rides" title={cells.map((c) => `${c.m.title}: ${c.st.label}`).join("\n")}>
      {cells.map((c) => <i key={c.m.id} style={{ background: CELL_COLORS[c.st.key] }} className={c.st.key === "problem" ? "rt" : ""} />)}
      <span className="tr-cap">{done}/{mods.length}</span>
    </span>
  );
}

/* One employee's numbers, used by Team rows, Home and the employee page. */
function employeeSnapshot(emp, state, x) {
  const today = todayISO();
  const rideSolo = RIDES.filter((r) => isIndependent(emp.levels?.[r.key] || "0")).length;
  const rideTraining = RIDES.filter((r) => (emp.levels?.[r.key] || "0") === "T").length;
  const retrainRides = RIDES.filter((r) => emp.retrain?.[r.key]).map((r) => r.key);
  const quizzesPending = state.quizzes.filter((q) => q.empId === emp.id && (q.status === "sent" || q.status === "failed"));
  const gapQuizzes = state.quizzes.filter((q) => q.empId === emp.id && q.status === "passed_gaps");

  const asg = (x?.assignments || []).filter((a) => a.employee_id === emp.id && x.modulesById[a.module_id]);
  const trainingsDone = asg.filter((a) => a.completed_at);
  const trainingsOpen = asg.filter((a) => !a.completed_at);
  const trainingsOverdue = trainingsOpen.filter((a) => a.due_at && String(a.due_at).slice(0, 10) < today);
  const trainingsFailed = trainingsOpen.filter((a) => a.quiz_status === "failed");

  const signed = {};
  (x?.acks || []).filter((a) => a.employee_id === emp.id)
    .forEach((a) => { signed[a.policy_id] = Math.max(signed[a.policy_id] || 0, a.version); });
  const policiesRequired = (x?.policies || []).filter((p) => p.published && policyRequiresSignature(p, emp, x.groupIdx));
  const policiesToSign = policiesRequired.filter((p) => (signed[p.id] || 0) < p.version);
  const policiesOverdue = policiesToSign.filter((p) => { const d = policyDueDate(p, emp, x.groupIdx); return d && d < today; });

  const docs = (x?.documents || []).filter((d) => d.employee_id === emp.id && d.expires_at);
  const docsExpired = docs.filter((d) => String(d.expires_at).slice(0, 10) < today);
  const docsExpiring = docs.filter((d) => { const du = daysUntil(String(d.expires_at).slice(0, 10)); return du !== null && du >= 0 && du <= 30; });

  // Trainings are always done WITH a trainer, so they never count as waiting on the employee.
  const waitingOnEmployee = quizzesPending.length + policiesToSign.length;
  const problems = retrainRides.length + trainingsOverdue.length + trainingsFailed.length + policiesOverdue.length + docsExpired.length;
  return {
    rideSolo, rideTraining, retrainRides, quizzesPending, gapQuizzes,
    trainingsDone, trainingsOpen, trainingsOverdue, trainingsFailed, trainingsTotal: asg.length,
    policiesRequired, policiesToSign, policiesOverdue, signedVersion: signed,
    docsExpired, docsExpiring,
    waitingOnEmployee, problems,
  };
}

/* The prioritized "needs attention" list. Each item:
   { id, emp, cat, tone, label, sub, sort, action?: { label, run } }       */
function buildAttention(state, user, x, h, onlyEmpId) {
  const items = [];
  const today = todayISO();
  const emps = state.employees.filter((e) => !e.archived && (!onlyEmpId || e.id === onlyEmpId));
  const empById = Object.fromEntries(state.employees.map((e) => [e.id, e]));
  emps.forEach((e) => {
    // Sign-offs and observations waiting on a manager/trainer
    signoffTasksFor(state, e).forEach((t) => items.push({
      id: `so-${t.id}`, emp: e, cat: "signoffs", tone: "warn", sort: 20,
      label: t.label, sub: t.sub,
      action: t.canDo(user) ? { label: "Sign", run: () => h.onCompleteSignoff(t.signoff) } : null,
    }));
    observationTasksFor(state, e).forEach((t) => {
      if (user.role === "trainer" && !t.canDo(user)) return;
      // A passed quiz with missed topics: review those during this observation.
      const missed = t.quizRef?.missedTopics?.length ? t.quizRef.missedTopics.map((x) => topicLabel(t.ride, x)).join(", ") : "";
      items.push({
        id: `ob-${t.id}`, emp: e, cat: "signoffs", tone: t.label.includes("RETRAIN") ? "danger" : "warn", sort: t.label.includes("RETRAIN") ? 10 : 25,
        label: t.label, sub: missed ? `${t.sub} · review during observation: ${missed}` : t.sub,
        action: t.canDo(user) ? { label: "Observe", run: () => (t.l2Obs ? h.onL2Obs(e, t.ride, t) : h.onObserve(e, t.ride)) } : null,
      });
    });
  });
  if (x) {
    // Training practicals / verifications waiting on a manager
    if (roleRank(user.role) >= roleRank("manager")) {
      x.assignments.forEach((a) => {
        const e = empById[a.employee_id]; const t = x.modulesById[a.module_id];
        if (!e || e.archived || !t || a.completed_at) return;
        if (onlyEmpId && e.id !== onlyEmpId) return;
        const quizCleared = a.quiz_status === "passed" || !t.has_quiz;
        const needsPractical = t.requires_practical && quizCleared && a.practical_status !== "complete";
        const needsVerify = a.review_status === "pending" && !needsPractical;
        if (needsPractical || needsVerify) items.push({
          id: `mx-${a.id}`, emp: e, cat: "signoffs", tone: "warn", sort: 22,
          label: `${t.title} · ${needsPractical ? "practical walk-through" : "competency verification"}`,
          sub: `Training ${needsPractical ? "quiz passed" : "awaiting sign-off"}`,
          action: { label: needsPractical ? "Practical" : "Verify", run: () => h.onModuleAction({ kind: needsPractical ? "practical" : "verify", assignment: a, emp: e, training: t }) },
        });
      });
    }
    emps.forEach((e) => {
      const s = employeeSnapshot(e, state, x);
      s.trainingsFailed.forEach((a) => items.push({
        id: `tf-${a.id}`, emp: e, cat: "retrain", tone: "danger", sort: 12,
        label: `Failed the ${x.modulesById[a.module_id].title} quiz`,
        sub: "Retrain before the retake", action: { label: "Results", run: () => h.openEmployee(e, "quizzes") },
      }));
      s.trainingsOverdue.filter((a) => a.quiz_status !== "failed").forEach((a) => items.push({
        id: `to-${a.id}`, emp: e, cat: "overdue", tone: "danger", sort: 40,
        label: `${x.modulesById[a.module_id].title} training date passed`,
        sub: `Was scheduled ${fmtDate(String(a.due_at).slice(0, 10))}${a.trainer_name ? ` with ${a.trainer_name}` : ""}`,
        action: { label: "Open", run: () => h.openEmployee(e, AREA_TAB[x.modulesById[a.module_id].section] || "general") },
      }));
      if (s.policiesOverdue.length) items.push({
        id: `po-${e.id}`, emp: e, cat: "policies", tone: "danger", sort: 45,
        label: `${s.policiesOverdue.length} polic${s.policiesOverdue.length === 1 ? "y" : "ies"} overdue for signature`,
        sub: s.policiesOverdue.map((p) => p.title).join(", "),
        action: { label: "Open", run: () => h.openEmployee(e, "policies") },
      });
      s.docsExpired.forEach((d) => items.push({
        id: `dx-${d.id}`, emp: e, cat: "cards", tone: "danger", sort: 15,
        label: `${d.category || "Card"} expired ${fmtDate(String(d.expires_at).slice(0, 10))}`,
        sub: "Can't work until renewed", action: { label: "Open", run: () => h.openEmployee(e, "documents") },
      }));
      s.docsExpiring.forEach((d) => items.push({
        id: `de-${d.id}`, emp: e, cat: "cards", tone: "warn", sort: 50,
        label: `${d.category || "Card"} expires ${fmtDate(String(d.expires_at).slice(0, 10))}`,
        sub: `in ${daysUntil(String(d.expires_at).slice(0, 10))} days`, action: { label: "Open", run: () => h.openEmployee(e, "documents") },
      }));
    });
  }
  // 30/60/90 check-ins due within a week (or up to a week late)
  emps.forEach((e) => {
    if (!e.startDate) return;
    [["30-day", 30], ["60-day", 60], ["90-day", 90]].forEach(([label, d]) => {
      const due = addDays(e.startDate, d); const du = daysUntil(due);
      if (du !== null && du >= -7 && du <= 7) items.push({
        id: `ci-${e.id}-${d}`, emp: e, cat: "checkins", tone: du < 0 ? "danger" : "info", sort: 60,
        label: `${label} check-in ${du < 0 ? `${-du}d overdue` : du === 0 ? "due today" : `due in ${du}d`}`,
        sub: `Due ${fmtDate(due)}`,
      });
    });
  });
  // Training steps assigned to me
  emps.forEach((e) => (e.schedule || []).forEach((sc) => {
    if (sc.assignedTo !== user.id) return;
    const du = daysUntil(sc.date);
    items.push({
      id: `sc-${sc.id}`, emp: e, cat: "mine", tone: du !== null && du < 0 ? "danger" : "info", sort: 55,
      label: `Assigned to you: ${RIDE_MAP[sc.ride].label} → ${sc.targetLevel}`,
      sub: sc.date ? (du < 0 ? `${-du}d overdue` : du === 0 ? "Today" : `in ${du}d`) : "No date",
    });
  }));
  // Restaurant / general trainings scheduled with me as the trainer
  if (x) (x.assignments || []).forEach((a) => {
    if (a.trainer_id !== user.id || a.completed_at) return;
    const e = emps.find((em) => em.id === a.employee_id); const t = x.modulesById[a.module_id];
    if (!e || !t) return;
    const day = a.due_at ? String(a.due_at).slice(0, 10) : null; const du = day ? daysUntil(day) : null;
    items.push({
      id: `mt-${a.id}`, emp: e, cat: "mine", tone: du !== null && du < 0 ? "danger" : "info", sort: 54,
      label: `You're training them: ${t.title}`,
      sub: day ? (du < 0 ? `Was scheduled ${fmtDate(day)}` : du === 0 ? "Today" : `${fmtDate(day)} (in ${du}d)`) : "No date set",
      action: { label: "Train now", run: () => h.onModuleAction({ kind: "open", assignment: a, emp: e, training: t }) },
    });
  });
  const toneRank = { danger: 0, warn: 1, info: 2 };
  items.sort((a, b) => toneRank[a.tone] - toneRank[b.tone] || a.sort - b.sort || a.emp.name.localeCompare(b.emp.name));
  return items;
}

const ATTENTION_CATS = [
  { key: "all", label: "All" },
  { key: "signoffs", label: "Sign-offs & observations" },
  { key: "retrain", label: "Retrain" },
  { key: "overdue", label: "Overdue trainings" },
  { key: "policies", label: "Policies" },
  { key: "cards", label: "Cards" },
  { key: "checkins", label: "Check-ins" },
  { key: "mine", label: "Assigned to me" },
];

function AttentionList({ items, onOpenEmp, showEmp = true, emptyText = "Nothing needs attention right now." }) {
  if (!items.length) return <Empty text={emptyText} />;
  return (
    <div className="att-list">
      {items.map((it) => (
        <div className={`att-row tone-${it.tone}`} key={it.id}>
          <span className="att-dot" />
          <div className="att-main">
            {showEmp && (
              <button className="att-emp" onClick={() => onOpenEmp(it.emp)}>{it.emp.name}</button>
            )}
            <span className="att-label">{it.label}</span>
            {it.sub && <span className="att-sub">{it.sub}</span>}
          </div>
          {it.action
            ? <button className="btn-primary sm" onClick={it.action.run}>{it.action.label}</button>
            : showEmp ? <button className="btn-ghost sm" onClick={() => onOpenEmp(it.emp)}>Open</button> : null}
        </div>
      ))}
    </div>
  );
}

/* Needs attention grouped by person: one row each, worst first, tap to
   see that person's items (with their action buttons). */
function AttentionByPerson({ items, onOpenEmp, emptyText }) {
  const [open, setOpen] = useState({});
  const [showAll, setShowAll] = useState(false);
  const toneRank = { danger: 0, warn: 1, info: 2 };
  const groups = useMemo(() => {
    const m = new Map();
    items.forEach((it) => {
      if (!m.has(it.emp.id)) m.set(it.emp.id, { emp: it.emp, items: [] });
      m.get(it.emp.id).items.push(it);
    });
    const list = Array.from(m.values()).map((g) => {
      const worst = g.items.reduce((w, i) => Math.min(w, toneRank[i.tone]), 2);
      const counts = { danger: 0, warn: 0, info: 0 };
      g.items.forEach((i) => { counts[i.tone] += 1; });
      return { ...g, worst, counts };
    });
    list.sort((a, b) => a.worst - b.worst || b.counts.danger - a.counts.danger || b.items.length - a.items.length || a.emp.name.localeCompare(b.emp.name));
    return list;
  }, [items]);
  if (!groups.length) return <Empty text={emptyText} />;
  const shown = showAll ? groups : groups.slice(0, 20);
  const toneName = ["danger", "warn", "info"];
  return (
    <div className="attp-list">
      {shown.map((g) => {
        const isOpen = !!open[g.emp.id];
        // A short preview of what's going on, worst first.
        const preview = g.items.slice(0, 2).map((i) => i.label).join(" · ") + (g.items.length > 2 ? ` · +${g.items.length - 2} more` : "");
        return (
          <div className={`attp-card tone-${toneName[g.worst]} ${isOpen ? "open" : ""}`} key={g.emp.id}>
            <button className="attp-head" onClick={() => setOpen((p) => ({ ...p, [g.emp.id]: !p[g.emp.id] }))}>
              <span className="tr-avatar">{initialsOf(g.emp.name)}</span>
              <span className="attp-who">
                <strong>{g.emp.name}</strong>
                <span className="ri-sub">{g.emp.jobTitle || "—"} · {deptOf(g.emp)}</span>
                {!isOpen && <span className="attp-preview">{preview}</span>}
              </span>
              <span className="attp-counts">
                {g.counts.danger > 0 && <span className="tag tag-fail">{g.counts.danger} urgent</span>}
                {g.counts.warn > 0 && <span className="tag tag-warn">{g.counts.warn} to action</span>}
                {g.counts.info > 0 && <span className="tag tag-info">{g.counts.info} FYI</span>}
              </span>
              {isOpen ? <ChevronUp size={16} className="tr-chev" /> : <ChevronDown size={16} className="tr-chev" />}
            </button>
            {isOpen && (
              <div className="attp-body">
                <AttentionList items={g.items} showEmp={false} onOpenEmp={() => {}} />
                <button className="btn-ghost sm" onClick={() => onOpenEmp(g.emp)}>
                  Open {g.emp.name.split(" ")[0]}'s page <ChevronRight size={13} />
                </button>
              </div>
            )}
          </div>
        );
      })}
      {groups.length > shown.length && (
        <button className="btn-ghost sm" style={{ marginTop: 6, alignSelf: "flex-start" }} onClick={() => setShowAll(true)}>
          Show all {groups.length} people
        </button>
      )}
    </div>
  );
}

/* ---------------- HOME ---------------- */
function ManagerHome({ state, user, extras, h, onOpenRide, onDismiss, onAdhocObserve }) {
  const [cat, setCat] = useState("all");
  const [showAll, setShowAll] = useState(false);
  const [openSection, setOpenSection] = useState({}); // coverage / activity / notifications
  const items = useMemo(() => buildAttention(state, user, extras, h), [state, user, extras, h]);
  const count = (c) => items.filter((i) => i.cat === c).length;
  const filtered = cat === "all" ? items : items.filter((i) => i.cat === cat);
  const people = new Set(filtered.map((i) => i.emp.id)).size;
  const active = state.employees.filter((e) => !e.archived);
  const waiting = extras ? active.reduce((n, e) => n + employeeSnapshot(e, state, extras).waitingOnEmployee, 0) : null;
  const unread = state.notifications.filter((n) => !n.read);

  const tiles = [
    { key: "signoffs", label: "Sign-offs & observations", n: count("signoffs"), tone: "warn" },
    { key: "retrain", label: "Retrain", n: count("retrain"), tone: "danger" },
    { key: "overdue", label: "Overdue trainings", n: count("overdue"), tone: "danger" },
    { key: "policies", label: "Policies overdue", n: count("policies"), tone: "danger" },
    { key: "cards", label: "Cards expiring", n: count("cards"), tone: "warn" },
  ];

  const coverage = RIDES.map((r) => {
    const counts = { T: 0, L2: 0, L3: 0, L4: 0 };
    active.forEach((e) => { const lv = e.levels[r.key]; if (counts[lv] !== undefined) counts[lv] += 1; });
    return { ride: r, counts, solo: counts.L2 + counts.L3 + counts.L4 };
  });
  const toggle = (k) => setOpenSection((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div className="home">
      <div className="home-hello">
        <div>
          <h2>Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, {user.name.split(" ")[0]}</h2>
          <span className="dim">
            {active.length} active team members
            {waiting !== null ? ` · ${waiting} open items waiting on employees` : ""}
          </span>
        </div>
        {can.observe(user) && (
          <button className="btn-primary" onClick={onAdhocObserve}><Eye size={14} /> New observation</button>
        )}
      </div>

      <div className="home-tiles">
        {tiles.map((t) => (
          <button key={t.key} className={`home-tile ${t.n ? `tone-${t.tone}` : "tone-ok"} ${cat === t.key ? "sel" : ""}`}
            onClick={() => { setCat(cat === t.key ? "all" : t.key); setShowAll(false); }}>
            <span className="ht-n">{extras || t.key === "signoffs" || t.key === "retrain" ? t.n : "…"}</span>
            <span className="ht-l">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="home-cols">
      <section className="panel home-att">
        <div className="panel-head">
          <h2><AlertCircle size={16} /> Needs attention</h2>
          <span className="count-pill">{people} {people === 1 ? "person" : "people"} · {filtered.length} item{filtered.length === 1 ? "" : "s"}</span>
        </div>
        <div className="chip-row">
          {ATTENTION_CATS.map((c) => {
            const n = c.key === "all" ? items.length : count(c.key);
            if (c.key !== "all" && n === 0) return null;
            return (
              <button key={c.key} className={`chip ${cat === c.key ? "on" : ""}`} onClick={() => { setCat(c.key); setShowAll(false); }}>
                {c.label} <span className="chip-n">{n}</span>
              </button>
            );
          })}
        </div>
        <div className="panel-body">
          {!extras && <div className="dim" style={{ fontSize: 12, marginBottom: 6 }}>Loading trainings, policies and cards…</div>}
          <AttentionByPerson items={filtered} onOpenEmp={(e) => h.openEmployee(e)} emptyText={cat === "all" ? "All clear — nothing needs attention right now." : "Nothing in this category."} />
        </div>
      </section>

      <section className="panel home-cov">
        <div className="panel-head">
          <h2><LayoutDashboard size={16} /> Ride coverage</h2>
          <span className="count-pill">{coverage.filter((c) => c.solo <= 2).length} thin</span>
        </div>
        <div className="panel-body">
          <div className="cov-mini">
            {coverage.map((c) => (
              <button key={c.ride.key} className="cov-mini-row" onClick={() => onOpenRide(c.ride.key)}>
                <span className="cmr-main">
                  <span className="cmr-name">{c.ride.label}</span>
                  <span className="cmr-bar">
                    {c.ride.ladder.map((lv) => c.counts[lv] > 0 && (
                      <span key={lv} style={{ flex: c.counts[lv], background: LEVELS[lv].color }} title={`${c.counts[lv]} at ${lv}`} />
                    ))}
                    {c.solo + c.counts.T === 0 && <span style={{ flex: 1, background: "var(--l0)" }} />}
                  </span>
                  <span className="dim cmr-sub">{c.solo} can run it · {c.counts.T} in training</span>
                </span>
                <span className={`tag ${c.solo === 0 ? "tag-fail" : c.solo <= 2 ? "tag-warn" : "tag-ok"}`}>{c.solo}</span>
                <ChevronRight size={14} />
              </button>
            ))}
          </div>
        </div>
      </section>
      </div>

      {unread.length > 0 && (
        <section className="panel">
          <button className="panel-head as-btn" onClick={() => toggle("notif")}>
            <h2><Bell size={16} /> Notifications</h2>
            <span className="count-pill">{unread.length}</span>
            {openSection.notif ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {openSection.notif && (
            <div className="panel-body">
              {unread.slice(0, 12).map((n) => (
                <div className="row-item" key={n.id}>
                  <div className="ri-main"><span>{n.text}</span><span className="ri-sub">{fmtDate(n.date)}</span></div>
                  <button className="btn-ghost sm" onClick={() => onDismiss(n.id)}>Dismiss</button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="panel">
        <button className="panel-head as-btn" onClick={() => toggle("log")}>
          <h2><Clock size={16} /> Recent activity</h2>
          {openSection.log ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {openSection.log && (
          <div className="panel-body log">
            {state.log.slice(0, 20).map((l, i) => (
              <div className={`log-row log-${l.type}`} key={i}>
                <span className="log-dot" /><span className="log-text">{l.text}</span><span className="log-date">{fmtDate(l.date)}</span>
              </div>
            ))}
            {state.log.length === 0 && <Empty text="No activity yet." />}
          </div>
        )}
      </section>
    </div>
  );
}

/* ---------------- TEAM ---------------- */
function TeamView({ state, user, extras, onOpenEmp }) {
  const [dept, setDept] = useState("all");
  const [q, setQ] = useState("");
  const [view, setView] = useState("list");
  const [showArchived, setShowArchived] = useState(false);
  const [sort, setSort] = useState("name");

  const pool = state.employees.filter((e) => (showArchived ? true : !e.archived));
  const deptCounts = {};
  pool.forEach((e) => { deptCounts[deptOf(e)] = (deptCounts[deptOf(e)] || 0) + 1; });
  const needle = q.trim().toLowerCase();
  let emps = pool
    .filter((e) => dept === "all" || deptOf(e) === dept)
    .filter((e) => !needle || e.name.toLowerCase().includes(needle) || (e.jobTitle || "").toLowerCase().includes(needle));
  const snaps = {};
  emps.forEach((e) => { snaps[e.id] = employeeSnapshot(e, state, extras); });
  emps = emps.sort((a, b) => {
    if (sort === "todo") return (snaps[b.id].problems * 100 + snaps[b.id].waitingOnEmployee) - (snaps[a.id].problems * 100 + snaps[a.id].waitingOnEmployee) || a.name.localeCompare(b.name);
    if (sort === "start") return String(b.startDate || "").localeCompare(String(a.startDate || ""));
    return a.name.localeCompare(b.name);
  });
  const showRides = (e) => deptOf(e) === "Attractions" || RIDES.some((r) => (e.levels?.[r.key] || "0") !== "0");
  const archivedCount = state.employees.filter((e) => e.archived).length;
  const matrixSection = dept === "Restaurant" ? "restaurant" : dept === "Kart & Ride Service Tech" ? "service_tech" : null;

  return (
    <div className="team">
      <div className="team-bar">
        <div className="search team-search">
          <Search size={14} />
          <input placeholder="Search name or job title…" value={q} onChange={(e) => setQ(e.target.value)} />
          {q && <button className="icon-btn" onClick={() => setQ("")}><X size={12} /></button>}
        </div>
        <div className="seg">
          <button className={view === "list" ? "on" : ""} onClick={() => setView("list")}>List</button>
          <button className={view === "matrix" ? "on" : ""} onClick={() => setView("matrix")}>Skills matrix</button>
        </div>
      </div>
      <div className="chip-row" style={{ padding: "0 0 10px" }}>
        <button className={`chip ${dept === "all" ? "on" : ""}`} onClick={() => setDept("all")}>Everyone <span className="chip-n">{pool.length}</span></button>
        {DEPARTMENTS.map((d) => (
          <button key={d} className={`chip ${dept === d ? "on" : ""}`} onClick={() => setDept(d)}>
            {d} <span className="chip-n">{deptCounts[d] || 0}</span>
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {view === "list" && (
          <select className="team-sort" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="name">Sort: Name</option>
            <option value="todo">Sort: Needs attention</option>
            <option value="start">Sort: Newest</option>
          </select>
        )}
        <label className="check-row sm"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Deactivated ({archivedCount})</label>
      </div>

      {view === "list" && (
        <div className="team-list">
          <div className="team-key">
            <span><i style={{ background: "var(--lT)" }} />T / in progress</span>
            <span><i style={{ background: "var(--l2)" }} />L2 / done</span>
            <span><i style={{ background: "var(--l3)" }} />L3</span>
            <span><i style={{ background: "var(--l4)" }} />L4</span>
            <span><i style={{ background: "#e5624a" }} />retrain / failed / overdue</span>
            <span><i style={{ background: "var(--l0)" }} />not started / not assigned</span>
          </div>
          {emps.length > 0 && (
            <div className="team-head">
              <span /><span>Team member</span><span>Rides (solo / 7)</span><span>Restaurant (done / total)</span><span>Issues & to-dos</span><span />
            </div>
          )}
          {emps.length === 0 && <Empty text="No one matches." />}
          {emps.map((e) => {
            const s = snaps[e.id];
            return (
              <button className={`team-row ${e.archived ? "archived" : ""}`} key={e.id} onClick={() => onOpenEmp(e)}>
                <span className="tr-avatar">{initialsOf(e.name)}</span>
                <span className="tr-who">
                  <strong>{e.name}</strong>
                  <span className="ri-sub">{e.jobTitle || "—"} · {deptOf(e)}{e.archived ? " · deactivated" : ""}</span>
                </span>
                <span className="tr-col tr-col-rides">
                  <span className="tr-col-label">Rides</span>
                  <span className="tr-rides" title={RIDES.map((r) => `${r.label}: ${LEVELS[e.levels[r.key] || "0"].label}`).join("\n")}>
                    {RIDES.map((r) => {
                      const lv = e.levels[r.key] || "0";
                      return <i key={r.key} className={e.retrain?.[r.key] ? "rt" : ""} style={{ background: lv === "0" ? "var(--l0)" : LEVELS[lv].color }} />;
                    })}
                    <span className="tr-cap">{s.rideSolo}/{RIDES.length}</span>
                  </span>
                </span>
                <span className="tr-col tr-col-rest">
                  <span className="tr-col-label">Restaurant</span>
                  <AreaTrainingSquares emp={e} extras={extras} section="restaurant" />
                </span>
                <span className="tr-flags">
                  {s.problems > 0 && <span className="tag tag-fail">{s.problems} issue{s.problems === 1 ? "" : "s"}</span>}
                  {s.waitingOnEmployee > 0
                    ? <span className="tag tag-warn">{s.waitingOnEmployee} to do</span>
                    : extras && s.problems === 0 ? <span className="tag tag-ok">Up to date</span> : null}
                </span>
                <ChevronRight size={16} className="tr-chev" />
              </button>
            );
          })}
        </div>
      )}

      {view === "matrix" && matrixSection && (
        <SectionDashboardView section={matrixSection} employees={state.employees} user={user} />
      )}
      {view === "matrix" && !matrixSection && (
        <>
          <div className="table-wrap">
            <table className="cert-table">
              <thead>
                <tr>
                  <th className="sticky-col">Crew</th>
                  {RIDES.map((r) => <th key={r.key} className="ride-col"><span>{r.label}</span></th>)}
                </tr>
              </thead>
              <tbody>
                {emps.map((e) => (
                  <tr key={e.id} className={e.archived ? "archived" : ""} onClick={() => onOpenEmp(e)} style={{ cursor: "pointer" }}>
                    <td className="sticky-col name-cell">
                      {e.name}
                      {deptOf(e) !== "Attractions" && <span className="mini-flag dept">{deptOf(e)}</span>}
                    </td>
                    {RIDES.map((r) => {
                      const lv = e.levels[r.key] || "0";
                      const rt = e.retrain?.[r.key];
                      return (
                        <td key={r.key}>
                          {rt
                            ? <span className="cell-badge rt" title={`Retrain at ${rt.level}`}>{String(rt.level).replace("RT-", "RT")}</span>
                            : <span className="cell-badge" style={{ background: LEVELS[lv].color }} title={LEVELS[lv].desc}>{LEVELS[lv].label}</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Legend />
        </>
      )}
    </div>
  );
}

/* ---------------- QUIZ RESULTS (one employee) ---------------- */
function QuizResultsPanel({ emp, state, extras }) {
  const [open, setOpen] = useState({});
  const ride = state.quizzes.filter((q) => q.empId === emp.id).map((q) => ({
    key: `r-${q.id}`, kind: "ride", title: `${RIDE_MAP[q.ride]?.label || q.ride} certification`, sub: `Ride quiz · target ${q.targetLevel}`,
    status: q.status, score: q.score, attempts: q.attempts || 0,
    date: q.completedDate || q.sentDate, missedTopics: (q.missedTopics || []).map((t) => topicLabel(q.ride, t)), missedRaw: q.missedTopics || [],
    history: q.attemptHistory || [], questions: q.questions || [], answers: q.answers, correctKey: "correct", textKey: "q",
    retrain: !!emp.retrain?.[q.ride], ride: q.ride,
    reached: !!q.targetLevel && hasReached(emp, q.ride, q.targetLevel),
  }));
  const mod = (extras?.moduleQuizzes || []).filter((q) => q.employee_id === emp.id).map((q) => ({
    key: `m-${q.id}`, kind: "module", title: extras.modulesById[q.module_id]?.title || "Training quiz", sub: "Training quiz",
    status: q.status, score: q.score, attempts: q.attempts || 0,
    date: q.completed_date || q.sent_date, missedTopics: Array.isArray(q.missed_topics) ? q.missed_topics : [], missedRaw: Array.isArray(q.missed_topics) ? q.missed_topics : [],
    history: q.attempt_history || [], questions: q.questions || [], answers: q.answers, correctKey: "correct_index", textKey: "prompt",
  }));
  const all = [...ride, ...mod].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));

  const verdict = (r) => {
    if (r.status === "sent") return { text: r.attempts ? "Retake waiting" : "Not taken yet", cls: "tag-info" };
    if (r.status === "failed") return { text: `Failed ${r.score ?? ""}%`, cls: "tag-fail" };
    if (r.status === "passed_gaps") return { text: `Passed ${r.score}%`, cls: "tag-ok" };
    if (r.score === null || r.score === undefined) return { text: r.status, cls: "tag-muted" };
    return { text: `Passed ${r.score}%`, cls: "tag-ok" };
  };
  const nextStep = (r) => {
    if (r.status === "failed" || r.retrain) return "Retrain on the topics below before the retake.";
    if (r.reached) return null; // already advanced — nothing left to do for this quiz
    if (r.status === "passed_gaps") return "Passed. Go over the topics below with them during the L2 observation.";
    if (r.status === "passed_full") return "Ready for the L2 observation.";
    return null;
  };
  // Missed questions for the latest attempt: prefer the stored attempt record;
  // fall back to answers + questions when available.
  const latestMissed = (r) => {
    const last = r.history[r.history.length - 1];
    if (last?.missed) return last.missed;
    if (r.answers && r.questions.length) {
      return r.questions.filter((qq) => r.answers[qq.id] !== qq[r.correctKey]).map((qq) => ({
        q: qq[r.textKey], topic: qq.topic, chosen: qq.choices?.[r.answers[qq.id]] ?? null, correct: qq.choices?.[qq[r.correctKey]] ?? null,
      }));
    }
    return null;
  };

  // Attempts taken before answers were saved: we still have the quiz's
  // questions and the topics missed, so list the questions on those topics.
  const topicQuestions = (r) => {
    if (!r.missedRaw.length || !r.questions.length) return [];
    const set = new Set(r.missedRaw.map(String));
    return r.questions.filter((qq) => qq.topic && set.has(String(qq.topic))).map((qq) => ({
      q: qq[r.textKey], topic: qq.topic, correct: qq.choices?.[qq[r.correctKey]] ?? null,
    }));
  };

  if (all.length === 0) return <Empty text="No quizzes yet." />;
  return (
    <div className="qr-list">
      {all.map((r) => {
        const v = verdict(r);
        const missed = latestMissed(r);
        const older = !missed ? topicQuestions(r) : [];
        const wrongCount = r.score !== null && r.score !== undefined && r.questions.length
          ? Math.round(r.questions.length * (1 - r.score / 100)) : null;
        const ns = nextStep(r);
        const isOpen = open[r.key];
        return (
          <div className={`qr-card ${r.status === "failed" || r.retrain ? "bad" : ""}`} key={r.key}>
            <div className="qr-head">
              <div>
                <strong>{r.title}</strong>
                <div className="ri-sub">{r.sub} · {r.date ? fmtDate(String(r.date).slice(0, 10)) : "—"} · {r.attempts} attempt{r.attempts === 1 ? "" : "s"}</div>
              </div>
              <span className={`tag ${v.cls}`}>{v.text}</span>
            </div>
            {r.retrain && <div className="qr-flag"><AlertTriangle size={13} /> In retrain on this ride</div>}
            {r.missedTopics.length > 0 && (
              <div className="qr-topics">
                <span className="qr-k">{r.status === "failed" || r.retrain ? "Retrain on:" : "Missed — review:"}</span>
                {r.missedTopics.map((t) => <span className="qr-topic" key={t}>{t}</span>)}
              </div>
            )}
            {ns && <div className="qr-next">{ns}</div>}
            {missed && missed.length > 0 && (
              <button className="btn-ghost-sm" onClick={() => setOpen((p) => ({ ...p, [r.key]: !p[r.key] }))}>
                {isOpen ? "Hide" : "Show"} the {missed.length} missed question{missed.length === 1 ? "" : "s"}
              </button>
            )}
            {missed && missed.length === 0 && r.score !== null && r.score !== undefined && (
              <div className="dim" style={{ fontSize: 12 }}>No questions missed on the latest attempt.</div>
            )}
            {!missed && older.length > 0 && (
              <button className="btn-ghost-sm" onClick={() => setOpen((p) => ({ ...p, [r.key]: !p[r.key] }))}>
                {isOpen ? "Hide" : "Show"} the {older.length} question{older.length === 1 ? "" : "s"} on the missed topics
              </button>
            )}
            {!missed && older.length === 0 && r.score !== null && r.score !== undefined && r.score < 100 && (
              <div className="dim" style={{ fontSize: 12 }}>Question-by-question detail is saved for attempts from Oct 28, 2026 on.</div>
            )}
            {isOpen && !missed && older.length > 0 && (
              <>
                <div className="qr-note">
                  Answers weren't saved for this attempt (taken before Oct 28, 2026), so these are the questions on the
                  topics {emp.name.split(" ")[0]} missed{wrongCount ? ` — ${wrongCount} answer${wrongCount === 1 ? " was" : "s were"} wrong in total` : ""}.
                  Review each one with the correct answer.
                </div>
                <ol className="qr-missed">
                  {older.map((m, i) => (
                    <li key={i}>
                      <div className="qm-q">{m.q}</div>
                      <div className="ri-sub">Topic: {m.topic}</div>
                      <div className="qm-a good"><Check size={12} /> Correct: {m.correct ?? "—"}</div>
                    </li>
                  ))}
                </ol>
              </>
            )}
            {isOpen && missed && (
              <ol className="qr-missed">
                {missed.map((m, i) => (
                  <li key={i}>
                    <div className="qm-q">{m.q}</div>
                    <div className="qm-a bad"><X size={12} /> Answered: {m.chosen ?? "—"}</div>
                    <div className="qm-a good"><Check size={12} /> Correct: {m.correct ?? "—"}</div>
                  </li>
                ))}
              </ol>
            )}
            {r.history.length > 1 && (
              <div className="qr-attempts">
                {r.history.map((a, i) => (
                  <span key={i} className={`tag ${a.score >= PASS_SCORE ? "tag-ok" : "tag-fail"}`}>
                    Try {a.attempt || i + 1}: {a.score}% · {fmtDate(String(a.at).slice(0, 10))}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- EMPLOYEE PAGE ---------------- */
const TRAINING_AREAS = [
  { tab: "rideops", section: "ride_ops", label: "Ride operations (all rides)", showAll: true, inRides: true },
  { tab: "restaurant", section: "restaurant", label: "Restaurant / Inside", showAll: true },
  { tab: "leadership", section: "leadership", label: "Leadership & Training", showAll: true },
  { tab: "general", section: "general", label: "General — for everyone (safety, compliance, OSHA and more)", showAll: true },
  { tab: "servicetech", section: "service_tech", label: "Kart & Ride Service Tech", showAll: false },
];
const AREA_TAB = { ride_ops: "rides", restaurant: "restaurant", leadership: "leadership", general: "general", service_tech: "servicetech" };
function areaRows(area, extras, myAsg) {
  if (!extras) return [];
  const mods = extras.modules.filter((m) => m.section === area.section && !m.archived).sort((x, y) => x.title.localeCompare(y.title));
  const mine = myAsg.filter((a) => extras.modulesById[a.module_id]?.section === area.section);
  return area.showAll
    ? mods.map((m) => ({ m, a: mine.find((a) => a.module_id === m.id && a.completed_at) || mine.find((a) => a.module_id === m.id) }))
    : mine.map((a) => ({ m: extras.modulesById[a.module_id], a }));
}
/* Can this person start training (T) on this ride yet? Mirrors the unlock chain:
   all 4 Kiddie Land rides at L2 → Road; Road L2 → Sprint; Sprint L2 → Euro. */
function rideAvailable(emp, ride) {
  if (AMUSEMENT_RIDES.includes(ride)) return { ok: true };
  if (ride === "road") return AMUSEMENT_RIDES.every((r) => hasReached(emp, r, "L2")) ? { ok: true } : { ok: false, why: "Opens when all 4 Kiddie Land rides are at L2" };
  const prev = TRACK_ORDER[TRACK_ORDER.indexOf(ride) - 1];
  return hasReached(emp, prev, "L2") ? { ok: true } : { ok: false, why: `Opens when ${RIDE_MAP[prev].label} is at L2` };
}

/* Pick the training date and trainer. Scheduling never means "do it alone". */
function ScheduleTrainingModal({ title, trainers, initialDate, initialTrainer, onClose, onSave }) {
  const [date, setDate] = useState(initialDate || "");
  const [trainerId, setTrainerId] = useState(initialTrainer || "");
  const [busy, setBusy] = useState(false);
  return (
    <div className="modal-overlay" style={{ zIndex: 210 }}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-head"><h3><CalendarClock size={18} /> Schedule training</h3><button className="icon-btn" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body">
          <p style={{ marginTop: 0 }}><strong>{title}</strong></p>
          <label className="field"><span>Training date</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
          <label className="field"><span>Trainer</span>
            <select value={trainerId} onChange={(e) => setTrainerId(e.target.value)}>
              <option value="">Choose later</option>
              {(trainers || []).map((t) => <option key={t.id} value={t.id}>{t.full_name || t.name}</option>)}
            </select>
          </label>
          <p className="dim" style={{ fontSize: 12 }}>The trainer goes through it with them on that day. It shows on the trainer's Home and on the employee's dashboard as upcoming training.</p>
        </div>
        <div className="modal-foot" style={{ justifyContent: "space-between" }}>
          <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary" disabled={busy} onClick={async () => { setBusy(true); await onSave(date || null, trainerId || null); setBusy(false); }}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

/* Read a ride chapter (or General Amusements) with the employee. */
function RideTrainingModal({ rideKey, modules, emp, canSignoff, onSignoff, onClose }) {
  const mod = moduleForKey(rideKey, modules);
  const label = rideKey === "_general" ? "General Amusements Training" : `${RIDE_MAP[rideKey]?.label} training`;
  return (
    <div className="modal-overlay" style={{ zIndex: 200 }}>
      <div className="modal" style={{ maxWidth: 1100, width: "95vw", height: "90vh", maxHeight: "90vh" }}>
        <div className="modal-head"><h3><ClipboardCheck size={18} /> {label} — {emp.name}</h3><button className="icon-btn" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body" style={{ padding: 20, flex: 1, minHeight: 0, overflowY: "auto" }}>
          {mod?.body_md ? <Markdown source={mod.body_md} /> : <Empty text="This training has no content yet." />}
        </div>
        <div className="modal-foot" style={{ justifyContent: "space-between" }}>
          <button className="btn-ghost" onClick={onClose}>Close</button>
          {canSignoff && rideKey !== "_general" && <button className="btn-primary" onClick={onSignoff}>Sign off training <ArrowRight size={14} /></button>}
        </div>
      </div>
    </div>
  );
}

const EMP_TABS = [
  { key: "overview", label: "Overview" },
  { key: "rides", label: "Rides" },
  { key: "restaurant", label: "Restaurant" },
  { key: "leadership", label: "Leadership" },
  { key: "general", label: "General" },
  { key: "servicetech", label: "Service Tech" },
  { key: "quizzes", label: "Quizzes" },
  { key: "policies", label: "Policies" },
  { key: "goals", label: "Goals" },
  { key: "documents", label: "Documents" },
  { key: "history", label: "History" },
];

function EmployeePage({ empId, initialTab, state, user, trainers, extras, reloadExtras, h, onBack, onViewAs, onAssign, onSignoff, onSchedule, onManage, onObserveEmp }) {
  const startTab = initialTab === "trainings" ? "restaurant" : (initialTab || "overview");
  const [tab, setTab] = useState(startTab);
  const [busyMod, setBusyMod] = useState(null);
  const [areaErr, setAreaErr] = useState("");
  const [schedFor, setSchedFor] = useState(null); // { m, a }
  const [rideRead, setRideRead] = useState(null); // ride key or "_general"
  useEffect(() => { setTab(startTab); }, [empId, initialTab]);
  useEffect(() => { window.scrollTo?.(0, 0); }, [empId]);
  const emp = state.employees.find((e) => e.id === empId);
  if (!emp) return <div className="emp-page"><button className="btn-ghost" onClick={onBack}><ArrowLeft size={14} /> Back</button><Empty text="Employee not found." /></div>;

  const s = employeeSnapshot(emp, state, extras);
  const items = buildAttention(state, user, extras, h, emp.id);
  const isMgr = roleRank(user.role) >= roleRank("manager");
  const myAsg = (extras?.assignments || []).filter((a) => a.employee_id === emp.id && extras.modulesById[a.module_id])
    .sort((a, b) => (a.completed_at ? 1 : 0) - (b.completed_at ? 1 : 0) || String(b.assigned_at).localeCompare(String(a.assigned_at)));
  const quizIssues = state.quizzes.filter((q) => q.empId === emp.id && q.status === "failed").length + s.trainingsFailed.length;
  const areaInfo = Object.fromEntries(TRAINING_AREAS.map((ar) => {
    const rows = areaRows(ar, extras, myAsg);
    return [ar.tab, { ...ar, rows, done: rows.filter((r) => r.a?.completed_at).length, open: rows.filter((r) => r.a && !r.a.completed_at).length }];
  }));
  const tabBadge = {
    rides: s.retrainRides.length + areaInfo.rideops.open, restaurant: areaInfo.restaurant.open, leadership: areaInfo.leadership.open, general: areaInfo.general.open, servicetech: areaInfo.servicetech.open, quizzes: quizIssues,
    policies: s.policiesToSign.length, documents: s.docsExpired.length + s.docsExpiring.length,
  };
  const myPolicies = (extras?.policies || []).filter((p) => p.published && policyVisibleTo(p, emp, extras.groupIdx));
  const canTrain = roleRank(user.role) >= roleRank("trainer");
  const myName = user.full_name || user.name || null;
  // Train now = sit down with them and open the training itself. Always available.
  const trainNow = async (m, a) => {
    setAreaErr("");
    if (a) { h.onModuleAction({ kind: "open", assignment: a, emp, training: m, reviewOnly: !!a.completed_at }); return; }
    setBusyMod(m.id);
    const res = await db.createModuleAssignment({ module_id: m.id, employee_id: emp.id, due_at: null, trainer_id: user.id, trainer_name: myName });
    setBusyMod(null);
    if (!res.ok) { setAreaErr(res.message); return; }
    await reloadExtras?.();
    h.onModuleAction({ kind: "open", assignment: res.assignment, emp, training: m });
  };
  // Schedule = pick the date and the trainer. Never homework.
  const saveSchedule = async (m, a, date, trainerId) => {
    setAreaErr("");
    const tr = (trainers || []).find((t) => t.id === trainerId);
    const patch = { due_at: date ? `${date}T12:00:00Z` : null, trainer_id: trainerId, trainer_name: tr ? (tr.full_name || tr.name) : null };
    const res = a ? await db.updateModuleAssignment(a.id, patch) : await db.createModuleAssignment({ module_id: m.id, employee_id: emp.id, ...patch });
    if (!res.ok) { setAreaErr(res.message); return; }
    setSchedFor(null);
    await reloadExtras?.();
  };

  const renderArea = (ar) => (
          <section className="panel">
            <div className="panel-head"><h2><ClipboardCheck size={16} /> {ar.label}</h2><span className="count-pill">{ar.done}/{ar.rows.length} done</span></div>
            <div className="panel-body">
              {!extras && <Empty text="Loading…" />}
              {extras && ar.rows.length === 0 && <Empty text="No trainings in this area yet." />}
              {ar.rows.map(({ m, a }) => {
                if (!a) return (
                  <div className="row-item" key={m.id} style={{ flexWrap: "wrap" }}>
                    <div className="ri-main"><strong>{m.title}</strong><span className="ri-sub">{m.description || ""}</span></div>
                    <span className="tag tag-muted">Not trained yet</span>
                    {canTrain && !emp.archived && (
                      <>
                        <button className="btn-primary sm" disabled={!!busyMod} onClick={() => trainNow(m, null)}>{busyMod === m.id ? "Opening…" : "Train now"}</button>
                        <button className="btn-ghost sm" onClick={() => setSchedFor({ m, a: null })}>Schedule</button>
                      </>
                    )}
                  </div>
                );
                const st = moduleAssignmentStatus(a, m);
                const quizCleared = a.quiz_status === "passed" || !m.has_quiz;
                const needsPractical = !a.completed_at && m.requires_practical && quizCleared && a.practical_status !== "complete";
                const needsVerify = !a.completed_at && a.review_status === "pending" && !needsPractical;
                return (
                  <div className="row-item" key={a.id} style={{ flexWrap: "wrap" }}>
                    <div className="ri-main"><strong>{m.title}</strong><span className="ri-sub">{!a.completed_at && a.trainer_name ? `Trainer: ${a.trainer_name}` : (m.description || "")}</span></div>
                    <span className={`tag ${st.cls}`}>{st.text}</span>
                    {isMgr && (needsPractical || needsVerify) && (
                      <button className="btn-primary sm" onClick={() => h.onModuleAction({ kind: needsPractical ? "practical" : "verify", assignment: a, emp, training: m })}>
                        {needsPractical ? "Practical" : "Verify"}
                      </button>
                    )}
                    {canTrain && (
                      <button className={`btn-${needsPractical || needsVerify || a.completed_at ? "ghost" : "primary"} sm`} onClick={() => trainNow(m, a)}>
                        {a.completed_at ? "Review" : "Train now"}
                      </button>
                    )}
                    {canTrain && !a.completed_at && (
                      <button className="btn-ghost sm" onClick={() => setSchedFor({ m, a })}>{a.due_at || a.trainer_id ? "Reschedule" : "Schedule"}</button>
                    )}
                  </div>
                );
              })}
              {areaErr && <div className="login-err"><AlertCircle size={14} /> {areaErr}</div>}
              {extras && canTrain && ar.showAll && <p className="dim" style={{ fontSize: 12, margin: "10px 6px 0" }}>Anyone can be trained on anything. <strong>Train now</strong> opens the training so you can go through it with them. <strong>Schedule</strong> sets the date and the trainer. Trainings are never homework; employees can read ahead but always train with a trainer.</p>}
            </div>
          </section>
  );

  return (
    <div className="emp-page">
      <button className="emp-back" onClick={onBack}><ArrowLeft size={15} /> Back</button>
      <div className="emp-hero">
        <span className="emp-avatar">{initialsOf(emp.name)}</span>
        <div className="emp-id">
          <h2>{emp.name} {emp.archived && <span className="mini-flag arch">Deactivated</span>}</h2>
          <span className="dim">{emp.jobTitle || "—"} · {deptOf(emp)} · started {emp.startDate ? fmtDate(emp.startDate) : "—"}</span>
          <div className="emp-chips">
            {s.problems > 0 && <span className="tag tag-fail">{s.problems} issue{s.problems === 1 ? "" : "s"}</span>}
            {s.waitingOnEmployee > 0 && <span className="tag tag-warn">{s.waitingOnEmployee} to do</span>}
            {extras && s.problems === 0 && s.waitingOnEmployee === 0 && <span className="tag tag-ok">Up to date</span>}
          </div>
        </div>
        <div className="emp-actions">
          {can.observe(user) && <button className="btn-ghost sm" onClick={() => onObserveEmp(emp)}><Eye size={13} /> Observe</button>}
          <button className="btn-ghost sm" onClick={() => onSignoff(emp)}><Check size={13} /> Sign off</button>
          <button className="btn-ghost sm" onClick={() => onSchedule(emp)}><Plus size={13} /> Schedule</button>
          {isMgr && <button className="btn-ghost sm" onClick={() => onManage(emp)}><ShieldCheck size={13} /> Manage</button>}
          {onViewAs && !emp.archived && <button className="btn-ghost sm" onClick={() => onViewAs(emp)}><User size={13} /> View as employee</button>}
        </div>
      </div>

      <div className="emp-tabs">
        {EMP_TABS.filter((t) => (t.key !== "documents" || can.viewDocuments(user)) && (t.key !== "servicetech" || areaInfo.servicetech.rows.length)).map((t) => (
          <button key={t.key} className={tab === t.key ? "on" : ""} onClick={() => setTab(t.key)}>
            {t.label}{tabBadge[t.key] ? <span className="et-n">{tabBadge[t.key]}</span> : null}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="emp-body">
          <div className="emp-stats">
            <button className="emp-stat" onClick={() => setTab("rides")}><span className="es-n">{s.rideSolo}<small>/{RIDES.length}</small></span><span className="es-l">rides solo{s.rideTraining ? ` · ${s.rideTraining} in training` : ""}</span></button>
            <button className="emp-stat" onClick={() => setTab("restaurant")}><span className="es-n">{areaInfo.restaurant.done}<small>/{areaInfo.restaurant.rows.length}</small></span><span className="es-l">restaurant trainings</span></button>
            <button className="emp-stat" onClick={() => setTab("general")}><span className="es-n">{areaInfo.general.done}<small>/{areaInfo.general.rows.length}</small></span><span className="es-l">general trainings</span></button>
            <button className="emp-stat" onClick={() => setTab("policies")}><span className="es-n">{s.policiesRequired.length - s.policiesToSign.length}<small>/{s.policiesRequired.length}</small></span><span className="es-l">policies signed</span></button>
            <button className="emp-stat" onClick={() => setTab("quizzes")}><span className="es-n">{quizIssues}</span><span className="es-l">failed quizzes</span></button>
          </div>
          <section className="panel">
            <div className="panel-head"><h2><AlertCircle size={16} /> Needs a manager</h2><span className="count-pill">{items.length}</span></div>
            <div className="panel-body"><AttentionList items={items} showEmp={false} onOpenEmp={() => {}} emptyText="Nothing needs a manager for this person right now." /></div>
          </section>
          {s.trainingsOpen.length > 0 && (
            <section className="panel">
              <div className="panel-head"><h2><BookOpen size={16} /> In training</h2><span className="count-pill">{s.trainingsOpen.length}</span></div>
              <div className="panel-body">
                {s.trainingsOpen.map((a) => {
                  const t = extras.modulesById[a.module_id]; const st = moduleAssignmentStatus(a, t);
                  return (
                    <div className="row-item" key={a.id} style={{ flexWrap: "wrap" }}>
                      <div className="ri-main"><strong>{t.title}</strong><span className="ri-sub">{a.trainer_name ? `Trainer: ${a.trainer_name}` : "No trainer set"}</span></div>
                      <span className={`tag ${st.cls}`}>{st.text}</span>
                      {canTrain && <button className="btn-ghost sm" onClick={() => trainNow(t, a)}>Train now</button>}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
          <section className="panel">
            <div className="panel-head"><h2><Clock size={16} /> Waiting on {emp.name.split(" ")[0]}</h2><span className="count-pill">{s.waitingOnEmployee}</span></div>
            <div className="panel-body">
              {s.waitingOnEmployee === 0 && <Empty text="Nothing outstanding." />}
              {s.quizzesPending.map((q) => (
                <div className="row-item" key={q.id}><div className="ri-main"><strong>{RIDE_MAP[q.ride].label} quiz</strong><span className="ri-sub">{q.status === "failed" ? "Retake" : "Sent"} {fmtDate(q.sentDate)}</span></div><span className="tag tag-info">Quiz</span></div>
              ))}
              {s.policiesToSign.map((p) => {
                const due = policyDueDate(p, emp, extras.groupIdx);
                return <div className="row-item" key={p.id}><div className="ri-main"><strong>{p.title}</strong><span className="ri-sub">Policy signature</span></div><span className={`tag ${due && due < todayISO() ? "tag-fail" : "tag-warn"}`}>{due ? `Due ${fmtDate(due)}` : "To sign"}</span></div>;
              })}
            </div>
          </section>
        </div>
      )}

      {tab === "rides" && (
        <div className="emp-body">
          <div className="row-item ga-row">
            <div className="ri-main"><strong>General Amusements Training</strong><span className="ri-sub">Day 1 for everyone — park rules and safety. Review it with them before any ride.</span></div>
            {canTrain && <button className="btn-primary sm" onClick={() => setRideRead("_general")}>Train now</button>}
          </div>
          <div className="ride-cards">
            {RIDES.map((r) => {
              const lv = emp.levels[r.key] || "0";
              const rt = emp.retrain?.[r.key];
              const nxt = nextLevel(lv, r.key);
              const avail = rideAvailable(emp, r.key);
              return (
                <div className={`ride-card ${lv === "0" ? "locked" : ""}`} key={r.key}>
                  <div className="rc-top">
                    <strong>{r.label}</strong>
                    {rt ? <span className="cell-badge rt">{String(rt.level).replace("RT-", "RT")}</span>
                      : <span className="cell-badge" style={{ background: LEVELS[lv].color }}>{LEVELS[lv].label}</span>}
                  </div>
                  <div className="pc-ladder">
                    {r.ladder.map((l) => <span key={l} className={`pc-step ${lvlRank(lv) >= lvlRank(l) ? "reached" : ""} ${lv === l ? "current" : ""}`}>{l}</span>)}
                  </div>
                  <div className="ri-sub">{rt ? "In retrain" : lv === "0" && !avail.ok ? avail.why : nxt ? `Next: ${nxt} — ${stepAction(nxt, r.key)}` : "Top level reached"}</div>
                  {canTrain && !emp.archived && (
                    <div className="rc-actions">
                      <button className="btn-primary sm" onClick={() => { if (lv === "0" && avail.ok) h.onStartRide(emp.id, r.key); setRideRead(r.key); }}>{lv === "0" ? "Train now" : "Open training"}</button>
                      <button className="btn-ghost sm" onClick={() => onSchedule(emp)}>Schedule</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {renderArea(areaInfo.rideops)}
          <section className="panel">
            <div className="panel-head"><h2><CalendarClock size={16} /> Scheduled steps</h2><span className="count-pill">{emp.schedule.length}</span></div>
            <div className="panel-body">
              {emp.schedule.length === 0 && <Empty text="No scheduled steps." />}
              {emp.schedule.map((sc) => (
                <div className="row-item" key={sc.id} style={{ flexWrap: "wrap" }}>
                  <div className="ri-main"><strong>{RIDE_MAP[sc.ride].label} → {sc.targetLevel}</strong><span className="ri-sub">{sc.date ? `Due ${fmtDate(sc.date)}` : "No date"}</span></div>
                  {isMgr ? (
                    <select className="team-sort" value={sc.assignedTo || ""} onChange={(ev) => onAssign(emp.id, sc.id, ev.target.value || null)}>
                      <option value="">Unassigned</option>
                      {(trainers || []).map((t) => <option key={t.id} value={t.id}>{t.full_name || t.name}</option>)}
                    </select>
                  ) : (
                    <span className="dim" style={{ fontSize: 12 }}>{(trainers || []).find((t) => t.id === sc.assignedTo)?.full_name || "Unassigned"}</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {areaInfo[tab] && !areaInfo[tab].inRides && <div className="emp-body">{renderArea(areaInfo[tab])}</div>}

      {tab === "quizzes" && (
        <div className="emp-body">
          <QuizResultsPanel emp={emp} state={state} extras={extras} />
        </div>
      )}

      {tab === "policies" && (
        <div className="emp-body">
          <section className="panel">
            <div className="panel-head"><h2><FileText size={16} /> Policies</h2><span className="count-pill">{s.policiesRequired.length - s.policiesToSign.length}/{s.policiesRequired.length} signed</span></div>
            <div className="panel-body">
              {extras && myPolicies.length === 0 && <Empty text="No policies assigned." />}
              {myPolicies.map((p) => {
                const must = policyRequiresSignature(p, emp, extras.groupIdx);
                const sv = s.signedVersion[p.id] || 0;
                const ack = extras.acks.find((a) => a.employee_id === emp.id && a.policy_id === p.id && a.version === sv);
                const due = policyDueDate(p, emp, extras.groupIdx);
                const st = !must ? { t: "Read only", c: "tag-muted" }
                  : sv >= p.version ? { t: `Signed ${ack ? fmtDate(String(ack.signed_at).slice(0, 10)) : ""}`, c: "tag-ok" }
                  : due && due < todayISO() ? { t: `Overdue — due ${fmtDate(due)}`, c: "tag-fail" }
                  : sv > 0 ? { t: "Updated — needs re-sign", c: "tag-warn" }
                  : { t: due ? `Sign by ${fmtDate(due)}` : "Not signed", c: "tag-warn" };
                return (
                  <div className="row-item" key={p.id} style={{ flexWrap: "wrap" }}>
                    <div className="ri-main"><strong>{p.title}</strong><span className="ri-sub">{p.category || "Policy"} · v{p.version}</span></div>
                    <span className={`tag ${st.c}`}>{st.t}</span>
                    {ack && (
                      <button className="btn-ghost sm" onClick={async () => {
                        let snap = p;
                        if (ack.version !== p.version) {
                          const r = await db.loadPolicyVersion(p.id, ack.version);
                          if (r.ok && r.version) snap = { ...p, ...r.version, version: ack.version };
                        }
                        printPolicy(snap, {
                          statement: ack.attestation?.statement || policyAckStatement(snap.title),
                          signed: { name: ack.attestation?.signer_name || emp.name, signed_at: ack.signed_at, signature: ack.attestation?.signature || null, typed_name: ack.attestation?.typed_name || null },
                        });
                      }}><Download size={12} /> Signed copy</button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}

      {tab === "goals" && (
        <div className="emp-body"><EmployeeGoalsBlock key={emp.id} emp={emp} user={user} /></div>
      )}

      {tab === "documents" && can.viewDocuments(user) && (
        <div className="emp-body"><section className="panel"><div className="panel-body"><EmployeeDocumentsPanel emp={emp} user={user} /></div></section></div>
      )}

      {schedFor && (
        <ScheduleTrainingModal
          title={schedFor.m.title} trainers={trainers}
          initialDate={schedFor.a?.due_at ? String(schedFor.a.due_at).slice(0, 10) : ""}
          initialTrainer={schedFor.a?.trainer_id || ""}
          onClose={() => setSchedFor(null)}
          onSave={(date, trainerId) => saveSchedule(schedFor.m, schedFor.a, date, trainerId)}
        />
      )}
      {rideRead && (
        <RideTrainingModal rideKey={rideRead} modules={extras?.modules || []} emp={emp}
          canSignoff={canTrain} onSignoff={() => { setRideRead(null); onSignoff(emp); }} onClose={() => setRideRead(null)} />
      )}

      {tab === "history" && (
        <div className="emp-body">
          <section className="panel">
            <div className="panel-head"><h2><Clock size={16} /> History</h2><span className="count-pill">{emp.history.length}</span></div>
            <div className="panel-body log">
              {emp.history.length === 0 && <Empty text="No history yet." />}
              {emp.history.map((x, i) => (
                <div className="log-row" key={i}><span className="log-dot" /><span className="log-text">{x.text}</span><span className="log-date">{fmtDate(x.date)}</span></div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   DASHBOARD
   ============================================================ */
function Dashboard({ state, user, onOpenSignoff, onOpenQuiz, onSelectEmp, onOpenRide, onCompleteSignoff, onDismiss, onObserve, onL2Obs, onAdhocObserve, onGoTab, onAction }) {
  const { employees, quizzes } = state;
  const [drawer, setDrawer] = useState(null); // { emp, section }
  // Dashboard tab: 'ride' (default, existing Ride Operator dashboard), 'service_tech', 'restaurant', 'general'
  const [dashboardTab, setDashboardTab] = useState("ride");
  // Non-ride module practical + verification extras — merged into the Verifications panel.
  // Only Manager+ can act on these.
  const isManager = roleRank(user.role) >= roleRank("manager");
  const [moduleExtras, setModuleExtras] = useState([]);
  const [modActionTarget, setModActionTarget] = useState(null); // { kind, assignment, emp, training }
  const reloadModuleExtras = useCallback(async () => {
    if (!isManager) { setModuleExtras([]); return; }
    const [aRes, mRes] = await Promise.all([
      db.loadModuleAssignments({}),
      db.loadModules(),
    ]);
    if (!aRes.ok || !mRes.ok) return;
    const modById = {};
    mRes.modules.forEach((m) => { modById[m.id] = m; });
    const empById = {};
    (employees || []).forEach((e) => { empById[e.id] = e; });
    const pending = [];
    for (const a of aRes.assignments) {
      if (a.completed_at) continue;
      const t = modById[a.module_id];
      const e = empById[a.employee_id];
      if (!t || !e || e.archived) continue;
      const quizCleared = a.quiz_status === "passed" || !t.has_quiz;
      const needsPractical =
        t.requires_practical && quizCleared && a.practical_status !== "complete";
      const needsVerify =
        a.review_status === "pending" && !needsPractical;
      if (needsPractical) {
        pending.push({ key: `p-${a.id}`, kind: "practical", assignment: a, emp: e, training: t });
      } else if (needsVerify) {
        pending.push({ key: `v-${a.id}`, kind: "verify", assignment: a, emp: e, training: t });
      }
    }
    // Verifications first, then practicals; each group by assignment age
    pending.sort((x, y) => {
      if (x.kind !== y.kind) return x.kind === "verify" ? -1 : 1;
      return new Date(x.assignment.assigned_at) - new Date(y.assignment.assigned_at);
    });
    setModuleExtras(pending);
  }, [isManager, employees]);
  useEffect(() => { reloadModuleExtras(); }, [reloadModuleExtras]);

  // Upcoming training steps: every employee's pending schedule entries + auto next targets
  const upcoming = useMemo(() => {
    const rows = [];
    employees.forEach((e) => {
      e.schedule.forEach((sc) => {
        rows.push({ emp: e, ...sc, du: daysUntil(sc.date) });
      });
    });
    rows.sort((a, b) => {
      if (a.date && b.date) return new Date(a.date) - new Date(b.date);
      if (a.date) return -1; if (b.date) return 1; return 0;
    });
    return rows;
  }, [employees]);

  // Items assigned to the currently signed-in user (Trainer / AGM/GM only)
  const assignedToMe = useMemo(() => {
    if (!user || roleRank(user.role) < roleRank("trainer")) return [];
    const rows = [];
    employees.forEach((e) => {
      (e.schedule || []).forEach((sc) => {
        if (sc.assignedTo === user.id) rows.push({ emp: e, ...sc, du: daysUntil(sc.date) });
      });
    });
    rows.sort((a, b) => {
      if (a.date && b.date) return new Date(a.date) - new Date(b.date);
      if (a.date) return -1; if (b.date) return 1; return 0;
    });
    return rows;
  }, [employees, user]);

  // Ready for next step (no schedule yet, but not maxed) -> suggested sign-offs
  const suggestions = useMemo(() => {
    const rows = [];
    employees.forEach((e) => {
      RIDES.forEach((r) => {
        const lv = e.levels[r.key];
        const nxt = nextLevel(lv, r.key);
        const scheduled = e.schedule.some((sc) => sc.ride === r.key);
        const inQuiz = quizzes.some((q) => q.empId === e.id && q.ride === r.key && (q.status === "sent" || q.status === "failed"));
        if (nxt && !scheduled && !inQuiz && lv !== "0") {
          rows.push({ emp: e, ride: r.key, current: lv, next: nxt });
        }
      });
    });
    return rows.slice(0, 12);
  }, [employees, quizzes]);

  const pendingQ = quizzes.filter((q) => q.status === "sent" || q.status === "failed");

  // Per-ride coverage: count of crew at each level for every ride
  const coverage = useMemo(() => {
    return RIDES.map((r) => {
      const counts = { T: 0, L2: 0, L3: 0, L4: 0 };
      employees.forEach((e) => {
        const lv = e.levels[r.key];
        if (counts[lv] !== undefined) counts[lv] += 1;
      });
      const independent = counts.L2 + counts.L3 + counts.L4; // L2+ runs solo
      const trainers = counts[r.trainer];
      const total = counts.T + independent;
      return { ride: r, counts, independent, trainers, total };
    });
  }, [employees]);

  // Milestones
  const milestones = useMemo(() => {
    const rows = [];
    employees.forEach((e) => {
      if (!e.startDate) return;
      [["30-day", 30], ["60-day", 60], ["90-day", 90]].forEach(([label, d]) => {
        const due = addDays(e.startDate, d);
        const du = daysUntil(due);
        if (du !== null && du >= -7 && du <= 21) rows.push({ emp: e, label, due, du });
      });
    });
    rows.sort((a, b) => a.du - b.du);
    return rows;
  }, [employees]);

  return (
    <div className="dash">
      <div className="dash-toolbar">
        <div className="total-banner">
          <Users size={18} />
          <span className="total-num">{employees.length}</span>
          <span className="total-label">total crew members</span>
        </div>
        {can.observe(user) && (
          <button className="btn-primary" onClick={() => onAdhocObserve()}>
            <Eye size={14} /> New observation
          </button>
        )}
      </div>

      {/* Dashboard section tabs */}
      <div style={{ display: "flex", gap: 4, padding: "0 4px 12px", flexWrap: "wrap", borderBottom: "1px solid var(--line)", marginBottom: 12 }}>
        {[
          { key: "ride", label: "Ride Operator" },
          { key: "ride_ops", label: "Ride Ops" },
          { key: "service_tech", label: "Service Tech" },
          { key: "restaurant", label: "Restaurant" },
          { key: "leadership", label: "Leadership" },
          { key: "general", label: "General" },
        ].map((t) => (
          <button
            key={t.key}
            className={dashboardTab === t.key ? "btn-primary sm" : "btn-ghost sm"}
            onClick={() => setDashboardTab(t.key)}
            style={{ padding: "6px 12px" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {dashboardTab !== "ride" && (
        <SectionDashboardView
          section={dashboardTab}
          employees={employees}
          user={user}
        />
      )}

      {dashboardTab === "ride" && (
      <>

      {state.notifications.filter((n) => !n.read).length > 0 && (
        <section className="panel notif-panel">
          <div className="panel-head"><h2><Bell size={16} /> Notifications</h2>
            <span className="count-pill">{state.notifications.filter((n) => !n.read).length}</span></div>
          <div className="panel-body">
            {state.notifications.filter((n) => !n.read).slice(0, 8).map((n) => (
              <div className="row-item notif-row" key={n.id}>
                <AlertCircle size={15} className="notif-ic" />
                <div className="ri-main"><span className="notif-text">{n.text}</span><span className="ri-sub">{fmtDate(n.date)}</span></div>
                <button className="btn-mini ghost" onClick={() => onDismiss(n.id)}>Dismiss</button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="panel coverage-panel">
        <div className="panel-head">
          <h2><LayoutDashboard size={16} /> Coverage by Ride</h2>
          <div className="cov-legend">
            <span><i className="dot" style={{ background: "var(--lT)" }} /> T</span>
            <span><i className="dot" style={{ background: "var(--l2)" }} /> L2</span>
            <span><i className="dot" style={{ background: "var(--l3)" }} /> L3</span>
            <span><i className="dot" style={{ background: "var(--l4)" }} /> L4</span>
          </div>
        </div>
        <div className="cov-grid">
          {coverage.map((c) => (
            <button className="cov-card" key={c.ride.key} onClick={() => onOpenRide(c.ride.key)}>
              <div className="cov-top">
                <span className="cov-name">{c.ride.label}</span>
                <span className={`cov-flag ${c.independent === 0 ? "danger" : c.independent <= 2 ? "warn" : "ok"}`}>
                  {c.independent} solo
                </span>
              </div>
              <div className="cov-bar">
                {c.ride.ladder.map((lv) => (
                  c.counts[lv] > 0 && <span key={lv} style={{ flex: c.counts[lv], background: LEVELS[lv].color }} title={`${c.counts[lv]} ${lv}`} />
                ))}
                {c.total === 0 && <span style={{ flex: 1, background: "var(--l0)" }} />}
              </div>
              <div className="cov-nums">
                {c.ride.ladder.map((lv) => (
                  <span className="cov-num" key={lv}>
                    <i style={{ background: LEVELS[lv].color }} />{LEVELS[lv].label} {c.counts[lv]}
                  </span>
                ))}
              </div>
              <span className="cov-open">View crew <ChevronRight size={13} /></span>
            </button>
          ))}
        </div>
      </section>

      {assignedToMe.length > 0 && (
        <section className="panel">
          <div className="panel-head"><h2><UserPlus size={16} /> Assigned to me</h2><span className="count-pill">{assignedToMe.length}</span></div>
          <div className="panel-body">
            {assignedToMe.slice(0, 12).map((u) => (
              <div className="row-item" key={u.id} onClick={() => onSelectEmp && onSelectEmp(u.emp)} style={{ cursor: "pointer" }}>
                <div className="ri-main">
                  <strong>{u.emp.name}</strong>
                  <span className="ri-sub">{RIDE_MAP[u.ride].label} → {u.targetLevel} · {stepAction(u.targetLevel, u.ride)}</span>
                </div>
                {u.date
                  ? <span className={`tag ${u.du < 0 ? "tag-fail" : u.du <= 3 ? "tag-warn" : "tag-info"}`}>{u.du < 0 ? `${Math.abs(u.du)}d overdue` : u.du === 0 ? "Today" : `in ${u.du}d`}</span>
                  : <span className="tag tag-muted">No due date</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="dash-sections">
        <TaskGroupSection
          title="Verifications" icon={PenLine} section="signoff" state={state} user={user}
          onOpenTab={() => onGoTab("signoffs")} onOpenEmp={(emp) => setDrawer({ emp, section: "signoff" })}
          extraRows={moduleExtras}
          onOpenExtra={(r) => setModActionTarget(r)}
        />
        <TaskGroupSection
          title="Quizzes" icon={FileQuestion} section="quiz" state={state} user={user}
          onOpenTab={() => onGoTab("quizzes")} onOpenEmp={(emp) => setDrawer({ emp, section: "quiz" })}
        />
        <TaskGroupSection
          title="Observations" icon={Eye} section="observation" state={state} user={user}
          onOpenTab={() => onGoTab("observations")} onOpenEmp={(emp) => setDrawer({ emp, section: "observation" })}
        />
      </div>

      <RecentlyCompleted state={state} />

      <div className="dash-cols">
        <section className="panel">
          <div className="panel-head"><h2><Calendar size={16} /> 30/60/90 Check-ins</h2></div>
          <div className="panel-body">
            {milestones.length === 0 && <Empty text="No upcoming check-ins in the next 3 weeks." />}
            {milestones.map((m, i) => (
              <div className="row-item" key={i}>
                <div className="ri-main">
                  <strong>{m.emp.name}</strong>
                  <span className="ri-sub">{m.label} review · due {fmtDate(m.due)}</span>
                </div>
                <span className={`tag ${m.du < 0 ? "tag-fail" : m.du <= 3 ? "tag-warn" : "tag-info"}`}>
                  {m.du < 0 ? `${Math.abs(m.du)}d ago` : m.du === 0 ? "Today" : `in ${m.du}d`}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="panel-head"><h2><CalendarClock size={16} /> Upcoming Scheduled Steps</h2><span className="count-pill">{upcoming.length}</span></div>
          <div className="panel-body">
            {upcoming.length === 0 && <Empty text="Nothing scheduled. Steps auto-create as crew advance." />}
            {upcoming.slice(0, 8).map((u) => (
              <div className="row-item" key={u.id}>
                <div className="ri-main">
                  <strong>{u.emp.name}</strong>
                  <span className="ri-sub">{RIDE_MAP[u.ride].label} → {u.targetLevel} · {stepAction(u.targetLevel, u.ride)}</span>
                </div>
                {u.date
                  ? <span className={`tag ${u.du < 0 ? "tag-fail" : u.du <= 3 ? "tag-warn" : "tag-info"}`}>{u.du < 0 ? `${Math.abs(u.du)}d overdue` : u.du === 0 ? "Today" : `in ${u.du}d`}</span>
                  : <span className="tag tag-muted">Unscheduled</span>}
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="panel">
        <div className="panel-head"><h2><Clock size={16} /> Activity Log</h2></div>
        <div className="panel-body log">
          {state.log.slice(0, 15).map((l, i) => (
            <div className={`log-row log-${l.type}`} key={i}>
              <span className="log-dot" />
              <span className="log-text">{l.text}</span>
              <span className="log-date">{fmtDate(l.date)}</span>
            </div>
          ))}
          {state.log.length === 0 && <Empty text="No activity yet." />}
        </div>
      </section>

      </>
      )}

      {drawer && (
        <TaskDrawer
          drawer={drawer} state={state} user={user} onClose={() => setDrawer(null)}
          onOpenQuiz={(q) => { onOpenQuiz(q); setDrawer(null); }}
          onCompleteSignoff={(so) => { onCompleteSignoff(so); setDrawer(null); }}
          onObserve={(emp, ride) => { onObserve(emp, ride); setDrawer(null); }}
          onL2Obs={(emp, ride, quizRef) => { onL2Obs(emp, ride, quizRef); setDrawer(null); }}
        />
      )}
      {modActionTarget?.kind === "practical" && (
        <ModulePracticalModal
          assignment={modActionTarget.assignment}
          emp={modActionTarget.emp}
          training={modActionTarget.training}
          user={user}
          onClose={() => setModActionTarget(null)}
          onComplete={async () => { setModActionTarget(null); await reloadModuleExtras(); }}
        />
      )}
      {modActionTarget?.kind === "verify" && (
        <ModuleReviewModal
          assignment={modActionTarget.assignment}
          emp={modActionTarget.emp}
          training={modActionTarget.training}
          user={user}
          onClose={() => setModActionTarget(null)}
          onComplete={async () => { setModActionTarget(null); await reloadModuleExtras(); }}
        />
      )}
    </div>
  );
}

/* ---- Dashboard grouped task section (one row per employee) ---- */
function TaskGroupSection({ title, icon: Icon, section, state, user, onOpenTab, onOpenEmp, extraRows, onOpenExtra, extraLabel }) {
  const allRows = groupedForSection(state, user, section);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const COLLAPSED_LIMIT = 5;

  // Filter by name query (case-insensitive). Whitespace trimmed.
  const q = query.trim().toLowerCase();
  const rows = q
    ? allRows.filter((r) => r.emp.name.toLowerCase().includes(q))
    : allRows;
  const totalTasks = rows.reduce((a, r) => a + r.tasks.length, 0);
  // Extra rows come pre-shaped: [{ key, emp, training, kind }]
  const filteredExtras = q
    ? (extraRows || []).filter((r) => r.emp.name.toLowerCase().includes(q))
    : (extraRows || []);
  const combinedTotal = rows.length + filteredExtras.length;
  const canExpand = combinedTotal > COLLAPSED_LIMIT;
  // Show ride rows first, then extras — but cap total at COLLAPSED_LIMIT
  let shown, shownExtras;
  if (expanded) {
    shown = rows;
    shownExtras = filteredExtras;
  } else if (rows.length >= COLLAPSED_LIMIT) {
    shown = rows.slice(0, COLLAPSED_LIMIT);
    shownExtras = [];
  } else {
    shown = rows;
    shownExtras = filteredExtras.slice(0, COLLAPSED_LIMIT - rows.length);
  }
  // Reset expansion when user searches (so filtered results are visible without an extra click)
  useEffect(() => { if (q) setExpanded(false); }, [q]);

  return (
    <section className="panel task-section">
      <div className="panel-head">
        <h2><Icon size={16} /> {title}</h2>
        <div className="ts-head-right">
          <span className="count-pill">
            {(extraRows && extraRows.length > 0)
              ? `${combinedTotal} pending`
              : `${rows.length} crew · ${totalTasks}`}
          </span>
          {canExpand && (
            <button className="ts-viewall" onClick={() => setExpanded((v) => !v)}>
              {expanded ? <>Show less <ChevronUp size={13} /></> : <>Show all ({combinedTotal}) <ChevronDown size={13} /></>}
            </button>
          )}
        </div>
      </div>
      {(allRows.length + (extraRows?.length || 0)) > 3 && (
        <div className="ts-search" style={{ padding: "8px 14px 0 14px" }}>
          <div className="search" style={{ width: "100%" }}>
            <Search size={14} />
            <input
              type="text"
              placeholder="Search by name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: "100%" }}
            />
            {query && (
              <button
                className="icon-btn"
                onClick={() => setQuery("")}
                title="Clear"
                style={{ padding: 2 }}
              ><X size={12} /></button>
            )}
          </div>
        </div>
      )}
      <div className="panel-body">
        {(allRows.length + (extraRows?.length || 0)) === 0 && <Empty text={`No active ${title.toLowerCase()}.`} />}
        {(allRows.length + (extraRows?.length || 0)) > 0 && (rows.length + filteredExtras.length) === 0 && <Empty text={`No matches for "${query}".`} />}
        {shown.map((r) => {
          const actionable = r.tasks.filter((t) => t.canDo(user)).length;
          return (
            <button className="ts-emp-row" key={r.emp.id} onClick={() => onOpenEmp(r.emp)}>
              <div className="ts-emp-main">
                <strong>{r.emp.name}</strong>
                <span className="ri-sub">{r.tasks.length} task{r.tasks.length > 1 ? "s" : ""}{actionable > 0 && roleRank(user.role) >= roleRank("manager") ? ` · ${actionable} you can action` : ""}</span>
              </div>
              <div className="ts-emp-right">
                <span className="ts-count">{r.tasks.length}</span>
                <ChevronRight size={16} />
              </div>
            </button>
          );
        })}
        {shownExtras.map((r) => (
          <button className="ts-emp-row" key={r.key} onClick={() => onOpenExtra && onOpenExtra(r)}>
            <div className="ts-emp-main">
              <strong>{r.emp.name}</strong>
              <span className="ri-sub">
                {r.training.title}
                {r.emp.department && ` · ${r.emp.department}`}
              </span>
            </div>
            <div className="ts-emp-right">
              <span className={`tag ${r.kind === "verify" ? "tag-warn" : "tag-info"}`}>
                {r.kind === "verify" ? "Verify" : "Practical"}
              </span>
              <ChevronRight size={16} />
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

/* ---- Recently completed (last 3 days) across all three flows ---- */
function RecentlyCompleted({ state }) {
  const cutoff = 3;
  const items = [];
  state.signoffs.filter((x) => x.status === "complete" && daysSince(x.date) <= cutoff)
    .forEach((so) => items.push({ date: so.date, name: so.empName, text: `${signoffKindLabel(so)} — ${RIDE_MAP[so.ride].label} → ${so.targetLevel}` }));
  state.quizzes.filter((q) => (q.status === "passed_full" || q.status === "passed_gaps" || q.status === "complete") && q.completedDate && daysSince(q.completedDate) <= cutoff)
    .forEach((q) => items.push({ date: q.completedDate, name: q.empName, text: `Quiz passed (${q.score}%) — ${RIDE_MAP[q.ride].label}` }));
  // observation passes from history-free log
  state.log.filter((l) => l.type === "pass" && daysSince(l.date) <= cutoff && /observation|TtT|passed/i.test(l.text))
    .slice(0, 10).forEach((l) => items.push({ date: l.date, name: "", text: l.text }));
  items.sort((a, b) => new Date(b.date) - new Date(a.date));
  if (items.length === 0) return null;
  return (
    <section className="panel">
      <div className="panel-head"><h2><Check size={16} /> Recently Completed <span className="rc-sub">last 3 days</span></h2><span className="count-pill">{items.length}</span></div>
      <div className="panel-body">
        {items.slice(0, 12).map((it, i) => (
          <div className="row-item" key={i}>
            <div className="ri-main">{it.name && <strong>{it.name}</strong>}<span className="ri-sub">{it.text}</span></div>
            <span className="tag tag-pass"><Check size={12} /> {fmtDate(it.date)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---- Side drawer: an employee's tasks within one section ---- */
function TaskDrawer({ drawer, state, user, onClose, onOpenQuiz, onCompleteSignoff, onObserve, onL2Obs }) {
  const { emp, section } = drawer;
  const live = state.employees.find((e) => e.id === emp.id) || emp;
  const tasks = section === "quiz" ? quizTasksFor(state, live)
    : section === "signoff" ? signoffTasksFor(state, live)
    : observationTasksFor(state, live);
  const visible = user.role === "trainer" ? tasks.filter((t) => t.canDo(user)) : tasks;
  const sectionTitle = section === "quiz" ? "Quizzes" : section === "signoff" ? "Training sign-offs" : "Observations";

  const actionFor = (t) => {
    if (section === "quiz") return { label: t.status === "failed" ? "Retake" : "Take quiz", run: () => onOpenQuiz(t.quiz) };
    if (section === "signoff") {
      if (t.l2Obs) return { label: "Observe", run: () => onL2Obs(live, t.ride, t) };
      return { label: "Sign", run: () => onCompleteSignoff(t.signoff) };
    }
    return { label: "Observe", run: () => onObserve(live, t.ride) };
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div>
            <h3>{live.name}</h3>
            <span className="drawer-sub">{sectionTitle} · {visible.length} task{visible.length !== 1 ? "s" : ""}</span>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="drawer-body">
          {visible.length === 0 && <Empty text={`No ${sectionTitle.toLowerCase()} for ${live.name}.`} />}
          {visible.map((t) => {
            const act = actionFor(t);
            const allowed = t.canDo(user);
            return (
              <div className="drawer-task" key={t.id}>
                <div className="dt-main">
                  <span className="dt-label">{t.label}</span>
                  {t.sub && <span className="dt-sub">{t.sub}</span>}
                </div>
                {allowed ? (
                  <button className="btn-mini" onClick={act.run}>{act.label}</button>
                ) : (
                  <span className="dt-sub" style={{ fontSize: 11, color: "var(--txt3)" }}><Lock size={11} /> {act.label} (not yours)</span>
                )}
              </div>
            );
          })}
          <p className="drawer-note">Signed in as {user.name} — {ROLES[user.role].label}. {roleRank(user.role) < roleRank("manager") ? "You only see tasks you can perform." : "Greyed-out actions are above your role."}</p>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <div className={`stat-card tone-${tone}`}>
      <div className="stat-icon"><Icon size={22} /></div>
      <div className="stat-meta">
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  );
}

/* ============================================================
   TRAINEE PERSONAL DASHBOARD
   ============================================================ */
function findMyEmployee(state, user) {
  if (user.employeeId) return state.employees.find((e) => e.id === user.employeeId);
  // fallback: match by name
  return state.employees.find((e) => e.name.toLowerCase() === user.name.toLowerCase());
}

// Build the personal path: current level per ride + what's next, using the unlock chain.
function personalPath(emp) {
  return RIDES.map((r) => {
    const cur = emp.levels[r.key];
    const rt = emp.retrain?.[r.key];
    const nxt = nextLevel(cur, r.key);
    const scheduled = hasScheduled(emp, r.key, nxt);
    return { ride: r, cur, next: nxt, rt, scheduled };
  });
}

/* ============================================================
   SectionDashboardView — non-ride dashboard tabs
   Shows: rows = employees (department-matched first, then cross-assigned grayed);
          cols = modules in this section; cells = status per module.
   Manager+ can click actionable cells to open Practical / Verification modals.
   ============================================================ */
function SectionDashboardView({ section, employees, user }) {
  const [modules, setModules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null); // { kind, assignment, emp, training }
  const isManager = roleRank(user.role) >= roleRank("manager");

  // Map section slug -> department that "primarily owns" it
  const SECTION_TO_DEPT = {
    service_tech: "Kart & Ride Service Tech",
    restaurant: "Restaurant",
    ride_ops: "Attractions",
    leadership: null,
    general: null, // General = everyone's primary
  };
  const primaryDept = SECTION_TO_DEPT[section];

  const reload = React.useCallback(async () => {
    setLoading(true);
    const [mRes, aRes] = await Promise.all([
      db.loadModules({ section }),
      db.loadModuleAssignments({}),
    ]);
    if (mRes.ok) setModules(mRes.modules);
    if (aRes.ok) setAssignments(aRes.assignments);
    setLoading(false);
  }, [section]);
  React.useEffect(() => { reload(); }, [reload]);

  const SECTION_LABEL = {
    service_tech: "Service Tech Training",
    restaurant: "Restaurant Training",
    ride_ops: "Ride Operations",
    leadership: "Leadership & Training",
    general: "General Training",
  }[section];

  // Assignments grouped by employee_id -> { module_id -> assignment }
  const assignmentsByEmp = React.useMemo(() => {
    const map = {};
    for (const a of assignments) {
      const modInSection = modules.find((m) => m.id === a.module_id);
      if (!modInSection) continue; // filter to this section
      if (!map[a.employee_id]) map[a.employee_id] = {};
      map[a.employee_id][a.module_id] = a;
    }
    return map;
  }, [assignments, modules]);

  // Determine which employees to display
  const displayRows = React.useMemo(() => {
    const active = (employees || []).filter((e) => !e.archived);
    // Primary group: employees whose department matches (or all for 'general')
    const primary = (section === "general" || section === "leadership")
      ? active
      : active.filter((e) => e.department === primaryDept);
    const primaryIds = new Set(primary.map((e) => e.id));
    // Cross-assigned group: everyone else who has at least one assignment
    const cross = active.filter((e) =>
      !primaryIds.has(e.id) && assignmentsByEmp[e.id]
    );
    // Sort each group by name
    const byName = (a, b) => a.name.localeCompare(b.name);
    primary.sort(byName);
    cross.sort(byName);
    return [
      ...primary.map((e) => ({ emp: e, primary: true })),
      ...cross.map((e) => ({ emp: e, primary: false })),
    ];
  }, [employees, assignmentsByEmp, primaryDept, section]);

  // Determine what a cell should show for a given emp + module assignment
  const cellFor = (a, mod) => {
    if (!a) return { text: "Not assigned", cls: "cell-none", action: null };
    if (a.completed_at) return { text: "Completed", cls: "cell-ok", action: null };
    if (a.review_status === "pending") {
      return { text: "Awaiting verify", cls: "cell-warn", action: isManager ? "verify" : null };
    }
    const quizCleared = a.quiz_status === "passed" || !mod.has_quiz;
    if (mod.requires_practical && quizCleared && a.practical_status !== "complete") {
      return { text: "Awaiting practical", cls: "cell-warn", action: isManager ? "practical" : null };
    }
    if (a.quiz_status === "sent") return { text: "Quiz in progress", cls: "cell-warn", action: null };
    if (a.quiz_status === "failed") return { text: "Quiz failed", cls: "cell-fail", action: null };
    if (a.document_viewed_at) return { text: "Doc read, quiz pending", cls: "cell-warn", action: null };
    if (a.due_at && new Date(a.due_at) < new Date()) return { text: "Overdue", cls: "cell-fail", action: null };
    return { text: "Assigned", cls: "cell-info", action: null };
  };

  const openAction = (kind, a, emp, mod) => {
    setTarget({ kind, assignment: a, emp, training: mod });
  };

  const rowStyle = (isPrimary) => ({
    borderBottom: "1px solid var(--line)",
    opacity: isPrimary ? 1 : 0.55,
  });
  const cellStyle = {
    padding: "8px 10px",
    fontSize: 12,
    verticalAlign: "top",
    borderRight: "1px solid var(--line)",
    minWidth: 130,
  };

  const cellBg = (cls) => {
    const map = {
      "cell-ok": "rgba(34,197,94,0.10)",
      "cell-warn": "rgba(245,158,11,0.10)",
      "cell-fail": "rgba(239,68,68,0.10)",
      "cell-info": "rgba(59,130,246,0.08)",
      "cell-none": "transparent",
    };
    return map[cls] || "transparent";
  };
  const cellFg = (cls) => {
    const map = {
      "cell-ok": "var(--ok)",
      "cell-warn": "var(--warn)",
      "cell-fail": "var(--err)",
      "cell-info": "var(--accent)",
      "cell-none": "var(--txt3)",
    };
    return map[cls] || "var(--txt2)";
  };

  return (
    <section className="panel" style={{ overflow: "hidden" }}>
      <div className="panel-head">
        <h2><LayoutDashboard size={16} /> {SECTION_LABEL}</h2>
        <span className="count-pill">{displayRows.length} crew · {modules.length} module{modules.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="panel-body" style={{ padding: 0, overflowX: "auto" }}>
        {loading && <Empty text="Loading..." />}
        {!loading && modules.length === 0 && (
          <Empty text={`No ${SECTION_LABEL.toLowerCase()} modules yet. Admin can add them under Modules → ${SECTION_LABEL}.`} />
        )}
        {!loading && modules.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "var(--panel2)", borderBottom: "2px solid var(--line)" }}>
                <th style={{ ...cellStyle, textAlign: "left", position: "sticky", left: 0, background: "var(--panel2)", zIndex: 1 }}>
                  Employee
                </th>
                {modules.map((m) => (
                  <th key={m.id} style={{ ...cellStyle, textAlign: "left" }}>
                    {m.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 && (
                <tr><td colSpan={modules.length + 1} style={cellStyle}><Empty text="No employees to show." /></td></tr>
              )}
              {displayRows.map(({ emp, primary }) => {
                const empAssigns = assignmentsByEmp[emp.id] || {};
                return (
                  <tr key={emp.id} style={rowStyle(primary)}>
                    <td style={{ ...cellStyle, position: "sticky", left: 0, background: primary ? "var(--panel)" : "var(--panel2)", zIndex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{emp.name}</div>
                      <div className="dim" style={{ fontSize: 10 }}>{emp.department || "—"}</div>
                    </td>
                    {modules.map((m) => {
                      const a = empAssigns[m.id];
                      const info = cellFor(a, m);
                      const clickable = info.action !== null;
                      return (
                        <td
                          key={m.id}
                          style={{
                            ...cellStyle,
                            background: cellBg(info.cls),
                            color: cellFg(info.cls),
                            cursor: clickable ? "pointer" : "default",
                          }}
                          onClick={clickable ? () => openAction(info.action, a, emp, m) : undefined}
                          title={clickable ? "Click to open" : ""}
                        >
                          {info.text}
                          {clickable && (
                            <div style={{ marginTop: 4, fontSize: 10, textDecoration: "underline" }}>
                              {info.action === "practical" ? "Practical →" : "Verify →"}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {target?.kind === "practical" && (
        <ModulePracticalModal
          assignment={target.assignment}
          emp={target.emp}
          training={target.training}
          user={user}
          onClose={() => setTarget(null)}
          onComplete={async () => { setTarget(null); await reload(); }}
        />
      )}
      {target?.kind === "verify" && (
        <ModuleReviewModal
          assignment={target.assignment}
          emp={target.emp}
          training={target.training}
          user={user}
          onClose={() => setTarget(null)}
          onComplete={async () => { setTarget(null); await reload(); }}
        />
      )}
    </section>
  );
}

/* ============================================================
   EMPLOYEE DASHBOARD + CAREER GOALS

   Layout (top to bottom):
     1. To Do — quizzes to take, trainings to complete, policies to sign
     2. My Progression — what I'm trained on (ride levels, completed trainings)
     3. Your Path Forward — ride ladder (ride staff) + next step toward each goal
     4. My Goals — goals the employee picked, with a live checklist
     5. Quiz history
   ============================================================ */

/* Status of one module assignment, shared by every list that shows one. */
function moduleAssignmentStatus(a, t) {
  if (a.completed_at) return { text: `Completed ${fmtDate(a.completed_at)}`, cls: "tag-ok", action: "Review" };
  if (a.review_status === "pending") return { text: "Awaiting Competency Verification", cls: "tag-warn", action: "View" };
  const quizCleared = a.quiz_status === "passed" || !t?.has_quiz;
  if (t?.requires_practical && quizCleared && a.practical_status !== "complete") {
    return { text: "Awaiting practical walk-through", cls: "tag-warn", action: "View" };
  }
  if (a.quiz_status === "sent") return { text: "Quiz in progress", cls: "tag-warn", action: "Continue" };
  if (a.quiz_status === "failed") return { text: "Quiz failed — retake", cls: "tag-fail", action: "Retake" };
  if (a.document_viewed_at) return { text: "Doc read, quiz pending", cls: "tag-warn", action: "Take quiz" };
  if (a.due_at && String(a.due_at).slice(0, 10) < todayISO()) return { text: `Scheduled ${fmtDate(String(a.due_at).slice(0, 10))} — passed`, cls: "tag-fail", action: "Start" };
  return { text: a.due_at ? `Scheduled ${fmtDate(String(a.due_at).slice(0, 10))}` : "In training", cls: "tag-info", action: "Start" };
}

/* Does this open assignment's next step belong under "Quizzes to take"? */
function assignmentNeedsQuiz(a, t) {
  if (a.completed_at || !t?.has_quiz || a.quiz_status === "passed") return false;
  return a.quiz_status === "sent" || a.quiz_status === "failed" || !!a.document_viewed_at;
}

/* Everything the dashboard and goal checklists need for one employee
   (or for everyone, when employeeId is omitted — managers only). */
async function loadGoalContext(employeeId) {
  const [mRes, aRes, pRes, ackRes, gRes, catRes, egRes, soRes] = await Promise.all([
    db.loadModules({ includeArchived: true }),
    db.loadModuleAssignments(employeeId ? { employeeId } : {}),
    db.loadPolicies(),
    db.loadPolicyAcknowledgments(employeeId),
    db.loadEmployeeGroups(),
    db.loadGoalCatalog(),
    db.loadEmployeeGoals(employeeId ? { employeeId } : {}),
    db.loadGoalSignoffs(employeeId ? { employeeId } : {}),
  ]);
  return {
    modules: mRes.ok ? mRes.modules : [],
    assignments: aRes.ok ? aRes.assignments : [],
    policies: pRes.ok ? pRes.policies : [],
    acks: ackRes.ok ? ackRes.acknowledgments : [],
    groups: gRes.ok ? gRes.groups : [],
    catalog: catRes.ok ? catRes.goals : [],
    employeeGoals: egRes.ok ? egRes.employeeGoals : [],
    signoffs: soRes.ok ? soRes.signoffs : [],
    goalsReady: catRes.ok, // false until the goals migration has been run
  };
}

/* Readable name for a requirement. */
function requirementLabel(req, ctx) {
  if (req.label && req.kind === "manual") return req.label;
  if (req.kind === "module") {
    const m = (ctx.modules || []).find((x) => String(x.id) === String(req.target));
    return req.label || (m ? `Complete "${m.title}" training` : "Complete a training (no longer available)");
  }
  if (req.kind === "ride_level") {
    const r = RIDE_MAP[req.target];
    return req.label || (r ? `${r.label} — reach ${req.level}` : `${req.target} — reach ${req.level}`);
  }
  if (req.kind === "policy") {
    const p = (ctx.policies || []).find((x) => String(x.id) === String(req.target));
    return req.label || (p ? `Sign the ${p.title}` : "Sign a policy (not published)");
  }
  return req.label || "Manager sign-off";
}

/* Is one requirement met for this employee? Returns { done, hint }. */
function requirementStatus(req, emp, ctx) {
  if (req.kind === "module") {
    const as = (ctx.assignments || []).filter((a) => a.employee_id === emp.id && String(a.module_id) === String(req.target));
    const done = as.find((a) => a.completed_at);
    if (done) return { done: true, hint: `Completed ${fmtDate(done.completed_at)}` };
    if (as.length) return { done: false, hint: "In training — your trainer will go through it with you" };
    return { done: false, hint: "Not assigned yet — ask a manager to assign it" };
  }
  if (req.kind === "ride_level") {
    const cur = emp.levels?.[req.target] || "0";
    if (hasReached(emp, req.target, req.level)) return { done: true, hint: `At ${LEVELS[cur]?.label || cur}` };
    return { done: false, hint: cur === "0" ? "Not started on this ride yet" : `Currently ${LEVELS[cur]?.label || cur}` };
  }
  if (req.kind === "policy") {
    const p = (ctx.policies || []).find((x) => String(x.id) === String(req.target));
    if (!p) return { done: false, hint: "This policy isn't published yet" };
    const signed = (ctx.acks || []).some((a) => a.employee_id === emp.id && a.policy_id === p.id && a.version >= p.version);
    if (signed) return { done: true, hint: "Signed" };
    return { done: false, hint: "Not signed yet" };
  }
  // manual
  const so = (ctx.signoffs || []).find((s) => s.requirement_id === req.id && s.employee_id === emp.id);
  if (so) return { done: true, hint: `Signed off${so.signed_by ? ` by ${so.signed_by}` : ""} ${fmtDate(so.signed_at)}`, signoff: so };
  return { done: false, hint: "A manager signs this off" };
}

function goalProgress(goal, emp, ctx) {
  const items = (goal.requirements || []).map((req) => ({ req, label: requirementLabel(req, ctx), ...requirementStatus(req, emp, ctx) }));
  const done = items.filter((i) => i.done).length;
  return { items, done, total: items.length, complete: items.length > 0 && done === items.length };
}

/* Goals for one employee: checklist cards + add/remove. Used on the
   employee's own dashboard and on the manager's employee detail. */
function GoalsPanel({ emp, ctx, canSignOff, actorName, onChanged, title = "My Goals", selfView = true }) {
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!ctx.goalsReady) return null; // goals tables not created yet
  const mine = ctx.employeeGoals.filter((g) => g.employee_id === emp.id);
  const chosen = mine.map((eg) => ctx.catalog.find((g) => g.id === eg.goal_id)).filter(Boolean);
  const available = ctx.catalog.filter((g) => !mine.some((m) => m.goal_id === g.id));

  const run = async (fn) => {
    setBusy(true); setErr("");
    const res = await fn();
    setBusy(false);
    if (res && !res.ok) { setErr(res.message); return; }
    await onChanged();
  };

  return (
    <section className="panel">
      <div className="panel-head">
        <h2><Trophy size={16} /> {title}</h2>
        <span className="count-pill">{chosen.length} goal{chosen.length === 1 ? "" : "s"}</span>
      </div>
      <div className="panel-body">
        {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
        {chosen.length === 0 && (
          <Empty text={selfView
            ? "What do you want to work toward? Pick a goal below and you'll see exactly what it takes."
            : "No goals picked yet."} />
        )}
        <div className="goal-grid">
          {chosen.map((g) => {
            const pr = goalProgress(g, emp, ctx);
            const pct = pr.total ? Math.round((pr.done / pr.total) * 100) : 0;
            return (
              <div className={`goal-card ${pr.complete ? "complete" : ""}`} key={g.id}>
                <div className="goal-head">
                  <div>
                    <strong>{g.title}</strong>
                    {g.description && <div className="ri-sub">{g.description}</div>}
                  </div>
                  <button className="icon-btn" title="Remove this goal" disabled={busy}
                    onClick={() => {
                      if (!window.confirm(`Remove "${g.title}" from ${selfView ? "your" : `${emp.name}'s`} goals?`)) return;
                      run(() => db.removeEmployeeGoal(emp.id, g.id));
                    }}>
                    <X size={14} />
                  </button>
                </div>
                {pr.total === 0 ? (
                  <div className="dim" style={{ fontSize: 12.5 }}>
                    What this goal takes hasn't been listed yet. {selfView ? "Your manager will add the steps." : "Add steps under Goals."}
                  </div>
                ) : (
                  <>
                    <div className="goal-bar"><span style={{ width: `${pct}%` }} /></div>
                    <div className="goal-meta">
                      {pr.complete ? <><Check size={12} /> Every step complete</> : `${pr.done} of ${pr.total} steps done`}
                    </div>
                    <ul className="goal-steps">
                      {pr.items.map((it) => (
                        <li key={it.req.id} className={it.done ? "done" : ""}>
                          {it.done ? <Check size={14} /> : <CircleDot size={14} />}
                          <div className="gs-main">
                            <span>{it.label}</span>
                            <span className="gs-hint">{it.hint}</span>
                          </div>
                          {canSignOff && it.req.kind === "manual" && (
                            it.done ? (
                              <button className="btn-ghost-sm" disabled={busy}
                                onClick={() => run(() => db.deleteGoalSignoff(it.req.id, emp.id))}>Undo</button>
                            ) : (
                              <button className="btn-ghost-sm" disabled={busy}
                                onClick={() => run(() => db.createGoalSignoff({ requirement_id: it.req.id, employee_id: emp.id, signed_by: actorName }))}>
                                <Check size={11} /> Sign off
                              </button>
                            )
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {available.length > 0 && (
          <div className="goal-add">
            <select value={adding} onChange={(e) => setAdding(e.target.value)}>
              <option value="">{selfView ? "I want to be…" : "Add a goal…"}</option>
              {available.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
            </select>
            <button className="btn-primary sm" disabled={!adding || busy}
              onClick={() => run(async () => { const r = await db.addEmployeeGoal(emp.id, adding, actorName); setAdding(""); return r; })}>
              <Plus size={14} /> Add goal
            </button>
            {selfView && <span className="dim" style={{ fontSize: 12 }}>Your managers can see the goals you pick.</span>}
          </div>
        )}
        {ctx.catalog.length === 0 && <div className="dim" style={{ fontSize: 12.5, marginTop: 8 }}>No goals have been set up yet.</div>}
      </div>
    </section>
  );
}

function TraineeDashboard({ state, user, onOpenQuiz }) {
  const emp = findMyEmployee(state, user);
  const [ctx, setCtx] = useState(null);
  const [courses, setCourses] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [activeCourse, setActiveCourse] = useState(null);
  const [activeTraining, setActiveTraining] = useState(null); // { assignment, training }
  const [readingPolicy, setReadingPolicy] = useState(null);

  const reload = useCallback(async () => {
    if (!emp) return;
    const [c, cRes, pRes] = await Promise.all([
      loadGoalContext(emp.id),
      db.loadCourses(),
      db.loadCourseProgressForEmployee(emp.id),
    ]);
    setCtx(c);
    if (cRes.ok) setCourses(cRes.courses);
    if (pRes.ok) {
      const map = {};
      pRes.progress.forEach((p) => { map[p.course_id] = p; });
      setProgressMap(map);
    }
  }, [emp?.id]);
  useEffect(() => { reload(); }, [reload]);

  if (!emp) return <div className="dash"><Empty text="Your training record isn't linked yet. Ask a manager to connect your account." /></div>;

  const myQuizzes = state.quizzes.filter((q) => q.empId === emp.id);
  const rideQuizzes = myQuizzes.filter((q) => q.status === "sent" || q.status === "failed");
  const mySignoffs = state.signoffs.filter((x) => x.empId === emp.id && x.status !== "complete");
  const path = personalPath(emp);
  const ridesAtL2 = AMUSEMENT_RIDES.filter((r) => hasReached(emp, r, "L2")).length;
  const showRides = (emp.department || "Attractions") === "Attractions" || RIDES.some((r) => (emp.levels?.[r.key] || "0") !== "0");

  const loading = !ctx;
  const modulesById = {};
  (ctx?.modules || []).forEach((m) => { modulesById[m.id] = m; });
  const myAssignments = (ctx?.assignments || []).filter((a) => a.employee_id === emp.id && modulesById[a.module_id]);
  const openAssignments = myAssignments.filter((a) => !a.completed_at);
  const quizAssignments = openAssignments.filter((a) => assignmentNeedsQuiz(a, modulesById[a.module_id]));
  const trainingAssignments = openAssignments.filter((a) => !assignmentNeedsQuiz(a, modulesById[a.module_id]));
  const doneAssignments = myAssignments.filter((a) => a.completed_at)
    .sort((a, b) => String(b.completed_at).localeCompare(String(a.completed_at)));

  const courseDone = (c) => !!progressMap[c.id]?.completed_at;
  const openCourses = courses.filter((c) => !courseDone(c));
  const doneCourses = courses.filter(courseDone);
  const courseStatus = (c) => {
    const p = progressMap[c.id];
    if (!p) return { label: "Not started", className: "tag-info", action: "Start" };
    if (p.completed_at) return { label: `Completed ${fmtDate(p.completed_at)}`, className: "tag-ok", action: "Review" };
    const total = Array.isArray(c.slides) ? c.slides.length : 0;
    const viewed = Array.isArray(p.slides_viewed) ? p.slides_viewed.length : 0;
    return { label: `In progress · ${viewed}/${total}`, className: "tag-warn", action: "Resume" };
  };

  // Policies assigned to me that still need my signature at the current version.
  const groupIdx = buildGroupIndex(ctx?.groups || []);
  const signedVersion = {};
  (ctx?.acks || []).filter((a) => a.employee_id === emp.id)
    .forEach((a) => { signedVersion[a.policy_id] = Math.max(signedVersion[a.policy_id] || 0, a.version); });
  const myPolicies = (ctx?.policies || []).filter((p) => p.published && policyVisibleTo(p, emp, groupIdx));
  const policiesToSign = myPolicies
    .filter((p) => policyRequiresSignature(p, emp, groupIdx) && (signedVersion[p.id] || 0) < p.version)
    .sort((a, b) => String(policyDueDate(a, emp, groupIdx) || "9999").localeCompare(String(policyDueDate(b, emp, groupIdx) || "9999")));
  const policiesSigned = myPolicies.filter((p) => (signedVersion[p.id] || 0) >= p.version && policyRequiresSignature(p, emp, groupIdx));

  const todoCount = rideQuizzes.length + quizAssignments.length + openCourses.length + policiesToSign.length;

  // Next step toward each goal I've picked.
  const myGoals = (ctx?.employeeGoals || []).filter((g) => g.employee_id === emp.id)
    .map((eg) => (ctx?.catalog || []).find((g) => g.id === eg.goal_id)).filter(Boolean);
  const goalNext = myGoals.map((g) => {
    const pr = goalProgress(g, emp, ctx);
    return { goal: g, pr, next: pr.items.find((i) => !i.done) };
  });

  const assignmentRow = (a) => {
    const t = modulesById[a.module_id];
    const st = moduleAssignmentStatus(a, t);
    return (
      <div className="row-item" key={a.id}>
        <div className="ri-main">
          <strong>{t.title}</strong>
          <span className="ri-sub">{t.description || "Training"}</span>
        </div>
        <span className={`tag ${st.cls}`}>{st.text}</span>
        <button className="btn-primary sm" onClick={() => setActiveTraining({ assignment: a, training: t })}>
          {st.action} <ArrowRight size={14} />
        </button>
      </div>
    );
  };
  const subHead = (icon, text, n) => (
    <div className="todo-sub">{icon}<span>{text}</span>{n > 0 && <span className="count-pill">{n}</span>}</div>
  );

  return (
    <div className="dash trainee-dash">
      <div className="trainee-hero">
        <div className="th-avatar">{emp.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}</div>
        <div>
          <h2>Welcome, {emp.name.split(" ")[0]}</h2>
          <span className="th-sub">
            {emp.jobTitle ? `${emp.jobTitle} · ` : ""}{emp.department || ""} · started {emp.startDate ? fmtDate(emp.startDate) : "—"}
          </span>
        </div>
      </div>

      {/* 1. TO DO */}
      <section className="panel outstanding-panel">
        <div className="panel-head">
          <h2><ClipboardCheck size={16} /> To Do</h2>
          <span className="count-pill">{loading ? "…" : todoCount}</span>
        </div>
        <div className="panel-body">
          {loading && <Empty text="Loading your to-do list…" />}
          {!loading && todoCount === 0 && (
            <Empty text="You're all caught up — nothing to do right now. Nice work!" />
          )}

          {!loading && (rideQuizzes.length + quizAssignments.length) > 0 && (
            <>
              {subHead(<FileQuestion size={14} />, "Quizzes to take", rideQuizzes.length + quizAssignments.length)}
              {rideQuizzes.map((q) => (
                <div className="row-item" key={q.id}>
                  <div className="ri-main">
                    <strong>{RIDE_MAP[q.ride].label} certification quiz</strong>
                    <span className="ri-sub">target {q.targetLevel} · {q.status === "failed" ? `failed ${q.score}% — retake available` : `sent ${fmtDate(q.sentDate)}`}</span>
                  </div>
                  <span className={`tag ${q.status === "failed" ? "tag-fail" : "tag-warn"}`}>{q.status === "failed" ? "Retake" : "Ready"}</span>
                  <button className="btn-primary sm" onClick={() => onOpenQuiz(q)}>{q.status === "failed" ? "Retake" : "Take quiz"} <ArrowRight size={14} /></button>
                </div>
              ))}
              {quizAssignments.map(assignmentRow)}
            </>
          )}

          {!loading && openCourses.length > 0 && (
            <>
              {subHead(<BookOpen size={14} />, "Courses", openCourses.length)}
              {openCourses.map((c) => {
                const st = courseStatus(c);
                return (
                  <div className="row-item" key={c.id}>
                    <div className="ri-main">
                      <strong>{c.title}</strong>
                      <span className="ri-sub">{c.estimated_minutes ? `~${c.estimated_minutes} min` : "Self-paced course"}</span>
                    </div>
                    <span className={`tag ${st.className}`}>{st.label}</span>
                    <button className="btn-primary sm" onClick={() => setActiveCourse(c)}>{st.action} <ArrowRight size={14} /></button>
                  </div>
                );
              })}
            </>
          )}

          {!loading && policiesToSign.length > 0 && (
            <>
              {subHead(<FileText size={14} />, "Policies to sign", policiesToSign.length)}
              {policiesToSign.map((p) => {
                const due = policyDueDate(p, emp, groupIdx);
                const overdue = due && due < todayISO();
                const resign = (signedVersion[p.id] || 0) > 0;
                return (
                  <div className="row-item" key={p.id}>
                    <div className="ri-main">
                      <strong>{p.title}</strong>
                      <span className="ri-sub">{p.category || "Policy"} · v{p.version}{resign ? " · updated since you signed" : ""}</span>
                    </div>
                    <span className={`tag ${overdue ? "tag-fail" : "tag-warn"}`}>
                      {overdue ? `Overdue — due ${fmtDate(due)}` : due ? `Sign by ${fmtDate(due)}` : "Signature required"}
                    </span>
                    <button className="btn-primary sm" onClick={() => setReadingPolicy(p)}>Read & sign <ArrowRight size={14} /></button>
                  </div>
                );
              })}
            </>
          )}

          {mySignoffs.length > 0 && (
            <div className="trainee-await">
              <AlertCircle size={14} /> You have {mySignoffs.length} step{mySignoffs.length > 1 ? "s" : ""} waiting on a supervisor sign-off before you advance.
            </div>
          )}
        </div>
      </section>

      {/* Training in progress — always done with a trainer, never homework */}
      {!loading && trainingAssignments.length > 0 && (
        <section className="panel">
          <div className="panel-head"><h2><BookOpen size={16} /> My training</h2><span className="count-pill">{trainingAssignments.length}</span></div>
          <div className="panel-body">
            <p className="dim" style={{ fontSize: 12.5, margin: "0 4px 8px" }}>Your trainer will go through each of these with you on shift. You're welcome to read ahead, but you don't need to do anything on your own.</p>
            {trainingAssignments.map((a) => {
              const t = modulesById[a.module_id];
              const day = a.due_at ? String(a.due_at).slice(0, 10) : null;
              return (
                <div className="row-item" key={a.id}>
                  <div className="ri-main">
                    <strong>{t.title}</strong>
                    <span className="ri-sub">{day ? `Scheduled ${fmtDate(day)}` : "In training"}{a.trainer_name ? ` · with ${a.trainer_name}` : ""}</span>
                  </div>
                  <button className="btn-ghost sm" onClick={() => setActiveTraining({ assignment: a, training: t, reviewOnly: true })}>Read ahead</button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 2. MY PROGRESSION — WHAT I'M TRAINED ON */}
      <section className="panel">
        <div className="panel-head">
          <h2><TrendingUp size={16} /> My Progression — What I'm Trained On</h2>
          {showRides && <span className="count-pill">{ridesAtL2}/4 rides at L2</span>}
        </div>
        <div className="panel-body">
          {showRides && (
            <div className="path-grid" style={{ marginBottom: 14 }}>
              {path.map((p) => (
                <div className={`path-card ${p.cur === "0" ? "locked" : ""}`} key={p.ride.key}>
                  <div className="pc-head">
                    <span className="pc-name">{p.ride.label}</span>
                    {p.rt
                      ? <span className="cell-badge rt">{p.rt.level.replace("RT-", "RT")}</span>
                      : <span className="cell-badge" style={{ background: LEVELS[p.cur].color }}>{LEVELS[p.cur].label}</span>}
                  </div>
                  <div className="pc-ladder">
                    {p.ride.ladder.map((lv) => (
                      <span key={lv} className={`pc-step ${lvlRank(p.cur) >= lvlRank(lv) ? "reached" : ""} ${p.cur === lv ? "current" : ""}`} title={LEVELS[lv].desc}>{lv}</span>
                    ))}
                  </div>
                  <div className="pc-next">
                    {p.cur === "0" ? <span className="pc-muted">Not started</span>
                      : p.next ? <span>Next: <strong>{p.next}</strong> · {stepAction(p.next, p.ride.key)}</span>
                      : <span className="pc-done"><Check size={12} /> Top level reached</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="todo-sub"><Award size={14} /><span>Completed trainings</span>
            {(doneAssignments.length + doneCourses.length) > 0 && <span className="count-pill">{doneAssignments.length + doneCourses.length}</span>}
          </div>
          {!loading && doneAssignments.length + doneCourses.length === 0 && <Empty text="No trainings completed yet." />}
          <div className="trained-list">
            {doneAssignments.map((a) => (
              <span className="trained-chip" key={a.id} title={`Completed ${fmtDate(a.completed_at)}`}>
                <Check size={12} /> {modulesById[a.module_id].title}
              </span>
            ))}
            {doneCourses.map((c) => (
              <span className="trained-chip" key={c.id} title={`Completed ${fmtDate(progressMap[c.id].completed_at)}`}>
                <Check size={12} /> {c.title}
              </span>
            ))}
          </div>
          {!loading && policiesSigned.length > 0 && (
            <div className="dim" style={{ fontSize: 12.5, marginTop: 10 }}>
              <FileText size={12} /> Policies signed: {policiesSigned.map((p) => p.title).join(", ")}
            </div>
          )}
        </div>
      </section>

      {/* 3. YOUR PATH FORWARD */}
      <section className="panel">
        <div className="panel-head"><h2><CalendarClock size={16} /> Your Path Forward</h2></div>
        <div className="panel-body">
          {goalNext.length > 0 && (
            <ol className="path-chain" style={{ marginBottom: showRides ? 14 : 0 }}>
              {goalNext.map(({ goal, pr, next }) => (
                <li key={goal.id} className={pr.complete ? "done" : "active"}>
                  {pr.complete ? <Check size={14} /> : <CircleDot size={14} />}
                  <span>
                    <strong>{goal.title}:</strong>{" "}
                    {pr.total === 0 ? "steps coming soon from your manager"
                      : pr.complete ? "every step complete — talk to your manager about next steps"
                      : <>next, {next.label.charAt(0).toLowerCase() + next.label.slice(1)} <span className="dim">({next.hint})</span></>}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {showRides && (
            <ol className="path-chain">
              <li className={ridesAtL2 === 4 ? "done" : "active"}>
                {ridesAtL2 === 4 ? <Check size={14} /> : <CircleDot size={14} />}
                Reach L2 on all 4 amusement rides (Slide, Tornado, Dragon, Kiddie) — <strong>{ridesAtL2}/4</strong>
              </li>
              <li className={hasReached(emp, "road", "L2") ? "done" : ridesAtL2 === 4 ? "active" : ""}>
                {hasReached(emp, "road", "L2") ? <Check size={14} /> : <CircleDot size={14} />}
                Train Road → L2, then push to L3 (and unlock Sprint)
              </li>
              <li className={hasReached(emp, "sprint", "L2") ? "done" : hasReached(emp, "road", "L2") ? "active" : ""}>
                {hasReached(emp, "sprint", "L2") ? <Check size={14} /> : <CircleDot size={14} />}
                Train Sprint → L2, then L3 (and unlock Euro)
              </li>
              <li className={hasReached(emp, "euro", "L3") ? "done" : hasReached(emp, "sprint", "L2") ? "active" : ""}>
                {hasReached(emp, "euro", "L3") ? <Check size={14} /> : <CircleDot size={14} />}
                Train Euro → L3
              </li>
              <li className="optional">
                <HelpCircle size={14} /> L4 (trainer) is optional — your supervisor will check in about it periodically once you reach L3.
              </li>
            </ol>
          )}
          {!showRides && goalNext.length === 0 && (
            <Empty text="Pick a goal below and your next steps will show up here." />
          )}
        </div>
      </section>

      {/* 4. MY GOALS */}
      {ctx && (
        <GoalsPanel emp={emp} ctx={ctx} canSignOff={false} actorName={emp.name} onChanged={reload} selfView />
      )}

      {/* 5. QUIZ HISTORY */}
      <MyQuizHistory quizzes={myQuizzes} ride={null} />

      {activeCourse && (
        <CoursePlayer
          course={activeCourse}
          emp={emp}
          progress={progressMap[activeCourse.id]}
          onClose={async () => { setActiveCourse(null); await reload(); }}
        />
      )}
      {activeTraining && (
        <ModulePlayerModal
          assignment={activeTraining.assignment}
          training={activeTraining.training}
          reviewOnly={!!activeTraining.reviewOnly}
          emp={emp}
          onClose={async () => { setActiveTraining(null); await reload(); }}
        />
      )}
      {readingPolicy && (
        <PolicyReaderModal
          policy={readingPolicy}
          me={emp}
          mustSign={policyRequiresSignature(readingPolicy, emp, groupIdx) && (signedVersion[readingPolicy.id] || 0) < readingPolicy.version}
          myAck={(ctx?.acks || []).find((a) => a.employee_id === emp.id && a.policy_id === readingPolicy.id && a.version === readingPolicy.version) || null}
          onClose={() => setReadingPolicy(null)}
          onSigned={async () => {
            setReadingPolicy(null);
            await reload();
            window.dispatchEvent(new Event("lvmgp:policies-changed"));
          }}
        />
      )}
    </div>
  );
}

/* Manager view of one employee's goals (on the Roster detail panel). */
function EmployeeGoalsBlock({ emp, user }) {
  const [ctx, setCtx] = useState(null);
  const reload = useCallback(async () => { setCtx(await loadGoalContext(emp.id)); }, [emp.id]);
  useEffect(() => { reload(); }, [reload]);
  if (!ctx || !ctx.goalsReady) return null;
  return (
    <GoalsPanel
      emp={emp}
      ctx={ctx}
      canSignOff={roleRank(user.role) >= roleRank("manager")}
      actorName={user.name}
      onChanged={reload}
      title={`${emp.name.split(" ")[0]}'s Goals`}
      selfView={false}
    />
  );
}

/* ---------------- Goals tab (managers) ---------------- */
function GoalsAdminView({ state, user }) {
  const [ctx, setCtx] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null); // goal or "new"
  const [err, setErr] = useState("");
  const canEdit = roleRank(user.role) >= roleRank("agm_gm");

  const reload = useCallback(async () => {
    const [c, cat] = await Promise.all([loadGoalContext(), db.loadGoalCatalog({ includeInactive: true })]);
    setCtx(c);
    if (cat.ok) { setCatalog(cat.goals); setErr(""); } else setErr(cat.message);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const empById = useMemo(() => Object.fromEntries(state.employees.map((e) => [e.id, e])), [state.employees]);

  return (
    <div className="users-view">
      <div className="users-head">
        <div>
          <h2 className="page-title"><Trophy size={20} /> Goals</h2>
          <p className="page-sub">
            The goals employees can pick on their dashboard, what each one takes, and who is working toward them.
          </p>
        </div>
        {canEdit && <button className="btn-primary" onClick={() => setEditing("new")}><Plus size={14} /> New goal</button>}
      </div>
      {err && (
        <div className="login-err">
          <AlertCircle size={14} /> {/relation|does not exist|schema cache/i.test(err)
            ? "The goals tables haven't been created yet — run goals_migration.sql in Supabase."
            : err}
        </div>
      )}
      {!ctx && !err && <Empty text="Loading goals…" />}
      {ctx && catalog.length === 0 && !err && <Empty text="No goals yet." />}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {ctx && catalog.map((g) => {
          const people = ctx.employeeGoals.filter((eg) => eg.goal_id === g.id)
            .map((eg) => empById[eg.employee_id]).filter((e) => e && !e.archived)
            .map((e) => ({ e, pr: goalProgress(g, e, ctx) }))
            .sort((a, b) => (b.pr.done / (b.pr.total || 1)) - (a.pr.done / (a.pr.total || 1)) || a.e.name.localeCompare(b.e.name));
          return (
            <section className="panel" key={g.id} style={{ opacity: g.active ? 1 : 0.6 }}>
              <div className="panel-head">
                <h2>{g.title}{!g.active && <span className="tag tag-muted" style={{ marginLeft: 8 }}>Hidden</span>}</h2>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span className="count-pill">{people.length} working toward it</span>
                  {canEdit && <button className="btn-ghost sm" onClick={() => setEditing(g)}>Edit</button>}
                </div>
              </div>
              <div className="panel-body">
                {g.description && <p className="dim" style={{ marginTop: 0, fontSize: 13 }}>{g.description}</p>}
                <div className="todo-sub"><span>What it takes</span></div>
                {g.requirements.length === 0
                  ? <div className="dim" style={{ fontSize: 12.5 }}>No steps listed yet{canEdit ? " — click Edit to add them." : "."}</div>
                  : (
                    <ol style={{ margin: "4px 0 10px", paddingLeft: 20, fontSize: 13 }}>
                      {g.requirements.map((r) => (
                        <li key={r.id}>{requirementLabel(r, ctx)}{r.kind === "manual" && <span className="dim"> (manager sign-off)</span>}</li>
                      ))}
                    </ol>
                  )}
                <div className="todo-sub"><span>Who's working toward it</span></div>
                {people.length === 0 && <div className="dim" style={{ fontSize: 12.5 }}>Nobody yet.</div>}
                {people.map(({ e, pr }) => (
                  <div className="row-item" key={e.id} style={{ padding: "6px 10px" }}>
                    <div className="ri-main">
                      <strong style={{ fontSize: 13 }}>{e.name}</strong>
                      <span className="ri-sub">{e.department} · {e.jobTitle}</span>
                    </div>
                    <div className="goal-bar" style={{ width: 120, margin: 0 }}>
                      <span style={{ width: `${pr.total ? Math.round((pr.done / pr.total) * 100) : 0}%` }} />
                    </div>
                    <span className={`tag ${pr.complete ? "tag-ok" : "tag-muted"}`}>
                      {pr.total ? (pr.complete ? "Ready" : `${pr.done}/${pr.total}`) : "—"}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>
      {ctx && (
        <p className="dim" style={{ fontSize: 12, marginTop: 14 }}>
          Manager sign-off steps are signed on the Roster: open the employee and use their Goals panel.
        </p>
      )}

      {editing && (
        <GoalEditModal
          goal={editing === "new" ? null : editing}
          ctx={ctx}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function GoalEditModal({ goal, ctx, onClose, onSaved }) {
  const isNew = !goal;
  const [title, setTitle] = useState(goal?.title || "");
  const [description, setDescription] = useState(goal?.description || "");
  const [active, setActive] = useState(goal?.active ?? true);
  const [sortOrder, setSortOrder] = useState(goal?.sort_order ?? 0);
  const [reqs, setReqs] = useState(() => (goal?.requirements || []).map((r) => ({ ...r })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const modules = (ctx?.modules || []).filter((m) => !m.archived && m.section !== "ride")
    .sort((a, b) => a.title.localeCompare(b.title));
  const policies = (ctx?.policies || []).slice().sort((a, b) => a.title.localeCompare(b.title));

  const setReq = (i, patch) => setReqs((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i, d) => setReqs((prev) => {
    const n = prev.slice(); const j = i + d;
    if (j < 0 || j >= n.length) return prev;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const add = (kind) => setReqs((prev) => [...prev, {
    kind,
    target: kind === "ride_level" ? "euro" : kind === "module" ? (modules[0]?.id || "") : kind === "policy" ? (policies[0]?.id || "") : null,
    level: kind === "ride_level" ? "L2" : null,
    label: "",
  }]);

  const save = async () => {
    if (!title.trim()) { setErr("Give the goal a name."); return; }
    for (const r of reqs) {
      if (r.kind === "manual" && !r.label?.trim()) { setErr("Every manager sign-off step needs a description."); return; }
      if (r.kind !== "manual" && !r.target) { setErr("One of the steps is missing its training, ride or policy."); return; }
    }
    setBusy(true); setErr("");
    const g = await db.saveGoal({ id: goal?.id, title, description, active, sort_order: Number(sortOrder) || 0 });
    if (!g.ok) { setBusy(false); setErr(g.message); return; }
    const r = await db.setGoalRequirements(g.goal.id, reqs);
    setBusy(false);
    if (!r.ok) { setErr(r.message); return; }
    onSaved();
  };

  const remove = async () => {
    if (!window.confirm(`Delete the goal "${goal.title}"? Employees who picked it will lose it, along with any manager sign-offs recorded for it. To keep the history, untick "Show to employees" instead.`)) return;
    setBusy(true);
    const r = await db.deleteGoal(goal.id);
    setBusy(false);
    if (!r.ok) { setErr(r.message); return; }
    onSaved();
  };

  return (
    <div className="modal-overlay">
      <div className="modal wide" style={{ maxWidth: 820, width: "94vw" }}>
        <div className="modal-head">
          <h3><Trophy size={18} /> {isNew ? "New goal" : `Edit goal — ${goal.title}`}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: "72vh", overflowY: "auto" }}>
          {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
          <label className="field"><span>Goal name (what employees pick)</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Party Captain" />
          </label>
          <label className="field"><span>Short description</span>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Host birthday parties from arrival to send-off." />
          </label>
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <label className="quiz-opt" style={{ cursor: "pointer" }}>
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              <span>Show to employees</span>
            </label>
            <label className="field" style={{ margin: 0, maxWidth: 160 }}><span>List order</span>
              <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
            </label>
          </div>

          <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, margin: "18px 0 6px" }}>
            What it takes — in order
          </div>
          {reqs.length === 0 && <div className="dim" style={{ fontSize: 12.5, marginBottom: 8 }}>No steps yet. Add them below.</div>}
          {reqs.map((r, i) => (
            <div className="goal-req-row" key={r.id || `new-${i}`}>
              <span className="goal-req-num">{i + 1}</span>
              <div className="goal-req-fields field" style={{ margin: 0 }}>
                {r.kind === "module" && (
                  <select value={r.target || ""} onChange={(e) => setReq(i, { target: e.target.value })}>
                    <option value="">Choose a training…</option>
                    {modules.map((m) => <option key={m.id} value={m.id}>Training: {m.title}</option>)}
                  </select>
                )}
                {r.kind === "ride_level" && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <select value={r.target || ""} onChange={(e) => setReq(i, { target: e.target.value, level: RIDE_MAP[e.target.value]?.ladder.includes(r.level) ? r.level : "L2" })}>
                      {RIDES.map((rd) => <option key={rd.key} value={rd.key}>Ride: {rd.label}</option>)}
                    </select>
                    <select value={r.level || "L2"} onChange={(e) => setReq(i, { level: e.target.value })}>
                      {(RIDE_MAP[r.target]?.ladder || ["T", "L2", "L3"]).filter((lv) => lv !== "T").map((lv) => (
                        <option key={lv} value={lv}>reach {lv}</option>
                      ))}
                    </select>
                  </div>
                )}
                {r.kind === "policy" && (
                  <select value={r.target || ""} onChange={(e) => setReq(i, { target: e.target.value })}>
                    <option value="">Choose a policy…</option>
                    {policies.map((p) => <option key={p.id} value={p.id}>Sign policy: {p.title}</option>)}
                  </select>
                )}
                {r.kind === "manual" && (
                  <input value={r.label || ""} onChange={(e) => setReq(i, { label: e.target.value })}
                    placeholder="e.g. Shadow 3 parties with a Party Captain (manager signs off)" />
                )}
              </div>
              <button className="icon-btn" title="Move up" onClick={() => move(i, -1)} disabled={i === 0}><ChevronUp size={14} /></button>
              <button className="icon-btn" title="Move down" onClick={() => move(i, 1)} disabled={i === reqs.length - 1}><ChevronDown size={14} /></button>
              <button className="icon-btn" title="Remove step" onClick={() => setReqs((p) => p.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
            </div>
          ))}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            <button className="btn-ghost sm" onClick={() => add("module")} disabled={modules.length === 0}><Plus size={12} /> Training</button>
            <button className="btn-ghost sm" onClick={() => add("ride_level")}><Plus size={12} /> Ride level</button>
            <button className="btn-ghost sm" onClick={() => add("policy")} disabled={policies.length === 0}><Plus size={12} /> Policy to sign</button>
            <button className="btn-ghost sm" onClick={() => add("manual")}><Plus size={12} /> Manager sign-off</button>
          </div>
          <p className="dim" style={{ fontSize: 12 }}>
            Trainings, ride levels and policies tick off automatically from the employee's records. Manager sign-off
            steps are for anything else (shadowing, a conversation, a trial shift) and are signed on the Roster.
          </p>
        </div>
        <div className="modal-foot" style={{ justifyContent: "space-between" }}>
          {!isNew ? <button className="btn-ghost" onClick={remove} disabled={busy}><Trash2 size={14} /> Delete goal</button> : <span />}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn-primary" onClick={save} disabled={busy || !title.trim()}>{busy ? "Saving..." : "Save goal"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MyQuizHistory({ quizzes, ride }) {
  const history = quizzes.filter((q) => q.score !== null && q.score !== undefined);
  return (
    <section className="panel">
      <div className="panel-head"><h2><Award size={16} /> My Quiz History</h2></div>
      <div className="panel-body">
        {history.length === 0 && <Empty text="No completed quizzes yet." />}
        {history.map((q) => {
          const passed = q.score >= PASS_SCORE;
          const missed = q.missedTopics || [];
          return (
            <div className="quiz-hist" key={q.id}>
              <div className="qh-head">
                <div>
                  <strong>{RIDE_MAP[q.ride].label}</strong>
                  <span className="ri-sub"> · {q.completedDate ? fmtDate(q.completedDate) : `attempt ${q.attempts}`}</span>
                </div>
                <span className={`tag ${passed ? "tag-pass" : "tag-fail"}`}>{passed ? <Check size={12} /> : <X size={12} />} {q.score}%</span>
              </div>
              {missed.length > 0
                ? <div className="qh-missed">Topics to review: {missed.map((t) => topicLabel(q.ride, t)).join(", ")}</div>
                : passed ? <div className="qh-perfect">No missed topics — great job!</div> : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* Trainee: full quizzes view (pending takeable + history) */
function MyQuizzesView({ state, user }) {
  const emp = findMyEmployee(state, user);
  if (!emp) return <Empty text="Your record isn't linked yet." />;
  const mine = state.quizzes.filter((q) => q.empId === emp.id);
  const pending = mine.filter((q) => q.status === "sent" || q.status === "failed");
  return (
    <div className="myquizzes">
      <section className="panel">
        <div className="panel-head"><h2><FileQuestion size={16} /> To Take</h2><span className="count-pill">{pending.length}</span></div>
        <div className="panel-body">
          {pending.length === 0 && <Empty text="Nothing to take right now." />}
          {pending.map((q) => (
            <div className="row-item" key={q.id}>
              <div className="ri-main"><strong>{RIDE_MAP[q.ride].label} certification</strong>
                <span className="ri-sub">target {q.targetLevel} · {q.status === "failed" ? `failed ${q.score}%` : "ready"}</span></div>
              <span className="ri-sub">Open from “My Training”</span>
            </div>
          ))}
        </div>
      </section>
      <MyQuizHistory quizzes={mine} ride={null} />
    </div>
  );
}

/* Trainee: read-only view of their own roster row */
function MyRecordView({ state, user }) {
  const emp = findMyEmployee(state, user);
  if (!emp) return <Empty text="Your record isn't linked yet." />;
  return (
    <div className="myrecord">
      <section className="panel">
        <div className="panel-head"><h2><Users size={16} /> My Certifications</h2></div>
        <div className="panel-body">
          <div className="myrec-grid">
            {RIDES.map((r) => {
              const lv = emp.levels[r.key];
              const rt = emp.retrain?.[r.key];
              return (
                <div className="myrec-cell" key={r.key}>
                  <span className="mrc-ride">{r.label}</span>
                  {rt ? <span className="cell-badge rt">{rt.level.replace("RT-", "RT")}</span>
                    : <span className="cell-badge" style={{ background: LEVELS[lv].color }}>{LEVELS[lv].label}</span>}
                  <span className="mrc-desc">{LEVELS[lv].desc}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="panel">
        <div className="panel-head"><h2><Clock size={16} /> My History</h2></div>
        <div className="panel-body log">
          {(emp.history || []).slice(0, 20).map((h, i) => (
            <div className="log-row" key={i}><span className="log-dot" /><span className="log-text">{h.text}</span><span className="log-date">{fmtDate(h.date)}</span></div>
          ))}
          {(!emp.history || emp.history.length === 0) && <Empty text="No history yet." />}
        </div>
      </section>
    </div>
  );
}

/* ============================================================
   COURSE PLAYER — self-paced course slide player
   Strict mode: must finish >=80% of audio (or answer KC) before Next enables.
   Resumes from current_slide_index across sessions.
   ============================================================ */
function CoursePlayer({ course, emp, progress: initialProgress, onClose }) {
  const slides = Array.isArray(course.slides) ? course.slides : [];
  const totalSlides = slides.length;

  // progress: server-side row (id + slides_viewed + knowledge_checks)
  const [progress, setProgress] = useState(initialProgress || null);
  const [idx, setIdx] = useState(0); // current slide index
  const [imageUrl, setImageUrl] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [mediaError, setMediaError] = useState("");
  const [audioPlayed, setAudioPlayed] = useState(0); // seconds of audio actually played
  const [audioDuration, setAudioDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [kcAnswer, setKcAnswer] = useState(null); // for knowledgeCheck slides
  const [kcSubmitted, setKcSubmitted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const audioRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const lastTickRef = React.useRef(0); // last currentTime, to add to audioPlayed monotonically

  // Fullscreen management. On mobile/iPad we also try to lock orientation to landscape.
  // Browser API quirks: webkitRequestFullscreen for Safari, mozRequestFullScreen for older FF.
  // iOS Safari doesn't grant fullscreen on arbitrary divs — for that we fall back to a
  // CSS-only "fake fullscreen" (position: fixed inset: 0) which covers the viewport.
  const enterFullscreen = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;
    let apiWorked = false;
    try {
      if (el.requestFullscreen) { await el.requestFullscreen(); apiWorked = true; }
      else if (el.webkitRequestFullscreen) { await el.webkitRequestFullscreen(); apiWorked = true; }
      else if (el.mozRequestFullScreen) { await el.mozRequestFullScreen(); apiWorked = true; }
      else if (el.msRequestFullscreen) { await el.msRequestFullscreen(); apiWorked = true; }
    } catch (e) { /* user denied or unsupported; fall through to CSS fallback */ }
    // CSS fallback when API didn't fire (iOS) — set state to apply the .is-fullscreen class
    if (!apiWorked) setIsFullscreen(true);
    // On phones/tablets, request landscape orientation lock (silently ignored on desktop)
    if (window.screen && window.screen.orientation && window.screen.orientation.lock) {
      try { await window.screen.orientation.lock("landscape"); } catch (_) { /* not all devices support this */ }
    }
  }, []);
  const exitFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
        else if (document.mozCancelFullScreen) await document.mozCancelFullScreen();
        else if (document.msExitFullscreen) await document.msExitFullscreen();
      }
      if (window.screen && window.screen.orientation && window.screen.orientation.unlock) {
        try { window.screen.orientation.unlock(); } catch (_) {}
      }
    } catch (e) {}
    // Always clear our flag (handles CSS-only fullscreen case)
    setIsFullscreen(false);
  }, []);

  // Track actual fullscreen state (handles ESC key, swipe-down on iOS, etc.)
  useEffect(() => {
    const onChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
      setIsFullscreen(!!fsEl);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    document.addEventListener("mozfullscreenchange", onChange);
    document.addEventListener("MSFullscreenChange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
      document.removeEventListener("mozfullscreenchange", onChange);
      document.removeEventListener("MSFullscreenChange", onChange);
    };
  }, []);

  // Auto-enter fullscreen on first mount. Will succeed if the player was opened via a user
  // click (e.g. Start button) since browsers honor user-gesture continuity for ~5s.
  // If it fails (e.g. opened programmatically) the user can click the Fullscreen button.
  useEffect(() => {
    // Small delay so the modal is mounted in the DOM before we try
    const t = setTimeout(() => { enterFullscreen(); }, 100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When user closes the player, ensure we exit fullscreen first
  const handleClose = useCallback(async () => {
    if (isFullscreen) await exitFullscreen();
    onClose();
  }, [isFullscreen, exitFullscreen, onClose]);

  // 1. On mount: ensure progress row exists and resume to current_slide_index
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let p = progress;
      if (!p) {
        const r = await db.ensureCourseProgress(course.id, emp.id);
        if (cancelled) return;
        if (!r.ok) { setMediaError(r.message || "Could not start course"); return; }
        p = r.progress;
        setProgress(p);
      }
      const startIdx = Math.min(p.current_slide_index || 0, Math.max(0, totalSlides - 1));
      setIdx(startIdx);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.id, emp.id]);

  // 2. When idx changes (or progress is first loaded): fetch image+audio signed URLs
  useEffect(() => {
    if (!progress) return;
    const slide = slides[idx];
    if (!slide) return;
    let cancelled = false;
    setMediaLoading(true);
    setMediaError("");
    setImageUrl(null);
    setAudioUrl(null);
    setAudioPlayed(0);
    setAudioDuration(0);
    setIsPlaying(false);
    setKcAnswer(null);
    setKcSubmitted(false);
    lastTickRef.current = 0;

    // Pre-fill saved KC answer if this is a knowledgeCheck slide
    if (slide.type === "knowledgeCheck" && slide.question?.id) {
      const saved = progress.knowledge_checks?.[slide.question.id];
      if (saved?.answer) {
        setKcAnswer(saved.answer);
        setKcSubmitted(true);
      }
    }

    (async () => {
      const [imgR, audR] = await Promise.all([
        slide.image ? db.getCourseImageUrl(slide.image) : Promise.resolve({ ok: false }),
        slide.audio ? db.getCourseAudioUrl(slide.audio) : Promise.resolve({ ok: false }),
      ]);
      if (cancelled) return;
      if (imgR.ok) setImageUrl(imgR.url); else setMediaError(imgR.message || "Image not found");
      if (audR.ok) setAudioUrl(audR.url);
      setMediaLoading(false);
    })();

    // Persist current_slide_index immediately (so a refresh resumes correctly)
    if (progress.id && progress.current_slide_index !== idx) {
      db.updateCourseProgress(progress.id, { current_slide_index: idx }).then((r) => {
        if (r.ok && !cancelled) setProgress((p) => p ? { ...p, current_slide_index: idx } : r.progress);
      });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, progress?.id]);

  const slide = slides[idx];
  const isKC = slide?.type === "knowledgeCheck";
  const isFinalExamIntro = slide?.type === "finalExamIntro";
  const isAck = slide?.type === "acknowledgment";
  const isLastSlide = idx === totalSlides - 1;

  // Strict-mode gate: when is the Next button enabled?
  const audioGateMet = audioDuration > 0 ? (audioPlayed / audioDuration) >= 0.8 : false;
  const kcGateMet = !isKC || kcSubmitted;
  // If a slide has no audio file at all (e.g. ack slide), gate is just KC (which doesn't apply)
  // and that's effectively a "click Next to acknowledge" slide.
  const noAudio = !slide?.audio;
  const nextEnabled = (noAudio || audioGateMet) && kcGateMet;

  const markSlideViewed = async (slideId) => {
    if (!progress) return null;
    const viewed = Array.isArray(progress.slides_viewed) ? progress.slides_viewed : [];
    if (viewed.includes(slideId)) return progress;
    const newViewed = [...viewed, slideId];
    const patch = { slides_viewed: newViewed };
    // If this is the last slide, mark course complete
    if (newViewed.length >= totalSlides) {
      patch.completed_at = new Date().toISOString();
    }
    const r = await db.updateCourseProgress(progress.id, patch);
    if (r.ok) setProgress(r.progress);
    return r.ok ? r.progress : progress;
  };

  const goNext = async () => {
    if (!nextEnabled) return;
    if (slide) await markSlideViewed(slide.id);
    if (idx < totalSlides - 1) {
      setIdx(idx + 1);
    } else {
      // Last slide acknowledged — close
      handleClose();
    }
  };
  const goPrev = () => { if (idx > 0) setIdx(idx - 1); };

  // Audio event handlers
  const onAudioPlay = () => setIsPlaying(true);
  const onAudioPause = () => setIsPlaying(false);
  const onAudioLoaded = (e) => setAudioDuration(e.target.duration || 0);
  const onAudioTime = (e) => {
    const t = e.target.currentTime || 0;
    const delta = t - lastTickRef.current;
    // Only add positive forward progress (ignore seeks backward, but count any forward play)
    if (delta > 0 && delta < 2) {
      setAudioPlayed((prev) => prev + delta);
    }
    lastTickRef.current = t;
  };
  const onAudioEnded = () => { setIsPlaying(false); };
  const toggleAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play(); else a.pause();
  };

  const submitKC = async () => {
    if (!isKC || !slide.question || !kcAnswer || !progress) return;
    const correct = kcAnswer === slide.question.correctChoiceId;
    const kcMap = { ...(progress.knowledge_checks || {}) };
    kcMap[slide.question.id] = {
      answer: kcAnswer,
      correct,
      answered_at: new Date().toISOString(),
    };
    const r = await db.updateCourseProgress(progress.id, { knowledge_checks: kcMap });
    if (r.ok) setProgress(r.progress);
    setKcSubmitted(true);
  };

  // Render
  if (!slide) {
    return (
      <div className="modal-overlay">
        <div className="modal course-player">
          <div className="modal-head"><h3>{course.title}</h3>
            <button className="icon-btn" onClick={handleClose}><X size={16} /></button>
          </div>
          <div className="modal-body"><Empty text="This course has no slides yet." /></div>
        </div>
      </div>
    );
  }

  const viewedCount = Array.isArray(progress?.slides_viewed) ? progress.slides_viewed.length : 0;
  const isViewed = progress?.slides_viewed?.includes(slide.id);
  // Gate hint text (what's blocking Next)
  let gateHint = "";
  if (!nextEnabled) {
    if (!noAudio && !audioGateMet) {
      const pct = audioDuration > 0 ? Math.round((audioPlayed / audioDuration) * 100) : 0;
      gateHint = `Listen to at least 80% of the narration to continue (${pct}% so far)`;
    } else if (isKC && !kcSubmitted) {
      gateHint = "Answer the question to continue";
    }
  }

  return (
    <div className="modal-overlay">
      <div ref={containerRef} className={`modal course-player ${isFullscreen ? "is-fullscreen" : ""}`} style={isFullscreen ? { maxWidth: "100vw", width: "100vw", height: "100vh", maxHeight: "100vh", borderRadius: 0, background: "#000" } : { maxWidth: 1100 }}>
        <div className="modal-head">
          <h3><BookOpen size={18} /> {course.title}</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="dim" style={{ fontSize: 12 }}>Slide {idx + 1} of {totalSlides} · {viewedCount}/{totalSlides} complete</span>
            <button className="icon-btn" onClick={isFullscreen ? exitFullscreen : enterFullscreen} title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
              <Maximize size={16} />
            </button>
            <button className="icon-btn" onClick={handleClose}><X size={16} /></button>
          </div>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14, padding: 18 }}>
          {/* Progress bar */}
          <div style={{ height: 4, background: "var(--panel2)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${totalSlides > 0 ? (viewedCount / totalSlides) * 100 : 0}%`,
              background: "var(--ok)",
              transition: "width 0.3s ease",
            }} />
          </div>

          {/* Slide image */}
          <div className="slide-image-wrap" style={{ background: "#fff", borderRadius: 8, overflow: "hidden", minHeight: 360, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {mediaLoading && <Empty text="Loading slide..." />}
            {!mediaLoading && mediaError && <Empty text={`Could not load slide media: ${mediaError}`} />}
            {!mediaLoading && imageUrl && (
              <img src={imageUrl} alt={slide.title || `Slide ${idx + 1}`} style={{ width: "100%", height: "auto", display: "block" }} />
            )}
          </div>

          {/* Audio controls */}
          {slide.audio && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "var(--panel2)", borderRadius: 6 }}>
              <button className="btn-mini" onClick={toggleAudio} disabled={!audioUrl} style={{ minWidth: 80 }}>
                {isPlaying ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Play</>}
              </button>
              <Volume2 size={14} className="dim" />
              <div style={{ flex: 1, height: 4, background: "var(--line)", borderRadius: 2, overflow: "hidden", position: "relative" }}>
                <div style={{
                  height: "100%",
                  width: `${audioDuration > 0 ? Math.min(100, (audioPlayed / audioDuration) * 100) : 0}%`,
                  background: audioGateMet ? "var(--ok)" : "var(--accent)",
                  transition: "width 0.2s linear",
                }} />
              </div>
              <span className="dim" style={{ fontSize: 11, minWidth: 70, textAlign: "right" }}>
                {audioDuration > 0
                  ? `${Math.round(Math.min(audioPlayed, audioDuration))}s / ${Math.round(audioDuration)}s`
                  : "—"}
              </span>
              {audioUrl && (
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  onPlay={onAudioPlay}
                  onPause={onAudioPause}
                  onLoadedMetadata={onAudioLoaded}
                  onTimeUpdate={onAudioTime}
                  onEnded={onAudioEnded}
                  style={{ display: "none" }}
                />
              )}
            </div>
          )}

          {/* Knowledge check */}
          {isKC && slide.question && (
            <div style={{ padding: 14, background: "var(--panel2)", borderRadius: 6, border: "1px solid var(--line)" }}>
              <div style={{ fontWeight: 600, marginBottom: 10 }}>{slide.question.prompt}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(slide.question.choices || []).map((ch) => {
                  const selected = kcAnswer === ch.id;
                  const isCorrect = kcSubmitted && ch.id === slide.question.correctChoiceId;
                  const isWrong = kcSubmitted && selected && !isCorrect;
                  return (
                    <label
                      key={ch.id}
                      style={{
                        display: "flex", alignItems: "center", gap: 8, padding: 8, borderRadius: 4,
                        cursor: kcSubmitted ? "default" : "pointer",
                        background: isCorrect ? "rgba(34,197,94,0.15)" : isWrong ? "rgba(239,68,68,0.15)" : selected ? "var(--panel)" : "transparent",
                        border: `1px solid ${isCorrect ? "var(--ok)" : isWrong ? "var(--err)" : selected ? "var(--accent)" : "var(--line)"}`,
                      }}
                    >
                      <input
                        type="radio"
                        name={`kc-${slide.id}`}
                        value={ch.id}
                        checked={selected}
                        disabled={kcSubmitted}
                        onChange={() => setKcAnswer(ch.id)}
                      />
                      <span><strong>{ch.id}.</strong> {ch.text}</span>
                      {isCorrect && <Check size={14} style={{ marginLeft: "auto", color: "var(--ok)" }} />}
                      {isWrong && <X size={14} style={{ marginLeft: "auto", color: "var(--err)" }} />}
                    </label>
                  );
                })}
              </div>
              {!kcSubmitted && (
                <button className="btn-primary sm" onClick={submitKC} disabled={!kcAnswer} style={{ marginTop: 10 }}>
                  Submit answer
                </button>
              )}
              {kcSubmitted && slide.question.feedback && (
                <div style={{ marginTop: 10, padding: 10, background: "var(--panel)", borderRadius: 4, fontSize: 13 }}>
                  {kcAnswer === slide.question.correctChoiceId ? "✓ " : ""}{slide.question.feedback}
                </div>
              )}
            </div>
          )}

          {/* Slide on-screen text (for context, especially when image text isn't crisp) */}
          {slide.onScreenText && !isKC && (
            <div style={{ padding: 12, background: "var(--panel2)", borderRadius: 6, fontSize: 13, whiteSpace: "pre-line", color: "var(--txt2)" }}>
              {slide.onScreenText}
            </div>
          )}

          {/* Gate hint / status */}
          {gateHint && (
            <div style={{ padding: "8px 12px", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 4, fontSize: 12, color: "var(--warn)" }}>
              <AlertCircle size={12} style={{ display: "inline", marginRight: 6, verticalAlign: -2 }} />
              {gateHint}
            </div>
          )}
          {isViewed && nextEnabled && (
            <div style={{ padding: "8px 12px", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 4, fontSize: 12, color: "var(--ok)" }}>
              <Check size={12} style={{ display: "inline", marginRight: 6, verticalAlign: -2 }} />
              Slide complete
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="modal-foot" style={{ justifyContent: "space-between" }}>
          <button className="btn-secondary" onClick={goPrev} disabled={idx === 0}>
            <ArrowLeft size={14} /> Previous
          </button>
          <button className="btn-primary" onClick={goNext} disabled={!nextEnabled}>
            {isLastSlide ? <>Finish <Check size={14} /></> : <>Next <ArrowRight size={14} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   REQUIRED TRAININGS — manager management + trainee viewer
   ============================================================ */

/* ------- Questions manager modal ------- */
function ModuleQuestionsModal({ training, onClose }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // question obj or "new"
  const [err, setErr] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    // Questions live in quiz_questions and are scoped by module key in the tags array
    const res = await db.loadModuleQuestions(training.key);
    if (res.ok) setQuestions(res.questions); else setErr(res.message);
    setLoading(false);
  }, [training.key]);
  useEffect(() => { reload(); }, [reload]);

  const archive = async (q) => {
    const previewText = (q.text || "").slice(0, 80);
    if (!confirm(`Archive this question?\n\n"${previewText}..."\n\nIt won't appear in new quiz attempts.`)) return;
    const res = await db.archiveQuizQuestion(q.id);
    if (res.ok) reload(); else setErr(res.message);
  };

  // Extract topic from a tag prefixed 'topic:' (see ModuleQuestionEditModal)
  const topicOf = (q) => {
    const tags = Array.isArray(q.tags) ? q.tags : [];
    const t = tags.find((x) => typeof x === "string" && x.startsWith("topic:"));
    return t ? t.slice(6) : "";
  };

  return (
    <div className="modal-overlay">
      <div className="modal wide">
        <div className="modal-head">
          <h3><FileQuestion size={18} /> Questions — {training.title}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <p className="dim" style={{ fontSize: 12 }}>{questions.length} active question{questions.length !== 1 ? "s" : ""}. Quiz draws {training.quiz_question_count} at random.</p>
            <button className="btn-primary sm" onClick={() => setEditing("new")}>
              <Plus size={12} /> Add question
            </button>
          </div>
          {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
          {loading && <Empty text="Loading..." />}
          {!loading && questions.length === 0 && <Empty text="No questions yet. Click 'Add question' to start." />}
          {!loading && questions.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {questions.map((q) => {
                const topic = topicOf(q);
                return (
                <div key={q.id} style={{ padding: 10, background: "var(--panel2)", borderRadius: 6, border: "1px solid var(--line)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{q.text}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12 }}>
                        {(q.choices || []).map((c, i) => (
                          <span key={i} style={{ color: i === q.correct ? "var(--ok)" : "var(--txt3)" }}>
                            {i === q.correct ? "✓ " : "  "}{String.fromCharCode(65 + i)}. {c}
                          </span>
                        ))}
                      </div>
                      {topic && <span className="dim" style={{ fontSize: 11 }}>Topic: {topic}</span>}
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button className="btn-mini ghost" onClick={() => setEditing(q)}><Pencil size={12} /></button>
                      <button className="btn-mini ghost" onClick={() => archive(q)}><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
      {editing && (
        <ModuleQuestionEditModal
          training={training}
          question={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

/* ------- Question edit modal ------- */
function ModuleQuestionEditModal({ training, question, onClose, onSaved }) {
  // training is a training_module row. Questions live in quiz_questions and are scoped
  // by having the module's key in their `tags` array.
  const isNew = !question;
  const [prompt, setPrompt] = useState(question?.text || "");
  const [choices, setChoices] = useState(question?.choices?.length > 1 ? question.choices : ["", "", "", ""]);
  const [correctIndex, setCorrectIndex] = useState(question?.correct ?? 0);
  // Topic is stored as a tag prefixed 'topic:' so we can separate it from module scoping
  const existingTopic = React.useMemo(() => {
    const tags = Array.isArray(question?.tags) ? question.tags : [];
    const t = tags.find((x) => typeof x === "string" && x.startsWith("topic:"));
    return t ? t.slice(6) : "";
  }, [question]);
  const [topic, setTopic] = useState(existingTopic);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const setChoice = (i, val) => setChoices((cur) => cur.map((c, idx) => idx === i ? val : c));
  const addChoice = () => setChoices((cur) => [...cur, ""]);
  const removeChoice = (i) => {
    setChoices((cur) => cur.filter((_, idx) => idx !== i));
    if (correctIndex >= i) setCorrectIndex(Math.max(0, correctIndex - (correctIndex === i ? 0 : 1)));
  };

  const save = async () => {
    const validChoices = choices.map((c) => c.trim()).filter(Boolean);
    if (!prompt.trim()) { setErr("Prompt is required"); return; }
    if (validChoices.length < 2) { setErr("At least 2 choices required"); return; }
    if (correctIndex >= validChoices.length) { setErr("Correct answer must be one of the choices"); return; }
    setBusy(true);
    setErr("");
    // Build tags: keep any non-topic tags from the existing question, add module key, add topic tag
    const priorTags = Array.isArray(question?.tags) ? question.tags : [];
    const cleaned = priorTags.filter((t) => typeof t === "string" && !t.startsWith("topic:"));
    const tagSet = new Set(cleaned);
    tagSet.add(training.key);  // module scoping
    if (topic.trim()) tagSet.add(`topic:${topic.trim()}`);
    const payload = {
      text: prompt.trim(),
      choices: validChoices,
      correct: correctIndex,
      tags: Array.from(tagSet),
      mandatory: question?.mandatory || false,
    };
    const res = isNew
      ? await db.createQuizQuestion(payload)
      : await db.updateQuizQuestion(question.id, payload);
    if (!res.ok) { setErr(res.message); setBusy(false); return; }
    onSaved();
    setBusy(false);
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 200 }}>
      <div className="modal wide">
        <div className="modal-head">
          <h3><FileQuestion size={18} /> {isNew ? "Add question" : "Edit question"}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <label className="field"><span>Question *</span>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
              style={{ width: "100%", padding: 8, background: "var(--panel2)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 6, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
          </label>
          <div className="field">
            <span>Choices (select the correct one)</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
              {choices.map((c, i) => (
                <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input type="radio" name="correct" checked={correctIndex === i} onChange={() => setCorrectIndex(i)} />
                  <input value={c} onChange={(e) => setChoice(i, e.target.value)} placeholder={`Choice ${String.fromCharCode(65 + i)}`} style={{ flex: 1 }} />
                  {choices.length > 2 && (
                    <button className="btn-mini ghost" onClick={() => removeChoice(i)}><X size={12} /></button>
                  )}
                </div>
              ))}
            </div>
            <button className="btn-mini ghost" style={{ marginTop: 6 }} onClick={addChoice} disabled={choices.length >= 6}>
              <Plus size={12} /> Add choice
            </button>
          </div>
          <label className="field"><span>Topic (optional, for grouping missed topics)</span>
            <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Safety procedures" />
          </label>
          {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
        </div>
        <div className="modal-foot">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={busy}>
            {busy ? "Saving..." : (isNew ? "Add question" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------- Assignment manager modal ------- */
function ModuleAssignModal({ training, employees, user, onClose }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reviewing, setReviewing] = useState(null); // { assignment, emp }
  const [practicalTarget, setPracticalTarget] = useState(null); // { assignment, emp }
  const [err, setErr] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    const res = await db.loadModuleAssignments({ moduleId: training.id });
    if (res.ok) setAssignments(res.assignments); else setErr(res.message);
    setLoading(false);
  }, [training.id]);
  useEffect(() => { reload(); }, [reload]);

  const empById = React.useMemo(() => {
    const m = {}; (employees || []).forEach((e) => { m[e.id] = e; }); return m;
  }, [employees]);

  const assignedEmpIds = new Set(assignments.map((a) => a.employee_id));
  const available = (employees || []).filter((e) => !e.archived && !assignedEmpIds.has(e.id));

  const assign = async (empId) => {
    const dueAt = new Date(Date.now() + training.due_days_after_assignment * 24 * 60 * 60 * 1000).toISOString();
    const res = await db.createModuleAssignment({
      module_id: training.id,
      employee_id: empId,
      due_at: dueAt,
    });
    if (!res.ok) { setErr(res.message); return; }
    reload();
  };

  const unassign = async (a) => {
    const emp = empById[a.employee_id];
    if (!confirm(`Remove ${emp?.name || "this employee"}'s assignment?\n\nAny quiz attempts will be lost.`)) return;
    const res = await db.deleteModuleAssignment(a.id);
    if (!res.ok) { setErr(res.message); return; }
    reload();
  };

  const statusLabel = (a) => {
    if (a.completed_at) return { text: `Completed ${fmtDate(a.completed_at)}`, cls: "tag-ok" };
    if (a.review_status === "pending") return { text: "Awaiting Competency Verification", cls: "tag-warn" };
    // Practical needed: quiz passed (or no quiz) and practical not yet done
    const quizCleared = a.quiz_status === "passed" || !training.has_quiz;
    if (training.requires_practical && quizCleared && a.practical_status !== "complete") {
      return { text: "Awaiting practical walk-through", cls: "tag-warn" };
    }
    if (a.quiz_status === "sent") return { text: "Quiz in progress", cls: "tag-warn" };
    if (a.quiz_status === "failed") return { text: "Quiz failed — retake", cls: "tag-fail" };
    if (a.document_viewed_at) return { text: "Doc read, quiz pending", cls: "tag-warn" };
    return { text: "Not started", cls: "tag-muted" };
  };

  // Which action buttons should show on this row?
  const canPractical = (a) => {
    if (!training.requires_practical) return false;
    if (a.completed_at || a.practical_status === "complete") return false;
    if (a.review_status === "pending") return false; // shouldn't reach review before practical
    const quizCleared = a.quiz_status === "passed" || !training.has_quiz;
    return quizCleared;
  };
  const canReviewFn = (a) => {
    return a.review_status === "pending" && !a.completed_at;
  };

  return (
    <div className="modal-overlay">
      <div className="modal wide">
        <div className="modal-head">
          <h3><UserPlus size={18} /> Assign — {training.title}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <p className="dim" style={{ fontSize: 12 }}>{assignments.length} assigned</p>
            <button className="btn-primary sm" onClick={() => setPickerOpen(true)} disabled={available.length === 0}>
              <Plus size={12} /> Assign to employee
            </button>
          </div>
          {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
          {loading && <Empty text="Loading..." />}
          {!loading && assignments.length === 0 && <Empty text="Not assigned to anyone yet." />}
          {!loading && assignments.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {assignments.map((a) => {
                const emp = empById[a.employee_id];
                const st = statusLabel(a);
                const showPractical = canPractical(a);
                const showReview = canReviewFn(a);
                return (
                  <div key={a.id} className="row-item">
                    <div className="ri-main">
                      <strong>{emp?.name || "(unknown)"}</strong>
                      <span className="ri-sub">Assigned {fmtDate(a.assigned_at)} · Due {a.due_at ? fmtDate(a.due_at) : "—"}</span>
                    </div>
                    <span className={`tag ${st.cls}`}>{st.text}</span>
                    {showPractical && (
                      <button className="btn-primary sm" onClick={() => setPracticalTarget({ assignment: a, emp })}>
                        Practical
                      </button>
                    )}
                    {showReview && (
                      <button className="btn-primary sm" onClick={() => setReviewing({ assignment: a, emp })}>
                        Verify competency
                      </button>
                    )}
                    <button className="btn-mini ghost" onClick={() => unassign(a)}><Trash2 size={12} /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
      {pickerOpen && (
        <ModuleEmployeePicker
          employees={available}
          onClose={() => setPickerOpen(false)}
          onPick={async (empId) => { await assign(empId); setPickerOpen(false); }}
        />
      )}
      {reviewing && (
        <ModuleReviewModal
          assignment={reviewing.assignment}
          emp={reviewing.emp}
          training={training}
          user={user}
          onClose={() => setReviewing(null)}
          onComplete={async () => { setReviewing(null); await reload(); }}
        />
      )}
      {practicalTarget && (
        <ModulePracticalModal
          assignment={practicalTarget.assignment}
          emp={practicalTarget.emp}
          training={training}
          user={user}
          onClose={() => setPracticalTarget(null)}
          onComplete={async () => { setPracticalTarget(null); await reload(); }}
        />
      )}
    </div>
  );
}

function ModuleEmployeePicker({ employees, onClose, onPick }) {
  const [q, setQ] = useState("");
  const qq = q.trim().toLowerCase();
  const filtered = qq ? employees.filter((e) => e.name.toLowerCase().includes(qq)) : employees;
  return (
    <div className="modal-overlay" style={{ zIndex: 200 }}>
      <div className="modal">
        <div className="modal-head">
          <h3><UserPlus size={18} /> Pick employee</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="search"><Search size={14} /><input placeholder="Search by name..." value={q} onChange={(e) => setQ(e.target.value)} autoFocus /></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 10, maxHeight: 400, overflowY: "auto" }}>
            {filtered.length === 0 && <Empty text="No matching employees." />}
            {filtered.map((e) => (
              <button key={e.id} className="ts-emp-row" onClick={() => onPick(e.id)}>
                <div className="ts-emp-main">
                  <strong>{e.name}</strong>
                  <span className="ri-sub">{e.department || "—"}</span>
                </div>
                <ChevronRight size={16} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------- Competency Verification modal ------- */
function ModuleReviewModal({ assignment, emp, training, user, onClose, onComplete }) {
  const [quiz, setQuiz] = useState(null);
  const [docUrl, setDocUrl] = useState(null);
  const [notes, setNotes] = useState(assignment.review_notes || "");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [qRes, dRes] = await Promise.all([
        db.loadModuleQuiz(assignment.id),
        training.document_path ? db.getModuleDocUrl(training.document_path) : Promise.resolve({ ok: false }),
      ]);
      if (qRes.ok) setQuiz(qRes.quiz);
      if (dRes.ok) setDocUrl(dRes.url);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignment.id, training.document_path]);

  const markComplete = async () => {
    setBusy(true);
    const now = new Date().toISOString();
    const res = await db.updateModuleAssignment(assignment.id, {
      review_status: "complete",
      reviewed_by: user.id,
      reviewed_at: now,
      review_notes: notes.trim() || null,
      completed_at: now,
    });
    setBusy(false);
    if (!res.ok) { setErr(res.message); return; }
    onComplete();
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 200 }}>
      <div className="modal" style={{ maxWidth: 1100, width: "95vw", height: "90vh", maxHeight: "90vh" }}>
        <div className="modal-head">
          <h3><ClipboardCheck size={18} /> Competency Verification — {emp?.name} · {training.title}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display: "flex", gap: 12, padding: 12, flex: 1, minHeight: 0 }}>
          {/* Left: document viewer */}
          <div style={{ flex: 2, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Training document</div>
            <div style={{ flex: 1, background: "#fff", borderRadius: 6, overflow: "hidden", border: "1px solid var(--line)" }}>
              {docUrl ? (
                <iframe src={docUrl} title={training.title} style={{ width: "100%", height: "100%", border: "none" }} />
              ) : (
                <Empty text="No document available." />
              )}
            </div>
          </div>
          {/* Right: quiz results + notes */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, overflowY: "auto" }}>
            <div>
              <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Trainee</div>
              <div style={{ fontWeight: 600 }}>{emp?.name}</div>
              <div style={{ fontSize: 12, color: "var(--txt2)" }}>{emp?.department || ""}</div>
            </div>
            <div>
              <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Quiz result</div>
              {loading && <Empty text="Loading..." />}
              {!loading && quiz && (
                <div style={{ padding: 10, background: "var(--panel2)", borderRadius: 6, border: "1px solid var(--line)" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: quiz.status === "passed" ? "var(--ok)" : "var(--err)" }}>
                    {quiz.score}% · {quiz.status === "passed" ? "Passed" : "Failed"}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--txt2)", marginTop: 4 }}>
                    Attempt {quiz.attempts || 1} · {quiz.completed_date ? fmtDate(quiz.completed_date) : ""}
                  </div>
                  {quiz.missed_topics?.length > 0 && (
                    <div style={{ fontSize: 12, marginTop: 8 }}>
                      <strong>Missed topics to discuss:</strong>
                      <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                        {quiz.missed_topics.map((t, i) => <li key={i}>{t}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {!loading && !quiz && <Empty text="No quiz on this training." />}
            </div>
            {/* Show individual quiz Q&A for review */}
            {quiz?.questions?.length > 0 && (
              <div>
                <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Question review</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {quiz.questions.map((q, i) => (
                    <div key={q.id || i} style={{ padding: 8, background: "var(--panel2)", borderRadius: 4, fontSize: 12 }}>
                      <div style={{ fontWeight: 500, marginBottom: 4 }}>{i + 1}. {q.prompt}</div>
                      <div style={{ color: "var(--ok)" }}>✓ {String.fromCharCode(65 + q.correct_index)}. {q.choices?.[q.correct_index]}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <label className="field">
              <span>Verification notes (optional)</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="What did you verify? What was discussed?"
                style={{ width: "100%", padding: 8, background: "var(--panel2)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 6, fontSize: 13, fontFamily: "inherit", resize: "vertical" }}
              />
            </label>
            {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
          </div>
        </div>
        <div className="modal-foot" style={{ justifyContent: "space-between" }}>
          <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary" onClick={markComplete} disabled={busy}>
            {busy ? "Saving..." : <>Verify competency <Check size={14} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------- Practical Walk-Through modal ------- */
function ModulePracticalModal({ assignment, emp, training, user, onClose, onComplete }) {
  // Steps: item pages first (one per item), then a final "sign" page.
  // Each item page has one item + optional notes. Trainer navigates Next/Back.
  // At the sign page, both trainer AND trainee sign to complete.

  const initialItems = React.useMemo(() => {
    const templateItems = (training.practical_items || []).map((it) =>
      typeof it === "string" ? { text: it } : { ...it, text: it.text || it.label || "" }
    );
    const priorResults = Array.isArray(assignment.practical_results) ? assignment.practical_results : [];
    return templateItems.map((it) => {
      const prior = priorResults.find((p) => p.text === it.text);
      return { text: it.text, checked: prior?.checked || false, note: prior?.note || "" };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignment.id, training.id]);

  const [items, setItems] = useState(initialItems);
  const [overallNotes, setOverallNotes] = useState(assignment.practical_notes || "");
  // Step 0..items.length-1 = one item per step. Step items.length = signature page.
  const [step, setStep] = useState(0);
  const isSignStep = step >= items.length;
  const currentItem = !isSignStep ? items[step] : null;

  // Signatures on the final page
  const [trainerName, setTrainerName] = useState(user.full_name || user.name || "");
  const [trainerSig, setTrainerSig] = useState(null);
  const [traineeName, setTraineeName] = useState(emp?.name || "");
  const [traineeSig, setTraineeSig] = useState(null);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggleCurrent = () => {
    setItems((cur) => cur.map((it, idx) => idx === step ? { ...it, checked: !it.checked } : it));
  };
  const setCurrentNote = (val) => {
    setItems((cur) => cur.map((it, idx) => idx === step ? { ...it, note: val } : it));
  };

  const canNext = isSignStep
    ? (trainerName.trim() && trainerSig && traineeName.trim() && traineeSig)
    : true; // items don't have to be checked to advance — the trainer can still note something
  const nextLabel = isSignStep ? "Complete practical" : (step === items.length - 1 ? "To signatures →" : "Next →");

  const goNext = () => {
    setErr("");
    if (isSignStep) return complete();
    setStep((s) => s + 1);
  };
  const goBack = () => {
    setErr("");
    setStep((s) => Math.max(0, s - 1));
  };

  const complete = async () => {
    if (!trainerName.trim() || !trainerSig) { setErr("Trainer must sign to complete."); return; }
    if (!traineeName.trim() || !traineeSig) { setErr("Trainee must sign to complete."); return; }
    setBusy(true);
    const now = new Date().toISOString();
    const patch = {
      practical_status: "complete",
      practical_completed_by: user.id,
      practical_completed_at: now,
      practical_results: items,
      practical_notes: overallNotes.trim() || null,
      practical_trainer_signature: { name: trainerName.trim(), dataUrl: trainerSig, date: now },
      practical_trainee_signature: { name: traineeName.trim(), dataUrl: traineeSig, date: now },
    };
    // Advance: if verification is required, go pending; otherwise mark complete.
    if (training.requires_supervisor_review || training.requires_competency_verification) {
      patch.review_status = "pending";
    } else {
      patch.completed_at = now;
    }
    const res = await db.updateModuleAssignment(assignment.id, patch);
    setBusy(false);
    if (!res.ok) { setErr(res.message); return; }
    onComplete();
  };

  // Header progress indicator
  const totalSteps = items.length + 1;
  const currentStepNumber = step + 1;

  return (
    <div className="modal-overlay" style={{ zIndex: 200 }}>
      <div className="modal wide" style={{ maxWidth: 760 }}>
        <div className="modal-head">
          <h3><ClipboardCheck size={18} /> Practical Walk-Through — {emp?.name}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Progress bar */}
        <div style={{ padding: "4px 20px", background: "var(--panel2)", borderBottom: "1px solid var(--line)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "var(--txt2)", marginBottom: 6 }}>
            <span><strong>{training.title}</strong></span>
            <span>{isSignStep ? "Signatures" : `Item ${currentStepNumber} of ${items.length}`}</span>
          </div>
          <div style={{ height: 4, background: "var(--line)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${(currentStepNumber / totalSteps) * 100}%`,
              background: "var(--accent)",
              transition: "width 0.2s",
            }} />
          </div>
        </div>

        <div className="modal-body">
          {items.length === 0 && (
            <Empty text="This training has no practical items defined. Ask an admin to add items in the module editor before running the practical." />
          )}

          {!isSignStep && currentItem && (
            <>
              <p className="dim" style={{ fontSize: 12, marginTop: 0 }}>
                Walk {emp?.name} through this item. When done, check it off and click Next.
              </p>
              <div style={{
                padding: 20,
                marginTop: 12,
                background: currentItem.checked ? "rgba(34,197,94,0.10)" : "var(--panel2)",
                border: `2px solid ${currentItem.checked ? "var(--ok)" : "var(--line)"}`,
                borderRadius: 8,
              }}>
                <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 16, lineHeight: 1.4 }}>
                  {currentItem.text}
                </div>
                <label style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer", padding: "8px 0" }}>
                  <input
                    type="checkbox"
                    checked={currentItem.checked}
                    onChange={toggleCurrent}
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 500 }}>
                    Trainee has demonstrated this
                  </span>
                </label>
                <label className="field" style={{ marginTop: 12 }}>
                  <span>Notes for this item (optional)</span>
                  <textarea
                    value={currentItem.note}
                    onChange={(e) => setCurrentNote(e.target.value)}
                    rows={2}
                    placeholder="Anything to remember about this step?"
                    style={{ width: "100%", padding: 8, background: "var(--panel)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 6, fontSize: 13, fontFamily: "inherit", resize: "vertical" }}
                  />
                </label>
              </div>
              <div className="dim" style={{ fontSize: 11, marginTop: 12, textAlign: "center" }}>
                {items.filter((it) => it.checked).length} of {items.length} items marked complete
              </div>
            </>
          )}

          {isSignStep && (
            <>
              <p className="dim" style={{ fontSize: 13, marginTop: 0 }}>
                Walk-through complete. Both trainer and trainee sign below to record completion.
              </p>
              <label className="field" style={{ marginTop: 8 }}>
                <span>Overall notes (optional)</span>
                <textarea
                  value={overallNotes}
                  onChange={(e) => setOverallNotes(e.target.value)}
                  rows={2}
                  placeholder="Anything to reinforce or follow up on?"
                  style={{ width: "100%", padding: 8, background: "var(--panel2)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 6, fontSize: 13, fontFamily: "inherit", resize: "vertical" }}
                />
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
                {/* Trainer */}
                <div style={{ padding: 12, background: "var(--panel2)", border: "1px solid var(--line)", borderRadius: 6 }}>
                  <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Trainer</div>
                  <label className="field">
                    <span>Name</span>
                    <input value={trainerName} onChange={(e) => setTrainerName(e.target.value)} />
                  </label>
                  <div className="signer-box" style={{ marginTop: 8 }}>
                    <SignatureCapture onChange={setTrainerSig} />
                  </div>
                </div>
                {/* Trainee */}
                <div style={{ padding: 12, background: "var(--panel2)", border: "1px solid var(--line)", borderRadius: 6 }}>
                  <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Trainee</div>
                  <label className="field">
                    <span>Name</span>
                    <input value={traineeName} onChange={(e) => setTraineeName(e.target.value)} />
                  </label>
                  <div className="signer-box" style={{ marginTop: 8 }}>
                    <SignatureCapture onChange={setTraineeSig} />
                  </div>
                </div>
              </div>

              {items.filter((it) => !it.checked).length > 0 && (
                <div style={{ marginTop: 12, padding: 10, background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 6, fontSize: 12 }}>
                  <AlertCircle size={14} style={{ color: "var(--warn)", verticalAlign: "middle", marginRight: 6 }} />
                  {items.filter((it) => !it.checked).length} item(s) weren't marked complete. You can still sign, but consider going back to review them.
                </div>
              )}
            </>
          )}

          {err && <div className="login-err" style={{ marginTop: 12 }}><AlertCircle size={14} /> {err}</div>}
        </div>
        <div className="modal-foot" style={{ justifyContent: "space-between" }}>
          <div>
            {step > 0 && (
              <button className="btn-ghost" onClick={goBack} disabled={busy}>
                ← Back
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn-primary" onClick={goNext} disabled={busy || items.length === 0 || !canNext}>
              {busy ? "Saving..." : nextLabel}
              {isSignStep && !busy && <Check size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   TRAINEE-SIDE: Assigned modules panel + player
   ============================================================ */

function AssignedModulesPanel({ emp, onOpenTraining }) {
  const [assignments, setAssignments] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    const [aRes, tRes] = await Promise.all([
      db.loadModuleAssignments({ employeeId: emp.id }),
      db.loadModules({ excludeSection: "ride" }),
    ]);
    if (aRes.ok) setAssignments(aRes.assignments);
    if (tRes.ok) setTrainings(tRes.modules);
    setLoading(false);
  }, [emp.id]);
  useEffect(() => { reload(); }, [reload]);

  const trainingById = React.useMemo(() => {
    const m = {}; trainings.forEach((t) => { m[t.id] = t; }); return m;
  }, [trainings]);

  const open = assignments.filter((a) => !a.completed_at);
  const done = assignments.filter((a) => a.completed_at);

  if (loading) return null;
  if (assignments.length === 0) return null;

  const statusFor = (a, t) => {
    if (a.completed_at) return { text: `Completed ${fmtDate(a.completed_at)}`, cls: "tag-ok", action: "Review" };
    if (a.review_status === "pending") return { text: "Awaiting Competency Verification", cls: "tag-warn", action: "View" };
    // Practical needed
    const quizCleared = a.quiz_status === "passed" || !t?.has_quiz;
    if (t?.requires_practical && quizCleared && a.practical_status !== "complete") {
      return { text: "Awaiting practical walk-through", cls: "tag-warn", action: "View" };
    }
    if (a.quiz_status === "sent") return { text: "Quiz in progress", cls: "tag-warn", action: "Continue" };
    if (a.quiz_status === "failed") return { text: "Quiz failed — retake", cls: "tag-fail", action: "Retake" };
    if (a.document_viewed_at) return { text: "Doc read, quiz pending", cls: "tag-warn", action: "Take quiz" };
    // Overdue check
    if (a.due_at && new Date(a.due_at) < new Date()) return { text: "Overdue", cls: "tag-fail", action: "Start" };
    return { text: `Due ${a.due_at ? fmtDate(a.due_at) : "—"}`, cls: "tag-info", action: "Start" };
  };

  return (
    <section className="panel">
      <div className="panel-head">
        <h2><ClipboardCheck size={16} /> My Trainings</h2>
        <span className="count-pill">{open.length} open{done.length > 0 ? ` · ${done.length} done` : ""}</span>
      </div>
      <div className="panel-body">
        {open.length === 0 && done.length > 0 && <Empty text="All required trainings complete. Nice work!" />}
        {[...open, ...done].map((a) => {
          const t = trainingById[a.module_id];
          if (!t) return null;
          const st = statusFor(a, t);
          return (
            <div className="row-item" key={a.id}>
              <div className="ri-main">
                <strong>{t.title}</strong>
                <span className="ri-sub">{t.description || ""}</span>
              </div>
              <span className={`tag ${st.cls}`}>{st.text}</span>
              <button className="btn-primary sm" onClick={() => onOpenTraining(a, t, reload)}>
                {st.action} <ArrowRight size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* Trainee-side training player: doc viewer -> quiz */
function ModulePlayerModal({ assignment, training, emp, onClose, reviewOnly }) {
  const [phase, setPhase] = useState(() => {
    if (reviewOnly || assignment.completed_at) return "doc";
    if (assignment.quiz_status === "sent" || assignment.quiz_status === "failed") return "quiz";
    if (assignment.document_viewed_at && training.has_quiz) return "quiz";
    return "doc";
  });
  const [docUrl, setDocUrl] = useState(null);
  const [docLoading, setDocLoading] = useState(true);
  const [docErr, setDocErr] = useState("");
  const [assign, setAssign] = useState(assignment);

  useEffect(() => {
    if (phase !== "doc") return;
    if (!training.document_path) { setDocLoading(false); return; }
    (async () => {
      setDocLoading(true);
      const res = await db.getModuleDocUrl(training.document_path);
      if (res.ok) setDocUrl(res.url); else setDocErr(res.message);
      setDocLoading(false);
    })();
  }, [phase, training.document_path]);

  const markDocRead = async () => {
    const now = new Date().toISOString();
    const patch = { document_viewed_at: now };
    if (!training.has_quiz) {
      patch.completed_at = now;
    }
    const res = await db.updateModuleAssignment(assign.id, patch);
    if (res.ok) {
      setAssign(res.assignment);
      if (training.has_quiz) setPhase("quiz");
      else onClose();
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 1100, width: "95vw", height: "90vh", maxHeight: "90vh" }}>
        <div className="modal-head">
          <h3><ClipboardCheck size={18} /> {training.title}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        {phase === "doc" && (
          <>
            <div className="modal-body" style={{ padding: 20, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflowY: "auto" }}>
              {training.description && (
                <p className="dim" style={{ marginTop: 0, fontSize: 13, marginBottom: 12 }}>{training.description}</p>
              )}
              {/* Primary content: markdown body */}
              {training.body_md ? (
                <Markdown source={training.body_md} />
              ) : (
                <Empty text="This training has no content yet." />
              )}
              {/* Optional PDF attachment */}
              {training.document_path && (
                <div style={{ marginTop: 20, padding: 12, background: "var(--panel2)", borderRadius: 6, border: "1px solid var(--line)" }}>
                  <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--txt3)", marginBottom: 8 }}>
                    Supplementary reference (PDF)
                  </div>
                  {docLoading && <Empty text="Loading PDF..." />}
                  {!docLoading && docErr && <div className="login-err"><AlertCircle size={14} /> Could not load PDF: {docErr}</div>}
                  {docUrl && (
                    <iframe
                      src={docUrl}
                      title={training.title + " (PDF)"}
                      style={{ width: "100%", height: 500, border: "1px solid var(--line)", background: "#fff", borderRadius: 4 }}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="modal-foot" style={{ justifyContent: "space-between" }}>
              <button className="btn-ghost" onClick={onClose}>Close</button>
              {!reviewOnly && !assign.completed_at && (
                <button className="btn-primary" onClick={markDocRead}>
                  {training.has_quiz ? "Reviewed together — take quiz" : "Mark complete"} <ArrowRight size={14} />
                </button>
              )}
            </div>
          </>
        )}
        {phase === "quiz" && (
          <ModuleQuizPhase
            assignment={assign}
            training={training}
            emp={emp}
            onDone={(newAssign) => { if (newAssign) setAssign(newAssign); onClose(); }}
            onBackToDoc={() => setPhase("doc")}
          />
        )}
      </div>
    </div>
  );
}

/* Quiz phase of the required training player */
function ModuleQuizPhase({ assignment, training, emp, onDone, onBackToDoc }) {
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null); // required_training_quizzes row
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null); // { score, passed, missed }
  const [err, setErr] = useState("");
  const [attesting, setAttesting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      // Load existing quiz if any, otherwise create fresh
      let existing = await db.loadModuleQuiz(assignment.id);
      if (!existing.ok) { setErr(existing.message); setLoading(false); return; }
      if (existing.quiz && existing.quiz.status === "sent") {
        setQuiz(existing.quiz);
      } else {
        // Draw fresh set of questions
        const qRes = await db.loadModuleQuestions(training.key);
        if (!qRes.ok) { setErr(qRes.message); setLoading(false); return; }
        const shuffled = [...qRes.questions].sort(() => Math.random() - 0.5);
        const drawn = shuffled.slice(0, Math.min(training.quiz_question_count || 10, qRes.questions.length));
        // Snapshot: use the module-question shape (text/correct/topic) so downstream
        // grading + rendering work consistently regardless of source schema.
        const snap = drawn.map((q) => {
          const tags = Array.isArray(q.tags) ? q.tags : [];
          const topicTag = tags.find((x) => typeof x === "string" && x.startsWith("topic:"));
          return {
            id: q.id,
            prompt: q.text,
            choices: q.choices,
            correct_index: q.correct,
            topic: topicTag ? topicTag.slice(6) : null,
          };
        });
        if (existing.quiz) {
          // retake — update in place
          const upd = await db.updateModuleQuiz(existing.quiz.id, {
            questions: snap, status: "sent", score: null,
            attempts: (existing.quiz.attempts || 0) + 1,
            missed_topics: [], sent_date: new Date().toISOString(), completed_date: null,
          });
          if (!upd.ok) { setErr(upd.message); setLoading(false); return; }
          setQuiz(upd.quiz);
        } else {
          const cr = await db.createModuleQuiz({
            assignment_id: assignment.id,
            employee_id: emp.id,
            module_id: training.id,
            questions: snap,
            status: "sent",
            attempts: 1,
          });
          if (!cr.ok) { setErr(cr.message); setLoading(false); return; }
          // Also mark on the assignment
          await db.updateModuleAssignment(assignment.id, { quiz_status: "sent", quiz_id: cr.quiz.id });
          setQuiz(cr.quiz);
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Answers are locked in first; the trainee signs the certification statement
  // before anything is graded. `attestation` is null when the quiz was already
  // attested on a previous attempt (stored once per quiz).
  const requestSubmit = () => {
    if (quiz?.attestation) submit(null);
    else setAttesting(true);
  };

  const submit = async (attestation) => {
    if (!quiz) return;
    setAttesting(false);
    const questions = quiz.questions || [];
    let correct = 0;
    const missed = new Set();
    questions.forEach((q) => {
      if (answers[q.id] === q.correct_index) correct += 1;
      else if (q.topic) missed.add(q.topic);
    });
    const score = questions.length > 0 ? Math.round((correct / questions.length) * 100) : 0;
    const passed = score >= 80;
    const missedArr = Array.from(missed);
    const patch = {
      status: passed ? "passed" : "failed",
      score,
      missed_topics: missedArr,
      completed_date: passed ? new Date().toISOString() : null,
      // Answers + a self-contained record of this attempt for the manager view.
      answers: { ...answers },
      attempt_history: [...(quiz.attempt_history || []), {
        at: new Date().toISOString(),
        attempt: quiz.attempts || 1,
        score,
        missedTopics: missedArr,
        missed: questions.filter((q) => answers[q.id] !== q.correct_index).map((q) => ({
          q: q.prompt, topic: q.topic,
          chosen: q.choices?.[answers[q.id]] ?? null,
          correct: q.choices?.[q.correct_index] ?? null,
        })),
      }],
      // Written once — the signature covers the attempt, not the score.
      ...(attestation && !quiz.attestation
        ? { attestation, attested_at: attestation.signed_at }
        : {}),
    };
    const upd = await db.updateModuleQuiz(quiz.id, patch);
    if (!upd.ok) { setErr(upd.message); return; }
    setQuiz(upd.quiz);
    // Update assignment
    const assignPatch = { quiz_status: passed ? "passed" : "failed" };
    let needsPractical = false;
    let needsReview = false;
    if (passed) {
      // Determine what still needs to happen
      needsPractical = training.requires_practical;
      needsReview = training.requires_competency_verification || training.requires_supervisor_review;
      if (needsPractical) {
        // practical_status stays 'none' until a trainer completes it
        // Nothing else to change on the assignment right now
      } else if (needsReview) {
        assignPatch.review_status = "pending";
      } else {
        // Nothing else required — complete
        assignPatch.completed_at = new Date().toISOString();
      }
    }
    const aUpd = await db.updateModuleAssignment(assignment.id, assignPatch);
    setResult({ score, passed, missed: missedArr, needsPractical, needsReview });
    setSubmitted(true);
    if (aUpd.ok && passed) {
      // Give user a beat to see the result, then close
      setTimeout(() => onDone(aUpd.assignment), 3000);
    }
  };

  const allAnswered = quiz && quiz.questions.every((q) => answers[q.id] !== undefined);

  if (loading) return <div className="modal-body"><Empty text="Loading quiz..." /></div>;
  if (err) return <div className="modal-body"><div className="login-err"><AlertCircle size={14} /> {err}</div></div>;
  if (!quiz) return <div className="modal-body"><Empty text="Could not load quiz." /></div>;

  if (attesting) {
    return (
      <QuizAttestationGate
        user={emp}
        onCancel={() => setAttesting(false)}
        onAttest={submit}
      />
    );
  }

  return (
    <>
      <div className="modal-body" style={{ flex: 1, overflowY: "auto" }}>
        {submitted && result && (
          <div style={{
            padding: 12, marginBottom: 12, borderRadius: 6,
            background: result.passed ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
            border: `1px solid ${result.passed ? "var(--ok)" : "var(--err)"}`,
          }}>
            <strong style={{ color: result.passed ? "var(--ok)" : "var(--err)" }}>
              {result.passed ? "✓ Passed" : "✗ Did not pass"} — {result.score}%
            </strong>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              {result.passed
                ? (result.needsPractical
                    ? "Quiz passed. A trainer will schedule a practical walk-through with you next."
                    : result.needsReview
                    ? "Quiz passed. A supervisor will verify your competency before this is marked complete."
                    : "Training complete. Closing in a moment...")
                : `Pass threshold is 80%. Review the material and try again.${result.missed.length > 0 ? ` Missed topics: ${result.missed.join(", ")}` : ""}`}
            </div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {(quiz.questions || []).map((q, i) => (
            <div key={q.id} style={{ padding: 12, background: "var(--panel2)", borderRadius: 6, border: "1px solid var(--line)" }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
                {i + 1}. {q.prompt}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {(q.choices || []).map((c, ci) => {
                  const selected = answers[q.id] === ci;
                  const isCorrect = submitted && ci === q.correct_index;
                  const isWrong = submitted && selected && !isCorrect;
                  return (
                    <label key={ci} style={{
                      display: "flex", alignItems: "flex-start", gap: 8, padding: 6, borderRadius: 4,
                      cursor: submitted ? "default" : "pointer",
                      background: isCorrect ? "rgba(34,197,94,0.15)" : isWrong ? "rgba(239,68,68,0.15)" : selected ? "var(--panel)" : "transparent",
                      border: `1px solid ${isCorrect ? "var(--ok)" : isWrong ? "var(--err)" : selected ? "var(--accent)" : "var(--line)"}`,
                    }}>
                      <input
                        type="radio"
                        name={`q-${q.id}`}
                        checked={selected}
                        disabled={submitted}
                        onChange={() => setAnswers({ ...answers, [q.id]: ci })}
                      />
                      <span style={{ fontSize: 12 }}><strong>{String.fromCharCode(65 + ci)}.</strong> {c}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="modal-foot" style={{ justifyContent: "space-between" }}>
        <button className="btn-ghost" onClick={onBackToDoc}>← Back to document</button>
        {!submitted && (
          <button className="btn-primary" onClick={requestSubmit} disabled={!allAnswered}>
            Submit quiz
          </button>
        )}
        {submitted && !result?.passed && (
          <button className="btn-primary" onClick={() => onDone()}>Close</button>
        )}
      </div>
    </>
  );
}

/* ============================================================
   MY TRAINING (non-trainees viewing their own progress)
   ============================================================ */
function MyTrainingView({ state, user, onOpenQuiz }) {
  const me = findMyEmployee(state, user);
  if (!me) {
    return (
      <div className="users-view">
        <h2 className="page-title"><GraduationCap size={20} /> My Training</h2>
        <p className="page-sub">Your login isn't linked to a roster record yet. An admin can link it from the Users screen.</p>
      </div>
    );
  }
  return (
    <div className="users-view">
      <div className="users-head">
        <div>
          <h2 className="page-title"><GraduationCap size={20} /> My Training</h2>
          <p className="page-sub">Your own ride training progress. (You still see the management views in the other tabs.)</p>
        </div>
      </div>
      <TraineeDashboard state={state} user={user} onOpenQuiz={onOpenQuiz} />
    </div>
  );
}

/* ============================================================
   ROSTER
   ============================================================ */
function Roster({ state, search, user, trainers, onAssign, onSignoff, onSchedule, onManage, onObserve, selected, onSelect, onViewAs }) {
  const [showArchived, setShowArchived] = useState(false);
  const [deptFilter, setDeptFilter] = useState("all");
  const emps = state.employees
    .filter((e) => e.name.toLowerCase().includes(search.toLowerCase()))
    .filter((e) => deptFilter === "all" ? true : (e.department || "Attractions") === deptFilter)
    .filter((e) => showArchived ? true : !e.archived);
  const archivedCount = state.employees.filter((e) => e.archived).length;
  // Counts per department for the filter labels
  const deptCounts = state.employees.filter((e) => !e.archived).reduce((acc, e) => {
    const d = e.department || "Attractions"; acc[d] = (acc[d] || 0) + 1; return acc;
  }, {});
  return (
    <div className="roster">
      <div className="roster-bar">
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, textTransform: "uppercase", color: "var(--txt3)", letterSpacing: 0.5 }}>Dept:</span>
          {["all", "Attractions", "Restaurant", "Kart & Ride Service Tech"].map((d) => (
            <button key={d} onClick={() => setDeptFilter(d)}
              className={deptFilter === d ? "btn-primary" : "btn-ghost"}
              style={{ padding: "4px 10px", fontSize: 12 }}>
              {d === "all" ? "All" : d} {d !== "all" && deptCounts[d] !== undefined && `(${deptCounts[d]})`}
            </button>
          ))}
        </div>
        <label className="check-row sm"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show deactivated ({archivedCount})</label>
      </div>
      <div className="table-wrap">
        <table className="cert-table">
          <thead>
            <tr>
              <th className="sticky-col">Crew</th>
              {RIDES.map((r) => <th key={r.key} className="ride-col"><span>{r.label}</span></th>)}
              <th>Start</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {emps.map((e) => (
              <tr key={e.id} className={`${selected?.id === e.id ? "sel" : ""} ${e.archived ? "archived" : ""}`} onClick={() => onSelect(e)}>
                <td className="sticky-col name-cell">
                  {e.name}
                  {(e.department && e.department !== "Attractions") && <span className="mini-flag dept">{e.department}</span>}
                  {e.archived && <span className="mini-flag arch">Deactivated</span>}
                </td>
                {RIDES.map((r) => {
                  const lv = e.levels[r.key];
                  const rt = e.retrain && e.retrain[r.key];
                  return (
                    <td key={r.key}>
                      {rt
                        ? <span className="cell-badge rt" title={`Retrain at ${rt.level}`}>{rt.level.replace("RT-", "RT")}</span>
                        : <span className="cell-badge" style={{ background: LEVELS[lv].color }} title={LEVELS[lv].desc}>{LEVELS[lv].label}</span>}
                    </td>
                  );
                })}
                <td className="date-cell">{e.startDate ? fmtDate(e.startDate) : "—"}</td>
                <td className="action-cell">
                  {can.observe(user) && <button className="btn-mini ghost" onClick={(ev) => { ev.stopPropagation(); onObserve(e); }}>Observe</button>}
                  <button className="btn-mini ghost" onClick={(ev) => { ev.stopPropagation(); onSignoff(e); }}>Sign off</button>
                  {roleRank(user.role) >= roleRank("manager") && <button className="btn-mini ghost" onClick={(ev) => { ev.stopPropagation(); onManage(e); }}>Manage</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <EmployeeDetail emp={state.employees.find((e) => e.id === selected.id)} state={state} user={user} trainers={trainers} onAssign={onAssign} onSignoff={onSignoff} onSchedule={onSchedule} onObserve={onObserve} onManage={onManage} onViewAs={onViewAs} />}

      <Legend />
    </div>
  );
}

/* CompetenciesBlock — shows Required Trainings for an employee, sorted by category
   match to their department first, then others they've been individually assigned.
   Used inside EmployeeDetail. Fetches its own data. */
function CompetenciesBlock({ emp }) {
  const [assignments, setAssignments] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [aRes, tRes] = await Promise.all([
        db.loadModuleAssignments({ employeeId: emp.id }),
        db.loadModules({ excludeSection: "ride" }),
      ]);
      if (cancelled) return;
      if (aRes.ok) setAssignments(aRes.assignments);
      if (tRes.ok) setTrainings(tRes.modules);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [emp.id]);

  const trainingById = React.useMemo(() => {
    const m = {}; trainings.forEach((t) => { m[t.id] = t; }); return m;
  }, [trainings]);

  // Section that matches this employee's department (for sorting)
  const primarySection = React.useMemo(() => {
    if (emp.department === "Kart & Ride Service Tech") return "service_tech";
    if (emp.department === "Restaurant") return "restaurant";
    if (emp.department === "Attractions") return "ride";
    return null;
  }, [emp.department]);

  // Combine assignment + training, sort: primary section first, then completed_at desc
  const rows = React.useMemo(() => {
    const enriched = assignments
      .map((a) => ({ a, t: trainingById[a.module_id] }))
      .filter((r) => r.t);
    enriched.sort((x, y) => {
      const xPrimary = x.t.section === primarySection ? 0 : 1;
      const yPrimary = y.t.section === primarySection ? 0 : 1;
      if (xPrimary !== yPrimary) return xPrimary - yPrimary;
      // Then not-completed first
      const xDone = x.a.completed_at ? 1 : 0;
      const yDone = y.a.completed_at ? 1 : 0;
      if (xDone !== yDone) return xDone - yDone;
      // Then by title
      return (x.t.title || "").localeCompare(y.t.title || "");
    });
    return enriched;
  }, [assignments, trainingById, primarySection]);

  const statusFor = (a, t) => {
    if (a.completed_at) return { text: `Verified ${fmtDate(a.completed_at)}`, cls: "tag-ok" };
    if (a.review_status === "pending") return { text: "Awaiting verification", cls: "tag-warn" };
    const quizCleared = a.quiz_status === "passed" || !t?.has_quiz;
    if (t?.requires_practical && quizCleared && a.practical_status !== "complete") {
      return { text: "Awaiting practical", cls: "tag-warn" };
    }
    if (a.quiz_status === "sent") return { text: "Quiz in progress", cls: "tag-warn" };
    if (a.quiz_status === "failed") return { text: "Quiz failed", cls: "tag-fail" };
    if (a.document_viewed_at) return { text: "Doc read, quiz pending", cls: "tag-warn" };
    if (a.due_at && new Date(a.due_at) < new Date()) return { text: "Overdue", cls: "tag-fail" };
    return { text: `Due ${a.due_at ? fmtDate(a.due_at) : "—"}`, cls: "tag-info" };
  };

  return (
    <div className="ed-block">
      <h4>Competencies</h4>
      {loading && <Empty text="Loading..." />}
      {!loading && rows.length === 0 && <Empty text="No required trainings assigned yet." />}
      {!loading && rows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {rows.map(({ a, t }) => {
            const st = statusFor(a, t);
            const isPrimary = primarySection && t.section === primarySection;
            return (
              <div key={a.id} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "6px 8px",
                background: isPrimary ? "var(--panel2)" : "transparent",
                borderRadius: 4,
                borderLeft: isPrimary ? "3px solid var(--accent)" : "3px solid transparent",
              }}>
                <span style={{ flex: 1, fontSize: 13 }}>
                  {t.title}
                </span>
                <span className={`tag ${st.cls}`}>{st.text}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EmployeeDetail({ emp, state, user, trainers, onAssign, onSignoff, onSchedule, onObserve, onManage, onViewAs }) {
  if (!emp) return null;
  // Department-aware ordering:
  // Attractions → Ride Certifications section first (ride operations are primary)
  // Everyone else → Competencies (Required Trainings) first, then Certifications if they cross-train
  const isAttractions = emp.department === "Attractions" || !emp.department;
  const certsBlock = (
    <div className="ed-block">
      <h4>Certifications & Next Targets</h4>
      {RIDES.map((r) => {
        const lv = emp.levels[r.key];
        const nxt = nextLevel(lv, r.key);
        return (
          <div className="ed-cert" key={r.key}>
            <span className="ed-ride">{r.label}</span>
            <span className="cell-badge sm" style={{ background: LEVELS[lv].color }}>{LEVELS[lv].label}</span>
            <ArrowRight size={12} className="ed-arrow" />
            <span className="ed-next">{nxt ? `${nxt} — ${stepAction(nxt, r.key)}` : "Top level ✓"}</span>
          </div>
        );
      })}
    </div>
  );
  const competenciesBlock = <CompetenciesBlock emp={emp} />;

  return (
    <div className="emp-detail">
      <div className="ed-head">
        <div>
          <h3>{emp.name} {emp.archived && <span className="mini-flag arch">Deactivated</span>}</h3>
          <span className="ed-sub">Started {emp.startDate ? fmtDate(emp.startDate) : "—"}{emp.department ? ` · ${emp.department}` : ""}</span>
        </div>
        <div className="ed-actions">
          {can.observe(user) && <button className="btn-mini" onClick={() => onObserve(emp)}><Eye size={13} /> Observe</button>}
          <button className="btn-mini ghost" onClick={() => onSignoff(emp)}><Check size={13} /> Sign off</button>
          <button className="btn-mini ghost" onClick={() => onSchedule(emp)}><Plus size={13} /> Schedule</button>
          {roleRank(user.role) >= roleRank("manager") && <button className="btn-mini ghost" onClick={() => onManage(emp)}><ShieldCheck size={13} /> Manage</button>}
          {onViewAs && !emp.archived && (
            <button className="btn-mini ghost" onClick={() => onViewAs(emp)} title="See the app exactly as this employee sees it (read only)">
              <User size={13} /> View as employee
            </button>
          )}
        </div>
      </div>

      <div className="ed-grid">
        {isAttractions ? certsBlock : competenciesBlock}

        <div className="ed-block">
          <h4>Scheduled Steps</h4>
          {emp.schedule.length === 0 && <Empty text="No scheduled steps." />}
          {emp.schedule.map((sc) => {
            const canAssign = user && (roleRank(user.role) >= roleRank("manager"));
            const assignedTrainer = sc.assignedTo ? (trainers || []).find((t) => t.id === sc.assignedTo) : null;
            return (
              <div className="ed-sched" key={sc.id}>
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                  <span>{RIDE_MAP[sc.ride].label} → {sc.targetLevel}</span>
                  <span className="ed-sched-date">{sc.date ? fmtDate(sc.date) : "TBD"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, fontSize: 12, color: "var(--txt3)" }}>
                  <span>Assigned to:</span>
                  {canAssign ? (
                    <select value={sc.assignedTo || ""} onChange={(e) => onAssign && onAssign(emp.id, sc.id, e.target.value || null)}
                      style={{ fontSize: 12, padding: "2px 6px", background: "var(--panel2)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 4 }}>
                      <option value="">— unassigned —</option>
                      {(trainers || []).map((t) => (
                        <option key={t.id} value={t.id}>{t.full_name} ({t.role === "agm_gm" ? "AGM/GM" : "Trainer"})</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ color: "var(--txt2)" }}>{assignedTrainer ? assignedTrainer.full_name : "unassigned"}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Secondary block: whatever the primary block wasn't */}
        {isAttractions ? competenciesBlock : certsBlock}

        <div className="ed-block">
          <h4>History</h4>
          <div className="ed-history">
            {emp.history.length === 0 && <Empty text="No history yet." />}
            {emp.history.slice(0, 8).map((h, i) => (
              <div className="ed-hist-row" key={i}>
                <span className="ed-hist-date">{fmtDate(h.date)}</span>
                <span>{h.text}</span>
              </div>
            ))}
          </div>
        </div>

        {can.viewDocuments(user) && (
          <div className="ed-block ed-docs-block">
            <EmployeeDocumentsPanel emp={emp} user={user} />
          </div>
        )}
      </div>
      <div style={{ marginTop: 14 }}>
        <EmployeeGoalsBlock key={emp.id} emp={emp} user={user} />
      </div>
    </div>
  );
}

/* ============================================================
   EMPLOYEE DOCUMENTS PANEL
   Per-employee documents (waivers, IDs, certs, etc.)
   Manager+ only.
   ============================================================ */
const DOC_CATEGORIES = [
  'Sign-off form', 'Quiz record', 'Observation notes',
  'Work Card', 'Food Handler Card', 'Certified Food Safety Manager',
  'Alcohol Awareness/TAM', 'Health Card', 'Other'
];

function EmployeeDocumentsPanel({ emp, user }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [busyId, setBusyId] = useState(null); // doc id currently being acted on
  const [confirmDelete, setConfirmDelete] = useState(null); // { doc, mode: 'soft' | 'hard' }
  const [editDoc, setEditDoc] = useState(null); // doc being edited
  const [rideFilter, setRideFilter] = useState("all"); // 'all' | 'untagged' | ride key
  const isAdmin = user?.role === "admin";

  const reload = React.useCallback(async () => {
    setLoading(true);
    setErr("");
    const res = await db.loadEmployeeDocuments(emp.id, { includeDeleted: true });
    if (!res.ok) { setErr(res.message); setLoading(false); return; }
    setDocs(res.documents);
    setLoading(false);
  }, [emp.id]);

  useEffect(() => { reload(); }, [reload]);

  const matchesFilter = (d) => {
    if (rideFilter === "all") return true;
    const rides = Array.isArray(d.rides) ? d.rides : [];
    if (rideFilter === "untagged") return rides.length === 0;
    return rides.includes(rideFilter);
  };

  const activeDocs = docs.filter((d) => !d.deleted_at).filter(matchesFilter);
  const deletedDocs = docs.filter((d) => d.deleted_at).filter(matchesFilter);

  const openDoc = async (doc) => {
    setBusyId(doc.id);
    const res = await db.getEmployeeDocumentUrl(doc.storage_path, 60);
    setBusyId(null);
    if (!res.ok) { setErr(res.message); return; }
    window.open(res.url, "_blank", "noopener,noreferrer");
  };

  const doSoftDelete = async (doc) => {
    setBusyId(doc.id);
    const res = await db.softDeleteEmployeeDocument(doc.id);
    setBusyId(null);
    setConfirmDelete(null);
    if (!res.ok) { setErr(res.message); return; }
    reload();
  };

  const doRestore = async (doc) => {
    setBusyId(doc.id);
    const res = await db.restoreEmployeeDocument(doc.id);
    setBusyId(null);
    if (!res.ok) { setErr(res.message); return; }
    reload();
  };

  const doHardDelete = async (doc) => {
    setBusyId(doc.id);
    const res = await db.hardDeleteEmployeeDocument(doc.id);
    setBusyId(null);
    setConfirmDelete(null);
    if (!res.ok) { setErr(res.message); return; }
    reload();
  };

  return (
    <>
      <div className="ed-docs-head">
        <h4><Folder size={14} /> Documents</h4>
        <div className="ed-docs-actions">
          {deletedDocs.length > 0 && (
            <button className="btn-mini ghost" onClick={() => setShowDeleted((v) => !v)}>
              {showDeleted ? "Hide" : `Show ${deletedDocs.length} deleted`}
            </button>
          )}
          <button className="btn-mini" onClick={() => setShowUpload(true)}><Plus size={13} /> Upload</button>
        </div>
      </div>

      <div className="ed-docs-filter">
        <span className="ed-docs-filter-label">Filter by ride:</span>
        <select value={rideFilter} onChange={(e) => setRideFilter(e.target.value)}>
          <option value="all">All documents</option>
          <option value="untagged">Untagged (employee-level)</option>
          {RIDES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
      </div>

      {err && <div className="login-err" style={{ marginTop: 6 }}><AlertCircle size={14} /> {err}</div>}

      {loading ? (
        <div className="empty">Loading…</div>
      ) : activeDocs.length === 0 ? (
        <Empty text="No documents yet." />
      ) : (
        <div className="ed-doc-list">
          {activeDocs.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              busy={busyId === doc.id}
              onOpen={() => openDoc(doc)}
              onEdit={() => setEditDoc(doc)}
              onDelete={() => setConfirmDelete({ doc, mode: 'soft' })}
            />
          ))}
        </div>
      )}

      {showDeleted && deletedDocs.length > 0 && (
        <div className="ed-deleted-docs">
          <div className="ed-deleted-head">Deleted documents</div>
          {deletedDocs.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              busy={busyId === doc.id}
              deleted
              isAdmin={isAdmin}
              onOpen={() => openDoc(doc)}
              onRestore={() => doRestore(doc)}
              onPurge={() => setConfirmDelete({ doc, mode: 'hard' })}
            />
          ))}
        </div>
      )}

      {showUpload && (
        <DocumentUploadModal
          emp={emp}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); reload(); }}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteDocModal
          doc={confirmDelete.doc}
          mode={confirmDelete.mode}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => confirmDelete.mode === 'hard' ? doHardDelete(confirmDelete.doc) : doSoftDelete(confirmDelete.doc)}
        />
      )}

      {editDoc && (
        <DocumentEditModal
          emp={emp}
          doc={editDoc}
          onClose={() => setEditDoc(null)}
          onSaved={() => { setEditDoc(null); reload(); }}
        />
      )}
    </>
  );
}

function DocumentRow({ doc, busy, deleted, isAdmin, onOpen, onEdit, onDelete, onRestore, onPurge }) {
  const rides = Array.isArray(doc.rides) ? doc.rides : [];
  return (
    <div className={`ed-doc-row ${deleted ? "deleted" : ""}`}>
      <div className="ed-doc-meta">
        <div className="ed-doc-line1">
          <span className={`tag tag-doc-cat`}>{doc.category}</span>
          {rides.map((rk) => {
            const r = RIDE_MAP[rk];
            return <span key={rk} className="tag tag-doc-ride">{r ? r.label : rk}</span>;
          })}
          <span className="ed-doc-filename" title={doc.filename}>{doc.filename}</span>
        </div>
        {doc.description && <div className="ed-doc-desc">{doc.description}</div>}
        <div className="ed-doc-line3">
          <span>{fmtDate(doc.uploaded_at)}</span>
          <span> · </span>
          <span>{fmtSize(doc.size_bytes)}</span>
          {doc.card_number && <><span> · </span><span>#{doc.card_number}</span></>}
          {doc.issued_at && <><span> · </span><span>issued {fmtDate(doc.issued_at)}</span></>}
          {doc.expires_at && (() => {
            const days = Math.floor((new Date(doc.expires_at) - new Date()) / 86400000);
            const cls = days < 0 ? "expiry-expired" : days <= 30 ? "expiry-soon" : "expiry-ok";
            const label = days < 0 ? `EXPIRED ${fmtDate(doc.expires_at)}` : `expires ${fmtDate(doc.expires_at)}`;
            return <><span> · </span><span className={cls}>{label}</span></>;
          })()}
        </div>
      </div>
      <div className="ed-doc-actions">
        <button className="btn-mini ghost" disabled={busy} onClick={onOpen} title="View"><Eye size={12} /> View</button>
        {!deleted && onEdit && <button className="btn-mini ghost" disabled={busy} onClick={onEdit} title="Edit metadata"><Pencil size={12} /></button>}
        {!deleted && onDelete && <button className="btn-mini ghost" disabled={busy} onClick={onDelete} title="Delete"><Trash2 size={12} /></button>}
        {deleted && onRestore && <button className="btn-mini ghost" disabled={busy} onClick={onRestore}><RotateCcw size={12} /> Restore</button>}
        {deleted && isAdmin && onPurge && <button className="btn-mini ghost" disabled={busy} onClick={onPurge} title="Permanently delete (admin only)"><Trash2 size={12} /> Purge</button>}
      </div>
    </div>
  );
}

/* Categories that have meaningful expiry/issued dates (credentials).
   Other categories don't show these date fields in the upload form. */
const TIME_BOUND_CATEGORIES = new Set([
  'Work Card', 'Food Handler Card', 'Certified Food Safety Manager', 'Alcohol Awareness/TAM', 'Health Card'
]);

/* Categories where a card/certificate number is relevant */
const CARD_NUMBER_CATEGORIES = new Set([
  'Work Card', 'Health Card', 'Alcohol Awareness/TAM', 'Food Handler Card', 'Certified Food Safety Manager'
]);

/* Business identity (City of Las Vegas Business License Form PL202) */
const BUSINESS_INFO = {
  name: 'Las Vegas Mini Grand Prix, LLC',
  address: '1401 N Rainbow Boulevard, Las Vegas, Nevada 89108',
  phone: '702-259-7000',
  amusementLicense: 'G70-07061',         // A06: Amusement Park
  coinOpLicense: 'G70-07063',            // C08: Coin Operated Amusement
  liquorLicense: 'L71-00024',            // L01: Beer & Wine On-Premise
};

function DocumentUploadModal({ emp, onClose, onUploaded, initialCategory }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState(initialCategory || "Other");
  const [description, setDescription] = useState("");
  const [rides, setRides] = useState([]); // selected ride keys
  const [issuedAt, setIssuedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const showDates = TIME_BOUND_CATEGORIES.has(category);
  const showCardNum = CARD_NUMBER_CATEGORIES.has(category);

  const toggleRide = (k) => {
    setRides((cur) => cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
  };

  const submit = async () => {
    setErr("");
    if (!file) { setErr("Please choose a file."); return; }
    setBusy(true);
    const res = await db.uploadEmployeeDocument(
      emp.id, file, category, description.trim() || null, rides,
      showDates && issuedAt ? issuedAt : null,
      showDates && expiresAt ? expiresAt : null,
      showCardNum && cardNumber.trim() ? cardNumber.trim() : null,
    );
    setBusy(false);
    if (!res.ok) { setErr(res.message); return; }
    onUploaded();
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-head">
        <h3><Plus size={18} /> Upload document — {emp.name}</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <label className="field">
          <span>File</span>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          {file && <span className="field-hint">{file.name} · {fmtSize(file.size)}</span>}
        </label>
        <label className="field">
          <span>Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        {showCardNum && (
          <label className="field">
            <span>Card / certificate number{category === "Work Card" || category === "Health Card" ? "" : " (optional)"}</span>
            <input type="text" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} placeholder={category === "Work Card" ? "Work Card # (required for City of LV)" : category === "Health Card" ? "Health Card #" : "Certificate #"} />
          </label>
        )}
        {showDates && (
          <div className="field-row">
            <label className="field" style={{ flex: 1 }}>
              <span>Issued date (optional)</span>
              <input type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span>Expires date (optional)</span>
              <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </label>
          </div>
        )}
        <label className="field">
          <span>Description (optional)</span>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Notes about this document" />
        </label>
        <div className="field">
          <span>Tag rides (optional)</span>
          <div className="doc-ride-chips">
            {RIDES.map((r) => (
              <button
                type="button"
                key={r.key}
                className={`doc-ride-chip ${rides.includes(r.key) ? "on" : ""}`}
                onClick={() => toggleRide(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <span className="field-hint">
            {rides.length === 0 ? "No rides tagged — this is an employee-level document." : `${rides.length} ride${rides.length > 1 ? "s" : ""} tagged.`}
          </span>
        </div>
        <p style={{ fontSize: 11, color: "var(--txt3)" }}>
          Max file size: 25 MB. Files are private — only managers, AGM, and admin can view them.
        </p>
        {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy || !file}>{busy ? "Uploading…" : "Upload"}</button>
      </div>
    </Modal>
  );
}

/* Edit metadata on an existing document. The file, filename, upload date, and
   uploaded-by are NOT editable here — those are part of the audit record.
   Editable: category, description, ride tags, issued date, expires date. */
function DocumentEditModal({ emp, doc, onClose, onSaved, initialCategory }) {
  const [category, setCategory] = useState(initialCategory || doc.category || "Other");
  const [description, setDescription] = useState(doc.description || "");
  const [rides, setRides] = useState(Array.isArray(doc.rides) ? [...doc.rides] : []);
  const [issuedAt, setIssuedAt] = useState(doc.issued_at ? doc.issued_at.slice(0, 10) : "");
  const [expiresAt, setExpiresAt] = useState(doc.expires_at ? doc.expires_at.slice(0, 10) : "");
  const [cardNumber, setCardNumber] = useState(doc.card_number || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const showDates = TIME_BOUND_CATEGORIES.has(category);
  const showCardNum = CARD_NUMBER_CATEGORIES.has(category);

  const toggleRide = (k) => {
    setRides((cur) => cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
  };

  const submit = async () => {
    setErr("");
    setBusy(true);
    const res = await db.updateEmployeeDocument(doc.id, {
      category,
      description: description.trim() || null,
      rides,
      // If category was switched off time-bound, blank out the dates
      issuedAt: showDates ? (issuedAt || "") : "",
      expiresAt: showDates ? (expiresAt || "") : "",
      // Only write the card number when it changed, so an unrelated edit can
      // never blank out a number that's already on file
      cardNumber: !showCardNum
        ? (doc.card_number ? "" : undefined)
        : (cardNumber.trim() === String(doc.card_number || "").trim() ? undefined : cardNumber.trim()),
    });
    setBusy(false);
    if (!res.ok) { setErr(res.message); return; }
    onSaved();
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-head">
        <h3><Pencil size={18} /> Edit document — {emp.name}</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <div style={{ background: "var(--panel2)", padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
          <div style={{ color: "var(--txt3)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>File (cannot be changed)</div>
          <div style={{ fontWeight: 500 }}>{doc.filename}</div>
          <div style={{ color: "var(--txt3)", fontSize: 11, marginTop: 4 }}>
            Uploaded {fmtDate(doc.uploaded_at)} · {fmtSize(doc.size_bytes)}
          </div>
        </div>
        <label className="field">
          <span>Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        {showCardNum && (
          <label className="field">
            <span>Card / certificate number</span>
            <input type="text" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} placeholder={category === "Work Card" ? "Work Card # (required for City of LV)" : category === "Health Card" ? "Health Card #" : "Certificate #"} />
          </label>
        )}
        {showDates && (
          <div className="field-row">
            <label className="field" style={{ flex: 1 }}>
              <span>Issued date</span>
              <input type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span>Expires date</span>
              <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </label>
          </div>
        )}
        {!showDates && (doc.issued_at || doc.expires_at) && (
          <p style={{ fontSize: 11, color: "var(--gold)", margin: "4px 0 8px" }}>
            ⚠ Issued/expires dates will be cleared because the category "{category}" is not time-bound.
          </p>
        )}
        <label className="field">
          <span>Description (optional)</span>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Notes about this document" />
        </label>
        <div className="field">
          <span>Tag rides (optional)</span>
          <div className="doc-ride-chips">
            {RIDES.map((r) => (
              <button
                type="button"
                key={r.key}
                className={`doc-ride-chip ${rides.includes(r.key) ? "on" : ""}`}
                onClick={() => toggleRide(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <span className="field-hint">
            {rides.length === 0 ? "No rides tagged — this is an employee-level document." : `${rides.length} ride${rides.length > 1 ? "s" : ""} tagged.`}
          </span>
        </div>
        {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save changes"}</button>
      </div>
    </Modal>
  );
}

function ConfirmDeleteDocModal({ doc, mode, onCancel, onConfirm }) {
  const isHard = mode === 'hard';
  return (
    <Modal onClose={onCancel}>
      <div className="modal-head">
        <h3><AlertCircle size={18} /> {isHard ? "Permanently delete document?" : "Delete document?"}</h3>
        <button className="icon-btn" onClick={onCancel}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <p>You're about to {isHard ? "permanently delete" : "delete"} <strong>{doc.filename}</strong>.</p>
        {isHard ? (
          <p style={{ color: "var(--err)" }}>
            <AlertCircle size={14} /> This cannot be undone. The file will be removed from storage and the record purged from the database.
          </p>
        ) : (
          <p style={{ color: "var(--txt3)", fontSize: 12 }}>
            The document will be hidden from the active list but can be restored by a manager. An admin can permanently delete it later.
          </p>
        )}
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="btn-primary" style={isHard ? { background: "var(--err)" } : undefined} onClick={onConfirm}>
          {isHard ? "Permanently delete" : "Delete"}
        </button>
      </div>
    </Modal>
  );
}

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function Legend() {
  return (
    <div className="legend">
      {Object.entries(LEVELS).filter(([k]) => k !== "0").map(([k, v]) => (
        <div className="legend-item" key={k}>
          <span className="cell-badge sm" style={{ background: v.color }}>{v.label}</span>
          <span>{v.desc}</span>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   SCHEDULE TAB
   ============================================================ */
function ScheduleView({ state, user, trainers, onAdd, onRemove, onAssign }) {
  const rows = [];
  state.employees.forEach((e) => e.schedule.forEach((sc) => rows.push({ emp: e, ...sc })));
  rows.sort((a, b) => {
    if (a.date && b.date) return new Date(a.date) - new Date(b.date);
    if (a.date) return -1; if (b.date) return 1; return 0;
  });
  const canAssign = roleRank(user.role) >= roleRank("manager");

  // Schedule steps assigned to the current user (visible if they're trainer-rank or higher)
  const assignedToMe = useMemo(() => {
    if (!user || roleRank(user.role) < roleRank("trainer")) return [];
    const out = [];
    state.employees.forEach((e) => {
      (e.schedule || []).forEach((sc) => {
        if (sc.assignedTo === user.id) out.push({ emp: e, ...sc, du: daysUntil(sc.date) });
      });
    });
    out.sort((a, b) => {
      if (a.date && b.date) return new Date(a.date) - new Date(b.date);
      if (a.date) return -1; if (b.date) return 1; return 0;
    });
    return out;
  }, [state.employees, user]);

  return (
    <div className="sched-tab">
      {assignedToMe.length > 0 && (
        <div className="panel">
          <div className="panel-head"><h2><UserPlus size={16} /> Assigned to me</h2><span className="count-pill">{assignedToMe.length}</span></div>
          <div className="panel-body">
            {assignedToMe.map((u) => (
              <div className="row-item" key={u.id}>
                <div className="ri-main">
                  <strong>{u.emp.name}</strong>
                  <span className="ri-sub">{RIDE_MAP[u.ride].label} → {u.targetLevel} · {stepAction(u.targetLevel, u.ride)}{u.note ? ` · ${u.note}` : ""}</span>
                </div>
                <div className="ri-right">
                  {u.date
                    ? <span className={`tag ${u.du < 0 ? "tag-fail" : u.du <= 3 ? "tag-warn" : "tag-info"}`}>{u.du < 0 ? `${Math.abs(u.du)}d overdue` : u.du === 0 ? "Today" : `in ${u.du}d`} · {fmtDate(u.date)}</span>
                    : <span className="tag tag-muted">No due date</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-head"><h2><Calendar size={16} /> All Scheduled Training</h2></div>
        <div className="panel-body">
          {rows.length === 0 && <Empty text="No training scheduled. Add steps from the Roster or here." />}
          {rows.map((r) => {
            const du = daysUntil(r.date);
            const trainer = (trainers || []).find((t) => t.id === r.assignedTo);
            return (
              <div className="row-item" key={r.id}>
                <div className="ri-main">
                  <strong>{r.emp.name}</strong>
                  <span className="ri-sub">{RIDE_MAP[r.ride].label} → {r.targetLevel} · {stepAction(r.targetLevel, r.ride)}{r.note ? ` · ${r.note}` : ""}</span>
                  <span className="ri-sub" style={{ marginTop: 2 }}>
                    {canAssign ? (
                      <>Trainer: <select value={r.assignedTo || ""} onChange={(e) => onAssign(r.emp.id, r.id, e.target.value || null)}
                        style={{ background: "var(--panel2)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 4, padding: "2px 4px", fontSize: 11, marginLeft: 4 }}>
                        <option value="">— Unassigned —</option>
                        {(trainers || []).map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                      </select></>
                    ) : (
                      <>Trainer: <strong style={{ color: trainer ? "var(--accent)" : "var(--txt3)" }}>{trainer ? trainer.full_name : "Unassigned"}</strong></>
                    )}
                  </span>
                </div>
                <div className="ri-right">
                  {r.date
                    ? <span className={`tag ${du < 0 ? "tag-fail" : du <= 3 ? "tag-warn" : "tag-info"}`}>{fmtDate(r.date)}</span>
                    : <span className="tag tag-muted">TBD</span>}
                  <button className="icon-btn" onClick={() => onRemove(r.emp.id, r.id)}><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MODALS
   ============================================================ */
function Modal({ children, onClose, wide }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${wide ? "wide" : ""}`} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/* ============================================================
   LOGIN
   ============================================================ */
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(""); setBusy(true);
    let identifier = email.trim();
    // If they typed a username (no @), add the hidden suffix
    if (identifier && !identifier.includes("@")) {
      identifier = identifier.toLowerCase() + "@lvmgp.local";
    }
    const res = await onLogin({ email: identifier, password });
    setBusy(false);
    if (!res?.ok) setErr(res?.message || "Sign in failed.");
  };

  return (
    <div className="login-wrap">
      <Style />
      <div className="login-card">
        <div className="login-brand">
          <div className="brand-mark lg"><Trophy size={26} /></div>
          <div>
            <div className="brand-name">RIDE OPS</div>
            <div className="brand-sub">Training Board · Las Vegas Mini Grand Prix</div>
          </div>
        </div>
        <h2 className="login-title">Sign in</h2>
        <label className="field"><span>Username or email</span>
          <div className="input-icon"><User size={15} />
            <input value={email} onChange={(e) => { setEmail(e.target.value); setErr(""); }}
              onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="username or you@lvmgp.com" autoFocus /></div>
        </label>
        <label className="field"><span>Password</span>
          <div className="input-icon"><Lock size={15} />
            <input type="password" value={password} onChange={(e) => { setPassword(e.target.value); setErr(""); }}
              onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="password" /></div>
        </label>
        {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
        <button className="btn-primary login-btn" onClick={submit} disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
      </div>
    </div>
  );
}

function ChangePasswordScreen({ user, onDone, onLogout }) {
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr("");
    if (pw1.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw1 !== pw2) { setErr("Passwords don't match."); return; }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: pw1 });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onDone();
  };

  return (
    <div className="login-wrap">
      <Style />
      <div className="login-card">
        <div className="login-brand">
          <div className="brand-mark lg"><Lock size={26} /></div>
          <div>
            <div className="brand-name">Change password</div>
            <div className="brand-sub">First-time sign-in for {user.name}</div>
          </div>
        </div>
        <p className="sched-target" style={{ marginBottom: 16 }}>Your account is using a temporary password. Please choose a new one before continuing.</p>
        <label className="field"><span>New password</span>
          <div className="input-icon"><Lock size={15} />
            <input type="password" value={pw1} onChange={(e) => { setPw1(e.target.value); setErr(""); }}
              onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="at least 8 characters" autoFocus /></div>
        </label>
        <label className="field"><span>Confirm new password</span>
          <div className="input-icon"><Lock size={15} />
            <input type="password" value={pw2} onChange={(e) => { setPw2(e.target.value); setErr(""); }}
              onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="repeat password" /></div>
        </label>
        {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
        <button className="btn-primary login-btn" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Set password"}</button>
        <button className="btn-ghost" style={{ marginTop: 8, width: "100%" }} onClick={onLogout}>Sign out instead</button>
      </div>
    </div>
  );
}

/* ============================================================
   SIGNATURE CAPTURE (canvas)
   ============================================================ */
/* ============================================================
   QuizAttestationGate — shown to trainee at end of quiz before submit.
   Trainee must sign (or type name as fallback) to certify they were
   trained and understood the material.
   ============================================================ */
const ATTESTATION_STATEMENT =
  "I certify that I have been trained on this material, understand what I read and reviewed, " +
  "had the opportunity to ask questions and had them answered to my satisfaction, and my answers " +
  "to this quiz reflect my own knowledge.";

/* Ride-level certification. Stands on its own — not tied to a quiz row — so it
   is signed in the present tense about training the operator has already
   completed. Nothing is backdated. */
const rideAttestationStatement = (rideLabel) =>
  `I certify that I have been trained to operate ${rideLabel}, understand the safety rules and ` +
  `operating procedures for this attraction, had the opportunity to ask questions and had them ` +
  `answered to my satisfaction, and I am able to operate it in accordance with that training.`;

function QuizAttestationGate({
  user,
  onCancel,
  onAttest,
  statement = ATTESTATION_STATEMENT,
  submitLabel = "I attest — submit quiz",
  cancelLabel = "Back",
}) {
  const [sigDataUrl, setSigDataUrl] = useState(null);
  const [typedName, setTypedName] = useState("");
  const [useTyped, setUseTyped] = useState(false);
  const [busy, setBusy] = useState(false);

  const signerName = user?.full_name || user?.name || user?.email || "Trainee";
  const canSubmit = useTyped ? typedName.trim().length > 1 : !!sigDataUrl;

  const submit = () => {
    if (!canSubmit) return;
    setBusy(true);
    const attestation = {
      signer_name: signerName,
      signature: useTyped ? null : sigDataUrl,
      typed_name: useTyped ? typedName.trim() : null,
      statement,
      signed_at: new Date().toISOString(),
    };
    onAttest(attestation);
    setBusy(false);
  };

  return (
    <>
      <div className="modal-body" style={{ padding: 20 }}>
        <div style={{
          padding: 16,
          background: "var(--panel2)",
          border: "1px solid var(--line)",
          borderRadius: 8,
          marginBottom: 16,
        }}>
          <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
            Certification Statement
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.5, margin: 0 }}>
            {statement}
          </p>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
            Signing as
          </div>
          <div style={{ fontSize: 15, fontWeight: 500 }}>{signerName}</div>
        </div>

        {!useTyped ? (
          <>
            <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
              Sign below
            </div>
            <div className="signer-box">
              <SignatureCapture onChange={setSigDataUrl} />
            </div>
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <button
                type="button"
                className="btn-ghost sm"
                onClick={() => { setUseTyped(true); setSigDataUrl(null); }}
              >
                Signature pad not working? Type name instead
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="field">
              <span>Type your full name to attest</span>
              <input
                autoFocus
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder={signerName}
              />
            </label>
            <div style={{ marginTop: 4, fontSize: 12 }}>
              <button
                type="button"
                className="btn-ghost sm"
                onClick={() => { setUseTyped(false); setTypedName(""); }}
              >
                Use signature pad instead
              </button>
            </div>
          </>
        )}
      </div>
      <div className="modal-foot" style={{ justifyContent: "space-between" }}>
        <button className="btn-ghost" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
        <button className="btn-primary" onClick={submit} disabled={!canSubmit || busy}>
          {busy ? "Submitting..." : submitLabel} <Check size={14} />
        </button>
      </div>
    </>
  );
}

/* ============================================================
   POLICY LIBRARY

   Policies are assigned to everyone, departments, job titles, custom
   groups, or individual people (see ASSIGNMENTS below). Assigned staff
   read the policy and sign that they received and understand it.

   Acknowledgments are recorded against policies.version — republishing a
   changed policy bumps the version and re-asks everyone, because a signature
   against v1 says nothing about v2's text.
   ============================================================ */
const policyAckStatement = (title) =>
  `I acknowledge that I have received and read the "${title}" policy, that I understand it, that I had ` +
  `the opportunity to ask questions and had them answered to my satisfaction, and that I agree to comply with it.`;

/* ------------------------------------------------------------
   ASSIGNMENTS

   Each policy_audiences row assigns the policy to one target:
     kind "all"        target "*"            everyone
     kind "department" target department     e.g. "Restaurant"
     kind "job_title"  target job title      e.g. "Inside Area Lead"
     kind "group"      target group id       custom group (Party Captains…)
     kind "employee"   target employee id    one person
   Each row says whether those people must sign (must_acknowledge) and
   optionally by when (due_date). No rows = nobody (the safe default).

   An employee is assigned if ANY row matches them; they must sign if ANY
   matching row requires it. Department, job-title and group assignments
   follow the person, so new hires pick them up automatically.
   ------------------------------------------------------------ */
const ALL_TARGET = "*";

/* employeeId -> Set(groupId), from loadEmployeeGroups(). */
function buildGroupIndex(groups) {
  const m = new Map();
  (groups || []).forEach((g) =>
    (g.memberIds || []).forEach((eid) => {
      if (!m.has(eid)) m.set(eid, new Set());
      m.get(eid).add(String(g.id));
    })
  );
  return m;
}

function audienceRowMatches(row, emp, groupIdx) {
  if (row.kind === "all") return true;
  if (!emp) return false;
  switch (row.kind) {
    case "department": return row.target === emp.department;
    case "job_title":  return row.target === emp.jobTitle;
    case "employee":   return row.target === String(emp.id);
    case "group":      return !!groupIdx?.get(emp.id)?.has(String(row.target));
    default:           return false;
  }
}

function policyMatchRows(policy, emp, groupIdx) {
  return (policy.audiences || []).filter((r) => audienceRowMatches(r, emp, groupIdx));
}

/* Is this policy assigned to this employee at all (sign or view)? */
function policyVisibleTo(policy, emp, groupIdx) {
  return policyMatchRows(policy, emp, groupIdx).length > 0;
}

/* Must this employee sign it? */
function policyRequiresSignature(policy, emp, groupIdx) {
  if (!policy.requires_acknowledgment) return false;
  return policyMatchRows(policy, emp, groupIdx).some((r) => r.must_acknowledge);
}

/* Earliest sign-by date that applies to this employee, or null. */
function policyDueDate(policy, emp, groupIdx) {
  const dates = policyMatchRows(policy, emp, groupIdx)
    .filter((r) => r.must_acknowledge && r.due_date)
    .map((r) => r.due_date)
    .sort();
  return dates[0] || null;
}

/* Human-readable summary of who a policy is assigned to. */
function policyAudienceLabel(policy, names) {
  const aud = policy.audiences || [];
  if (aud.length === 0) return "not assigned";
  const parts = [];
  if (aud.some((a) => a.kind === "all")) parts.push("everyone");
  const of = (k) => aud.filter((a) => a.kind === k);
  of("department").forEach((a) => parts.push(a.target));
  of("job_title").forEach((a) => parts.push(a.target));
  of("group").forEach((a) => parts.push(names?.groups?.get(String(a.target)) || "a group"));
  const people = of("employee");
  if (people.length === 1) parts.push(names?.emps?.get(String(people[0].target)) || "1 person");
  else if (people.length > 1) parts.push(`${people.length} people`);
  return parts.join(", ");
}

/* Signed/required counts for one policy across the active roster. */
function policySignCounts(policy, employees, groupIdx, acks) {
  const signed = new Set(
    acks.filter((a) => a.policy_id === policy.id && a.version >= policy.version).map((a) => a.employee_id)
  );
  let required = 0, done = 0;
  employees.forEach((e) => {
    if (policyRequiresSignature(policy, e, groupIdx)) {
      required += 1;
      if (signed.has(e.id)) done += 1;
    }
  });
  return { required, done };
}

function PoliciesView({ state, user }) {
  const [policies, setPolicies] = useState([]);
  const [acks, setAcks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [reading, setReading] = useState(null);
  const [editing, setEditing] = useState(null);     // policy obj or "new"
  const [assigning, setAssigning] = useState(null); // policy obj
  const [showGroups, setShowGroups] = useState(false);

  const canEdit = roleRank(user?.role) >= roleRank("agm_gm");
  // In "View as employee" the real login is a manager, so drafts can be loaded
  // to explain why something assigned isn't showing — staff never see drafts.
  const previewing = !!user?.preview;
  const me = useMemo(() => findMyEmployee(state, user), [state, user]);
  const activeEmployees = useMemo(() => state.employees.filter((e) => !e.archived), [state.employees]);

  const reload = useCallback(async () => {
    setLoading(true);
    const [pRes, aRes, gRes] = await Promise.all([
      db.loadPolicies({ includeUnpublished: canEdit || previewing }),
      // Editors load everyone's signatures for the signed/required counts.
      canEdit
        ? db.loadPolicyAcknowledgments()
        : me ? db.loadPolicyAcknowledgments(me.id) : Promise.resolve({ ok: true, acknowledgments: [] }),
      db.loadEmployeeGroups(),
    ]);
    if (!pRes.ok) { setErr(pRes.message); setLoading(false); return; }
    setErr("");
    setPolicies(pRes.policies);
    if (aRes.ok) setAcks(aRes.acknowledgments);
    if (gRes.ok) setGroups(gRes.groups);
    setLoading(false);
    window.dispatchEvent(new Event("lvmgp:policies-changed"));
    return pRes.policies;
  }, [canEdit, previewing, me]);
  useEffect(() => { reload(); }, [reload]);

  const groupIdx = useMemo(() => buildGroupIndex(groups), [groups]);
  const names = useMemo(() => ({
    groups: new Map(groups.map((g) => [String(g.id), g.name])),
    emps: new Map(state.employees.map((e) => [String(e.id), e.name])),
  }), [groups, state.employees]);

  const myAcks = useMemo(() => (me ? acks.filter((a) => a.employee_id === me.id) : []), [acks, me]);
  // policyId -> highest version this person has signed
  const signedVersion = useMemo(() => {
    const m = {};
    myAcks.forEach((a) => { m[a.policy_id] = Math.max(m[a.policy_id] || 0, a.version); });
    return m;
  }, [myAcks]);

  const assignedToMe = (p) => policyVisibleTo(p, me, groupIdx);
  const mustSignNow = (p) =>
    p.published && policyRequiresSignature(p, me, groupIdx) && (signedVersion[p.id] || 0) < p.version;

  const visible = useMemo(
    () => (canEdit ? policies : policies.filter((p) => p.published && assignedToMe(p))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [policies, canEdit, me, groupIdx]
  );

  const statusFor = (p) => {
    if (!p.published) {
      return (p.audiences || []).length
        ? { text: "Draft — staff can't see it yet", cls: "tag-warn" }
        : { text: "Draft", cls: "tag-muted" };
    }
    if ((p.audiences || []).length === 0) return { text: "Not assigned", cls: "tag-fail" };
    if (!assignedToMe(p)) return { text: "Not assigned to you", cls: "tag-muted" };
    if (!policyRequiresSignature(p, me, groupIdx)) return { text: "Read only", cls: "tag-muted" };
    const sv = signedVersion[p.id] || 0;
    if (sv >= p.version) return { text: "Signed", cls: "tag-ok" };
    const due = policyDueDate(p, me, groupIdx);
    if (due && due < todayISO()) return { text: `Overdue — due ${fmtDate(due)}`, cls: "tag-fail" };
    if (sv > 0) return { text: due ? `Updated — re-sign by ${fmtDate(due)}` : "Updated — re-sign", cls: "tag-warn" };
    return { text: due ? `Sign by ${fmtDate(due)}` : "Signature required", cls: "tag-warn" };
  };

  const outstanding = visible.filter(mustSignNow).length;

  const byCategory = useMemo(() => {
    const m = {};
    visible.forEach((p) => { (m[p.category || "General"] = m[p.category || "General"] || []).push(p); });
    // Staff: anything waiting on their signature floats to the top of its category.
    Object.values(m).forEach((list) => list.sort((a, b) =>
      (mustSignNow(b) ? 1 : 0) - (mustSignNow(a) ? 1 : 0) || a.title.localeCompare(b.title)));
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, signedVersion]);

  return (
    <div className="users-view">
      <div className="users-head">
        <div>
          <h2 className="page-title"><FileText size={20} /> Policies</h2>
          <p className="page-sub">
            {outstanding > 0
              ? `${outstanding} polic${outstanding === 1 ? "y needs" : "ies need"} your signature.`
              : "Policies assigned to you. Opening one records that you received it."}
          </p>
        </div>
        {canEdit && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn-ghost" onClick={() => setShowGroups(true)}>
              <Users size={14} /> Groups
            </button>
            <button className="btn-primary" onClick={() => setEditing("new")}>
              <Plus size={14} /> New policy
            </button>
          </div>
        )}
      </div>

      {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
      {previewing && !loading && (() => {
        const drafts = policies.filter((p) => !p.published && assignedToMe(p));
        if (!drafts.length) return null;
        return (
          <div className="login-err" style={{ marginBottom: 14, display: "block" }}>
            <strong>Preview note:</strong> {drafts.length === 1 ? "this policy is" : "these policies are"} assigned
            to {user.name} but still {drafts.length === 1 ? "a draft" : "drafts"}, so {user.name.split(" ")[0]} can't
            see {drafts.length === 1 ? "it" : "them"} yet: <strong>{drafts.map((d) => d.title).join(", ")}</strong>.
            Exit preview, then open Policies → Edit and tick Published.
          </div>
        );
      })()}
      {loading && <Empty text="Loading policies..." />}
      {!loading && visible.length === 0 && (
        <Empty text={canEdit ? "No policies yet." : "No policies have been assigned to you yet."} />
      )}

      {!loading && Object.keys(byCategory).sort().map((cat) => (
        <div key={cat} style={{ marginBottom: 18 }}>
          <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
            {cat}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {byCategory[cat].map((p) => {
              const st = statusFor(p);
              const counts = canEdit && p.published ? policySignCounts(p, activeEmployees, groupIdx, acks) : null;
              return (
                <div key={p.id} className="row-item" style={{ flexWrap: "wrap" }}>
                  <div className="ri-main">
                    <strong>{p.title}</strong>
                    <span className="ri-sub">
                      v{p.version}
                      {p.effective_date ? ` · effective ${fmtDate(p.effective_date)}` : ""}
                      {canEdit ? ` · ${policyAudienceLabel(p, names)}` : ""}
                    </span>
                  </div>
                  {counts && counts.required > 0 && (
                    <span className={`tag ${counts.done === counts.required ? "tag-ok" : "tag-muted"}`}
                          title="Signed at the current version / required to sign">
                      {counts.done}/{counts.required} signed
                    </span>
                  )}
                  <span className={`tag ${st.cls}`}>{st.text}</span>
                  <button className="btn-primary sm" onClick={() => setReading(p)}>
                    {mustSignNow(p) ? "Read & sign" : "Open"}
                  </button>
                  {canEdit && (
                    <>
                      <button className="btn-ghost sm" onClick={() => setAssigning(p)}>Assign</button>
                      <button className="btn-ghost sm" onClick={() => setEditing(p)}>Edit</button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {reading && (
        <PolicyReaderModal
          policy={reading}
          me={me}
          mustSign={mustSignNow(reading)}
          myAck={myAcks.find((a) => a.policy_id === reading.id && a.version === reading.version) || null}
          onClose={() => setReading(null)}
          onSigned={() => { setReading(null); reload(); }}
        />
      )}
      {editing && (
        <PolicyEditModal
          policy={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async (saved, { isNew }) => {
            setEditing(null);
            const list = await reload();
            // A brand-new policy goes straight to Assign so it doesn't sit unassigned.
            if (isNew && saved) setAssigning((list || []).find((x) => x.id === saved.id) || saved);
          }}
        />
      )}
      {assigning && (
        <AssignPolicyModal
          policy={assigning}
          employees={activeEmployees}
          groups={groups}
          onManageGroups={() => setShowGroups(true)}
          onClose={() => setAssigning(null)}
          onSaved={() => { setAssigning(null); reload(); }}
        />
      )}
      {showGroups && (
        <PolicyGroupsModal
          employees={activeEmployees}
          groups={groups}
          onClose={() => setShowGroups(false)}
          onChanged={async () => {
            const gRes = await db.loadEmployeeGroups();
            if (gRes.ok) setGroups(gRes.groups);
          }}
        />
      )}
    </div>
  );
}

/* Reader. Logs a view on open, then offers the signature if one is due. */
function PolicyReaderModal({ policy, me, mustSign, myAck, onClose, onSigned }) {
  const [signing, setSigning] = useState(false);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Fire-and-forget: a failure to log the view must never block reading.
    db.recordPolicyView({
      policy_id: policy.id,
      employee_id: me?.id || null,
      version: policy.version,
    }).catch(() => {});
  }, [policy.id, policy.version, me]);

  const sign = async (attestation) => {
    if (!me) { setErr("Your login isn't linked to a roster record, so it can't be signed."); return; }
    setSaving(true);
    const res = await db.createPolicyAcknowledgment({
      policy_id: policy.id,
      employee_id: me.id,
      version: policy.version,
      attestation,
    });
    setSaving(false);
    if (!res.ok) { setErr(res.message); setSigning(false); return; }
    onSigned();
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 900, width: "92vw", height: "88vh", maxHeight: "88vh" }}>
        <div className="modal-head">
          <h3><FileText size={18} /> {policy.title}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>

        {signing ? (
          <QuizAttestationGate
            user={me}
            statement={policyAckStatement(policy.title)}
            submitLabel={saving ? "Saving..." : "I acknowledge — sign"}
            cancelLabel="Back to policy"
            onCancel={() => setSigning(false)}
            onAttest={sign}
          />
        ) : (
          <>
            <div className="modal-body" style={{ flex: 1, overflowY: "auto", padding: 20 }}>
              <div className="dim" style={{ fontSize: 12, marginBottom: 12 }}>
                Version {policy.version}
                {policy.effective_date ? ` · effective ${fmtDate(policy.effective_date)}` : ""}
              </div>
              {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
              <PolicyBody html={policy.body_html} />
            </div>
            <div className="modal-foot" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
              <button className="btn-ghost" onClick={onClose}>Close</button>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginLeft: "auto" }}>
                <button
                  className="btn-ghost"
                  title="Print, or choose Save as PDF in the print dialog"
                  onClick={() => printPolicy(policy, { statement: policyAckStatement(policy.title) })}
                >
                  <Download size={14} /> Print / PDF
                </button>
                {myAck && (
                  <button
                    className="btn-ghost"
                    title="Print this policy with your signature"
                    onClick={() => printPolicy(policy, {
                      statement: myAck.attestation?.statement || policyAckStatement(policy.title),
                      signed: {
                        name: myAck.attestation?.signer_name || me?.name || "",
                        signed_at: myAck.signed_at,
                        signature: myAck.attestation?.signature || null,
                        typed_name: myAck.attestation?.typed_name || null,
                      },
                    })}
                  >
                    <FileText size={14} /> My signed copy
                  </button>
                )}
              </div>
              {mustSign && (
                <button className="btn-primary" onClick={() => setSigning(true)}>
                  Acknowledge this policy <ArrowRight size={14} />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


/* Admin editor: content and publishing. Who gets it is set under Assign. */
function PolicyEditModal({ policy, onClose, onSaved }) {
  const isNew = !policy;
  const [title, setTitle] = useState(policy?.title || "");
  const [key, setKey] = useState(policy?.key || "");
  const [category, setCategory] = useState(policy?.category || "");
  const [body, setBody] = useState(policy?.body_html || "");
  const [effective, setEffective] = useState(policy?.effective_date || "");
  const [requiresAck, setRequiresAck] = useState(policy?.requires_acknowledgment ?? true);
  const [published, setPublished] = useState(policy?.published ?? false);
  const [bump, setBump] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60);
  const unassigned = !isNew && (policy.audiences || []).length === 0;

  const save = async () => {
    if (!title.trim()) { setErr("Title is required."); return; }
    setSaving(true);
    setErr("");
    const fields = {
      title: title.trim(),
      category: category.trim() || null,
      body_html: body,
      effective_date: effective || null,
      requires_acknowledgment: requiresAck,
      published,
    };
    const res = isNew
      ? await db.createPolicy({ ...fields, key: key.trim() || slug(title) })
      : await db.updatePolicy(policy.id, fields, { bumpVersion: bump });
    setSaving(false);
    if (!res.ok) { setErr(res.message); return; }
    onSaved(res.policy, { isNew });
  };

  return (
    <div className="modal-overlay">
      <div className="modal wide" style={{ maxWidth: 1000, width: "94vw" }}>
        <div className="modal-head">
          <h3><PenLine size={18} /> {isNew ? "New policy" : `Edit — ${policy.title}`}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: "76vh", overflowY: "auto" }}>
          {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}

          <label className="field">
            <span>Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Attendance Policy" />
          </label>
          <label className="field">
            <span>Category</span>
            <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="HR / Safety / Operations" />
          </label>
          {isNew && (
            <label className="field">
              <span>Key (leave blank to auto-generate)</span>
              <input value={key} onChange={(e) => setKey(e.target.value)} placeholder={slug(title) || "attendance_policy"} />
            </label>
          )}
          <label className="field">
            <span>Effective date</span>
            <input type="date" value={effective || ""} onChange={(e) => setEffective(e.target.value)} />
          </label>
          <div className="field">
            <span>Policy text</span>
            <PolicyRichEditor value={body} onChange={setBody} />
          </div>

          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            <label className="quiz-opt" style={{ cursor: "pointer" }}>
              <input type="checkbox" checked={requiresAck} onChange={(e) => setRequiresAck(e.target.checked)} />
              <span>Requires a signed acknowledgment of receipt and understanding</span>
            </label>
            <label className="quiz-opt" style={{ cursor: "pointer" }}>
              <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
              <span>Published (drafts are invisible to staff)</span>
            </label>
            {!isNew && (
              <label className="quiz-opt" style={{ cursor: "pointer" }}>
                <input type="checkbox" checked={bump} onChange={(e) => setBump(e.target.checked)} />
                <span>
                  Content changed — bump to v{(policy.version || 1) + 1} and re-ask everyone to sign
                </span>
              </label>
            )}
          </div>
          {isNew && (
            <p className="dim" style={{ fontSize: 12, marginTop: 10 }}>
              After you create it, you'll choose who it's assigned to.
            </p>
          )}
          {published && unassigned && (
            <div className="login-err" style={{ marginTop: 12 }}>
              <AlertCircle size={14} /> Not assigned to anyone yet — nobody will see it until you use Assign.
            </div>
          )}
          {!isNew && bump && (
            <p className="dim" style={{ fontSize: 12 }}>
              Existing v{policy.version} signatures are kept as evidence for that version. Anyone
              required to sign will be prompted again for v{(policy.version || 1) + 1}.
            </p>
          )}
        </div>
        <div className="modal-foot" style={{ justifyContent: "space-between" }}>
          <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn-ghost"
            style={{ marginLeft: "auto" }}
            title="See how this will look printed"
            onClick={() => printPolicy(
              { title: title || "Untitled policy", category, body_html: body, effective_date: effective || null,
                requires_acknowledgment: requiresAck, version: (policy?.version || 1) + (bump ? 1 : 0) },
              { statement: policyAckStatement(title || "Untitled policy") }
            )}
          >
            <Download size={14} /> Print preview
          </button>
          <button className="btn-primary" onClick={save} disabled={saving || !title.trim()}>
            {saving ? "Saving..." : isNew ? "Create & assign" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* One clickable target chip: off -> must sign -> read only -> off. */
function AudChip({ label, mode, onClick, sub }) {
  return (
    <button
      type="button"
      className={mode ? "btn-primary sm" : "btn-ghost sm"}
      style={mode === "view" ? { opacity: 0.75 } : undefined}
      onClick={onClick}
      title="Click to cycle: must sign → read only → not assigned"
    >
      {label}
      {sub ? <span style={{ opacity: 0.7, fontWeight: 400 }}>&nbsp;{sub}</span> : null}
      {mode === "sign" ? " · sign" : mode === "view" ? " · read" : ""}
    </button>
  );
}

/* Choose who a policy goes to: everyone, departments, job titles, groups,
   or individual people — each as "must sign" or "read only". */
function AssignPolicyModal({ policy, employees, groups, onClose, onSaved, onManageGroups }) {
  const keyOf = (kind, target) => `${kind}|${target}`;
  const [sel, setSel] = useState(() => {
    const m = {};
    (policy.audiences || []).forEach((a) => {
      m[keyOf(a.kind, a.target)] = { kind: a.kind, target: String(a.target), mode: a.must_acknowledge ? "sign" : "view" };
    });
    return m;
  });
  const [due, setDue] = useState(() => (policy.audiences || []).find((a) => a.due_date)?.due_date || "");
  const [q, setQ] = useState("");
  const [showWho, setShowWho] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const modeOf = (kind, target) => sel[keyOf(kind, String(target))]?.mode;
  const cycle = (kind, target) => {
    const k = keyOf(kind, String(target));
    setSel((prev) => {
      const next = { ...prev };
      const cur = next[k]?.mode;
      if (!cur) next[k] = { kind, target: String(target), mode: policy.requires_acknowledgment ? "sign" : "view" };
      else if (cur === "sign") next[k] = { ...next[k], mode: "view" };
      else delete next[k];
      return next;
    });
  };

  const departments = useMemo(
    () => Array.from(new Set(employees.map((e) => e.department).filter(Boolean))).sort(), [employees]);
  const jobTitles = useMemo(
    () => Array.from(new Set(employees.map((e) => e.jobTitle).filter(Boolean))).sort(), [employees]);
  const countBy = (field, v) => employees.filter((e) => e[field] === v).length;

  const people = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = employees.filter((e) =>
      !needle ||
      e.name.toLowerCase().includes(needle) ||
      (e.department || "").toLowerCase().includes(needle) ||
      (e.jobTitle || "").toLowerCase().includes(needle));
    // Individually selected people first, then alphabetical.
    return list.sort((a, b) =>
      (modeOf("employee", b.id) ? 1 : 0) - (modeOf("employee", a.id) ? 1 : 0) || a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, q, sel]);

  // Live preview of who this reaches, using the same rules staff screens use.
  const preview = useMemo(() => {
    const rows = Object.values(sel).map((s) => ({ kind: s.kind, target: s.target, must_acknowledge: s.mode === "sign" }));
    const fake = { ...policy, audiences: rows };
    const gi = buildGroupIndex(groups);
    const reached = employees.filter((e) => policyVisibleTo(fake, e, gi));
    const signers = reached.filter((e) => policyRequiresSignature(fake, e, gi));
    return { reached, signers };
  }, [sel, employees, groups, policy]);

  const save = async () => {
    setSaving(true);
    setErr("");
    const rows = Object.values(sel).map((s) => ({
      kind: s.kind,
      target: s.target,
      must_acknowledge: s.mode === "sign",
      due_date: s.mode === "sign" && due ? due : null,
    }));
    const res = await db.setPolicyAudience(policy.id, rows);
    setSaving(false);
    if (!res.ok) { setErr(res.message); return; }
    onSaved();
  };

  const sectionHead = (text, extra) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0 6px" }}>
      <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>{text}</div>
      {extra}
    </div>
  );
  const chipRow = { display: "flex", flexWrap: "wrap", gap: 6 };

  return (
    <div className="modal-overlay">
      <div className="modal wide" style={{ maxWidth: 820, width: "94vw" }}>
        <div className="modal-head">
          <h3><Users size={18} /> Assign — {policy.title}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
          <p className="dim" style={{ fontSize: 12, marginTop: 0 }}>
            Click to cycle: <strong>must sign</strong> → <strong>read only</strong> → not assigned.
            Department, job-title and group assignments follow the person, so new hires get them automatically.
            {!policy.requires_acknowledgment && " This policy is set to not require signatures, so everyone assigned can only read it."}
          </p>

          {sectionHead("Everyone")}
          <div style={chipRow}>
            <AudChip label="All employees" sub={`(${employees.length})`} mode={modeOf("all", ALL_TARGET)} onClick={() => cycle("all", ALL_TARGET)} />
          </div>

          {sectionHead("Departments")}
          <div style={chipRow}>
            {departments.map((d) => (
              <AudChip key={d} label={d} sub={`(${countBy("department", d)})`} mode={modeOf("department", d)} onClick={() => cycle("department", d)} />
            ))}
          </div>

          {sectionHead("Job titles")}
          <div style={chipRow}>
            {jobTitles.map((t) => (
              <AudChip key={t} label={t} sub={`(${countBy("jobTitle", t)})`} mode={modeOf("job_title", t)} onClick={() => cycle("job_title", t)} />
            ))}
          </div>

          {sectionHead("Groups", (
            <button type="button" className="btn-ghost-sm" onClick={onManageGroups}>
              <Pencil size={11} /> Manage groups
            </button>
          ))}
          <div style={chipRow}>
            {groups.length === 0 && <span className="dim" style={{ fontSize: 12 }}>No groups yet — create one with Manage groups.</span>}
            {groups.map((g) => (
              <AudChip key={g.id} label={g.name} sub={`(${(g.memberIds || []).length})`} mode={modeOf("group", g.id)} onClick={() => cycle("group", g.id)} />
            ))}
          </div>

          {sectionHead("Individual people")}
          <div className="search" style={{ marginBottom: 6 }}>
            <Search size={14} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, department or job title"
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 8 }}>
            {people.map((e) => {
              const mode = modeOf("employee", e.id);
              return (
                <div key={e.id} className="row-item" style={{ borderRadius: 0, padding: "6px 10px" }}>
                  <div className="ri-main">
                    <strong style={{ fontSize: 13 }}>{e.name}</strong>
                    <span className="ri-sub">{e.department} · {e.jobTitle}</span>
                  </div>
                  <AudChip label={mode ? "Assigned" : "Assign"} mode={mode} onClick={() => cycle("employee", e.id)} />
                </div>
              );
            })}
            {people.length === 0 && <Empty text="No matches." />}
          </div>

          {sectionHead("Sign by (optional)")}
          <div className="field" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 10, flexWrap: "wrap", margin: 0 }}>
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} style={{ maxWidth: 200 }} />
            {due && <button type="button" className="btn-ghost-sm" onClick={() => setDue("")}>Clear</button>}
            <span className="dim" style={{ fontSize: 12 }}>
              Shown to staff, and late signatures are flagged as overdue in the report.
            </span>
          </div>

          <div style={{ marginTop: 18, padding: 12, border: "1px solid var(--line2)", borderRadius: 10, background: "var(--panel2)" }}>
            <strong>
              Reaches {preview.reached.length} {preview.reached.length === 1 ? "person" : "people"}
              {policy.requires_acknowledgment ? ` · ${preview.signers.length} must sign` : ""}
            </strong>
            {preview.reached.length > 0 && (
              <button type="button" className="btn-ghost-sm" style={{ marginLeft: 10 }} onClick={() => setShowWho((v) => !v)}>
                {showWho ? "Hide" : "Show who"}
              </button>
            )}
            {showWho && (
              <div className="dim" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.6 }}>
                {preview.reached.map((e) => (
                  <span key={e.id} style={{ marginRight: 10, whiteSpace: "nowrap" }}>
                    {e.name}{preview.signers.includes(e) ? "" : " (read)"}
                  </span>
                ))}
              </div>
            )}
            {Object.keys(sel).length === 0 && (
              <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>Nothing selected — nobody will see this policy.</div>
            )}
          </div>
          {!policy.published && (
            <p className="dim" style={{ fontSize: 12 }}>
              This policy is still a draft. Staff will see it once you tick Published under Edit.
            </p>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save assignments"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Create and maintain custom groups (Party Captains, Openers, …). */
function PolicyGroupsModal({ employees, groups, onClose, onChanged }) {
  const [editing, setEditing] = useState(null); // group obj or "new"
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [members, setMembers] = useState(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const open = (g) => {
    setErr("");
    setQ("");
    setEditing(g);
    setName(g === "new" ? "" : g.name);
    setDesc(g === "new" ? "" : g.description || "");
    setMembers(new Set(g === "new" ? [] : g.memberIds || []));
  };

  const toggle = (id) => setMembers((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const save = async () => {
    if (!name.trim()) { setErr("Name the group."); return; }
    setBusy(true);
    setErr("");
    let id = editing === "new" ? null : editing.id;
    if (editing === "new") {
      const r = await db.createEmployeeGroup({ name, description: desc });
      if (!r.ok) { setBusy(false); setErr(r.message); return; }
      id = r.group.id;
    } else {
      const r = await db.updateEmployeeGroup(id, { name, description: desc });
      if (!r.ok) { setBusy(false); setErr(r.message); return; }
    }
    const m = await db.setGroupMembers(id, Array.from(members));
    setBusy(false);
    if (!m.ok) { setErr(m.message); return; }
    await onChanged();
    setEditing(null);
  };

  const remove = async () => {
    if (!window.confirm(`Delete the group "${editing.name}"? Any policies assigned to it will no longer reach its members.`)) return;
    setBusy(true);
    const r = await db.deleteEmployeeGroup(editing.id);
    setBusy(false);
    if (!r.ok) { setErr(r.message); return; }
    await onChanged();
    setEditing(null);
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return employees
      .filter((e) => !needle || e.name.toLowerCase().includes(needle) ||
        (e.department || "").toLowerCase().includes(needle) || (e.jobTitle || "").toLowerCase().includes(needle))
      .sort((a, b) => (members.has(b.id) ? 1 : 0) - (members.has(a.id) ? 1 : 0) || a.name.localeCompare(b.name));
  }, [employees, q, members]);

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal" style={{ maxWidth: 640, width: "94vw" }}>
        <div className="modal-head">
          <h3><Users size={18} /> {editing ? (editing === "new" ? "New group" : `Group — ${editing.name}`) : "Groups"}</h3>
          <button className="icon-btn" onClick={editing ? () => setEditing(null) : onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
          {!editing && (
            <>
              <p className="dim" style={{ fontSize: 12, marginTop: 0 }}>
                Groups let you assign a policy to a hand-picked set of people, such as Party Captains or
                Openers. Changing a group's members changes who its policies reach.
              </p>
              {groups.length === 0 && <Empty text="No groups yet." />}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {groups.map((g) => (
                  <div key={g.id} className="row-item">
                    <div className="ri-main">
                      <strong>{g.name}</strong>
                      <span className="ri-sub">
                        {(g.memberIds || []).length} member{(g.memberIds || []).length === 1 ? "" : "s"}
                        {g.description ? ` · ${g.description}` : ""}
                      </span>
                    </div>
                    <button className="btn-ghost sm" onClick={() => open(g)}>Edit</button>
                  </div>
                ))}
              </div>
            </>
          )}
          {editing && (
            <>
              <label className="field">
                <span>Group name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Party Captains" />
              </label>
              <label className="field">
                <span>Description (optional)</span>
                <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Hosts birthday parties" />
              </label>
              <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, margin: "12px 0 6px" }}>
                Members ({members.size})
              </div>
              <div className="search" style={{ marginBottom: 6 }}>
                <Search size={14} />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" style={{ width: "100%" }} />
              </div>
              <div style={{ maxHeight: 300, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 8 }}>
                {filtered.map((e) => (
                  <label key={e.id} className="quiz-opt" style={{ cursor: "pointer", borderRadius: 0, margin: 0 }}>
                    <input type="checkbox" checked={members.has(e.id)} onChange={() => toggle(e.id)} />
                    <span>{e.name} <span className="dim" style={{ fontSize: 12 }}>· {e.department} · {e.jobTitle}</span></span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="modal-foot" style={{ justifyContent: "space-between" }}>
          {!editing ? (
            <>
              <button className="btn-ghost" onClick={onClose}>Close</button>
              <button className="btn-primary" onClick={() => open("new")}><Plus size={14} /> New group</button>
            </>
          ) : (
            <>
              <button className="btn-ghost" onClick={() => setEditing(null)} disabled={busy}>Back</button>
              <div style={{ display: "flex", gap: 8 }}>
                {editing !== "new" && (
                  <button className="btn-ghost" onClick={remove} disabled={busy}><Trash2 size={14} /> Delete</button>
                )}
                <button className="btn-primary" onClick={save} disabled={busy || !name.trim()}>
                  {busy ? "Saving..." : "Save group"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   RideCertificationQueue — one certification signature per ride, per operator.

   Any employee sitting at L2+ on a ride is certified to run it independently,
   so there should be a signed statement on file saying they were trained on it.
   This finds the rides where that signature is missing and collects it.

   Deliberately NOT tied to a quiz row: the operator signs today, in the present
   tense, about training they have already completed. Nothing is backdated and
   no signature is generated on anyone's behalf.

   Rides still at T are skipped — those operators aren't certified yet and will
   attest at the quiz gate on their way to L2.
   ============================================================ */
function RideCertificationQueue({ state, user, paused = false }) {
  const [signed, setSigned] = useState(null); // Set of ride keys already on file
  // In "View as employee" the manager can close this to look at the rest of
  // the employee's screens. Real employees can't dismiss it (only skip).
  const previewing = !!user?.preview;
  const [previewDismissed, setPreviewDismissed] = useState(false);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const me = useMemo(() => findMyEmployee(state, user), [state, user]);

  useEffect(() => {
    if (!me) {
      console.warn(
        "[certification queue] hidden: your login isn't linked to a roster record " +
        "(user.employeeId is empty and no employee name matches). Link it on the Users screen."
      );
      return;
    }
    let alive = true;
    (async () => {
      if (typeof db.loadRideAttestations !== "function") {
        if (!alive) return;
        console.error("[certification queue] db.loadRideAttestations is missing — paste the db.js additions.");
        setErr("db.js is missing loadRideAttestations / createRideAttestation. Paste the db.js additions and redeploy.");
        setSigned(new Set());
        return;
      }
      let res;
      try {
        res = await db.loadRideAttestations(me.id);
      } catch (e) {
        if (!alive) return;
        console.error("[certification queue] load threw", e);
        setErr(`Could not load certifications: ${e.message}`);
        setSigned(new Set());
        return;
      }
      if (!alive) return;
      if (!res.ok) {
        console.error("[certification queue] load failed:", res.message);
        setErr(res.message);
        setSigned(new Set());
        return;
      }
      setSigned(new Set((res.attestations || []).map((a) => a.ride)));
    })();
    return () => { alive = false; };
  }, [me]);

  // A ride needs a certification as soon as the employee PASSES its quiz
  // (so it's signed right after training, not days later when the L2
  // observation is done), and also for anyone already operating at L2+
  // who never signed one.
  const passedQuizRides = useMemo(() => {
    if (!me) return new Set();
    return new Set(
      (state.quizzes || [])
        .filter((q) => q.empId === me.id &&
          (q.status === "passed_full" || q.status === "passed_gaps" || q.status === "complete"))
        .map((q) => q.ride)
    );
  }, [state.quizzes, me]);

  const pending = useMemo(() => {
    if (!me || !signed) return [];
    return RIDES
      .map((r) => r.key)
      .filter((ride) => (isIndependent(me.levels?.[ride]) || passedQuizRides.has(ride)) && !signed.has(ride));
  }, [me, signed, passedQuizRides]);

  // Wait while another window (e.g. the quiz results) is open, then pop up.
  if (!me || signed === null || paused) return null;
  if (previewing && previewDismissed) return null;

  if (pending.length === 0) {
    const certified = RIDES.map((r) => r.key).filter((k) => isIndependent(me.levels?.[k]));
    console.info(
      certified.length === 0
        ? `[certification queue] nothing to sign: ${me.name} isn't at L2+ on any ride yet (levels: ${JSON.stringify(me.levels || {})}).`
        : `[certification queue] nothing to sign: all ${certified.length} certified ride(s) already signed.`
    );
    return null;
  }

  const ride = pending[Math.min(idx, pending.length - 1)];
  const rideLabel = RIDE_MAP[ride].label;
  const remaining = pending.length;

  const handleAttest = async (attestation) => {
    setBusy(true);
    setErr("");
    const res = await db.createRideAttestation({
      employee_id: me.id,
      ride,
      level_at_signing: me.levels?.[ride] || null,
      attestation,
    });
    setBusy(false);
    if (!res.ok) { setErr(res.message || "Could not save. Try again."); return; }
    setSigned((prev) => new Set(prev).add(ride));
    setIdx(0);
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-head">
          <h3><ShieldCheck size={18} /> Training certification required</h3>
          {previewing && (
            <button className="btn-ghost sm" onClick={() => setPreviewDismissed(true)}
              title="Only in preview — the employee can't close this">
              <X size={13} /> Close (preview only)
            </button>
          )}
        </div>
        <div style={{ padding: "12px 20px 0" }}>
          <p className="dim" style={{ fontSize: 13, margin: 0 }}>
            You've been trained on {remaining === 1 ? "a ride" : `${remaining} rides`} that
            {remaining === 1 ? " doesn't" : " don't"} have a signed training certification on
            file yet. Please sign for {remaining === 1 ? "it" : "each one"} — this is a record
            that you were trained, not a test.
          </p>
          <div style={{ marginTop: 12, fontSize: 15, fontWeight: 600 }}>{rideLabel}</div>
          <div className="dim" style={{ fontSize: 12, marginTop: 2 }}>
            {remaining > 1 ? `${remaining} remaining` : "Last one"} · current level {me.levels?.[ride]}
          </div>
          {err && (
            <div className="login-err" style={{ marginTop: 10 }}>
              <AlertCircle size={14} /> {err}
            </div>
          )}
        </div>
        <QuizAttestationGate
          key={ride}
          user={me}
          statement={rideAttestationStatement(rideLabel)}
          submitLabel={busy ? "Saving..." : "I certify — sign"}
          cancelLabel={remaining > 1 ? "Skip for now" : "Later"}
          onCancel={() => setIdx((i) => (i + 1) % Math.max(pending.length, 1))}
          onAttest={handleAttest}
        />
      </div>
    </div>
  );
}

function SignatureCapture({ onChange }) {
  const canvasRef = React.useRef(null);
  const drawing = React.useRef(false);
  const [hasInk, setHasInk] = useState(false);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#1b2733";
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: (t.clientX - r.left) * (canvas.width / r.width), y: (t.clientY - r.top) * (canvas.height / r.height) };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const end = () => { if (!drawing.current) return; drawing.current = false; setHasInk(true); onChange(canvas.toDataURL("image/png")); };
    canvas.addEventListener("mousedown", start); canvas.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end);
    return () => {
      canvas.removeEventListener("mousedown", start); canvas.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      canvas.removeEventListener("touchstart", start); canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", end);
    };
  }, [onChange]);

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false); onChange(null);
  };

  return (
    <div className="sig-wrap">
      <canvas ref={canvasRef} width={520} height={150} className="sig-canvas" />
      <div className="sig-tools">
        <span className="sig-hint">{hasInk ? "Signature captured" : "Sign above with mouse or finger"}</span>
        <button className="btn-mini ghost" type="button" onClick={clear}><RotateCcw size={12} /> Clear</button>
      </div>
    </div>
  );
}

/* ============================================================
   START PROGRESSION (training-gate sign-off -> unlocks quiz)
   ============================================================ */
function StartProgressionModal({ emp, user, onClose, onConfirm }) {
  // rides whose next step is a QUIZ gate (target L2, or trainer cert L4). L3 is observations.
  const opts = RIDES.map((r) => ({ ride: r.key, current: emp.levels[r.key], next: nextLevel(emp.levels[r.key], r.key) }))
    .filter((o) => o.next && o.next !== "L3");
  const [ride, setRide] = useState(opts[0]?.ride || "");
  const [sig, setSig] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [notes, setNotes] = useState("");
  const sel = opts.find((o) => o.ride === ride);
  const allowed = canActAs(user, "trainer_manager");

  if (!opts.length) {
    return (
      <Modal onClose={onClose}>
        <div className="modal-head"><h3><Check size={18} /> Training sign-off — {emp.name}</h3>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button></div>
        <div className="modal-body"><Empty text="No quiz-gated steps available. Remaining steps are observation-based (L2→L3) — use the Sign-offs/observation tracker." /></div>
        <div className="modal-foot"><button className="btn-ghost" onClick={onClose}>Close</button></div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <div className="modal-head">
        <h3><Check size={18} /> Training sign-off — {emp.name}</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        {!allowed && <div className="login-err"><Lock size={14} /> Your role ({ROLES[user.role].label}) can't sign off training. A Trainer, Manager, or AGM/GM must do this.</div>}
        <label className="field"><span>Ride / Skill</span>
          <select value={ride} onChange={(e) => setRide(e.target.value)}>
            {opts.map((o) => <option key={o.ride} value={o.ride}>{RIDE_MAP[o.ride].label} ({o.current} → {o.next})</option>)}
          </select>
        </label>
        <div className="signoff-note">
          <p><FileQuestion size={14} /> Signing off confirms you reviewed the {RIDE_MAP[ride]?.label} module with {emp.name} and unlocks their certification quiz (target {sel?.next}).</p>
        </div>
        <label className="check-row" style={{ marginTop: 10, padding: 10, background: "rgba(217,154,43,0.10)", border: "1px solid rgba(217,154,43,0.30)", borderRadius: 6 }}>
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          <span style={{ fontSize: 13 }}>By signing, I confirm that I have <strong>trained, demonstrated, operated alongside, and observed</strong> {emp.name} <strong>properly running the {RIDE_MAP[ride]?.label} ride</strong> and they are ready to attempt the certification quiz.</span>
        </label>
        <label className="field"><span>Notes (optional)</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            placeholder="Any specific observations, areas of focus, or context for this sign-off..."
            style={{ width: "100%", padding: 8, background: "var(--panel2)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 6, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
        </label>
        <div className="signer-box">
          <div className="signer-id"><ShieldCheck size={15} /> Signing as <strong>{user.name}</strong> — {user.title}</div>
          <SignatureCapture onChange={setSig} />
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!allowed || !ride || !sig || !confirmed}
          onClick={() => onConfirm(ride, sel.next, { role: "trainer_manager", name: user.name, title: user.title, dataUrl: sig, date: todayISO() }, notes.trim())}>
          Sign off &amp; unlock quiz
        </button>
      </div>
    </Modal>
  );
}

/* ============================================================
   COMPLETE A PENDING SIGN-OFF (100% AGM/GM, or 80-99% dual)
   ============================================================ */
function CompleteSignoffModal({ task, user, onClose, onSign }) {
  const [sig, setSig] = useState(null);
  const [whichRole, setWhichRole] = useState(task.requiredRoles.find((r) => !task.signatures.some((s) => s.role === r)) || task.requiredRoles[0]);
  const remaining = task.requiredRoles.filter((r) => !task.signatures.some((s) => s.role === r));
  const allowed = canActAs(user, whichRole);
  const roleLabel = (r) => r === "agm_gm" ? "AGM/GM" : r === "manager" ? "Manager" : r === "employee" ? "Employee" : "Trainer/Manager";

  return (
    <Modal onClose={onClose}>
      <div className="modal-head">
        <h3><PenLine size={18} /> Sign-off — {task.empName}</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <div className="signoff-summary">
          <div><strong>{RIDE_MAP[task.ride].label}</strong> → advance to {task.targetLevel}</div>
          <div className="ss-kind">
            {task.kind === "advance_full" && "100% pass — AGM/GM sign-off required"}
            {task.kind === "advance_gaps" && "80–99% pass — retrain missed topics, then employee + manager sign-off"}
            {task.kind === "advance_obs" && `${task.note || "Observations complete"} — AGM+ sign-off required to advance`}
            {task.kind === "train_gate" && "Training complete — sign off to unlock the certification quiz"}
          </div>
          {task.missedTopics?.length > 0 && (
            <div className="ss-topics">Missed topics to retrain: {task.missedTopics.map((t) => topicLabel(task.ride, t)).join(", ")}</div>
          )}
        </div>

        <div className="role-track">
          {task.requiredRoles.map((r) => {
            const done = task.signatures.find((s) => s.role === r);
            return (
              <div key={r} className={`role-pill ${done ? "done" : whichRole === r ? "active" : ""}`} onClick={() => !done && setWhichRole(r)}>
                {done ? <Check size={13} /> : <CircleDot size={13} />} {roleLabel(r)}
                {done && <span className="role-by">{done.name}</span>}
              </div>
            );
          })}
        </div>

        {remaining.length === 0 ? (
          <Empty text="All required signatures captured." />
        ) : (
          <>
            {!allowed && <div className="login-err"><Lock size={14} /> Your role ({ROLES[user.role].label}) can't provide the {roleLabel(whichRole)} signature.</div>}
            <div className="signer-box">
              <div className="signer-id"><ShieldCheck size={15} /> Signing as <strong>{user.name}</strong> — {user.title} ({roleLabel(whichRole)})</div>
              <SignatureCapture onChange={setSig} />
            </div>
          </>
        )}
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!allowed || !sig || remaining.length === 0}
          onClick={() => onSign({ role: whichRole, name: user.name, title: user.title, dataUrl: sig, date: todayISO() })}>
          Add signature
        </button>
      </div>
    </Modal>
  );
}



/* ============================================================
   MODULES VIEWER
   ============================================================ */

/* ============================================================
   BulkSectionAssignModal — assign every module in a section to every
   active employee in a department in one pass.

   Skips anyone who already has the module assigned, so it's safe to re-run
   after new hires without duplicating or resetting anyone's progress.
   ============================================================ */
function BulkSectionAssignModal({ section, sectionLabel, modules, employees, onClose, onDone }) {
  const [dept, setDept] = useState("");
  const [picked, setPicked] = useState(() => new Set(modules.map((m) => m.id)));
  const [existing, setExisting] = useState(null); // moduleId -> Set(employeeId)
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(null); // { created, skipped, failed }
  const [err, setErr] = useState("");

  const departments = useMemo(() => {
    const set = new Set();
    (employees || []).filter((e) => !e.archived).forEach((e) => { if (e.department) set.add(e.department); });
    return Array.from(set).sort();
  }, [employees]);

  // Pre-load who already has each module, so the preview is accurate.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const map = {};
      for (const m of modules) {
        const res = await db.loadModuleAssignments({ moduleId: m.id });
        if (cancelled) return;
        if (!res.ok) { setErr(res.message); setLoading(false); return; }
        map[m.id] = new Set((res.assignments || []).map((a) => a.employee_id));
      }
      if (cancelled) return;
      setExisting(map);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [modules]);

  const targets = useMemo(
    () => (employees || []).filter((e) => !e.archived && e.department === dept),
    [employees, dept]
  );

  // The exact set of rows that would be created.
  const plan = useMemo(() => {
    if (!existing || !dept) return [];
    const out = [];
    modules.filter((m) => picked.has(m.id)).forEach((m) => {
      targets.forEach((e) => {
        if (!(existing[m.id] || new Set()).has(e.id)) out.push({ module: m, emp: e });
      });
    });
    return out;
  }, [existing, dept, modules, picked, targets]);

  const alreadyHave = useMemo(() => {
    if (!existing || !dept) return 0;
    let n = 0;
    modules.filter((m) => picked.has(m.id)).forEach((m) => {
      targets.forEach((e) => { if ((existing[m.id] || new Set()).has(e.id)) n += 1; });
    });
    return n;
  }, [existing, dept, modules, picked, targets]);

  const toggle = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const run = async () => {
    setRunning(true);
    setErr("");
    let created = 0, failed = 0;
    for (const item of plan) {
      const days = item.module.due_days_after_assignment;
      const dueAt = days
        ? new Date(Date.now() + days * 86400000).toISOString()
        : null;
      const res = await db.createModuleAssignment({
        module_id: item.module.id,
        employee_id: item.emp.id,
        due_at: dueAt,
      });
      if (res.ok) created += 1; else failed += 1;
    }
    setRunning(false);
    setDone({ created, skipped: alreadyHave, failed });
    if (typeof onDone === "function") onDone();
  };

  return (
    <div className="modal-overlay">
      <div className="modal wide">
        <div className="modal-head">
          <h3><UserPlus size={18} /> Bulk assign — {sectionLabel}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
          {loading && <Empty text="Checking existing assignments..." />}

          {!loading && !done && (
            <>
              <label className="field">
                <span>Assign to everyone in department</span>
                <select value={dept} onChange={(e) => setDept(e.target.value)}>
                  <option value="">— Select a department —</option>
                  {departments.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </label>

              <div className="dim" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, margin: "14px 0 6px" }}>
                Modules ({picked.size} of {modules.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 240, overflowY: "auto" }}>
                {modules.map((m) => (
                  <label key={m.id} className="quiz-opt" style={{ cursor: "pointer" }}>
                    <input type="checkbox" checked={picked.has(m.id)} onChange={() => toggle(m.id)} />
                    <span>{m.title}</span>
                  </label>
                ))}
              </div>

              {dept && (
                <div style={{ marginTop: 16, padding: 12, background: "var(--panel2)", border: "1px solid var(--line)", borderRadius: 6 }}>
                  <strong style={{ fontSize: 14 }}>
                    {plan.length} new assignment{plan.length === 1 ? "" : "s"}
                  </strong>
                  <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
                    {targets.length} active employee{targets.length === 1 ? "" : "s"} in {dept} × {picked.size} module
                    {picked.size === 1 ? "" : "s"}
                    {alreadyHave > 0 && ` · ${alreadyHave} already assigned and will be left alone`}
                  </div>
                  {targets.length === 0 && (
                    <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>
                      No active employees in this department.
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {done && (
            <div style={{ padding: 12, background: "var(--panel2)", border: "1px solid var(--line)", borderRadius: 6 }}>
              <strong style={{ color: "var(--ok)" }}>✓ {done.created} assignment{done.created === 1 ? "" : "s"} created</strong>
              <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
                {done.skipped} already assigned and left untouched
                {done.failed > 0 && ` · ${done.failed} failed`}
              </div>
              {done.failed > 0 && (
                <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>
                  Re-running is safe — it will retry only the ones that didn't land.
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-foot" style={{ justifyContent: "space-between" }}>
          <button className="btn-ghost" onClick={onClose} disabled={running}>
            {done ? "Close" : "Cancel"}
          </button>
          {!done && (
            <button
              className="btn-primary"
              onClick={run}
              disabled={running || loading || plan.length === 0}
            >
              {running ? `Assigning... (${plan.length})` : `Assign ${plan.length || ""}`.trim()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ModulesView({ trainingModules, user, onChanged, employees }) {
  // The four sections (extensible: add here + labels below to grow)
  const SECTIONS = [
    { key: "ride", label: "Ride Training" },
    { key: "ride_ops", label: "Ride Operations" },
    { key: "restaurant", label: "Restaurant Training" },
    { key: "leadership", label: "Leadership & Training" },
    { key: "general", label: "General" },
    { key: "service_tech", label: "Service Tech Training" },
  ];
  const [activeSection, setActiveSection] = useState("ride");

  // Ride modules: hardcoded order matching the original design
  const rideOrder = ["_general", "slide", "tornado", "dragon", "kiddieKart", "road", "sprint", "euro"];

  // Non-ride modules loaded from DB
  const [nonRideModules, setNonRideModules] = useState([]);
  const [nonRideLoading, setNonRideLoading] = useState(false);
  const [nonRideErr, setNonRideErr] = useState("");
  const reloadNonRide = React.useCallback(async () => {
    setNonRideLoading(true);
    const res = await db.loadModules();
    if (res.ok) setNonRideModules(res.modules.filter((m) => m.section && m.section !== "ride"));
    else setNonRideErr(res.message);
    setNonRideLoading(false);
  }, []);
  React.useEffect(() => { reloadNonRide(); }, [reloadNonRide]);

  // Modules for the active section
  const modulesForSection = React.useMemo(() => {
    if (activeSection === "ride") {
      return rideOrder.map((k) => moduleForKey(k, trainingModules));
    }
    return nonRideModules
      .filter((m) => m.section === activeSection)
      .map((m) => ({ source: "db", key: m.key, title: m.title, body_md: m.body_md || "", ...m }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, nonRideModules, trainingModules]);

  const [open, setOpen] = useState("_general");
  const [reading, setReading] = useState(false);
  const [narrow, setNarrow] = useState(typeof window !== "undefined" && window.innerWidth <= 820);
  const [editing, setEditing] = useState(null); // module key being edited, or null
  const [creating, setCreating] = useState(false); // when true, creating a new module for the active section
  const [managingQuestions, setManagingQuestions] = useState(null); // module obj
  const [managingAssignments, setManagingAssignments] = useState(null); // module obj
  const [bulkAssigning, setBulkAssigning] = useState(false); // bulk department assign for active section

  React.useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth <= 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Keep `open` valid whenever section changes
  React.useEffect(() => {
    if (activeSection === "ride") {
      if (!rideOrder.includes(open)) setOpen("_general");
    } else {
      const first = modulesForSection[0]?.key;
      if (!modulesForSection.find((m) => m.key === open)) setOpen(first || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, nonRideModules]);

  // Look up a module for display (works across sections)
  const mod = React.useMemo(() => {
    if (activeSection === "ride") return moduleForKey(open, trainingModules);
    const m = modulesForSection.find((x) => x.key === open);
    return m || null;
  }, [activeSection, open, modulesForSection, trainingModules]);

  const openModule = (k) => { setOpen(k); if (narrow) setReading(true); };
  const canEdit = user?.role === "admin";
  const canAssign = roleRank(user?.role) >= roleRank("manager");
  const isNonRide = activeSection !== "ride";

  // Section tabs (top-of-page navigation)
  const SectionTabs = (
    <div style={{ display: "flex", gap: 4, marginBottom: 12, padding: "0 4px", flexWrap: "wrap" }}>
      {SECTIONS.map((s) => (
        <button
          key={s.key}
          className={activeSection === s.key ? "btn-primary sm" : "btn-ghost sm"}
          onClick={() => setActiveSection(s.key)}
          style={{ padding: "6px 12px" }}
        >
          {s.label}
        </button>
      ))}
      {canAssign && isNonRide && modulesForSection.length > 0 && (
        <button
          className="btn-ghost sm"
          onClick={() => setBulkAssigning(true)}
          style={{ marginLeft: "auto", padding: "6px 12px" }}
        >
          <UserPlus size={12} /> Bulk assign
        </button>
      )}
      {canEdit && isNonRide && (
        <button
          className="btn-ghost sm"
          onClick={() => setCreating(true)}
          style={{ marginLeft: canAssign && modulesForSection.length > 0 ? 0 : "auto", padding: "6px 12px" }}
        >
          <Plus size={12} /> New module
        </button>
      )}
    </div>
  );

  const ModuleBody = mod ? (
    <div className="panel-body mod-body">
      {mod.description && (
        <p className="dim" style={{ fontSize: 13, marginTop: 0, marginBottom: 12 }}>{mod.description}</p>
      )}
      <Markdown source={mod.body_md} />
      {mod.source === "legacy" && (
        <div className="login-err" style={{ marginTop: 14, fontSize: 12 }}>
          <AlertCircle size={13} /> This module's content is not yet saved to the database. An admin can save it via the Edit button.
        </div>
      )}
    </div>
  ) : null;

  // ----- Narrow screens (phone / iPad): list, or full-screen reader -----
  if (narrow) {
    if (!reading) {
      return (
        <div>
          {SectionTabs}
          {isNonRide && nonRideLoading && <Empty text="Loading modules..." />}
          {isNonRide && !nonRideLoading && modulesForSection.length === 0 && (
            <Empty text={canEdit ? "No modules in this section yet. Tap 'New module' to add one." : "No modules in this section yet."} />
          )}
          <div className="mod-mobile-list">
            {modulesForSection.map((m) => (
              <button key={m.key} className="mod-mobile-item" onClick={() => openModule(m.key)}>
                <div className="mmi-text">
                  <span className="mmi-title">{m.title}</span>
                  <span className="mmi-sub">
                    {isNonRide ? (m.description || "") : (m.source === "legacy" ? "Default content" : "Custom content")}
                  </span>
                </div>
                <ChevronRight size={18} />
              </button>
            ))}
          </div>
          {creating && (
            <ModuleEditModal
              moduleKey={makeNewModuleKey(activeSection)}
              initial={{ section: activeSection, title: "", body_md: "" }}
              onClose={() => setCreating(false)}
              onSaved={() => { setCreating(false); onChanged(); reloadNonRide(); }}
            />
          )}
        </div>
      );
    }
    return (
      <div className="mod-reader">
        <div className="mod-reader-bar">
          <button className="mod-back" onClick={() => setReading(false)}><ArrowLeft size={18} /> Modules</button>
          {canEdit && <button className="btn-mini" onClick={() => setEditing(open)}><Pencil size={13} /> Edit</button>}
          {isNonRide && canAssign && mod && mod.source === "db" && (
            <button className="btn-mini" onClick={() => setManagingAssignments(mod)}>
              <UserPlus size={13} /> Assign
            </button>
          )}
        </div>
        <h2 className="mod-reader-title"><BookOpen size={18} /> {mod?.title || ""}</h2>
        {ModuleBody}
        {editing && (
          <ModuleEditModal
            moduleKey={editing}
            initial={moduleForKey(editing, trainingModules) || modulesForSection.find((m) => m.key === editing)}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); onChanged(); reloadNonRide(); }}
          />
        )}
        {managingAssignments && (
          <ModuleAssignModal
            training={managingAssignments}
            employees={employees}
            user={user}
            onClose={() => { setManagingAssignments(null); reloadNonRide(); }}
          />
        )}
        {bulkAssigning && (
          <BulkSectionAssignModal
            section={activeSection}
            sectionLabel={SECTIONS.find((x) => x.key === activeSection)?.label || activeSection}
            modules={modulesForSection}
            employees={employees}
            onClose={() => setBulkAssigning(false)}
            onDone={() => reloadNonRide()}
          />
        )}
      </div>
    );
  }

  // ----- Desktop: side-by-side with section tabs -----
  return (
    <div>
      {SectionTabs}
      {isNonRide && nonRideErr && <div className="login-err"><AlertCircle size={14} /> {nonRideErr}</div>}
      <div className="modules-tab">
        <div className="mod-list">
          {isNonRide && nonRideLoading && <Empty text="Loading..." />}
          {isNonRide && !nonRideLoading && modulesForSection.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: "var(--txt3)" }}>
              {canEdit ? "No modules in this section yet. Click 'New module' above to add one." : "No modules yet."}
            </div>
          )}
          {modulesForSection.map((m) => (
            <button key={m.key} className={`mod-tab ${open === m.key ? "active" : ""}`} onClick={() => setOpen(m.key)}>
              {m.title}
            </button>
          ))}
        </div>
        <div className="mod-content panel">
          <div className="panel-head" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <h2 style={{ flex: 1 }}><BookOpen size={16} /> {mod?.title || "(select a module)"}</h2>
            {canEdit && mod && (
              <button className="btn-mini" onClick={() => setEditing(open)}>
                <Pencil size={13} /> Edit
              </button>
            )}
            {isNonRide && canEdit && mod && mod.source === "db" && (
              <button className="btn-mini" onClick={() => setManagingQuestions(mod)}>
                <FileQuestion size={13} /> Questions
              </button>
            )}
            {isNonRide && canAssign && mod && mod.source === "db" && (
              <button className="btn-mini" onClick={() => setManagingAssignments(mod)}>
                <UserPlus size={13} /> Assign
              </button>
            )}
          </div>
          {ModuleBody}
          {isNonRide && mod && (
            <div style={{ padding: "8px 20px 12px", fontSize: 12, color: "var(--txt3)", borderTop: "1px solid var(--line)", display: "flex", gap: 12, flexWrap: "wrap" }}>
              {mod.has_quiz && <span>📝 Quiz ({mod.quiz_question_count || 10} questions, 80% pass)</span>}
              {mod.requires_practical && <span>🛠 Practical Walk-Through ({(mod.practical_items || []).length} items)</span>}
              {mod.requires_competency_verification && <span>👥 Competency Verification required</span>}
              {mod.document_path && <span>📄 PDF attachment</span>}
            </div>
          )}
        </div>
      </div>
      {(editing || creating) && (
        <ModuleEditModal
          moduleKey={creating ? makeNewModuleKey(activeSection) : editing}
          initial={
            creating
              ? { section: activeSection, title: "", body_md: "" }
              : (moduleForKey(editing, trainingModules) || modulesForSection.find((m) => m.key === editing))
          }
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); onChanged(); reloadNonRide(); }}
        />
      )}
      {managingQuestions && (
        <ModuleQuestionsModal
          training={managingQuestions}
          onClose={() => { setManagingQuestions(null); reloadNonRide(); }}
        />
      )}
      {managingAssignments && (
        <ModuleAssignModal
          training={managingAssignments}
          employees={employees}
          user={user}
          onClose={() => { setManagingAssignments(null); reloadNonRide(); }}
        />
      )}
      {bulkAssigning && (
        <BulkSectionAssignModal
          section={activeSection}
          sectionLabel={SECTIONS.find((x) => x.key === activeSection)?.label || activeSection}
          modules={modulesForSection}
          employees={employees}
          onClose={() => setBulkAssigning(false)}
          onDone={() => reloadNonRide()}
        />
      )}
    </div>
  );
}

/* Helper: look up a module by key across DB + legacy fallback */
function moduleForKey(k, trainingModules) {
  if (!k) return null;
  const dbMod = (trainingModules || []).find((m) => m.key === k);
  if (dbMod) return { source: "db", key: dbMod.key, title: dbMod.title, body_md: dbMod.body_md || "", ...dbMod };
  const legacy = MODULES[k];
  if (legacy) {
    return { source: "legacy", key: k, title: legacy.title, body_md: legacyToMarkdown(legacy) };
  }
  return { source: "missing", key: k, title: k, body_md: "_(no content yet)_" };
}

/* Generate a slug-like key for a new module — timestamp-suffixed to avoid collisions
   with other modules being created around the same time */
function makeNewModuleKey(section) {
  return `${section}_${Date.now().toString(36)}`;
}

/* Lightweight markdown renderer. Supports headings (#/##/###), bullets,
   numbered lists, blockquotes (incl. nested), bold (**text**), italic (*text*),
   and paragraph breaks. No external dependencies. */
function Markdown({ source }) {
  const blocks = React.useMemo(() => mdParseBlocks(source || ""), [source]);
  return <div className="md">{blocks.map((b, i) => mdRender(b, i))}</div>;
}

function mdParseBlocks(src) {
  // Split by blank lines but keep blockquote/list groups together.
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  let guard = 0;

  // Peek ahead past blank lines: returns next non-blank line index, or -1
  const peekNextNonBlank = (from) => {
    let j = from;
    while (j < lines.length && !lines[j].trim()) j++;
    return j < lines.length ? j : -1;
  };

  // Detect a numbered or lettered list line at given indent. Returns null or { type, indent, text }
  const detectListLine = (line) => {
    if (!line) return null;
    // Number list: "1. text"  optionally indented
    let m = line.match(/^(\s*)(\d+)\.\s+(.*)$/);
    if (m) return { type: "ol", indent: m[1].length, text: m[3] };
    // Letter list: "a. text" or "A. text"  optionally indented
    m = line.match(/^(\s*)([a-zA-Z])\.\s+(.*)$/);
    if (m) return { type: "ol-alpha", indent: m[1].length, text: m[3] };
    // Bullet list: "- text" or "* text" optionally indented
    m = line.match(/^(\s*)[-*]\s+(.*)$/);
    if (m) return { type: "ul", indent: m[1].length, text: m[2] };
    return null;
  };

  // Parse a contiguous block of list lines into a nested-list tree.
  // Returns { node, nextI } where node is { kind, items: [{ text, children }] }
  // Auto-promotion: if rootType is 'ol' and we encounter same-indent 'ol-alpha' items,
  // they get attached as children of the most recent numbered item (no explicit indent needed).
  const parseListBlock = (startI) => {
    const firstDet = detectListLine(lines[startI]);
    if (!firstDet) return { node: null, nextI: startI };
    const rootType = firstDet.type; // ol, ol-alpha, or ul
    const rootIndent = firstDet.indent;
    const items = [];
    let i = startI;
    while (i < lines.length) {
      // Allow blank lines inside a list — they don't terminate it, as long as the next non-blank
      // line is still a list item at the same or deeper indent (OR an auto-promotable type).
      if (!lines[i].trim()) {
        const next = peekNextNonBlank(i);
        if (next < 0) break;
        const nd = detectListLine(lines[next]);
        if (!nd) break;
        if (nd.indent < rootIndent) break;
        // Auto-promote check: lettered items after a numbered list ARE allowed even at root indent
        if (nd.indent === rootIndent && nd.type !== rootType) {
          const isAutoPromote = rootType === "ol" && nd.type === "ol-alpha";
          if (!isAutoPromote) break;
        }
        i = next;
        continue;
      }
      const det = detectListLine(lines[i]);
      if (!det) break;
      if (det.indent < rootIndent) break;
      if (det.indent > rootIndent) {
        // Explicit nested list — attach as children of the last item
        const child = parseListBlock(i);
        if (items.length > 0) items[items.length - 1].children.push(child.node);
        i = child.nextI;
        continue;
      }
      // Same indent — check if type matches root, or if auto-promote applies
      if (det.type !== rootType) {
        // Auto-promote: lettered items inside a numbered list at root indent
        // become children of the most recent numbered item
        if (rootType === "ol" && det.type === "ol-alpha" && items.length > 0) {
          const child = parseListBlock(i); // will collect this and any following ol-alpha lines
          items[items.length - 1].children.push(child.node);
          i = child.nextI;
          continue;
        }
        break;
      }
      items.push({ text: det.text, children: [] });
      i++;
    }
    return { node: { kind: rootType, items }, nextI: i };
  };

  while (i < lines.length) {
    const startI = i;
    if (++guard > 200000) { console.warn("md parse guard tripped"); break; }
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    // Heading
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) { blocks.push({ kind: "heading", level: h[1].length, text: h[2] }); i++; continue; }
    // Blockquote (collect consecutive > lines)
    if (line.startsWith(">")) {
      const buf = [];
      while (i < lines.length && lines[i].startsWith(">")) {
        buf.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ kind: "quote", text: buf.join("\n") });
      continue;
    }
    // Any list type (handled by the nested-list parser; supports blank lines within)
    const det = detectListLine(line);
    if (det) {
      const lst = parseListBlock(i);
      if (lst.node) {
        blocks.push(lst.node);
        i = lst.nextI;
        continue;
      }
    }
    // Paragraph: collect the current line PLUS any continuation lines that aren't block-starters.
    const buf = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^(#{1,6}\s|>|\s*[-*]\s|\s*\d+\.\s|\s*[a-zA-Z]\.\s)/.test(lines[i])) {
      buf.push(lines[i]);
      i++;
    }
    blocks.push({ kind: "p", text: buf.join(" ") });
    if (i === startI) i++;
  }
  return blocks;
}

function mdInline(text) {
  // Bold then italic. Returns React nodes.
  const parts = [];
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let last = 0;
  let m;
  let idx = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[2] !== undefined) parts.push(<strong key={`b${idx}`}>{m[2]}</strong>);
    else if (m[3] !== undefined) parts.push(<em key={`i${idx}`}>{m[3]}</em>);
    last = re.lastIndex;
    idx++;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}

function mdRender(b, key) {
  if (b.kind === "heading") {
    const H = `h${Math.min(b.level + 1, 4)}`; // h1 in md → h2 visually (h1 is page title)
    return React.createElement(H, { key, className: `md-h md-h${b.level}` }, mdInline(b.text));
  }
  if (b.kind === "p") return <p className="md-p" key={key}>{mdInline(b.text)}</p>;
  // List items now support nested children. Render recursively.
  if (b.kind === "ul" || b.kind === "ol" || b.kind === "ol-alpha") {
    const Tag = b.kind === "ul" ? "ul" : "ol";
    const className = b.kind === "ul" ? "md-ul" : b.kind === "ol-alpha" ? "md-ol md-ol-alpha" : "md-ol";
    return (
      <Tag className={className} key={key}>
        {b.items.map((it, idx) => (
          <li key={idx}>
            {mdInline(typeof it === "string" ? it : it.text)}
            {it && Array.isArray(it.children) && it.children.length > 0 && (
              <div className="md-li-children">{it.children.map((ch, ci) => mdRender(ch, ci))}</div>
            )}
          </li>
        ))}
      </Tag>
    );
  }
  if (b.kind === "quote") {
    // Recursively parse the quote content so nested formatting works
    const inner = mdParseBlocks(b.text);
    return <blockquote className="md-quote" key={key}>{inner.map((ib, i) => mdRender(ib, i))}</blockquote>;
  }
  return null;
}

/* Legacy → markdown converter: used when DB row is missing for a module
   (defensive fallback so we never show a blank page). */
function legacyToMarkdown(mod) {
  const lines = [];
  if (mod.intro) { lines.push(mod.intro); lines.push(""); }
  (mod.sections || []).forEach((sec) => {
    lines.push("## " + sec.heading);
    lines.push("");
    (sec.points || []).forEach((p) => lines.push("- " + p));
    lines.push("");
    (sec.detail || []).forEach((d) => {
      if (d.type === "prose") lines.push(d.text, "");
      else if (d.type === "steps") {
        if (d.title) lines.push("**" + d.title + ":**");
        (d.items || []).forEach((it, i) => lines.push((i + 1) + ". " + it));
        lines.push("");
      } else if (d.type === "checklist") {
        if (d.title) lines.push("**" + d.title + ":**");
        (d.items || []).forEach((it) => lines.push("- [ ] " + it));
        lines.push("");
      } else if (d.type === "warn") {
        lines.push("> ⚠️ **Warning:** " + d.text, "");
      } else if (d.type === "script") {
        lines.push("**Safety script:**");
        (d.lines || []).forEach((l) => lines.push("> " + l));
        lines.push("");
      } else if (d.type === "toolkit") {
        lines.push("**Talking point toolkit:**");
        (d.lines || []).forEach((l) => lines.push("> *" + l + "*"));
        lines.push("");
      }
    });
  });
  return lines.join("\n").trim();
}

/* ============================================================
   MODULE EDIT MODAL (admin only)
   ============================================================ */
function ModuleEditModal({ moduleKey, initial, onClose, onSaved }) {
  // Base content fields
  const [title, setTitle] = useState(initial?.title || "");
  const [body, setBody] = useState(initial?.body_md || "");
  const [description, setDescription] = useState(initial?.description || "");
  // Section: 'ride' | 'service_tech' | 'restaurant' | 'general'
  // Ride modules are locked to 'ride' since they map to hardcoded ride keys.
  // Detection is KEY-BASED only. Don't infer from initial.section because that
  // field may be missing on legacy rows loaded via loadTrainingModules; using
  // section as a signal caused non-ride modules to be misidentified as ride
  // modules when they were loaded without the section column populated.
  const isRideModule = !!RIDE_MAP[moduleKey] || moduleKey === "_general";
  const [section, setSection] = useState(
    initial?.section || (isRideModule ? "ride" : "general")
  );
  // Non-ride workflow fields
  const [hasQuiz, setHasQuiz] = useState(initial?.has_quiz ?? false);
  const [quizCount, setQuizCount] = useState(initial?.quiz_question_count ?? 10);
  const [requiresPractical, setRequiresPractical] = useState(initial?.requires_practical ?? false);
  const [practicalItemsText, setPracticalItemsText] = useState(
    (initial?.practical_items || []).map((it) => (typeof it === "string" ? it : (it.text || it.label || ""))).join("\n")
  );
  const [requiresVerification, setRequiresVerification] = useState(initial?.requires_competency_verification ?? false);
  const [dueDays, setDueDays] = useState(initial?.due_days_after_assignment ?? 14);
  const [docFile, setDocFile] = useState(null);
  const [docPath, setDocPath] = useState(initial?.document_path || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const showNonRideFields = section !== "ride";

  const save = async () => {
    setErr("");
    if (!title.trim()) { setErr("Title is required."); return; }
    setBusy(true);
    try {
      // Upload optional PDF first if a new one was chosen
      let finalDocPath = docPath;
      if (docFile) {
        const upRes = await db.uploadModuleDoc(docFile, moduleKey);
        if (!upRes.ok) { setErr("Upload failed: " + upRes.message); setBusy(false); return; }
        finalDocPath = upRes.path;
      }
      // Build patch. Include the extended fields only when relevant.
      const patch = {
        title: title.trim(),
        body_md: body,
        section,
        description: description.trim() || null,
      };
      if (showNonRideFields) {
        patch.has_quiz = hasQuiz;
        patch.quiz_question_count = parseInt(quizCount, 10) || 10;
        patch.requires_practical = requiresPractical;
        patch.practical_items = requiresPractical
          ? practicalItemsText.split("\n").map((s) => s.trim()).filter(Boolean).map((text) => ({ text }))
          : [];
        patch.requires_competency_verification = requiresVerification;
        patch.due_days_after_assignment = parseInt(dueDays, 10) || 14;
        patch.document_path = finalDocPath;
      }
      const res = await db.updateTrainingModule(moduleKey, patch);
      if (!res.ok) {
        // Row doesn't exist yet — create it via direct insert. Include the section-appropriate fields.
        const insertPayload = { key: moduleKey, ...patch, sort_order: 0 };
        const insert = await supabase.from("training_modules").insert(insertPayload).select().single();
        if (insert.error) { setErr(insert.error.message || res.message); setBusy(false); return; }
      }
      onSaved();
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  };

  return (
    <Modal onClose={onClose} wide>
      <div className="modal-head">
        <h3><Pencil size={18} /> Edit module — {moduleKey}</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <label className="field"><span>Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="field"><span>Section</span>
          <select value={section} onChange={(e) => setSection(e.target.value)} disabled={isRideModule}>
            <option value="ride">Ride Training</option>
            <option value="ride_ops">Ride Operations (all rides)</option>
            <option value="restaurant">Restaurant Training</option>
            <option value="leadership">Leadership & Training</option>
            <option value="general">General Training</option>
            <option value="service_tech">Service Tech Training</option>
          </select>
          {isRideModule && <p className="sched-target" style={{ marginTop: 4 }}>Ride modules are locked to Ride Training and can't be moved.</p>}
        </label>
        {showNonRideFields && (
          <>
            <label className="field"><span>Description (one line, shown on the module card)</span>
              <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this training covers" />
            </label>
          </>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <label style={{ fontSize: 12, color: "var(--txt3)", textTransform: "uppercase", letterSpacing: 0.5 }}>Body (markdown)</label>
          <button type="button" className="btn-mini ghost" onClick={() => setShowPreview((v) => !v)}>
            {showPreview ? "Edit" : "Preview"}
          </button>
        </div>
        {showPreview ? (
          <div style={{ minHeight: 400, maxHeight: "60vh", overflowY: "auto", border: "1px solid var(--line)", borderRadius: 6, padding: "12px 16px", background: "var(--panel2)" }}>
            <Markdown source={body} />
          </div>
        ) : (
          <textarea value={body} onChange={(e) => setBody(e.target.value)}
            placeholder="Markdown content..."
            style={{
              width: "100%", minHeight: 400, maxHeight: "60vh",
              padding: 12, fontFamily: "Spline Sans Mono, monospace", fontSize: 12, lineHeight: 1.6,
              background: "var(--panel2)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 6, resize: "vertical",
            }} />
        )}
        <p style={{ fontSize: 11, color: "var(--txt3)", marginTop: 6 }}>
          Supported: <code>## Heading</code>, <code>- bullet</code>, <code>1. numbered</code>, <code>**bold**</code>, <code>*italic*</code>, <code>&gt; blockquote</code>
        </p>

        {showNonRideFields && (
          <div style={{ marginTop: 16, padding: 12, background: "var(--panel2)", borderRadius: 6, border: "1px solid var(--line)" }}>
            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--txt3)", marginBottom: 10 }}>
              Assignment & workflow
            </div>

            <label className="field"><span>Optional PDF attachment (supplementary reference)</span>
              <input type="file" accept="application/pdf" onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
              {docPath && !docFile && <p className="dim" style={{ fontSize: 12, marginTop: 4 }}>Current: {docPath}</p>}
              {docFile && <p className="dim" style={{ fontSize: 12, marginTop: 4 }}>New: {docFile.name} ({Math.round(docFile.size / 1024)} KB)</p>}
            </label>

            <div style={{ display: "flex", gap: 12 }}>
              <label className="field" style={{ flex: 1 }}><span>Due days after assignment</span>
                <input type="number" min="1" max="365" value={dueDays} onChange={(e) => setDueDays(e.target.value)} />
              </label>
              <label className="field" style={{ flex: 1 }}><span>Quiz questions to draw</span>
                <input type="number" min="1" max="50" value={quizCount} onChange={(e) => setQuizCount(e.target.value)} disabled={!hasQuiz} />
              </label>
            </div>

            <label className="check-row">
              <input type="checkbox" checked={hasQuiz} onChange={(e) => setHasQuiz(e.target.checked)} />
              <span>Has quiz (80% pass, unlimited retakes)</span>
            </label>
            <label className="check-row">
              <input type="checkbox" checked={requiresPractical} onChange={(e) => setRequiresPractical(e.target.checked)} />
              <span>Requires Practical Walk-Through (trainer demonstrates then checks off items)</span>
            </label>
            {requiresPractical && (
              <label className="field"><span>Practical items (one per line)</span>
                <textarea
                  value={practicalItemsText}
                  onChange={(e) => setPracticalItemsText(e.target.value)}
                  rows={6}
                  placeholder={"Show location of PPE storage\nDemonstrate proper use of hearing protection\n..."}
                  style={{ width: "100%", padding: 8, background: "var(--panel)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 6, fontSize: 13, fontFamily: "inherit", resize: "vertical" }}
                />
              </label>
            )}
            <label className="check-row">
              <input type="checkbox" checked={requiresVerification} onChange={(e) => setRequiresVerification(e.target.checked)} />
              <span>Requires Competency Verification after {hasQuiz ? "quiz passes" : "document is read"}</span>
            </label>
          </div>
        )}

        {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
      </div>
    </Modal>
  );
}


function QuizModal({ quiz, user, onClose, onSubmit }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [attesting, setAttesting] = useState(false);
  const allAnswered = quiz.questions.every((q) => answers[q.id] !== undefined);

  // Grade + close out. `attestation` is null when the quiz was already attested
  // on a previous attempt (stored once per quiz).
  const finish = (attestation) => {
    setAttesting(false);
    setResult(onSubmit(answers, attestation));
  };

  // Answers are locked in first; the trainee then signs the certification
  // statement before anything is graded.
  const handleSubmit = () => {
    if (quiz.attestation) finish(null);
    else setAttesting(true);
  };

  return (
    <Modal onClose={onClose} wide>
      <div className="modal-head">
        <h3>
          <FileQuestion size={18} />{" "}
          {attesting
            ? `${RIDE_MAP[quiz.ride].label} — Certification Statement`
            : `${RIDE_MAP[quiz.ride].label} Certification Quiz`}
        </h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>

      {attesting && !result ? (
        <QuizAttestationGate
          user={user}
          onCancel={() => setAttesting(false)}
          onAttest={finish}
        />
      ) : !result ? (
        <>
          <div className="modal-body quiz-body">
            <p className="quiz-intro">For <strong>{quiz.empName}</strong> · target {quiz.targetLevel} · pass mark {PASS_SCORE}%</p>
            {quiz.questions.map((q, qi) => (
              <div className="quiz-q" key={q.id}>
                <div className="quiz-q-text"><span className="quiz-num">{qi + 1}</span>{q.q}</div>
                <div className="quiz-opts">
                  {q.choices.map((opt, oi) => (
                    <label key={oi} className={`quiz-opt ${answers[q.id] === oi ? "picked" : ""}`}>
                      <input type="radio" name={q.id} checked={answers[q.id] === oi}
                        onChange={() => setAnswers({ ...answers, [q.id]: oi })} />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="modal-foot">
            <button className="btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary" disabled={!allAnswered} onClick={handleSubmit}>Submit quiz</button>
          </div>
        </>
      ) : (
        <div className="modal-body quiz-result">
          <div className={`result-badge ${result.kind === "fail" ? "fail" : result.kind === "full" ? "pass" : "gaps"}`}>
            {result.kind === "fail" ? <X size={40} /> : result.kind === "full" ? <Check size={40} /> : <AlertCircle size={40} />}
          </div>
          <h2>{result.scoreVal}%</h2>
          {result.kind === "full" && (
            <>
              <p className="result-msg pass">Perfect score! {quiz.empName} passed {RIDE_MAP[quiz.ride].label}.</p>
              <p className="result-sub">An <strong>AGM/GM sign-off</strong> is now required before advancing to {quiz.targetLevel}. It's in the Sign-offs queue and the manager has been notified.</p>
            </>
          )}
          {result.kind === "gaps" && (
            <>
              <p className="result-msg gaps">Passed at {result.scoreVal}% — above the 80% mark but not perfect.</p>
              <p className="result-sub">Topics you missed: <strong>{result.missed.map((t) => topicLabel(quiz.ride, t)).join(", ")}</strong>. Your manager will go over them with you during your L2 observation.</p>
            </>
          )}
          {result.kind === "fail" && (
            <>
              <p className="result-msg fail">Below the {PASS_SCORE}% pass mark — status set to <strong>Retrain (RT1)</strong>.</p>
              <p className="result-sub">{quiz.empName} must be retrained before another attempt. The manager has been notified. Missed topics: {result.missed.map((t) => topicLabel(quiz.ride, t)).join(", ")}.</p>
            </>
          )}
          <button className="btn-primary" onClick={onClose}>Done</button>
        </div>
      )}
    </Modal>
  );
}

function ScheduleModal({ emp, trainers, onClose, onAdd }) {
  const opts = RIDES.map((r) => ({ ride: r.key, current: emp.levels[r.key], next: nextLevel(emp.levels[r.key], r.key) })).filter((o) => o.next);
  const [ride, setRide] = useState(opts[0]?.ride || "");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const sel = opts.find((o) => o.ride === ride);

  return (
    <Modal onClose={onClose}>
      <div className="modal-head">
        <h3><Calendar size={18} /> Schedule training — {emp.name}</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <label className="field">
          <span>Ride / Skill</span>
          <select value={ride} onChange={(e) => setRide(e.target.value)}>
            {opts.map((o) => <option key={o.ride} value={o.ride}>{RIDE_MAP[o.ride].label} ({o.current} → {o.next})</option>)}
          </select>
        </label>
        {sel && <p className="sched-target">Target: <strong>{sel.next}</strong> — {stepAction(sel.next, sel.ride || ride)}</p>}
        <label className="field"><span>Date</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label className="field">
          <span>Assign to trainer (optional)</span>
          <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
            <option value="">— Unassigned —</option>
            {(trainers || []).map((t) => (
              <option key={t.id} value={t.id}>{t.full_name} ({ROLES[t.role]?.label || t.role})</option>
            ))}
          </select>
        </label>
        <label className="field"><span>Note (optional)</span><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Shift, location, etc." /></label>
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!ride || !sel} onClick={() => sel && onAdd(ride, sel.next, date, note, assignedTo || null)}>Add to schedule</button>
      </div>
    </Modal>
  );
}

function RideDetailModal({ rideKey, employees, onClose, onSignoff, onStart }) {
  const ride = RIDE_MAP[rideKey];
  // group employees by level for this ride, sorted highest rank first; "0" (not in training) last
  const rows = employees
    .map((e) => ({ emp: e, lv: e.levels[rideKey] }))
    .sort((a, b) => {
      const ra = LEVELS[a.lv]?.rank ?? 0, rb = LEVELS[b.lv]?.rank ?? 0;
      if (rb !== ra) return rb - ra;
      return a.emp.name.localeCompare(b.emp.name);
    });
  const active = rows.filter((r) => r.lv !== "0");
  const inactive = rows.filter((r) => r.lv === "0");

  return (
    <Modal onClose={onClose} wide>
      <div className="modal-head">
        <h3><LayoutDashboard size={18} /> {ride.label} — Crew Status</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <div className="ride-ladder">
          Levels: {ride.ladder.map((lv, i) => (
            <span key={lv}>
              <span className="cell-badge sm" style={{ background: LEVELS[lv].color }}>{LEVELS[lv].label}</span>
              {isTrainer(lv, rideKey) && <em className="ladder-tag">trainer</em>}
              {i < ride.ladder.length - 1 && <ArrowRight size={12} className="ladder-arrow" />}
            </span>
          ))}
        </div>

        <div className="ride-list">
          {active.map((r) => {
            const ride = RIDE_MAP[rideKey];
            // Tracks group L2 operates independently but should be paired with L3+ on shift.
            // Kiddie Land L2 is fully solo.
            const isTracksL2 = ride?.group === "tracks" && r.lv === "L2";
            const label = isTrainer(r.lv, rideKey) ? "Can train"
                        : isTracksL2 ? "With L3+"
                        : isIndependent(r.lv) ? "Solo"
                        : "Shadowing";
            return (
              <div className="ride-row" key={r.emp.id}>
                <span className="cell-badge sm" style={{ background: LEVELS[r.lv].color }}>{LEVELS[r.lv].label}</span>
                <span className="ride-name">{r.emp.name}</span>
                <span className="ride-meta">{label}</span>
                <button className="btn-mini ghost" onClick={() => onSignoff(r.emp)}>Sign off</button>
              </div>
            );
          })}
          {inactive.length > 0 && (
            <>
              <div className="ride-divider">Not in training</div>
              {inactive.map((r) => (
                <div className="ride-row muted" key={r.emp.id}>
                  <span className="cell-badge sm" style={{ background: LEVELS["0"].color }}>—</span>
                  <span className="ride-name">{r.emp.name}</span>
                  <span className="ride-meta"></span>
                  <button className="btn-mini ghost" onClick={() => onStart(r.emp)}>Start</button>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ============================================================
   MANAGE EMPLOYEE (level change / retrain / archive / delete)
   ============================================================ */
function ManageEmployeeModal({ emp, user, onClose, onChangeName, onChangeLevel, onOverrideLevel, onChangeDept, onChangeJobTitle, onChangeStartDate, onArchive, onDelete, onRetrain }) {
  const [ride, setRide] = useState(RIDES[0].key);
  const r = RIDE_MAP[ride];
  const cur = emp.levels[ride];
  const inRT = emp.retrain && emp.retrain[ride];
  const levelOpts = ["0", ...r.ladder];
  const canEditDept = roleRank(user.role) >= roleRank("manager");
  const canOverride = roleRank(user.role) >= roleRank("agm_gm");
  const [showOverride, setShowOverride] = useState(false);
  const [overrideLvl, setOverrideLvl] = useState(cur || "T");
  const [overrideNote, setOverrideNote] = useState("");

  return (
    <Modal onClose={onClose} wide>
      <div className="modal-head">
        <h3><ShieldCheck size={18} /> Manage — {emp.name}</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <div className="manage-perm">Signed in as <strong>{user.name}</strong> — {ROLES[user.role].label}</div>

        {canEditDept && (
          <label className="field"><span>Employee name <span className="dim" style={{ fontWeight: 400 }}>(for correcting spelling / preferred name)</span></span>
            <input
              type="text"
              defaultValue={emp.name}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (!val) { e.target.value = emp.name; return; }
                if (val !== emp.name) {
                  if (confirm(`Rename "${emp.name}" to "${val}"?\n\nThis changes the name everywhere — past records, sign-offs, and reports.`)) {
                    onChangeName(val);
                  } else {
                    e.target.value = emp.name;
                  }
                }
              }}
              placeholder="Full name"
            />
          </label>
        )}

        {canEditDept && (
          <label className="field"><span>Department</span>
            <select value={emp.department || "Attractions"} onChange={(e) => onChangeDept(e.target.value)}>
              <option value="Attractions">Attractions</option>
              <option value="Restaurant">Restaurant</option>
              <option value="Kart & Ride Service Tech">Kart & Ride Service Tech</option>
            </select>
            <p className="sched-target" style={{ marginTop: 4 }}>Non-Attractions: auto-progression won't create new steps automatically until they're manually assigned a ride.</p>
          </label>
        )}

        {canEditDept && (
          <label className="field"><span>Job title <span className="dim" style={{ fontWeight: 400 }}>(for City of Las Vegas employee listing)</span></span>
            <input
              type="text"
              defaultValue={emp.jobTitle || "Ride Attendant"}
              onBlur={(e) => {
                const val = e.target.value.trim() || "Ride Attendant";
                if (val !== (emp.jobTitle || "Ride Attendant")) onChangeJobTitle(val);
              }}
              placeholder="Ride Attendant"
            />
            <p className="sched-target" style={{ marginTop: 4 }}>Defaults to "Ride Attendant". Used on regulatory forms like Form PL202 (City of Las Vegas Employee Listing).</p>
          </label>
        )}

        {canEditDept && (
          <label className="field"><span>Start date</span>
            <input
              type="date"
              defaultValue={emp.startDate || ""}
              onBlur={(e) => {
                const val = e.target.value || null;
                if (val !== (emp.startDate || null)) onChangeStartDate(val);
              }}
            />
            <p className="sched-target" style={{ marginTop: 4 }}>Used on reports and tenure calculations. Format: YYYY-MM-DD.</p>
          </label>
        )}

        <label className="field"><span>Ride / Skill</span>
          <select value={ride} onChange={(e) => setRide(e.target.value)}>
            {RIDES.map((rr) => <option key={rr.key} value={rr.key}>{rr.label} — currently {LEVELS[emp.levels[rr.key]].label}</option>)}
          </select>
        </label>

        {can.changeLevel(user) ? (
          <div className="manage-block">
            <div className="mb-title">Change level (AGM+)</div>
            <div className="level-btns">
              {levelOpts.map((lv) => (
                <button key={lv} className={`lvl-btn ${cur === lv ? "active" : ""}`} disabled={cur === lv}
                  onClick={() => onChangeLevel(ride, lv)} style={{ borderColor: LEVELS[lv].color }}>
                  {LEVELS[lv].label}
                </button>
              ))}
            </div>
            <p className="mb-note">Changes are audit-logged to the employee's history.</p>
          </div>
        ) : can.startTraining(user) ? (
          <div className="manage-block">
            <div className="mb-title">Start training (Manager+)</div>
            {cur === "0" ? (
              <>
                <p className="mb-note" style={{ marginTop: 0 }}>
                  Move <strong>{emp.name}</strong> into Trainee status on <strong>{RIDE_MAP[ride].label}</strong>. This starts the training process and auto-creates the T → L2 step.
                </p>
                <button
                  className="btn-primary"
                  onClick={() => onChangeLevel(ride, "T")}
                  style={{ marginTop: 8 }}
                >
                  Move to Trainee (T)
                </button>
              </>
            ) : (
              <p className="mb-note" style={{ marginTop: 0 }}>
                {emp.name} is already at <strong>{LEVELS[cur].label}</strong> on {RIDE_MAP[ride].label}. Higher-level changes require AGM+ access.
              </p>
            )}
          </div>
        ) : <div className="login-err"><Lock size={14} /> Level changes require AGM+ access.</div>}

        {canOverride && (
          <div className="manage-block override-block">
            <div className="mb-title">
              <span style={{ color: "var(--gold)" }}>Override Level — Migration Entry (AGM+)</span>
            </div>
            <p className="mb-note" style={{ marginTop: 0 }}>
              Use this when an employee was <strong>already certified</strong> on this ride before the system was set up, and you're entering their current state.
              Unlike the regular Change Level, this bypasses the progression chain (no auto-creation of new training steps) <strong>and cancels any pending steps at or below the chosen level</strong> — useful when entering an L2 status causes a duplicate T→L2 training to be created.
            </p>
            {!showOverride ? (
              <button className="btn-ghost" style={{ borderColor: "var(--gold)", color: "var(--gold)" }} onClick={() => { setShowOverride(true); setOverrideLvl(cur || "T"); setOverrideNote(""); }}>
                Override level for {r.label}…
              </button>
            ) : (
              <div className="override-form">
                <label className="field">
                  <span>Set {r.label} level to:</span>
                  <select value={overrideLvl} onChange={(e) => setOverrideLvl(e.target.value)}>
                    {levelOpts.map((lv) => <option key={lv} value={lv}>{LEVELS[lv].label} — {LEVELS[lv].desc}</option>)}
                  </select>
                </label>
                <label className="field">
                  <span>Reason / source (optional, for audit trail)</span>
                  <input type="text" value={overrideNote} onChange={(e) => setOverrideNote(e.target.value)} placeholder="e.g. Migrated from prior whiteboard records on 2026-06-04" />
                </label>
                <div style={{ background: "var(--panel)", padding: 10, borderRadius: 6, fontSize: 12, marginBottom: 10, border: "1px solid var(--gold)" }}>
                  <strong style={{ color: "var(--gold)" }}>What this will do:</strong>
                  <ul style={{ margin: "6px 0 0 18px", padding: 0, color: "var(--txt2)" }}>
                    <li>Set <strong>{r.label}</strong> level to <strong>{LEVELS[overrideLvl].label}</strong> (currently {LEVELS[cur || "0"].label})</li>
                    <li>Cancel any pending training steps, sign-offs, and quizzes for <strong>{r.label}</strong> at or below <strong>{LEVELS[overrideLvl].label}</strong></li>
                    <li>Record this as a "migration override" entry in the employee's history</li>
                    <li>Will <em>not</em> auto-create new training steps for higher levels or other rides</li>
                  </ul>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn-ghost" onClick={() => setShowOverride(false)}>Cancel</button>
                  <button className="btn-primary" style={{ background: "var(--gold)", color: "#1a1008" }}
                    disabled={overrideLvl === cur}
                    onClick={() => {
                      if (confirm(`Override ${r.label} level to ${LEVELS[overrideLvl].label} for ${emp.name}?\n\nThis will cancel any pending steps at or below this level. This action is logged but cannot be undone via the UI.`)) {
                        onOverrideLevel(ride, overrideLvl, overrideNote.trim());
                        setShowOverride(false);
                      }
                    }}>
                    Apply override
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {can.setRetrain(user) && (
          <div className="manage-block">
            <div className="mb-title">Retrain status (Manager+)</div>
            {inRT ? (
              <p className="mb-note">Currently in <strong>RT ({inRT.level})</strong> on {r.label} — opened by {inRT.openedBy}. Needs 2 consecutive passing observations within 30 days to clear (record them via Observe). {inRT.needDifferentAgm ? `A re-observation by a DIFFERENT AGM+ than ${inRT.needDifferentAgm.name} is required.` : ""}</p>
            ) : (
              <div className="level-btns">
                {r.ladder.filter((l) => l !== "T").map((lv) => (
                  <button key={lv} className="lvl-btn rt" onClick={() => onRetrain(ride, "RT-" + lv)}>Place in RT ({lv})</button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="manage-block">
          <div className="mb-title">Activation status</div>
          <div className="manage-actions">
            {can.archive(user) ? (
              <button className={`btn-ghost ${emp.archived ? "" : "warn-btn"}`} onClick={() => {
                const msg = emp.archived
                  ? `Reactivate ${emp.name}? This will restore their login.`
                  : `Deactivate ${emp.name}? This will disable their login. Their roster record and training history are preserved. You can reactivate later.`;
                if (confirm(msg)) onArchive(!emp.archived);
              }}>
                {emp.archived ? "Reactivate employee" : "Deactivate employee"}
              </button>
            ) : <span className="mb-note"><Lock size={13} /> Deactivation requires AGM+.</span>}
            {can.deleteRecord(user)
              ? <button className="btn-danger" onClick={onDelete}><Trash2 size={14} /> Permanently delete (Admin)</button>
              : <span className="mb-note"><Lock size={13} /> Permanent deletion is Admin-only. AGM+ uses Deactivate.</span>}
          </div>
          <p className="mb-note" style={{ marginTop: 8 }}>
            <strong>Deactivate:</strong> disables login, hides from active lists, preserves all records and history. Use when an employee leaves.<br />
            <strong>Permanent delete:</strong> removes the employee and ALL their records forever. Admin-only, no undo.
          </p>
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}

/* ============================================================
   OBSERVATION ENTRY (standard L2->L3, TtT, L3->L4, RT, or ad-hoc)
   ============================================================ */
/* ============================================================
   ADHOC OBSERVATION PICKER
   Selects employee → then ride → then opens the appropriate L2 obs modal
   (T→L2 or L2→L3 depending on the employee's current level on that ride)
   ============================================================ */
function AdhocObservationPicker({ employees, preselectedEmp, onPick, onClose }) {
  const [step, setStep] = useState(preselectedEmp ? "ride" : "employee");
  const [emp, setEmp] = useState(preselectedEmp || null);
  const [search, setSearch] = useState("");

  const filtered = (employees || [])
    .filter((e) => !e.archived)
    .filter((e) => e.name.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 25);

  const ridesForEmp = (employee) => {
    if (!employee) return [];
    return RIDES.map((r) => {
      const lvl = employee.levels?.[r.key] || "0";
      // For adhoc obs, surface rides where the employee is at T, L2, or L3 (so all trained or in-progress)
      // L4 employees too — observation is meaningful at any level
      return { key: r.key, label: r.label, level: lvl };
    });
  };

  if (step === "employee") {
    return (
      <Modal onClose={onClose}>
        <div className="modal-head">
          <h3><Eye size={18} /> New Ad-hoc Observation · Step 1 of 2</h3>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <p className="signoff-note">
            <HelpCircle size={14} /> Select an employee to observe. The checklist used will match their current level on the ride (T→L2 or L2→L3).
          </p>
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%", padding: 8, marginBottom: 10, background: "var(--panel2)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 6, fontSize: 14 }}
            autoFocus
          />
          <div style={{ maxHeight: "50vh", overflowY: "auto", border: "1px solid var(--line)", borderRadius: 6 }}>
            {filtered.length === 0 ? (
              <Empty text="No employees match that search." />
            ) : filtered.map((e) => (
              <button key={e.id}
                onClick={() => { setEmp(e); setStep("ride"); }}
                style={{
                  width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 12px", borderBottom: "1px solid var(--line)", background: "transparent",
                  color: "var(--txt)", cursor: "pointer", textAlign: "left",
                }}
                onMouseEnter={(ev) => ev.currentTarget.style.background = "var(--panel2)"}
                onMouseLeave={(ev) => ev.currentTarget.style.background = "transparent"}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{e.name}</div>
                  <div style={{ fontSize: 11, color: "var(--txt3)" }}>{e.role || "Crew"}</div>
                </div>
                <ChevronRight size={14} color="var(--txt3)" />
              </button>
            ))}
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </Modal>
    );
  }

  // step === "ride"
  const rides = ridesForEmp(emp);
  return (
    <Modal onClose={onClose}>
      <div className="modal-head">
        <h3><Eye size={18} /> Observe {emp.name} · Step 2 of 2</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <p className="signoff-note">
          <HelpCircle size={14} /> Pick the ride to observe {emp.name} on. The checklist used will be based on their current level on that ride.
        </p>
        <div style={{ maxHeight: "60vh", overflowY: "auto", border: "1px solid var(--line)", borderRadius: 6 }}>
          {rides.map((r) => {
            const lvl = r.level;
            // Determine which checklist will be used + what this observation will count toward
            let detail = "";
            let progression = "t_to_l2";
            if (lvl === "0") {
              detail = "Not yet trained — observation will use T→L2 checklist (informational)";
              progression = "t_to_l2";
            } else if (lvl === "T") {
              detail = "Currently at T — observation will use T→L2 checklist";
              progression = "t_to_l2";
            } else if (lvl === "L2") {
              detail = "Currently at L2 — observation will use L2→L3 checklist (counts toward L3 advancement)";
              progression = "l2_to_l3";
            } else {
              detail = `Already at ${lvl} — observation will use L2→L3 checklist (informational only)`;
              progression = "l2_to_l3";
            }
            return (
              <button key={r.key}
                onClick={() => onPick(emp, r.key, progression)}
                style={{
                  width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "12px", borderBottom: "1px solid var(--line)", background: "transparent",
                  color: "var(--txt)", cursor: "pointer", textAlign: "left",
                }}
                onMouseEnter={(ev) => ev.currentTarget.style.background = "var(--panel2)"}
                onMouseLeave={(ev) => ev.currentTarget.style.background = "transparent"}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{r.label}</div>
                  <div style={{ fontSize: 11, color: "var(--txt3)" }}>{detail}</div>
                </div>
                <span className={`tag tag-${lvl.toLowerCase()}`} style={{ fontSize: 11 }}>{lvl || "0"}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="modal-foot">
        {!preselectedEmp && <button className="btn-ghost" onClick={() => setStep("employee")}>← Back</button>}
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

function ObservationModal({ emp, user, preRide, onClose, onRecord }) {
  const [ride, setRide] = useState(preRide || RIDES[0].key);
  const [result, setResult] = useState("pass");
  const [note, setNote] = useState("");
  const [agmObserved, setAgmObserved] = useState(false);
  const [trainee, setTrainee] = useState("");
  const cur = emp.levels[ride];
  const inRT = emp.retrain && emp.retrain[ride];
  const allowed = can.observe(user);

  // Determine context from the employee's current state on this ride
  let context = "adhoc", slot = null, label = "Ad-hoc observation (no level effect)";
  if (inRT) { context = "rt"; label = `Retrain observation — RT (${inRT.level})`; }
  else if (cur === "L2" && ride === "kiddieKart") { context = "ttt"; label = "Train-the-Trainer session (Kiddie L2→L3)"; }
  else if (cur === "L2") { context = "obs"; label = "L2→L3 observation"; }
  else if (cur === "L3" && RIDE_MAP[ride].group === "tracks") { context = "ttt_l4"; label = "Train-the-Trainer observation (L3→L4)"; }

  // For standard L2->L3, choose which slot this observation fills
  const obsList = (emp.observations?.[ride] || []).filter((o) => !o.kind);
  const filled = new Set(obsList.filter((o) => o.result === "pass").map((o) => o.slot));
  // observers who already passed a slot for THIS set (used for soft warning + completion check)
  const priorObservers = new Set(obsList.filter((o) => o.result === "pass").map((o) => (o.observer?.name || "").toLowerCase()));
  const [chosenSlot, setChosenSlot] = useState(OBS_PLAN.find((p) => !filled.has(p.slot))?.slot || "trainer");

  // The current signed-in user has already observed this set. Previously this was
  // blocked; now it's allowed (some parks don't always have multiple AGMs on shift).
  // A soft notice still reminds them at least 2 distinct observers are needed for completion.
  const dupObserver = context === "obs" && priorObservers.has(user.name.toLowerCase()) && result === "pass";
  const wouldBeOnlyObserver = dupObserver && priorObservers.size === 1;

  // RT different-AGM guard
  const rtBlocked = context === "rt" && inRT.needDifferentAgm &&
    inRT.needDifferentAgm.name?.toLowerCase() === user.name.toLowerCase();

  const blocked = rtBlocked;  // dup-observer is no longer a hard block

  const submit = () => {
    if (blocked) return;
    const observer = { name: user.name, title: user.title, role: user.role };
    if (context === "obs") onRecord(ride, { context: "obs", slot: chosenSlot, result, observer, note });
    else if (context === "ttt") onRecord(ride, { context: "ttt", result, observer, traineeTrained: trainee, agmObserved: agmObserved || roleRank(user.role) >= roleRank("agm_gm"), note });
    else if (context === "ttt_l4") onRecord(ride, { context: "ttt_l4", result, observer, traineeTrained: trainee, agmObserved: agmObserved || roleRank(user.role) >= roleRank("agm_gm"), note });
    else if (context === "rt") onRecord(ride, { context: "rt", result, observer, note });
    else onRecord(ride, { context: "adhoc", slot: null, result, observer, note });
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-head">
        <h3><Eye size={18} /> Record observation — {emp.name}</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        {!allowed && <div className="login-err"><Lock size={14} /> Recording observations requires Trainer+ access.</div>}
        <label className="field"><span>Ride / Skill</span>
          <select value={ride} onChange={(e) => setRide(e.target.value)}>
            {RIDES.map((rr) => <option key={rr.key} value={rr.key}>{rr.label} — {LEVELS[emp.levels[rr.key]].label}{emp.retrain?.[rr.key] ? " · RT" : ""}</option>)}
          </select>
        </label>
        <div className="obs-context">{label}</div>

        {context === "obs" && (
          <label className="field"><span>Which observation slot</span>
            <select value={chosenSlot} onChange={(e) => setChosenSlot(e.target.value)}>
              {OBS_PLAN.map((p) => <option key={p.slot} value={p.slot} disabled={filled.has(p.slot)}>{p.label}{filled.has(p.slot) ? " (done)" : ""}</option>)}
            </select>
          </label>
        )}

        {(context === "ttt" || context === "ttt_l4") && (
          <>
            <label className="field"><span>Employee they trained</span>
              <input value={trainee} onChange={(e) => setTrainee(e.target.value)} placeholder="Trainee name" /></label>
            {roleRank(user.role) < roleRank("agm_gm") && (
              <label className="check-row"><input type="checkbox" checked={agmObserved} onChange={(e) => setAgmObserved(e.target.checked)} /> An AGM+ observed this session</label>
            )}
            {context === "ttt" && <p className="mb-note">Kiddie L2→L3 needs {TTT_SESSIONS_REQUIRED} passing Train-the-Trainer sessions; at least one with or observed by AGM+.</p>}
          </>
        )}

        {rtBlocked && <div className="login-err"><AlertCircle size={14} /> The last RT observation was failed by you. A DIFFERENT AGM+ must perform this re-observation.</div>}
        {dupObserver && <div className="obs-info-note" style={{ fontSize: 12, color: "var(--txt2)", padding: "8px 10px", background: "rgba(217,154,43,0.10)", border: "1px solid rgba(217,154,43,0.30)", borderRadius: 6, marginTop: 8 }}>
          <AlertCircle size={13} style={{ verticalAlign: "-2px" }} /> You've already recorded a passing observation for this set. This is allowed, but the final set still needs <strong>at least 2 distinct observers</strong> to complete{wouldBeOnlyObserver ? " — someone else will need to observe at least once before this employee can advance to L3" : ""}.
        </div>}

        <div className="field"><span>Result</span>
          <div className="result-toggle">
            <button className={result === "pass" ? "active pass" : ""} onClick={() => setResult("pass")}><Check size={14} /> Pass</button>
            <button className={result === "fail" ? "active fail" : ""} onClick={() => setResult("fail")}><X size={14} /> Fail</button>
          </div>
        </div>
        <label className="field"><span>Notes (optional)</span><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What you observed" /></label>
        <div className="signer-id"><ShieldCheck size={15} /> Observing as <strong>{user.name}</strong> — {user.title}</div>
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!allowed || blocked} onClick={submit}>Record observation</button>
      </div>
    </Modal>
  );
}

function AddEmployeeModal({ onClose, onAdd }) {
  const [name, setName] = useState("");
  const [date, setDate] = useState(todayISO());
  const [department, setDepartment] = useState("Attractions");
  return (
    <Modal onClose={onClose}>
      <div className="modal-head">
        <h3><UserPlus size={18} /> Add crew member</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <label className="field"><span>Name</span><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" autoFocus /></label>
        <label className="field"><span>Start date</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label className="field"><span>Department</span>
          <select value={department} onChange={(e) => setDepartment(e.target.value)}>
            <option value="Attractions">Attractions</option>
            <option value="Restaurant">Restaurant</option>
            <option value="Kart & Ride Service Tech">Kart & Ride Service Tech</option>
          </select>
        </label>
        <p className="sched-target">{department === "Attractions"
          ? "Attractions crew start at Level 0 across all rides and the system auto-creates their first training steps."
          : `${department} crew are added to the roster but won't get auto-created ride training. You can still manually assign training if cross-training is needed.`}</p>
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name.trim()} onClick={() => onAdd(name.trim(), date, department)}>Add crew</button>
      </div>
    </Modal>
  );
}

/* ============================================================
   QUIZ EDITOR (AGM/GM+)
   ============================================================ */
/* ============================================================
   REPORTS VIEW
   Workforce matrix, Per-employee history, Per-ride staffing,
   Stuck trainees, Expiring documents.
   - Manager+ can view all reports + export to CSV
   - AGM+ can additionally export to PDF (print)
   ============================================================ */
function ReportsView({ state, user, onChangeJobTitle }) {
  const [report, setReport] = useState("matrix"); // 'matrix' | 'employee' | 'ride' | 'stuck' | 'expiring'
  const canPdf = roleRank(user.role) >= roleRank("agm_gm");

  return (
    <div className="reports-view">
      <div className="reports-nav">
        <button className={`reports-tab ${report === 'matrix' ? 'active' : ''}`} onClick={() => setReport('matrix')}>Workforce matrix</button>
        <button className={`reports-tab ${report === 'employee' ? 'active' : ''}`} onClick={() => setReport('employee')}>Per-employee history</button>
        <button className={`reports-tab ${report === 'coverage' ? 'active' : ''}`} onClick={() => setReport('coverage')}>Ride coverage (point-in-time)</button>
        <button className={`reports-tab ${report === 'ride' ? 'active' : ''}`} onClick={() => setReport('ride')}>Per-ride staffing</button>
        <button className={`reports-tab ${report === 'stuck' ? 'active' : ''}`} onClick={() => setReport('stuck')}>Stuck trainees</button>
        <button className={`reports-tab ${report === 'certs' ? 'active' : ''}`} onClick={() => setReport('certs')}>Training certifications</button>
        <button className={`reports-tab ${report === 'policies' ? 'active' : ''}`} onClick={() => setReport('policies')}>Policy acknowledgments</button>
        <button className={`reports-tab ${report === 'expiring' ? 'active' : ''}`} onClick={() => setReport('expiring')}>Expiring documents</button>
        <button className={`reports-tab ${report === 'pl202' ? 'active' : ''}`} onClick={() => setReport('pl202')}>City of LV Employee Listing (PL202)</button>
      </div>
      <div className="reports-body">
        {report === 'matrix' && <WorkforceMatrixReport state={state} canPdf={canPdf} />}
        {report === 'employee' && <PerEmployeeHistoryReport state={state} canPdf={canPdf} />}
        {report === 'coverage' && <RideCoverageReport state={state} canPdf={canPdf} />}
        {report === 'ride' && <PerRideStaffingReport state={state} canPdf={canPdf} />}
        {report === 'stuck' && <StuckTraineesReport state={state} canPdf={canPdf} />}
        {report === 'certs' && <CertificationSignoffReport state={state} canPdf={canPdf} />}
        {report === 'policies' && <PolicyComplianceReport state={state} canPdf={canPdf} />}
        {report === 'expiring' && <ExpiringDocumentsReport state={state} canPdf={canPdf} />}
        {report === 'pl202' && <Pl202Report state={state} canPdf={canPdf} user={user} onChangeJobTitle={onChangeJobTitle} />}
      </div>
    </div>
  );
}

/* ----------------- CSV / PDF EXPORT HELPERS ----------------- */
/* ============================================================
   COMPLIANCE: Reconstruct an employee's certification level on a
   given ride as of a specific date by replaying events chronologically.

   Returns {
     level: "0" | "T" | "L2" | "L3" | "L4" | null,
     basis: "asserted" | "computed" | "no_records",
     basisDetail: <human readable description>,
     supportingEvents: [<events that established the level>]
   }

   Rules:
   - Sign-offs with status="complete" set the level to their targetLevel
   - L2→L3 observations: when the 4th passing observation from ≥2 distinct
     observers (and with ≥1 day spread) occurs, level becomes L3.
     (Simplified: any L2→L3 PASS observation event is treated as evidence;
      we count and apply the L3 transition when threshold is reached.)
   - T→L2 PASS observation: level becomes L2 if currently at T
   - Retrain events (history text containing "retrain") drop level back
     to T (best-effort heuristic)
   - If no events are found at all, basis = "no_records"
   ============================================================ */
function reconstructLevelAtDate(emp, rideKey, asOfDate, state) {
  if (!emp || !rideKey) return { level: null, basis: "no_records", basisDetail: "No employee or ride specified.", supportingEvents: [] };
  const cutoff = asOfDate;
  const inScope = (d) => (d || "").slice(0, 10) <= cutoff;

  // Collect ordered events
  const events = [];
  (state.signoffs || []).filter((s) => s.empId === emp.id && s.ride === rideKey && s.status === "complete").forEach((s) => {
    const sigDates = (s.signatures || []).map((sg) => sg.date).filter(Boolean).sort();
    const lastSig = sigDates[sigDates.length - 1];
    if (!lastSig || !inScope(lastSig)) return;
    events.push({ date: lastSig, kind: "signoff", target: s.targetLevel, signatures: s.signatures, raw: s });
  });
  (state.l2_observations || []).filter((o) => o.employee_id === emp.id && o.ride === rideKey).forEach((o) => {
    const date = (o.created_at || "").slice(0, 10);
    if (!inScope(date)) return;
    if ((o.outcome || "").toUpperCase() !== "PASS") return;
    events.push({ date, kind: "observation", progression: o.progression || "t_to_l2", observer: o.observer_name, raw: o });
  });
  (emp.history || []).forEach((h) => {
    if (!h.date || !inScope(h.date)) return;
    const t = (h.text || "").toLowerCase();
    if (!t.includes((RIDE_MAP[rideKey]?.label || rideKey).toLowerCase())) return;
    if (/retrain|reset|demoted|dropped/.test(t)) {
      events.push({ date: h.date, kind: "retrain", text: h.text, raw: h });
    }
  });

  events.sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  if (events.length === 0) {
    return { level: null, basis: "no_records", basisDetail: "No certification events on or before this date.", supportingEvents: [] };
  }

  // Replay
  let level = "0";
  const supporting = [];
  const l2tol3Observers = new Set();
  const l2tol3Dates = [];
  let l2tol3PassCount = 0;

  for (const ev of events) {
    if (ev.kind === "signoff") {
      level = ev.target || level;
      supporting.push(ev);
      // Reset L2→L3 tracking if the signoff isn't to L3
      if (ev.target !== "L3" && ev.target !== "L4") {
        l2tol3Observers.clear(); l2tol3Dates.length = 0; l2tol3PassCount = 0;
      }
    } else if (ev.kind === "observation") {
      if (ev.progression === "t_to_l2") {
        if (level === "0" || level === "T") {
          // T→L2 PASS observation typically requires both a signoff path and the observation;
          // we count this as evidence but only flip to L2 if a corresponding signoff exists, OR
          // (loose) if level is T and the obs is PASS.
          // We don't flip the level here unless paired with a signoff — keep level at T,
          // log the observation as supporting evidence
          supporting.push(ev);
        } else {
          supporting.push(ev);
        }
      } else if (ev.progression === "l2_to_l3") {
        if (level === "L2") {
          l2tol3PassCount++;
          if (ev.observer) l2tol3Observers.add(ev.observer);
          l2tol3Dates.push(ev.date);
          supporting.push(ev);
          // Apply L3 transition when 4+ passes from 2+ distinct observers AND ≥1 day spread
          if (l2tol3PassCount >= 4 && l2tol3Observers.size >= 2) {
            const sortedDates = [...l2tol3Dates].sort();
            const first = new Date(sortedDates[0]);
            const last = new Date(sortedDates[sortedDates.length - 1]);
            const daysSpread = (last - first) / 86400000;
            if (daysSpread >= 1) {
              level = "L3";
            }
          }
        } else {
          supporting.push(ev);
        }
      }
    } else if (ev.kind === "retrain") {
      // Conservative: drop to T
      level = "T";
      supporting.push(ev);
      l2tol3Observers.clear(); l2tol3Dates.length = 0; l2tol3PassCount = 0;
    }
  }

  const basisDetail = `Computed by replaying ${supporting.length} record${supporting.length === 1 ? "" : "s"} (sign-offs, observations, training events) on or before ${fmtDateFull(asOfDate)}.`;

  return {
    level,
    basis: "computed",
    basisDetail,
    supportingEvents: supporting,
  };
}

function exportCsv(filename, rows) {
  // rows is array of arrays. First row is headers.
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const csv = rows.map((row) => row.map(escape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function printReport(title) {
  // Use browser print to produce PDF. Caller has already rendered a .print-mode container.
  document.title = title;
  window.print();
}

/* ============================================================
   ZIP BUNDLE HELPER (store-only, no compression — for already-compressed files)
   Minimal in-browser zip writer. Avoids adding a JSZip dependency.

   Usage:
     await bundleDocsZip(filename, [
       { name: "doc1.pdf", blob: <Blob> },
       { name: "doc2.jpg", blob: <Blob> },
     ])
   Triggers a browser download of the resulting .zip.
   ============================================================ */
async function bundleDocsZip(filename, files) {
  // ZIP file format reference:
  //  - Local file header for each file
  //  - File data
  //  - Central directory header for each file
  //  - End of central directory record
  // We use "stored" (method=0, no compression) since PDFs/JPGs are already compressed.

  const crc32Table = (() => {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
      t[n] = c >>> 0;
    }
    return t;
  })();
  const crc32 = (buf) => {
    let c = 0xffffffff;
    for (let i = 0; i < buf.length; i++) c = (c >>> 8) ^ crc32Table[(c ^ buf[i]) & 0xff];
    return (c ^ 0xffffffff) >>> 0;
  };

  // DOS date/time for "now"
  const now = new Date();
  const dosTime = ((now.getHours() & 0x1f) << 11) | ((now.getMinutes() & 0x3f) << 5) | ((Math.floor(now.getSeconds() / 2)) & 0x1f);
  const dosDate = (((now.getFullYear() - 1980) & 0x7f) << 9) | (((now.getMonth() + 1) & 0xf) << 5) | (now.getDate() & 0x1f);

  // Dedupe filenames (some employees may have multiple docs with same uploaded filename)
  const usedNames = new Set();
  const safeName = (name) => {
    let n = name.replace(/[^a-zA-Z0-9._() -]+/g, "_");
    let unique = n, i = 1;
    while (usedNames.has(unique)) {
      const dot = n.lastIndexOf(".");
      unique = dot > 0 ? `${n.slice(0, dot)}_${i}${n.slice(dot)}` : `${n}_${i}`;
      i++;
    }
    usedNames.add(unique);
    return unique;
  };

  const enc = new TextEncoder();
  const localChunks = [];
  const centralChunks = [];
  let offset = 0;
  let entries = 0;

  for (const f of files) {
    if (!f.blob) continue;
    const nameStr = safeName(f.name || "document");
    const nameBuf = enc.encode(nameStr);
    const data = new Uint8Array(await f.blob.arrayBuffer());
    const crc = crc32(data);
    const size = data.length;

    // Local file header (30 bytes + name)
    const localHeader = new ArrayBuffer(30);
    const lh = new DataView(localHeader);
    lh.setUint32(0, 0x04034b50, true); // signature
    lh.setUint16(4, 20, true);  // version needed
    lh.setUint16(6, 0, true);   // flags
    lh.setUint16(8, 0, true);   // method (stored)
    lh.setUint16(10, dosTime, true);
    lh.setUint16(12, dosDate, true);
    lh.setUint32(14, crc, true);
    lh.setUint32(18, size, true); // compressed size = uncompressed (stored)
    lh.setUint32(22, size, true);
    lh.setUint16(26, nameBuf.length, true);
    lh.setUint16(28, 0, true); // extra field length
    localChunks.push(new Uint8Array(localHeader), nameBuf, data);

    // Central directory header (46 bytes + name)
    const centralHeader = new ArrayBuffer(46);
    const ch = new DataView(centralHeader);
    ch.setUint32(0, 0x02014b50, true);
    ch.setUint16(4, 20, true);  // version made by
    ch.setUint16(6, 20, true);  // version needed
    ch.setUint16(8, 0, true);   // flags
    ch.setUint16(10, 0, true);  // method
    ch.setUint16(12, dosTime, true);
    ch.setUint16(14, dosDate, true);
    ch.setUint32(16, crc, true);
    ch.setUint32(20, size, true);
    ch.setUint32(24, size, true);
    ch.setUint16(28, nameBuf.length, true);
    ch.setUint16(30, 0, true); // extra
    ch.setUint16(32, 0, true); // comment
    ch.setUint16(34, 0, true); // disk number
    ch.setUint16(36, 0, true); // internal attrs
    ch.setUint32(38, 0, true); // external attrs
    ch.setUint32(42, offset, true); // local header offset
    centralChunks.push(new Uint8Array(centralHeader), nameBuf);

    offset += 30 + nameBuf.length + size;
    entries++;
  }

  const centralStart = offset;
  let centralSize = 0;
  for (const c of centralChunks) centralSize += c.length;

  // End of central directory record
  const eocd = new ArrayBuffer(22);
  const ev = new DataView(eocd);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(4, 0, true);
  ev.setUint16(6, 0, true);
  ev.setUint16(8, entries, true);
  ev.setUint16(10, entries, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, centralStart, true);
  ev.setUint16(20, 0, true);

  const blob = new Blob([...localChunks, ...centralChunks, new Uint8Array(eocd)], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ExportButtons({ csvRows, csvFilename, canPdf, onPrint }) {
  return (
    <div className="report-export">
      <button className="btn-mini ghost" onClick={() => exportCsv(csvFilename, csvRows)}>
        <Download size={13} /> Export CSV
      </button>
      {canPdf && onPrint && (
        <button className="btn-mini ghost" onClick={onPrint}>
          <Download size={13} /> Print / Save PDF
        </button>
      )}
    </div>
  );
}

/* ----------------- WORKFORCE MATRIX ----------------- */
function WorkforceMatrixReport({ state, canPdf }) {
  const emps = useMemo(() =>
    state.employees.filter((e) => !e.archived).sort((a, b) => a.name.localeCompare(b.name)),
    [state.employees]);

  const levelClass = (lvl) => lvl ? `tag tag-${String(lvl).toLowerCase()}` : "tag tag-muted";

  const csvRows = useMemo(() => {
    const headers = ["Employee", "Role", "Department", ...RIDES.map((r) => r.label)];
    const rows = emps.map((e) => [
      e.name, e.role || "", e.department || "",
      ...RIDES.map((r) => e.levels[r.key] || "0"),
    ]);
    return [headers, ...rows];
  }, [emps]);

  return (
    <div className="report-card print-target">
      <div className="report-head">
        <h2>Workforce Matrix</h2>
        <ExportButtons
          csvRows={csvRows}
          csvFilename={`workforce-matrix-${todayISO()}.csv`}
          canPdf={canPdf}
          onPrint={() => printReport(`Workforce Matrix - ${todayISO()}`)}
        />
      </div>
      <p className="report-sub">Current certification level for each employee across all rides. Generated {fmtDate(todayISO())}.</p>
      <div className="report-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Role</th>
              {RIDES.map((r) => <th key={r.key}>{r.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {emps.map((e) => (
              <tr key={e.id}>
                <td>{e.name}</td>
                <td className="dim">{e.role || ""}</td>
                {RIDES.map((r) => {
                  const lvl = e.levels[r.key];
                  return <td key={r.key} className="cell-cert">{lvl ? <span className={levelClass(lvl)}>{lvl}</span> : <span className="dim">—</span>}</td>;
                })}
              </tr>
            ))}
            {emps.length === 0 && <tr><td colSpan={RIDES.length + 2}><Empty text="No active employees." /></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------- PER-EMPLOYEE HISTORY ----------------- */
function PerEmployeeHistoryReport({ state, canPdf }) {
  const emps = useMemo(() =>
    state.employees.filter((e) => !e.archived).sort((a, b) => a.name.localeCompare(b.name)),
    [state.employees]);
  const [empId, setEmpId] = useState(emps[0]?.id || "");
  const emp = state.employees.find((e) => e.id === empId);

  // Mode: 'current' (operational) or 'pit' (point-in-time / compliance)
  const [mode, setMode] = useState("current");
  const [asOfDate, setAsOfDate] = useState(todayISO());
  const [incidentRef, setIncidentRef] = useState(""); // Optional incident/case reference

  // Filter state
  const [ridesEnabled, setRidesEnabled] = useState(() => new Set(RIDES.map((r) => r.key)));
  const [catsEnabled, setCatsEnabled] = useState(() => new Set(DOC_CATEGORIES));

  const toggleRide = (key) => {
    setRidesEnabled((cur) => { const next = new Set(cur); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };
  const setAllRides = (on) => setRidesEnabled(on ? new Set(RIDES.map((r) => r.key)) : new Set());
  const setGroupRides = (group) => setRidesEnabled(new Set(RIDES.filter((r) => r.group === group).map((r) => r.key)));

  const toggleCat = (cat) => {
    setCatsEnabled((cur) => { const next = new Set(cur); if (next.has(cat)) next.delete(cat); else next.add(cat); return next; });
  };
  const setAllCats = (on) => setCatsEnabled(on ? new Set(DOC_CATEGORIES) : new Set());

  // Documents
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);

  useEffect(() => {
    if (!emp) { setDocs([]); return; }
    let cancelled = false;
    (async () => {
      setDocsLoading(true);
      const res = await db.loadEmployeeDocuments(emp.id);
      if (cancelled) return;
      setDocs(res.ok ? res.documents : []);
      setDocsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [emp]);

  // Stable report ID for compliance mode (regenerates on key changes)
  const reportId = useMemo(() => {
    if (!emp) return "";
    const ymd = todayISO().replace(/-/g, "");
    const empInitials = emp.name.split(/\s+/).map(w => w[0]).join("").toUpperCase().slice(0, 3);
    const empIdShort = (emp.id || "").slice(0, 4).toUpperCase();
    return mode === "pit" ? `CR-${ymd}-${empInitials}-${empIdShort}` : "";
  }, [emp, mode]);

  // In compliance mode, exclude docs uploaded AFTER the as-of date, AND docs with issued_at after the cutoff
  const isDocInScope = (d) => {
    if (mode !== "pit") return true;
    const cutoff = asOfDate;
    if (d.uploaded_at && d.uploaded_at.slice(0, 10) > cutoff) return false;
    if (d.issued_at && d.issued_at > cutoff) return false;
    // Note: we INCLUDE expired docs in compliance mode if they were valid at any point on/before cutoff,
    // because expired-on-as-of-date is information the lawyer needs (e.g., "Work Card expired before incident")
    return true;
  };

  // Apply category filter and (in PIT mode) scope filter to docs
  const filteredDocs = useMemo(() =>
    docs.filter((d) => catsEnabled.has(d.category) && isDocInScope(d)),
    [docs, catsEnabled, mode, asOfDate]);

  // Split into ride-tagged and employee-level
  const docsByRide = useMemo(() => {
    const map = {};
    RIDES.forEach((r) => { map[r.key] = []; });
    filteredDocs.forEach((d) => {
      const rides = Array.isArray(d.rides) ? d.rides : [];
      if (rides.length === 0) return;
      rides.forEach((rk) => { if (map[rk]) map[rk].push(d); });
    });
    return map;
  }, [filteredDocs]);

  const employeeLevelDocs = useMemo(() =>
    filteredDocs.filter((d) => !Array.isArray(d.rides) || d.rides.length === 0),
    [filteredDocs]);

  // Build per-ride event list with dedupe + asserted-level reconstruction
  const perRide = useMemo(() => {
    if (!emp) return [];
    const cutoff = mode === "pit" ? asOfDate : null;
    const inScope = (dateStr) => !cutoff || (dateStr || "").slice(0, 10) <= cutoff;
    const result = [];
    for (const r of RIDES) {
      if (!ridesEnabled.has(r.key)) continue;
      const events = [];
      const seenKeys = new Set(); // dedupe key set

      const pushUnique = (ev, key) => {
        if (seenKeys.has(key)) return;
        seenKeys.add(key);
        events.push(ev);
      };

      // Quizzes
      (state.quizzes || []).filter((q) => q.empId === emp.id && q.ride === r.key).forEach((q) => {
        if (q.sentDate && inScope(q.sentDate)) {
          pushUnique(
            { date: q.sentDate, type: "Quiz", detail: `Sent → ${q.targetLevel}${q.supervisor ? ` by ${q.supervisor}` : ""}`, severity: "info" },
            `q-sent-${q.id || q.sentDate}-${q.targetLevel}`
          );
        }
        if (q.completedDate && inScope(q.completedDate)) {
          const isPass = q.status === "passed_full" || q.status === "passed_gaps" || q.status === "complete";
          const isFail = q.status === "failed";
          const scoreSuffix = (typeof q.score === "number") ? ` (${q.score}%)` : "";
          let label;
          if (isPass) label = `PASSED${scoreSuffix}${q.status === "passed_gaps" ? " — passed with gaps" : ""}`;
          else if (isFail) label = `FAILED${scoreSuffix} — re-quiz required`;
          else label = `Completed${scoreSuffix}`;
          pushUnique(
            { date: q.completedDate, type: "Quiz", detail: label, severity: isPass ? "good" : isFail ? "bad" : "info" },
            `q-done-${q.id || q.completedDate}-${q.status}`
          );
        }
      });

      // Observations
      (state.l2_observations || []).filter((o) => o.employee_id === emp.id && o.ride === r.key && inScope(o.created_at)).forEach((o) => {
        const progLabel = o.progression === "l2_to_l3" ? "L2→L3" : "T→L2";
        const outcomeUpper = (o.outcome || "").toUpperCase();
        const isPass = outcomeUpper === "PASS";
        const isFail = outcomeUpper === "FAIL";
        const date = (o.created_at || "").slice(0, 10);
        pushUnique(
          {
            date, type: "Observation",
            detail: `${progLabel} observation: ${outcomeUpper}${o.observer_name ? ` — observer: ${o.observer_name}` : ""}`,
            severity: isPass ? "good" : isFail ? "bad" : "info",
          },
          `o-${o.id || `${date}-${o.observer_name || ""}-${progLabel}`}`
        );
      });

      // Sign-offs (dedupe on kind+target+status+sigDate — defends against the duplicate-rows bug)
      (state.signoffs || []).filter((s) => s.empId === emp.id && s.ride === r.key).forEach((s) => {
        const sigDates = (s.signatures || []).map((sig) => sig.date).filter(Boolean).sort();
        const lastSig = sigDates[sigDates.length - 1] || "";
        if (!inScope(lastSig)) return;
        const sigNames = (s.signatures || []).map((sig) => `${sig.name}${sig.title ? ` (${sig.title})` : ""}`).join(", ");
        pushUnique(
          {
            date: lastSig, type: "Sign-off",
            detail: `${s.kind} → ${s.targetLevel} (${s.status})${sigNames ? ` — signed by: ${sigNames}` : ""}`,
            severity: s.status === "complete" ? "good" : "info",
          },
          `s-${lastSig}-${s.kind}-${s.targetLevel}-${s.status}` // collapses duplicate signoff rows
        );
      });

      // History entries (non-redundant, with migration relabel)
      const rideLabelLower = r.label.toLowerCase();
      (emp.history || []).forEach((h) => {
        if (!h.text || !inScope(h.date)) return;
        const t = h.text.toLowerCase();
        if (/quiz|passed|failed/.test(t)) return;
        if (/sign[\s-]?off/.test(t)) return;
        if (/observation|observer/.test(t)) return;
        if (!t.includes(rideLabelLower)) return;
        // Relabel migration entries
        const isMigration = /pre[\s-]?imported|prior\s+records|prior\s+tracking/.test(t);
        const displayText = isMigration
          ? h.text.replace(/Pre-imported from prior records:?/i, "Historical record (migrated from prior tracking system):")
          : h.text;
        pushUnique(
          { date: h.date, type: isMigration ? "Migrated" : "Event", detail: displayText, severity: "info" },
          `h-${h.date}-${displayText.slice(0, 40)}`
        );
      });

      events.sort((a, b) => (a.date || "").localeCompare(b.date || ""));

      // Compute the asserted level for compliance mode
      let assertion = null;
      if (mode === "pit") {
        assertion = reconstructLevelAtDate(emp, r.key, asOfDate, state);
      }

      result.push({
        ride: r,
        level: emp.levels[r.key],  // current level (used in current-state mode)
        events,
        docs: docsByRide[r.key] || [],
        assertion,
      });
    }
    return result;
  }, [emp, state.quizzes, state.l2_observations, state.signoffs, ridesEnabled, docsByRide, mode, asOfDate, state]);

  const ridesWithContent = perRide.filter((rg) =>
    rg.events.length > 0
    || (mode === "current" && rg.level)
    || (mode === "pit" && rg.assertion?.level && rg.assertion.level !== "0")
    || rg.docs.length > 0
  );
  const ridesWithoutContent = perRide.filter((rg) =>
    rg.events.length === 0
    && !(mode === "current" && rg.level)
    && !(mode === "pit" && rg.assertion?.level && rg.assertion.level !== "0")
    && rg.docs.length === 0
  );

  // CSV export
  const csvRows = useMemo(() => {
    if (!emp) return [];
    const rows = [];
    if (mode === "pit") {
      rows.push([`LAS VEGAS MINI GRAND PRIX — COMPLIANCE TRAINING RECORD`]);
      rows.push([`Report ID: ${reportId}`]);
      rows.push([`Employee: ${emp.name}`]);
      rows.push([`As of date: ${fmtDateFull(asOfDate)}`]);
      rows.push([`Report generated: ${fmtDateFull(todayISO())}`]);
      if (incidentRef) rows.push([`Prepared in reference to: ${incidentRef}`]);
      rows.push([]);
      rows.push([`Certification levels as of ${fmtDateFull(asOfDate)}, based on records on file:`]);
      perRide.forEach((rg) => {
        if (!rg.assertion) return;
        const lvl = rg.assertion.level && rg.assertion.level !== "0" ? rg.assertion.level : "Not certified";
        rows.push([`  ${rg.ride.label}: ${lvl}`]);
      });
      rows.push([]);
      rows.push([`This record sets forth all training, certification, observation, sign-off, and documentation events for the named employee that occurred on or before the as-of date. Records were drawn directly from the operational training database of Las Vegas Mini Grand Prix.`]);
    } else {
      rows.push([`Training history for: ${emp.name}`]);
      rows.push([`Generated: ${fmtDateFull(todayISO())}`]);
    }
    const filterNotes = [];
    if (ridesEnabled.size < RIDES.length) filterNotes.push(`Rides: ${RIDES.filter((r) => ridesEnabled.has(r.key)).map((r) => r.label).join(", ")}`);
    if (catsEnabled.size < DOC_CATEGORIES.length) filterNotes.push(`Categories: ${[...catsEnabled].join(", ")}`);
    if (filterNotes.length > 0) rows.push([`Filters applied: ${filterNotes.join(" · ")}`]);
    rows.push([]);
    rows.push(["Ride", mode === "current" ? "Current level" : "Asserted level as of date", "Event date", "Event type", "Detail"]);
    perRide.forEach((rg) => {
      if (rg.events.length === 0 && rg.docs.length === 0 && !(mode === "current" && rg.level) && !(mode === "pit" && rg.assertion?.level && rg.assertion.level !== "0")) return;
      const summary = mode === "current"
        ? (rg.level || "—")
        : (rg.assertion?.level && rg.assertion.level !== "0" ? rg.assertion.level : "Not certified");
      if (rg.events.length === 0) {
        rows.push([rg.ride.label, summary, "", "", "(no event history)"]);
      } else {
        rg.events.forEach((ev, idx) => {
          rows.push([
            idx === 0 ? rg.ride.label : "",
            idx === 0 ? summary : "",
            ev.date ? fmtDateFull(ev.date) : "", ev.type, ev.detail,
          ]);
        });
      }
      rg.docs.forEach((d) => {
        const rideLabels = (d.rides || []).map((rk) => RIDE_MAP[rk]?.label || rk).join(", ");
        rows.push(["", "",
          d.uploaded_at ? fmtDateFull(d.uploaded_at) : "",
          "Document",
          `[${d.category}] ${d.filename}${rideLabels ? ` (tagged: ${rideLabels})` : ""}${d.issued_at ? ` (issued ${fmtDateFull(d.issued_at)})` : ""}${d.expires_at ? ` (expires ${fmtDateFull(d.expires_at)})` : ""}`]);
      });
    });
    if (employeeLevelDocs.length > 0) {
      rows.push([]);
      rows.push(["Employee-level documents (not tagged to any ride)"]);
      rows.push(["Category", "Filename", "Uploaded", "Issued", "Expires", "Description"]);
      employeeLevelDocs.forEach((d) => {
        rows.push([d.category, d.filename,
          d.uploaded_at ? fmtDateFull(d.uploaded_at) : "",
          d.issued_at ? fmtDateFull(d.issued_at) : "",
          d.expires_at ? fmtDateFull(d.expires_at) : "",
          d.description || ""]);
      });
    }
    return rows;
  }, [emp, mode, asOfDate, perRide, employeeLevelDocs, ridesEnabled, catsEnabled, reportId, incidentRef]);

  // Document bundle
  const [bundling, setBundling] = useState(false);
  const [pdfBuilding, setPdfBuilding] = useState(false);
  const [pdfProgress, setPdfProgress] = useState("");
  const referencedDocs = useMemo(() => {
    const out = new Map();
    perRide.forEach((rg) => rg.docs.forEach((d) => { if (!out.has(d.id)) out.set(d.id, d); }));
    employeeLevelDocs.forEach((d) => { if (!out.has(d.id)) out.set(d.id, d); });
    return Array.from(out.values());
  }, [perRide, employeeLevelDocs]);

  const downloadBundle = async () => {
    if (referencedDocs.length === 0) return;
    setBundling(true);
    try {
      const fetched = [];
      for (const d of referencedDocs) {
        const urlRes = await db.getEmployeeDocumentUrl(d.storage_path, 120);
        if (!urlRes.ok) continue;
        try {
          const resp = await fetch(urlRes.url);
          if (!resp.ok) continue;
          const blob = await resp.blob();
          fetched.push({ name: `${d.category}__${d.filename}`, blob });
        } catch (_) { /* skip */ }
      }
      const tag = mode === "pit" ? `compliance-${asOfDate}` : "current";
      await bundleDocsZip(`${(emp?.name || "employee").replace(/\s+/g, "_")}-docs-${tag}.zip`, fetched);
    } finally {
      setBundling(false);
    }
  };

  /* Build the combined compliance PDF: dossier + all attachments stitched in */
  const buildCombinedPdf = async () => {
    if (!emp || mode !== "pit") return;
    setPdfBuilding(true);
    setPdfProgress(`Fetching ${referencedDocs.length} document${referencedDocs.length === 1 ? "" : "s"}…`);
    try {
      // Pre-fetch all referenced doc blobs
      const refsWithBlobs = [];
      for (let i = 0; i < referencedDocs.length; i++) {
        const d = referencedDocs[i];
        setPdfProgress(`Fetching ${i + 1}/${referencedDocs.length}: ${d.filename}`);
        try {
          const urlRes = await db.getEmployeeDocumentUrl(d.storage_path, 180);
          if (!urlRes.ok) { refsWithBlobs.push({ ...d, blob: null }); continue; }
          const resp = await fetch(urlRes.url);
          if (!resp.ok) { refsWithBlobs.push({ ...d, blob: null }); continue; }
          const blob = await resp.blob();
          refsWithBlobs.push({ ...d, blob });
        } catch (_) {
          refsWithBlobs.push({ ...d, blob: null });
        }
      }
      setPdfProgress("Generating dossier…");

      // Build the input structure for the PDF generator
      const ridesCoveredText = ridesEnabled.size === RIDES.length
        ? "All rides (7)"
        : RIDES.filter((r) => ridesEnabled.has(r.key)).map((r) => r.label).join(", ");
      const rideLabelMap = {};
      RIDES.forEach((r) => { rideLabelMap[r.key] = r.label; });

      const input = {
        reportId,
        employee: {
          name: emp.name,
          role: emp.role || "",
          department: emp.department || "",
        },
        asOfDate,
        generatedDate: todayISO(),
        ridesCoveredText,
        incidentRef: incidentRef.trim() || "",
        rides: perRide.map((rg) => ({
          ride: { key: rg.ride.key, label: rg.ride.label },
          assertion: rg.assertion,
          events: rg.events,
          docs: rg.docs,
        })),
        employeeLevelDocs,
        referencedDocs: refsWithBlobs,
        rideLabelMap,
      };

      const blob = await buildCompliancePdf(input, (i, total, name) => {
        setPdfProgress(`Embedding ${i + 1}/${total}: ${name}`);
      });

      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Compliance-Record-${(emp.name || "employee").replace(/\s+/g, "_")}-${asOfDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      console.error("PDF build failed:", e);
      alert(`PDF build failed: ${e.message || String(e)}`);
    } finally {
      setPdfBuilding(false);
      setPdfProgress("");
    }
  };

  const openDoc = async (d) => {
    const r = await db.getEmployeeDocumentUrl(d.storage_path, 60);
    if (r.ok) window.open(r.url, "_blank", "noopener,noreferrer");
  };

  if (emps.length === 0) return <Empty text="No active employees." />;

  // Render a doc row inside a ride section
  const renderDocRow = (d) => {
    let expCls = "", expLabel = "";
    if (d.expires_at) {
      const refDate = mode === "pit" ? asOfDate : todayISO();
      const days = Math.floor((new Date(d.expires_at) - new Date(refDate)) / 86400000);
      expCls = days < 0 ? "expiry-expired" : days <= 30 ? "expiry-soon" : "expiry-ok";
      expLabel = days < 0 ? `${fmtDateFull(d.expires_at)} (EXPIRED)` : `expires ${fmtDateFull(d.expires_at)}`;
    }
    return (
      <tr key={d.id}>
        <td className="mono dim" style={{ width: 120 }}>{d.uploaded_at ? fmtDate(d.uploaded_at) : "—"}</td>
        <td style={{ width: 110 }}><span className="tag tag-evt tag-evt-document">Document</span></td>
        <td>
          <span className="tag tag-doc-cat" style={{ marginRight: 6 }}>{d.category}</span>
          <button className="btn-link no-print" onClick={() => openDoc(d)} title="Open file">{d.filename}</button>
          <span className="print-only">{d.filename}</span>
          <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>
            Uploaded {fmtDateFull(d.uploaded_at)}
            {d.issued_at && <> · Issued {fmtDateFull(d.issued_at)}</>}
            {expLabel && <> · <span className={expCls}>{expLabel}</span></>}
          </div>
          {d.description && <div className="dim" style={{ fontSize: 11, marginTop: 2, fontStyle: "italic" }}>{d.description}</div>}
        </td>
      </tr>
    );
  };

  return (
    <div className={`report-card print-target ${mode === "pit" ? "compliance-mode" : ""}`}>
      <div className="report-head">
        <h2>{mode === "pit" ? "Compliance Training Record" : "Per-Employee Training History"}</h2>
        <div className="report-export">
          {mode === "pit" && canPdf && (
            <button
              className="btn-mini"
              style={{ background: "var(--gold)", color: "#1a1008", borderColor: "var(--gold)", fontWeight: 600 }}
              onClick={buildCombinedPdf}
              disabled={pdfBuilding}
              title="Build a single PDF with the compliance dossier and all referenced attachments stitched together"
            >
              <Download size={13} /> {pdfBuilding ? "Building…" : `Build Combined PDF${referencedDocs.length > 0 ? ` (+${referencedDocs.length} attachments)` : ""}`}
            </button>
          )}
          <button className="btn-mini ghost" onClick={() => exportCsv(`training-history-${(emp?.name || "employee").replace(/\s+/g, "_")}-${mode === "pit" ? "asof-" + asOfDate : todayISO()}.csv`, csvRows)}>
            <Download size={13} /> Export CSV
          </button>
          {canPdf && (
            <button className="btn-mini ghost" onClick={() => printReport(`${mode === "pit" ? "Compliance Record" : "Training History"} - ${emp?.name} - ${mode === "pit" ? "as of " + asOfDate : todayISO()}`)}>
              <Download size={13} /> Print / Save PDF
            </button>
          )}
          {referencedDocs.length > 0 && (
            <button className="btn-mini ghost" onClick={downloadBundle} disabled={bundling} title={`Download ${referencedDocs.length} referenced doc${referencedDocs.length === 1 ? "" : "s"} as a zip`}>
              <Download size={13} /> {bundling ? "Bundling…" : `Download docs as ZIP (${referencedDocs.length})`}
            </button>
          )}
        </div>
      </div>

      {pdfBuilding && pdfProgress && (
        <div className="pdf-progress no-print">
          <div className="pdf-progress-bar">
            <div className="pdf-progress-indeterminate" />
          </div>
          <div className="pdf-progress-label">{pdfProgress}</div>
        </div>
      )}

      {/* Mode toggle */}
      <div className="report-mode-toggle no-print">
        <button className={`mode-btn ${mode === "current" ? "active" : ""}`} onClick={() => setMode("current")}>Current state</button>
        <button className={`mode-btn ${mode === "pit" ? "active" : ""}`} onClick={() => setMode("pit")}>Compliance record (point-in-time)</button>
      </div>

      <div className="report-controls no-print">
        <label className="field" style={{ maxWidth: 360, flex: "1 1 240px" }}>
          <span>Employee</span>
          <select value={empId} onChange={(e) => setEmpId(e.target.value)}>
            {emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
        {mode === "pit" && (
          <>
            <label className="field" style={{ maxWidth: 200, flex: "0 0 200px" }}>
              <span>As of date</span>
              <input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
            </label>
            <label className="field" style={{ flex: "1 1 320px" }}>
              <span>Prepared in reference to (optional)</span>
              <input type="text" value={incidentRef} onChange={(e) => setIncidentRef(e.target.value)} placeholder="e.g. Claim 2026-0123 — incident on June 4, 2026" />
            </label>
          </>
        )}
      </div>

      {/* Filter panel */}
      <div className="report-filters no-print">
        <div className="report-filter-group">
          <div className="report-filter-head">
            <span className="report-filter-label">Rides shown</span>
            <div className="report-filter-actions">
              <button className="btn-mini ghost" onClick={() => setAllRides(true)}>All</button>
              <button className="btn-mini ghost" onClick={() => setAllRides(false)}>None</button>
              <button className="btn-mini ghost" onClick={() => setGroupRides("kiddieLand")}>Kiddie Land</button>
              <button className="btn-mini ghost" onClick={() => setGroupRides("tracks")}>Tracks</button>
            </div>
          </div>
          <div className="report-checkbox-list">
            {RIDES.map((r) => (
              <label key={r.key} className="report-checkbox">
                <input type="checkbox" checked={ridesEnabled.has(r.key)} onChange={() => toggleRide(r.key)} />
                <span>{r.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="report-filter-group">
          <div className="report-filter-head">
            <span className="report-filter-label">Document categories shown</span>
            <div className="report-filter-actions">
              <button className="btn-mini ghost" onClick={() => setAllCats(true)}>All</button>
              <button className="btn-mini ghost" onClick={() => setAllCats(false)}>None</button>
            </div>
          </div>
          <div className="report-checkbox-list">
            {DOC_CATEGORIES.map((c) => (
              <label key={c} className="report-checkbox">
                <input type="checkbox" checked={catsEnabled.has(c)} onChange={() => toggleCat(c)} />
                <span>{c}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {emp && (
        <>
          {/* Compliance header */}
          {mode === "pit" ? (
            <div className="compliance-doc">
              {/* Document header (shows on every printed page via running header pattern) */}
              <div className="compliance-page-header">
                <div className="compliance-page-header-left">LAS VEGAS MINI GRAND PRIX</div>
                <div className="compliance-page-header-right">Compliance Training Record</div>
              </div>

              <div className="compliance-title-block">
                <div className="compliance-org">LAS VEGAS MINI GRAND PRIX</div>
                <div className="compliance-title">Compliance Training Record</div>
                <div className="compliance-subtitle">Point-in-Time Certification &amp; Audit Trail</div>
                <div className="compliance-reportid">Report ID: <strong>{reportId}</strong></div>
              </div>

              <table className="compliance-attest">
                <tbody>
                  <tr>
                    <th>Subject employee</th>
                    <td><strong>{emp.name}</strong>{emp.role ? ` · ${emp.role}` : ""}{emp.department ? ` · ${emp.department}` : ""}</td>
                  </tr>
                  <tr><th>As-of date</th><td><strong>{fmtDateFull(asOfDate)}</strong></td></tr>
                  <tr><th>Report generated</th><td>{fmtDateFull(todayISO())}</td></tr>
                  <tr><th>Rides covered</th><td>{ridesEnabled.size === RIDES.length ? "All rides (7)" : RIDES.filter((r) => ridesEnabled.has(r.key)).map((r) => r.label).join(", ") || "(none selected)"}</td></tr>
                  {incidentRef && (
                    <tr><th>Prepared in reference to</th><td>{incidentRef}</td></tr>
                  )}
                </tbody>
              </table>

              {/* Asserted certifications summary */}
              <div className="compliance-summary">
                <h3 className="compliance-section-title">Certification Status as of {fmtDateFull(asOfDate)}</h3>
                <p className="compliance-prose">
                  Based on training, sign-off, observation, and certification records maintained by Las Vegas Mini Grand Prix, the
                  subject employee held the following certification levels on the as-of date:
                </p>
                <table className="compliance-cert-table">
                  <thead>
                    <tr><th>Ride</th><th>Level</th><th>Basis</th></tr>
                  </thead>
                  <tbody>
                    {perRide.map((rg) => {
                      const a = rg.assertion;
                      const lvl = a?.level && a.level !== "0" ? a.level : null;
                      return (
                        <tr key={rg.ride.key}>
                          <td><strong>{rg.ride.label}</strong></td>
                          <td>
                            {lvl ? <span className={`tag tag-${String(lvl).toLowerCase()}`}>{lvl}</span> : <span className="dim">Not certified</span>}
                          </td>
                          <td className="dim" style={{ fontSize: 11.5 }}>
                            {a?.basis === "no_records" ? "No certification records on or before this date."
                              : a?.supportingEvents?.length > 0 ? `Established by ${a.supportingEvents.length} record${a.supportingEvents.length === 1 ? "" : "s"} on file.`
                              : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="compliance-prose" style={{ marginTop: 14 }}>
                  Certification levels above were computed by chronologically replaying sign-off, quiz, observation, and training records on file on or before the as-of date.
                  The complete audit trail supporting each ride's certification is provided in the sections that follow.
                  Where supporting documents are referenced (sign-off forms, work cards, etc.), file links are provided in the digital
                  version of this report; a bundled copy is available via the "Download docs" button.
                </p>
              </div>
            </div>
          ) : (
            <p className="report-sub">
              <strong>{emp.name}</strong>
              {emp.role ? ` · ${emp.role}` : ""}
              {emp.department ? ` · ${emp.department}` : ""}
              {" · Generated "}{fmtDateFull(todayISO())}
              {ridesEnabled.size < RIDES.length && <> · <em>Filtered to {ridesEnabled.size} of {RIDES.length} rides</em></>}
              {catsEnabled.size < DOC_CATEGORIES.length && <> · <em>{catsEnabled.size} of {DOC_CATEGORIES.length} doc categories</em></>}
            </p>
          )}

          {/* Section heading for compliance mode */}
          {mode === "pit" && ridesWithContent.length > 0 && (
            <h3 className="compliance-section-title" style={{ marginTop: 24 }}>Audit Trail by Ride</h3>
          )}

          {ridesWithContent.length === 0 && ridesEnabled.size > 0 && (
            <Empty text={mode === "pit"
              ? `No certification records on file for the selected rides on or before ${fmtDateFull(asOfDate)}.`
              : "No activity, certifications, or documents for the selected rides and categories."} />
          )}
          {ridesEnabled.size === 0 && (
            <Empty text="No rides selected. Use the filter above to choose rides to display." />
          )}

          {ridesWithContent.map((rg) => (
            <div key={rg.ride.key} className="ride-history-block">
              <div className="ride-history-head">
                <h3 className="ride-history-title">{rg.ride.label}</h3>
                <span className="ride-history-level">
                  {mode === "current" ? (
                    <>
                      Current:{" "}
                      {rg.level
                        ? <span className={`tag tag-${String(rg.level).toLowerCase()}`}>{rg.level}</span>
                        : <span className="dim">—</span>}
                    </>
                  ) : (
                    rg.assertion?.level && rg.assertion.level !== "0"
                      ? <>Certified as of {fmtDateFull(asOfDate)}: <span className={`tag tag-${String(rg.assertion.level).toLowerCase()}`}>{rg.assertion.level}</span></>
                      : <span className="dim">Not certified as of {fmtDateFull(asOfDate)}</span>
                  )}
                  {rg.docs.length > 0 && <span className="dim" style={{ marginLeft: 10 }}>· {rg.docs.length} doc{rg.docs.length === 1 ? "" : "s"}</span>}
                </span>
              </div>
              {rg.events.length === 0 && rg.docs.length === 0 ? (
                <p className="dim" style={{ fontSize: 12.5, fontStyle: "italic", margin: 0 }}>
                  No supporting events on file.
                </p>
              ) : (
                <table className="report-table progression-table">
                  <tbody>
                    {rg.events.map((ev, i) => (
                      <tr key={`ev-${i}`} className={`sev-${ev.severity}`}>
                        <td className="mono dim" style={{ width: 120 }}>{fmtDate(ev.date)}</td>
                        <td style={{ width: 110 }}><span className={`tag tag-evt tag-evt-${ev.type.toLowerCase().replace(/[^a-z]/g, '')}`}>{ev.type}</span></td>
                        <td>{ev.detail}</td>
                      </tr>
                    ))}
                    {rg.docs.map((d) => renderDocRow(d))}
                  </tbody>
                </table>
              )}
            </div>
          ))}

          {ridesWithoutContent.length > 0 && (
            <div className="ride-history-block muted">
              <h3 className="ride-history-title dim" style={{ fontSize: 12 }}>
                {mode === "pit" ? "No certification records on or before this date:" : "Not yet started:"} {ridesWithoutContent.map((rg) => rg.ride.label).join(" · ")}
              </h3>
            </div>
          )}

          {/* Employee-level documents */}
          <div className="ride-history-block">
            <div className="ride-history-head">
              <h3 className="ride-history-title"><Folder size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Employee-level documents</h3>
              <span className="dim" style={{ fontSize: 12 }}>{employeeLevelDocs.length} document{employeeLevelDocs.length === 1 ? "" : "s"}</span>
            </div>
            {docsLoading ? (
              <Empty text="Loading documents…" />
            ) : employeeLevelDocs.length === 0 ? (
              <Empty text={docs.length > 0 ? (mode === "pit" ? "No employee-level documents in scope for this as-of date." : "All documents are tagged to specific rides (shown above).") : "No documents on file."} />
            ) : (
              <table className="report-table">
                <thead>
                  <tr><th>Category</th><th>Filename</th><th>Uploaded</th><th>Issued</th><th>Expires</th></tr>
                </thead>
                <tbody>
                  {employeeLevelDocs.map((d) => {
                    let expCls = "", expLabel = "";
                    if (d.expires_at) {
                      const refDate = mode === "pit" ? asOfDate : todayISO();
                      const days = Math.floor((new Date(d.expires_at) - new Date(refDate)) / 86400000);
                      expCls = days < 0 ? "expiry-expired" : days <= 30 ? "expiry-soon" : "expiry-ok";
                      expLabel = days < 0 ? `${fmtDate(d.expires_at)} (EXPIRED)` : fmtDate(d.expires_at);
                    }
                    return (
                      <tr key={d.id}>
                        <td><span className="tag tag-doc-cat">{d.category}</span></td>
                        <td className="dim">
                          <button className="btn-link no-print" onClick={() => openDoc(d)} title="Open file">{d.filename}</button>
                          <span className="print-only">{d.filename}</span>
                          {d.description ? <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>{d.description}</div> : null}
                        </td>
                        <td className="dim mono">{d.uploaded_at ? fmtDate(d.uploaded_at) : "—"}</td>
                        <td className="dim mono">{d.issued_at ? fmtDate(d.issued_at) : "—"}</td>
                        <td className={"mono " + expCls}>{expLabel || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Signature footer for compliance mode */}
          {mode === "pit" && (
            <div className="compliance-footer">
              <h3 className="compliance-section-title">Certification &amp; Signature</h3>
              <p className="compliance-prose">
                I certify that the information presented in this record was produced from the operational training database of
                Las Vegas Mini Grand Prix on {fmtDateFull(todayISO())}, and accurately reflects the records on file as of that date.
              </p>
              <div className="compliance-sig-block">
                <div className="compliance-sig-row">
                  <div className="compliance-sig">
                    <div className="compliance-sig-line"></div>
                    <div className="compliance-sig-label">Prepared by (signature)</div>
                  </div>
                  <div className="compliance-sig" style={{ flex: "0 0 180px" }}>
                    <div className="compliance-sig-line"></div>
                    <div className="compliance-sig-label">Date</div>
                  </div>
                </div>
                <div className="compliance-sig-row">
                  <div className="compliance-sig">
                    <div className="compliance-sig-line"></div>
                    <div className="compliance-sig-label">Name (print)</div>
                  </div>
                  <div className="compliance-sig">
                    <div className="compliance-sig-line"></div>
                    <div className="compliance-sig-label">Title / role</div>
                  </div>
                </div>
              </div>
              <div className="compliance-page-footer">
                <span>Report ID: {reportId}</span>
                <span>Generated {fmtDateFull(todayISO())}</span>
                <span>Las Vegas Mini Grand Prix</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
function RideCoverageReport({ state, canPdf }) {
  const [rideKey, setRideKey] = useState(RIDES[0].key);
  const [asOfDate, setAsOfDate] = useState(todayISO());
  const ride = RIDE_MAP[rideKey];

  const rows = useMemo(() => {
    const cutoff = asOfDate;
    const inScope = (dateStr) => (dateStr || "").slice(0, 10) <= cutoff;
    const out = [];

    state.employees.filter((e) => !e.archived).forEach((e) => {
      const events = [];

      // Quizzes for this ride
      (state.quizzes || []).filter((q) => q.empId === e.id && q.ride === rideKey).forEach((q) => {
        if (q.completedDate && inScope(q.completedDate)) {
          const isPass = q.status === "passed_full" || q.status === "passed_gaps" || q.status === "complete";
          const isFail = q.status === "failed";
          const scoreSuffix = (typeof q.score === "number") ? ` (${q.score}%)` : "";
          let label;
          if (isPass) label = `Quiz PASSED${scoreSuffix}`;
          else if (isFail) label = `Quiz FAILED${scoreSuffix}`;
          else label = `Quiz Completed${scoreSuffix}`;
          events.push({ date: q.completedDate, type: "Quiz", detail: label });
        }
      });

      // Observations
      (state.l2_observations || []).filter((o) => o.employee_id === e.id && o.ride === rideKey && inScope(o.created_at)).forEach((o) => {
        const progLabel = o.progression === "l2_to_l3" ? "L2→L3" : "T→L2";
        const outcomeUpper = (o.outcome || "").toUpperCase();
        events.push({
          date: (o.created_at || "").slice(0, 10), type: "Observation",
          detail: `${progLabel} observation: ${outcomeUpper}${o.observer_name ? ` (observer: ${o.observer_name})` : ""}`,
        });
      });

      // Sign-offs
      (state.signoffs || []).filter((s) => s.empId === e.id && s.ride === rideKey).forEach((s) => {
        const sigDates = (s.signatures || []).map((sig) => sig.date).filter(Boolean).sort();
        const lastSig = sigDates[sigDates.length - 1] || "";
        if (!inScope(lastSig)) return;
        const sigNames = (s.signatures || []).map((sig) => sig.name).join(", ");
        events.push({
          date: lastSig, type: "Sign-off",
          detail: `${s.kind} → ${s.targetLevel} (${s.status})${sigNames ? ` — signed by ${sigNames}` : ""}`,
        });
      });

      // History mentions
      const rideLabelLower = ride.label.toLowerCase();
      (e.history || []).forEach((h) => {
        if (!h.text || !inScope(h.date)) return;
        const t = h.text.toLowerCase();
        if (/quiz|passed|failed|sign[\s-]?off|observation|observer/.test(t)) return;
        if (!t.includes(rideLabelLower)) return;
        events.push({ date: h.date, type: "Event", detail: h.text });
      });

      if (events.length === 0) return; // no recorded action — exclude

      events.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      const last = events[events.length - 1];

      out.push({
        employee: e,
        currentLevel: e.levels[rideKey],
        eventCount: events.length,
        firstEventDate: events[0].date,
        lastEvent: last,
      });
    });

    out.sort((a, b) => (b.lastEvent.date || "").localeCompare(a.lastEvent.date || ""));
    return out;
  }, [state.employees, state.quizzes, state.l2_observations, state.signoffs, rideKey, asOfDate, ride]);

  const csvRows = useMemo(() => {
    const out = [];
    out.push([`RIDE COVERAGE REPORT`]);
    out.push([`Ride: ${ride.label}`]);
    out.push([`As of date: ${fmtDate(asOfDate)}`]);
    out.push([`Generated: ${fmtDate(todayISO())}`]);
    out.push([`Employees with recorded actions: ${rows.length}`]);
    out.push([`The system makes no assertion of certification level on the as-of date; review the audit trail to determine qualification.`]);
    out.push([]);
    out.push(["Employee", "Role", "Dept", "Current level (today)", "First recorded action date", "Latest action type", "Latest action date", "Latest action detail", "Total events on/before as-of date"]);
    rows.forEach((r) => {
      out.push([
        r.employee.name, r.employee.role || "", r.employee.department || "",
        r.currentLevel || "—",
        r.firstEventDate ? fmtDate(r.firstEventDate) : "",
        r.lastEvent.type,
        fmtDate(r.lastEvent.date),
        r.lastEvent.detail,
        r.eventCount,
      ]);
    });
    return out;
  }, [rows, ride, asOfDate]);

  return (
    <div className="report-card print-target">
      <div className="report-head">
        <h2>Ride Coverage — Point-in-Time</h2>
        <ExportButtons
          csvRows={csvRows}
          csvFilename={`ride-coverage-${rideKey}-asof-${asOfDate}.csv`}
          canPdf={canPdf}
          onPrint={() => printReport(`Ride Coverage - ${ride.label} - as of ${asOfDate}`)}
        />
      </div>
      <div className="report-controls no-print">
        <label className="field" style={{ maxWidth: 240 }}>
          <span>Ride</span>
          <select value={rideKey} onChange={(e) => setRideKey(e.target.value)}>
            {RIDES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
        <label className="field" style={{ maxWidth: 240 }}>
          <span>As of date</span>
          <input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
        </label>
      </div>

      <div className="compliance-header">
        <div className="compliance-title">Ride Coverage Report — Point-in-Time</div>
        <table className="compliance-attest">
          <tbody>
            <tr><th>Ride</th><td><strong>{ride.label}</strong></td></tr>
            <tr><th>As of date</th><td><strong>{fmtDate(asOfDate)}</strong></td></tr>
            <tr><th>Report generated</th><td>{fmtDate(todayISO())}</td></tr>
            <tr><th>Employees with recorded actions</th><td>{rows.length}</td></tr>
          </tbody>
        </table>
        <p className="compliance-disclaimer">
          This report lists every employee with a recorded training, quiz, observation, sign-off, or other certification-related action on or before the as-of date for the named ride.
          The system makes no assertion of certification level on the as-of date; the reader should review each employee's audit trail to determine qualification status.
          For full per-employee audit detail, use the Per-Employee report in Point-in-Time mode.
        </p>
      </div>

      {rows.length === 0 ? (
        <Empty text={`No employees have recorded actions for ${ride.label} on or before ${fmtDate(asOfDate)}.`} />
      ) : (
        <table className="report-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Current level<br/><span style={{ fontWeight: 400, fontSize: 10, textTransform: 'none' }}>(today)</span></th>
              <th>First action</th>
              <th>Latest action on or before as-of date</th>
              <th>Events</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.employee.id}>
                <td>
                  <strong>{r.employee.name}</strong>
                  {r.employee.role ? <div className="dim" style={{ fontSize: 11 }}>{r.employee.role}</div> : null}
                </td>
                <td>{r.currentLevel ? <span className={`tag tag-${String(r.currentLevel).toLowerCase()}`}>{r.currentLevel}</span> : <span className="dim">—</span>}</td>
                <td className="mono dim">{r.firstEventDate ? fmtDate(r.firstEventDate) : "—"}</td>
                <td>
                  <span className={`tag tag-evt tag-evt-${r.lastEvent.type.toLowerCase().replace(/[^a-z]/g, '')}`} style={{ marginRight: 6 }}>{r.lastEvent.type}</span>
                  <span className="mono dim">{fmtDate(r.lastEvent.date)}</span>
                  <div className="dim" style={{ fontSize: 12, marginTop: 2 }}>{r.lastEvent.detail}</div>
                </td>
                <td className="mono">{r.eventCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PerRideStaffingReport({ state, canPdf }) {
  const [rideKey, setRideKey] = useState(RIDES[0].key);
  const ride = RIDE_MAP[rideKey];

  const certified = useMemo(() =>
    state.employees.filter((e) => !e.archived && e.levels[rideKey])
      .map((e) => ({ ...e, level: e.levels[rideKey] }))
      .sort((a, b) => {
        // Sort by level (L4 > L3 > L2 > T > 0) then name
        const order = (l) => ({ L4: 4, L3: 3, L2: 2, T: 1 }[l] || 0);
        const d = order(b.level) - order(a.level);
        return d !== 0 ? d : a.name.localeCompare(b.name);
      }),
    [state.employees, rideKey]);

  const counts = useMemo(() => {
    const c = { T: 0, L2: 0, L3: 0, L4: 0 };
    certified.forEach((e) => { c[e.level] = (c[e.level] || 0) + 1; });
    return c;
  }, [certified]);

  const csvRows = useMemo(() => {
    const headers = ["Employee", "Role", "Department", "Level"];
    const rows = certified.map((e) => [e.name, e.role || "", e.department || "", e.level]);
    return [
      [`Per-ride staffing: ${ride.label}`],
      [`Generated: ${fmtDate(todayISO())}`],
      [`T: ${counts.T} · L2: ${counts.L2} · L3: ${counts.L3} · L4: ${counts.L4} · Total: ${certified.length}`],
      [],
      headers,
      ...rows,
    ];
  }, [certified, counts, ride]);

  return (
    <div className="report-card print-target">
      <div className="report-head">
        <h2>Per-Ride Staffing</h2>
        <ExportButtons
          csvRows={csvRows}
          csvFilename={`staffing-${rideKey}-${todayISO()}.csv`}
          canPdf={canPdf}
          onPrint={() => printReport(`${ride.label} Staffing - ${todayISO()}`)}
        />
      </div>
      <div className="report-controls no-print">
        <label className="field" style={{ maxWidth: 360 }}>
          <span>Ride</span>
          <select value={rideKey} onChange={(e) => setRideKey(e.target.value)}>
            {RIDES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
      </div>
      <p className="report-sub">
        <strong>{ride.label}</strong> · {certified.length} certified ·
        T: {counts.T} · L2: {counts.L2} · L3: {counts.L3} · L4: {counts.L4} · Generated {fmtDate(todayISO())}
      </p>
      <table className="report-table">
        <thead>
          <tr><th>Employee</th><th>Role</th><th>Level</th></tr>
        </thead>
        <tbody>
          {certified.length === 0 && <tr><td colSpan={3}><Empty text="No one is certified on this ride yet." /></td></tr>}
          {certified.map((e) => (
            <tr key={e.id}>
              <td>{e.name}</td>
              <td className="dim">{e.role || ""}</td>
              <td><span className={`tag tag-${String(e.level).toLowerCase()}`}>{e.level}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ----------------- STUCK TRAINEES ----------------- */
function StuckTraineesReport({ state, canPdf }) {
  const [thresholdDays, setThresholdDays] = useState(14);

  const stuck = useMemo(() => {
    // Heuristic: someone is "stuck" on ride R if their level is T or in-flight (sign-off pending)
    // and the most recent activity entry mentioning that ride is older than `thresholdDays`.
    const cutoffMs = Date.now() - thresholdDays * 86400000;
    const rows = [];
    state.employees.filter((e) => !e.archived).forEach((e) => {
      RIDES.forEach((r) => {
        const lvl = e.levels[r.key];
        if (lvl === "T") {
          // Find most recent history entry mentioning this ride
          const recentEntry = (e.history || []).find((h) => (h.text || "").toLowerCase().includes(r.label.toLowerCase()));
          const recentDate = recentEntry?.date;
          if (!recentDate) {
            // No history — has been at T since they were created (placeholder)
            rows.push({ employee: e, ride: r, sinceDate: null, daysStuck: null, lastNote: "(no recent activity)" });
          } else if (new Date(recentDate).getTime() < cutoffMs) {
            const daysStuck = Math.floor((Date.now() - new Date(recentDate).getTime()) / 86400000);
            rows.push({ employee: e, ride: r, sinceDate: recentDate, daysStuck, lastNote: recentEntry.text });
          }
        }
      });
    });
    return rows.sort((a, b) => (b.daysStuck || 999) - (a.daysStuck || 999));
  }, [state.employees, thresholdDays]);

  const csvRows = useMemo(() => {
    const headers = ["Employee", "Ride", "Days at T", "Last activity date", "Last activity note"];
    const rows = stuck.map((s) => [
      s.employee.name, s.ride.label,
      s.daysStuck != null ? s.daysStuck : "(unknown)",
      s.sinceDate ? fmtDate(s.sinceDate) : "—",
      s.lastNote,
    ]);
    return [
      [`Stuck trainees (>${thresholdDays} days at T)`],
      [`Generated: ${fmtDate(todayISO())}`],
      [],
      headers,
      ...rows,
    ];
  }, [stuck, thresholdDays]);

  return (
    <div className="report-card print-target">
      <div className="report-head">
        <h2>Stuck Trainees</h2>
        <ExportButtons
          csvRows={csvRows}
          csvFilename={`stuck-trainees-${thresholdDays}d-${todayISO()}.csv`}
          canPdf={canPdf}
          onPrint={() => printReport(`Stuck Trainees (${thresholdDays}d+) - ${todayISO()}`)}
        />
      </div>
      <div className="report-controls no-print">
        <label className="field" style={{ maxWidth: 240 }}>
          <span>Threshold (days at T)</span>
          <input type="number" min={1} max={365} value={thresholdDays} onChange={(e) => setThresholdDays(parseInt(e.target.value, 10) || 14)} />
        </label>
      </div>
      <p className="report-sub">Trainees currently at <strong>T</strong> on at least one ride, with no recorded activity on that ride within the last <strong>{thresholdDays} days</strong>.</p>
      <table className="report-table">
        <thead>
          <tr><th>Employee</th><th>Ride</th><th>Days stuck</th><th>Last activity</th></tr>
        </thead>
        <tbody>
          {stuck.length === 0 && <tr><td colSpan={4}><Empty text={`No trainees stuck longer than ${thresholdDays} days. 🎉`} /></td></tr>}
          {stuck.map((s, i) => (
            <tr key={i}>
              <td>{s.employee.name}</td>
              <td>{s.ride.label}</td>
              <td className="cell-warn">{s.daysStuck != null ? `${s.daysStuck}d` : "—"}</td>
              <td className="dim" style={{ maxWidth: 360 }}>{s.lastNote}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ----------------- TRAINING CERTIFICATIONS (who still owes a signature) ----------------- */
function CertificationSignoffReport({ state, canPdf }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [view, setView] = useState("outstanding"); // 'outstanding' | 'signed'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await db.loadRideAttestations(); // no arg = everyone
      if (cancelled) return;
      if (!res.ok) { setErr(res.message); setLoading(false); return; }
      setRows(res.attestations || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Map: employeeId -> Set(rides already signed)
  const signedBy = useMemo(() => {
    const m = {};
    rows.forEach((a) => { (m[a.employee_id] = m[a.employee_id] || new Set()).add(a.ride); });
    return m;
  }, [rows]);

  // Everyone at L2+ on a ride with no signature on file for it.
  const outstanding = useMemo(() => {
    const out = [];
    state.employees.filter((e) => !e.archived).forEach((e) => {
      const missing = RIDES.filter(
        (r) => isIndependent(e.levels?.[r.key]) && !(signedBy[e.id] || new Set()).has(r.key)
      );
      if (missing.length) {
        out.push({
          employee: e,
          missing,
          detail: missing.map((r) => `${r.label} (${e.levels[r.key]})`).join(", "),
        });
      }
    });
    return out.sort((a, b) => b.missing.length - a.missing.length || a.employee.name.localeCompare(b.employee.name));
  }, [state.employees, signedBy]);

  const signed = useMemo(() => {
    const byId = Object.fromEntries(state.employees.map((e) => [e.id, e]));
    return rows.map((a) => ({
      name: byId[a.employee_id]?.name || "(unknown)",
      ride: RIDE_MAP[a.ride]?.label || a.ride,
      level: a.level_at_signing || "—",
      signedAt: a.signed_at,
      signer: a.attestation?.signer_name || "—",
      how: a.attestation?.signature ? "Drawn" : "Typed",
    }));
  }, [rows, state.employees]);

  const totalOutstanding = outstanding.reduce((n, r) => n + r.missing.length, 0);

  const csvRows = useMemo(() => {
    if (view === "signed") {
      return [
        ["Training certifications on file"],
        [`Generated: ${fmtDate(todayISO())}`],
        [],
        ["Employee", "Ride", "Level at signing", "Signed", "Signed by", "Signature"],
        ...signed.map((s) => [s.name, s.ride, s.level, fmtDate(s.signedAt), s.signer, s.how]),
      ];
    }
    return [
      ["Outstanding training certifications"],
      [`Generated: ${fmtDate(todayISO())}`],
      [`${totalOutstanding} signature(s) outstanding across ${outstanding.length} employee(s)`],
      [],
      ["Employee", "Department", "Rides missing", "Detail"],
      ...outstanding.map((r) => [r.employee.name, r.employee.department || "", r.missing.length, r.detail]),
    ];
  }, [view, outstanding, signed, totalOutstanding]);

  return (
    <div className="report-card print-target">
      <div className="report-head">
        <h2>Training Certifications</h2>
        <ExportButtons
          csvRows={csvRows}
          csvFilename={`training-certifications-${view}-${todayISO()}.csv`}
          canPdf={canPdf}
          onPrint={() => printReport(`Training Certifications - ${todayISO()}`)}
        />
      </div>

      <div className="report-controls no-print" style={{ display: "flex", gap: 8 }}>
        <button
          className={view === "outstanding" ? "btn-primary" : "btn-ghost"}
          onClick={() => setView("outstanding")}
        >
          Outstanding ({totalOutstanding})
        </button>
        <button
          className={view === "signed" ? "btn-primary" : "btn-ghost"}
          onClick={() => setView("signed")}
        >
          On file ({signed.length})
        </button>
      </div>

      {loading && <Empty text="Loading certifications..." />}
      {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}

      {!loading && !err && view === "outstanding" && (
        <>
          <p className="report-sub">
            Active crew certified at <strong>L2 or above</strong> on a ride with no signed training
            certification on file for it. They're prompted to sign at their next login — this is the
            list of who hasn't yet.
          </p>
          <table className="report-table">
            <thead>
              <tr><th>Employee</th><th>Department</th><th>Missing</th><th>Rides</th></tr>
            </thead>
            <tbody>
              {outstanding.length === 0 && (
                <tr><td colSpan={4}><Empty text="Every certified operator has signed. 🎉" /></td></tr>
              )}
              {outstanding.map((r) => (
                <tr key={r.employee.id}>
                  <td>{r.employee.name}</td>
                  <td className="dim">{r.employee.department || "—"}</td>
                  <td className="cell-warn">{r.missing.length}</td>
                  <td className="dim" style={{ maxWidth: 420 }}>{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {!loading && !err && view === "signed" && (
        <>
          <p className="report-sub">Signed training certifications on file, most recent first.</p>
          <table className="report-table">
            <thead>
              <tr><th>Employee</th><th>Ride</th><th>Level</th><th>Signed</th><th>Signed by</th><th>Signature</th></tr>
            </thead>
            <tbody>
              {signed.length === 0 && (
                <tr><td colSpan={6}><Empty text="No certifications signed yet." /></td></tr>
              )}
              {signed.map((s, i) => (
                <tr key={i}>
                  <td>{s.name}</td>
                  <td>{s.ride}</td>
                  <td>{s.level}</td>
                  <td>{fmtDate(s.signedAt)}</td>
                  <td className="dim">{s.signer}</td>
                  <td className="dim">{s.how}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

/* ----------------- POLICY ACKNOWLEDGMENTS ----------------- */
function PolicyComplianceReport({ state, canPdf }) {
  const [policies, setPolicies] = useState([]);
  const [acks, setAcks] = useState([]);
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [policyId, setPolicyId] = useState("");
  const [groupIdx, setGroupIdx] = useState(() => new Map());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [p, a, v, g] = await Promise.all([
        db.loadPolicies({ includeUnpublished: true }),
        db.loadPolicyAcknowledgments(),
        db.loadPolicyViews(),
        db.loadEmployeeGroups(),
      ]);
      if (cancelled) return;
      if (!p.ok) { setErr(p.message); setLoading(false); return; }
      setPolicies(p.policies || []);
      if (a.ok) setAcks(a.acknowledgments || []);
      if (v.ok) setViews(v.views || []);
      if (g.ok) setGroupIdx(buildGroupIndex(g.groups));
      if (!policyId && (p.policies || []).length) setPolicyId(p.policies[0].id);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const policy = policies.find((p) => p.id === policyId) || null;

  // Everyone required to sign THIS policy at its current version.
  const rows = useMemo(() => {
    if (!policy) return [];
    const signedBy = {};
    const signedAt = {};
    acks.filter((a) => a.policy_id === policy.id).forEach((a) => {
      if (a.version >= (signedBy[a.employee_id] || 0)) signedAt[a.employee_id] = a.signed_at;
      signedBy[a.employee_id] = Math.max(signedBy[a.employee_id] || 0, a.version);
    });
    const lastView = {};
    views.filter((v) => v.policy_id === policy.id).forEach((v) => {
      if (!lastView[v.employee_id] || v.viewed_at > lastView[v.employee_id]) {
        lastView[v.employee_id] = v.viewed_at;
      }
    });
    return state.employees
      .filter((e) => !e.archived)
      .filter((e) => policyVisibleTo(policy, e, groupIdx))
      .map((e) => {
        const mustSign = policyRequiresSignature(policy, e, groupIdx);
        const sv = signedBy[e.id] || 0;
        const current = sv >= policy.version;
        const dueDate = mustSign ? policyDueDate(policy, e, groupIdx) : null;
        return {
          emp: e,
          mustSign,
          signedVersion: sv,
          signedAt: signedAt[e.id] || null,
          current,
          dueDate,
          overdue: mustSign && !current && !!dueDate && dueDate < todayISO(),
          lastViewed: lastView[e.id] || null,
        };
      })
      .sort((a, b) => {
        const rank = (r) => (r.overdue ? 0 : r.mustSign && !r.current ? 1 : r.mustSign ? 2 : 3);
        return rank(a) - rank(b) || a.emp.name.localeCompare(b.emp.name);
      });
  }, [policy, acks, views, state.employees, groupIdx]);

  const due = rows.filter((r) => r.mustSign && !r.current).length;
  const overdueCount = rows.filter((r) => r.overdue).length;
  const neverOpened = rows.filter((r) => !r.lastViewed).length;

  // Print exactly what this employee signed: the text as it stood at the
  // version they signed (from policy_versions), with their signature.
  const printSignedCopy = async (emp, version) => {
    const ack = acks.find((a) => a.policy_id === policy.id && a.employee_id === emp.id && a.version === version);
    if (!ack) return;
    let snap = policy;
    if (version !== policy.version) {
      const res = await db.loadPolicyVersion(policy.id, version);
      if (!res.ok || !res.version) {
        window.alert(`The text of v${version} isn't on file, so a signed copy can't be printed for that version.`);
        return;
      }
      snap = { ...policy, ...res.version, version };
    }
    printPolicy(snap, {
      statement: ack.attestation?.statement || policyAckStatement(snap.title),
      signed: {
        name: ack.attestation?.signer_name || emp.name,
        signed_at: ack.signed_at,
        signature: ack.attestation?.signature || null,
        typed_name: ack.attestation?.typed_name || null,
      },
    });
  };

  const csvRows = useMemo(() => {
    if (!policy) return [];
    return [
      [`Policy acknowledgments — ${policy.title} (v${policy.version})`],
      [`Generated: ${fmtDate(todayISO())}`],
      [`${due} outstanding · ${overdueCount} overdue · ${neverOpened} never opened`],
      [],
      ["Employee", "Department", "Job title", "Required", "Sign by", "Status", "Signed on", "Signed version", "Last viewed"],
      ...rows.map((r) => [
        r.emp.name,
        r.emp.department || "",
        r.emp.jobTitle || "",
        r.mustSign ? "Signature" : "Read only",
        r.dueDate || "",
        r.mustSign ? (r.current ? "Signed" : r.overdue ? "Overdue" : r.signedVersion ? "Outdated" : "Not signed") : "—",
        r.signedAt ? fmtDateFull(r.signedAt) : "",
        r.signedVersion || "—",
        r.lastViewed ? fmtDate(r.lastViewed) : "Never",
      ]),
    ];
  }, [policy, rows, due, overdueCount, neverOpened]);

  return (
    <div className="report-card print-target">
      <div className="report-head">
        <h2>Policy Acknowledgments</h2>
        <ExportButtons
          csvRows={csvRows}
          csvFilename={`policy-acknowledgments-${todayISO()}.csv`}
          canPdf={canPdf}
          onPrint={() => printReport(`Policy Acknowledgments - ${todayISO()}`)}
        />
      </div>

      {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
      {loading && <Empty text="Loading..." />}
      {!loading && policies.length === 0 && <Empty text="No policies created yet." />}

      {!loading && policies.length > 0 && (
        <>
          <div className="report-controls no-print">
            <label className="field" style={{ maxWidth: 420 }}>
              <span>Policy</span>
              <select value={policyId} onChange={(e) => setPolicyId(e.target.value)}>
                {policies.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title} (v{p.version}){p.published ? "" : " — draft"}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {policy && (
            <p className="report-sub">
              <strong>{due}</strong> outstanding signature{due === 1 ? "" : "s"} at v{policy.version} ·{" "}
              {overdueCount > 0 && <><strong className="cell-warn">{overdueCount}</strong> overdue ·{" "}</>}
              <strong>{neverOpened}</strong> {neverOpened === 1 ? "person has" : "people have"} never opened it.
            </p>
          )}

          <table className="report-table">
            <thead>
              <tr>
                <th>Employee</th><th>Department</th><th>Required</th><th>Sign by</th>
                <th>Status</th><th>Last viewed</th><th className="no-print">Signed copy</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7}><Empty text="This policy isn't assigned to anyone." /></td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.emp.id}>
                  <td>{r.emp.name}</td>
                  <td className="dim">{r.emp.department || "—"}</td>
                  <td className="dim">{r.mustSign ? "Signature" : "Read only"}</td>
                  <td className={r.overdue ? "cell-warn" : "dim"}>{r.dueDate ? fmtDate(r.dueDate) : "—"}</td>
                  <td className={r.mustSign && !r.current ? "cell-warn" : ""}>
                    {!r.mustSign
                      ? "—"
                      : r.current
                      ? `Signed v${r.signedVersion}${r.signedAt ? ` · ${fmtDate(r.signedAt)}` : ""}`
                      : r.overdue
                      ? (r.signedVersion ? `Overdue (has v${r.signedVersion})` : "Overdue")
                      : r.signedVersion
                      ? `Outdated (v${r.signedVersion})`
                      : "Not signed"}
                  </td>
                  <td className="dim">{r.lastViewed ? fmtDate(r.lastViewed) : "Never"}</td>
                  <td className="no-print">
                    {r.signedVersion > 0 && (
                      <button
                        className="btn-ghost-sm"
                        title={`Print ${r.emp.name}'s signed copy (v${r.signedVersion}) — Save as PDF for their file`}
                        onClick={() => printSignedCopy(r.emp, r.signedVersion)}
                      >
                        <Download size={12} /> v{r.signedVersion}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

/* ----------------- EXPIRING DOCUMENTS ----------------- */
function ExpiringDocumentsReport({ state, canPdf }) {
  const [horizonDays, setHorizonDays] = useState(60);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await db.loadAllEmployeeDocuments();
      if (cancelled) return;
      if (!res.ok) { setErr(res.message); setLoading(false); return; }
      setDocs(res.documents);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const empById = useMemo(() => Object.fromEntries(state.employees.map((e) => [e.id, e])), [state.employees]);

  const rows = useMemo(() => {
    const horizonMs = Date.now() + horizonDays * 86400000;
    return docs
      .filter((d) => d.expires_at)
      .map((d) => {
        const exp = new Date(d.expires_at).getTime();
        return { ...d, expiresMs: exp, daysOut: Math.floor((exp - Date.now()) / 86400000) };
      })
      .filter((d) => d.expiresMs <= horizonMs) // includes already-expired (negative daysOut)
      .sort((a, b) => a.expiresMs - b.expiresMs);
  }, [docs, horizonDays]);

  const csvRows = useMemo(() => {
    const headers = ["Employee", "Category", "Document", "Issued", "Expires", "Days until expiry"];
    const data = rows.map((d) => {
      const emp = empById[d.employee_id];
      return [
        emp?.name || "(unknown)",
        d.category,
        d.filename,
        d.issued_at ? fmtDate(d.issued_at) : "—",
        fmtDate(d.expires_at),
        d.daysOut,
      ];
    });
    return [
      [`Expiring documents (within ${horizonDays} days, including already-expired)`],
      [`Generated: ${fmtDate(todayISO())}`],
      [],
      headers,
      ...data,
    ];
  }, [rows, horizonDays, empById]);

  return (
    <div className="report-card print-target">
      <div className="report-head">
        <h2>Expiring Documents</h2>
        <ExportButtons
          csvRows={csvRows}
          csvFilename={`expiring-docs-${horizonDays}d-${todayISO()}.csv`}
          canPdf={canPdf}
          onPrint={() => printReport(`Expiring Documents (${horizonDays}d) - ${todayISO()}`)}
        />
      </div>
      <div className="report-controls no-print">
        <label className="field" style={{ maxWidth: 280 }}>
          <span>Horizon (days)</span>
          <select value={horizonDays} onChange={(e) => setHorizonDays(parseInt(e.target.value, 10))}>
            <option value={30}>Next 30 days</option>
            <option value={60}>Next 60 days</option>
            <option value={90}>Next 90 days</option>
            <option value={180}>Next 180 days</option>
            <option value={365}>Next 365 days</option>
          </select>
        </label>
      </div>
      <p className="report-sub">Documents expiring within the next <strong>{horizonDays} days</strong>, plus already-expired documents. Generated {fmtDate(todayISO())}.</p>
      {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
      {loading ? <Empty text="Loading…" /> : (
        <table className="report-table">
          <thead>
            <tr><th>Employee</th><th>Category</th><th>Document</th><th>Issued</th><th>Expires</th><th>Days</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6}><Empty text={`No documents expiring within ${horizonDays} days. 🎉`} /></td></tr>}
            {rows.map((d) => {
              const emp = empById[d.employee_id];
              const cls = d.daysOut < 0 ? "expiry-expired" : d.daysOut <= 30 ? "expiry-soon" : "expiry-ok";
              return (
                <tr key={d.id}>
                  <td>{emp?.name || <span className="dim">(unknown)</span>}</td>
                  <td>{d.category}</td>
                  <td className="dim">{d.filename}</td>
                  <td className="dim">{d.issued_at ? fmtDate(d.issued_at) : "—"}</td>
                  <td className="mono">{fmtDate(d.expires_at)}</td>
                  <td className={cls + " mono"}>{d.daysOut < 0 ? `${-d.daysOut}d ago` : `${d.daysOut}d`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ============================================================
   CITY OF LAS VEGAS — EMPLOYEE LISTING (Form PL202)
   Required regulatory document. Reproduces the form layout
   in full so the print output IS the form (signable, submittable).

   For each active employee:
   - Work Card # and expiration: pulled independently from all of the
     employee's Work Card documents (latest expiration wins; the number
     comes from the newest card that has one), so a second upload with a
     blank field never hides a value on another upload
   - Alcohol Awareness/TAM expiration
   - Health Card expiration — reads "Health Card" AND "Food Handler Card"
     (same SNHD card)
   - Job title from emp.jobTitle

   On screen, managers can click any value to correct it, add missing
   numbers/dates, upload missing cards, and re-tag cards filed as "Other".
   None of the editing controls print.
   ============================================================ */
const PL202_FETCH_CATEGORIES = ["Work Card", "Alcohol Awareness/TAM", "Health Card", "Food Handler Card", "Other"];

/* Given one employee's docs for a single credential type, work out what the
   form should show and which document an edit should be written to. */
function pl202PickCred(docs) {
  if (!docs || docs.length === 0) return null;
  const exp = (d) => (d.expires_at ? String(d.expires_at).slice(0, 10) : "");
  const num = (d) => (d.card_number ? String(d.card_number).trim() : "");
  // Latest expiration first (blank expirations last), then newest upload
  const byExpiry = [...docs].sort((a, b) => {
    const ae = exp(a), be = exp(b);
    if (ae !== be) { if (!ae) return 1; if (!be) return -1; return be.localeCompare(ae); }
    return String(b.uploaded_at || "").localeCompare(String(a.uploaded_at || ""));
  });
  const newest = [...docs].sort((a, b) => String(b.uploaded_at || "").localeCompare(String(a.uploaded_at || "")))[0];
  const expDoc = byExpiry.find((d) => exp(d)) || null;
  const numDoc = byExpiry.find((d) => num(d)) || null;
  return {
    count: docs.length,
    newest,
    expDoc,
    numDoc,
    number: numDoc ? num(numDoc) : "",
    expires: expDoc ? exp(expDoc) : "",
    // Fill blanks on the newest upload; edit an existing value where it lives
    numTarget: num(newest) ? (numDoc || newest) : newest,
    expTarget: exp(newest) ? (expDoc || newest) : newest,
    // Number and expiration came from two different cards
    mixedSources: !!(numDoc && expDoc && exp(numDoc) !== exp(expDoc)),
  };
}

function Pl202Report({ state, canPdf, user, onChangeJobTitle }) {
  // All state declared up front (avoid temporal-dead-zone bugs in hooks below)
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [showRestaurant, setShowRestaurant] = useState(false);
  const [editing, setEditing] = useState(null);   // { empId, field }
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [actionErr, setActionErr] = useState("");
  const [uploadFor, setUploadFor] = useState(null); // { emp, category }
  const [retag, setRetag] = useState(null);         // { emp, doc }
  const canEdit = roleRank(user?.role) >= roleRank("manager");

  // One query for every credential document (paged — never silently truncated)
  const reload = useCallback(async () => {
    setLoading(true);
    setLoadErr("");
    const res = await db.loadCredentialDocuments(PL202_FETCH_CATEGORIES);
    if (!res.ok) {
      setLoadErr(res.message || "Unknown error");
      setDocs([]);
      setLoading(false);
      return;
    }
    setDocs(res.documents);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const docsByEmp = useMemo(() => {
    const map = {};
    docs.forEach((d) => { (map[d.employee_id] = map[d.employee_id] || []).push(d); });
    return map;
  }, [docs]);

  // Employees to include — active, in Attractions dept by default
  const employees = useMemo(() => {
    return state.employees
      .filter((e) => !e.archived)
      .filter((e) => showRestaurant ? true : (e.department || "Attractions") === "Attractions")
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [state.employees, showRestaurant]);

  const fmtForForm = (iso) => {
    if (!iso) return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
    return m ? `${m[2]}/${m[3]}/${m[1]}` : "";
  };

  const rows = useMemo(() => {
    const today = todayISO();
    return employees.map((e) => {
      const mine = docsByEmp[e.id] || [];
      const work = pl202PickCred(mine.filter((d) => d.category === "Work Card"));
      const alcohol = pl202PickCred(mine.filter((d) => d.category === "Alcohol Awareness/TAM"));
      const health = pl202PickCred(mine.filter((d) => d.category === "Health Card" || d.category === "Food Handler Card"));
      const otherDocs = mine.filter((d) => d.category === "Other");
      const jobTitle = e.jobTitle || "Ride Attendant";

      const issues = [];
      if (!work) {
        issues.push(otherDocs.length > 0
          ? `No Work Card on file, but ${otherDocs.length} document${otherDocs.length > 1 ? "s are" : " is"} filed as "Other"`
          : "No Work Card uploaded");
      } else {
        if (!work.number) issues.push("Work Card # not entered");
        if (!work.expires) issues.push("Work Card expiration not entered");
        else if (work.expires < today) issues.push(`Work Card expired ${fmtForForm(work.expires)}`);
        if (work.mixedSources) issues.push("Work Card # and expiration come from different uploads; confirm the number is current");
      }
      if ((e.department || "Attractions") !== "Attractions" && jobTitle === "Ride Attendant") {
        issues.push(`Job title is the "Ride Attendant" default but department is ${e.department}`);
      }
      return { emp: e, jobTitle, work, alcohol, health, otherDocs, issues };
    });
  }, [employees, docsByEmp]);

  const needsAttention = rows.filter((r) => r.issues.length > 0);

  /* ---------- inline editing ---------- */
  const isEditing = (empId, field) => !!editing && editing.empId === empId && editing.field === field;
  const startEdit = (empId, field, value) => {
    setEditing({ empId, field });
    setDraft(value || "");
    setSaveErr("");
  };
  const cancelEdit = () => { setEditing(null); setDraft(""); setSaveErr(""); };

  const saveEdit = async (row) => {
    if (!editing) return;
    const field = editing.field;
    const val = draft.trim();

    if (field === "jobTitle") {
      const next = val || "Ride Attendant";
      if (next !== row.jobTitle && typeof onChangeJobTitle === "function") onChangeJobTitle(row.emp.id, next);
      cancelEdit();
      return;
    }

    let target = null;
    let patch = null;
    if (field === "workNum" && row.work)        { target = row.work.numTarget;    patch = { cardNumber: val }; }
    if (field === "workExp" && row.work)        { target = row.work.expTarget;    patch = { expiresAt: val }; }
    if (field === "alcoholExp" && row.alcohol)  { target = row.alcohol.expTarget; patch = { expiresAt: val }; }
    if (field === "healthExp" && row.health)    { target = row.health.expTarget;  patch = { expiresAt: val }; }
    if (!target || !patch) { cancelEdit(); return; }

    setSaving(true);
    setSaveErr("");
    const res = await db.updateEmployeeDocument(target.id, patch);
    setSaving(false);
    if (!res.ok) { setSaveErr(res.message || "Save failed"); return; }
    if (res.document) {
      setDocs((cur) => cur.map((d) => (d.id === res.document.id ? { ...d, ...res.document } : d)));
    }
    cancelEdit();
  };

  const openDoc = async (doc) => {
    setActionErr("");
    const res = await db.getEmployeeDocumentUrl(doc.storage_path, 60);
    if (!res.ok) { setActionErr(`Couldn't open ${doc.filename}: ${res.message}`); return; }
    window.open(res.url, "_blank", "noopener,noreferrer");
  };

  /* Renders one table cell's contents.
     - editor open → input + save/cancel (screen only)
     - has value   → value (click to edit, if allowed)
     - blank       → "+ Add" (doc exists) or upload button (no doc) — screen only */
  const cell = (row, field, opts) => {
    const { text, raw, type = "text", editableDoc, onUpload, uploadLabel, required } = opts;
    const empId = row.emp.id;

    if (isEditing(empId, field)) {
      return (
        <div className="no-print">
          <div className="pl202-edit">
            <input
              autoFocus
              type={type}
              value={draft}
              disabled={saving}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit(row);
                if (e.key === "Escape") cancelEdit();
              }}
            />
            <button className="pl202-edit-btn" title="Save" disabled={saving} onClick={() => saveEdit(row)}><Check size={12} /></button>
            <button className="pl202-edit-btn" title="Cancel" disabled={saving} onClick={cancelEdit}><X size={12} /></button>
          </div>
          {saveErr && <div className="pl202-edit-err">{saveErr}</div>}
        </div>
      );
    }

    if (text) {
      if (canEdit && editableDoc && !editing) {
        return (
          <button className="pl202-cell-btn print-keep" title="Click to edit" onClick={() => startEdit(empId, field, raw)}>
            {text}
          </button>
        );
      }
      return text;
    }

    if (!canEdit || editing) return "";
    if (editableDoc) {
      return (
        <button className={`pl202-add no-print ${required ? "required" : "optional"}`} onClick={() => startEdit(empId, field, "")}>
          <Plus size={11} /> Add
        </button>
      );
    }
    if (onUpload) {
      return (
        <button className={`pl202-add no-print ${required ? "required" : "optional"}`} onClick={onUpload}>
          <Upload size={11} /> {uploadLabel || "Upload"}
        </button>
      );
    }
    return "";
  };

  return (
    <div className="report-card print-target pl202-card">
      <div className="report-head no-print">
        <h2>City of Las Vegas — Employee Listing (Form PL202)</h2>
        <div className="report-export">
          {canPdf && !loading && !loadErr && (
            <button className="btn-mini ghost" onClick={() => { cancelEdit(); printReport(`PL202 - Employee Listing - ${todayISO()}`); }}>
              <Download size={13} /> Print / Save PDF
            </button>
          )}
        </div>
      </div>

      <div className="report-controls no-print" style={{ flexDirection: "column", alignItems: "stretch", gap: 10 }}>
        <div className="pl202-about">
          This is the complete City of Las Vegas Form PL202, ready to print and sign. It lists active Attractions employees
          with credentials pulled from their uploaded documents.
          {canEdit && (
            <>{" "}Click any value in the table to correct it. Blank cells show <strong>Add</strong> or <strong>Upload</strong>{" "}
            buttons; move your mouse over a row to see them for Alcohol Awareness and Health Card. None of these buttons print.</>
          )}
        </div>

        <label className="report-checkbox">
          <input type="checkbox" checked={showRestaurant} onChange={(e) => setShowRestaurant(e.target.checked)} />
          <span>Include Restaurant department employees (e.g. for separate Beer & Wine license listing)</span>
        </label>

        {actionErr && <div className="pl202-load-err"><AlertCircle size={14} /> {actionErr}</div>}

        {!loading && !loadErr && needsAttention.length > 0 && (
          <details className="pl202-issues" open>
            <summary>
              <AlertTriangle size={14} /> {needsAttention.length} of {rows.length} employees need attention before this form is complete
            </summary>
            <ul>
              {needsAttention.map((r) => (
                <li key={r.emp.id}>
                  <strong>{r.emp.name}</strong>: {r.issues.join("; ")}
                  {!r.work && r.otherDocs.length > 0 && (
                    <div className="pl202-issue-docs">
                      {r.otherDocs.map((d) => (
                        <div key={d.id} className="pl202-issue-doc">
                          <span className="pl202-issue-filename" title={d.filename}>{d.filename}</span>
                          <button className="pl202-link" onClick={() => openDoc(d)}>View</button>
                          {canEdit && <button className="pl202-link" onClick={() => setRetag({ emp: r.emp, doc: d })}>Re-tag as Work Card</button>}
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      {loading ? <Empty text="Loading employee credentials…" /> : loadErr ? (
        <div className="pl202-load-err no-print">
          <AlertCircle size={14} />
          <span>Employee credentials didn't load, so this form can't be trusted and isn't shown. ({loadErr})</span>
          <button className="btn-mini ghost" onClick={reload}><RotateCcw size={12} /> Try again</button>
        </div>
      ) : (
        /* The print form */
        <div className="pl202-form">
          {/* Header */}
          <div className="pl202-header">
            <div className="pl202-header-left">
              <div className="pl202-seal">CITY OF<br/>LAS VEGAS<br/>NEVADA</div>
            </div>
            <div className="pl202-header-mid">
              <div className="pl202-agency">CITY OF LAS VEGAS</div>
              <div className="pl202-agency">DEPARTMENT OF COMMUNITY DEVELOPMENT</div>
              <div className="pl202-agency">BUSINESS LICENSING DIVISION</div>
            </div>
            <div className="pl202-header-right">
              <div>Fax (702) 382-6642</div>
              <div>TDD (702) 386-9108</div>
              <div>E-mail us at <span className="pl202-email">license@lasvegasnevada.gov</span></div>
            </div>
          </div>

          <h2 className="pl202-title">Employee Listing</h2>

          {/* Business info */}
          <div className="pl202-business">
            <div className="pl202-business-row">
              <div className="pl202-bf">
                <span className="pl202-label">Business Name:</span>
                <div className="pl202-value">{BUSINESS_INFO.name}</div>
              </div>
              <div className="pl202-bf">
                <span className="pl202-label">Business License #(s):</span>
                <div className="pl202-value pl202-licenses">
                  <div><strong>{BUSINESS_INFO.amusementLicense}</strong> &mdash; A06 Amusement Park</div>
                  <div><strong>{BUSINESS_INFO.coinOpLicense}</strong> &mdash; C08 Coin Operated Amusement</div>
                  <div><strong>{BUSINESS_INFO.liquorLicense}</strong> &mdash; L01 Beer &amp; Wine On-Premise</div>
                </div>
              </div>
            </div>
            <div className="pl202-business-row">
              <div className="pl202-bf">
                <span className="pl202-label">Business Address:</span>
                <div className="pl202-value">{BUSINESS_INFO.address}</div>
                <div className="pl202-sub">(Number, Street, City, State and Zip Code)</div>
              </div>
              <div className="pl202-bf">
                <span className="pl202-label">Business Phone #:</span>
                <div className="pl202-value">{BUSINESS_INFO.phone}</div>
              </div>
            </div>
          </div>

          <p className="pl202-instr">
            Print or type each employee's name, job title, work card number, expiration date and health card expiration date (if applicable):
          </p>

          {/* Employee table */}
          <table className="pl202-table">
            <thead>
              <tr>
                <th>Employee's Name</th>
                <th>Job Title</th>
                <th>Work Card Number</th>
                <th>Work Card<br/>Expiration Date</th>
                <th>Alcohol Awareness<br/>Expiration Date</th>
                <th>Health Card<br/>Expiration Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.emp.id}>
                  <td>{r.emp.name}</td>
                  <td>
                    {cell(r, "jobTitle", {
                      text: r.jobTitle,
                      raw: r.jobTitle,
                      editableDoc: typeof onChangeJobTitle === "function",
                    })}
                  </td>
                  <td>
                    {cell(r, "workNum", {
                      text: r.work?.number || "",
                      raw: r.work?.number || "",
                      editableDoc: !!r.work,
                      onUpload: () => setUploadFor({ emp: r.emp, category: "Work Card" }),
                      uploadLabel: "Upload Work Card",
                      required: true,
                    })}
                  </td>
                  <td>
                    {cell(r, "workExp", {
                      text: fmtForForm(r.work?.expires),
                      raw: r.work?.expires || "",
                      type: "date",
                      editableDoc: !!r.work,
                      required: true,
                    })}
                  </td>
                  <td>
                    {cell(r, "alcoholExp", {
                      text: fmtForForm(r.alcohol?.expires),
                      raw: r.alcohol?.expires || "",
                      type: "date",
                      editableDoc: !!r.alcohol,
                      onUpload: () => setUploadFor({ emp: r.emp, category: "Alcohol Awareness/TAM" }),
                      uploadLabel: "Upload",
                    })}
                  </td>
                  <td>
                    {cell(r, "healthExp", {
                      text: fmtForForm(r.health?.expires),
                      raw: r.health?.expires || "",
                      type: "date",
                      editableDoc: !!r.health,
                      onUpload: () => setUploadFor({ emp: r.emp, category: "Health Card" }),
                      uploadLabel: "Upload",
                    })}
                  </td>
                </tr>
              ))}
              {/* Pad with blank rows to look like the form (up to 15 total) */}
              {Array.from({ length: Math.max(0, 15 - rows.length) }).map((_, i) => (
                <tr key={`blank-${i}`}><td></td><td></td><td></td><td></td><td></td><td></td></tr>
              ))}
            </tbody>
          </table>

          {/* Regulatory text */}
          <p className="pl202-warn">
            EMPLOYEE LIST MUST BE MAINTAINED, CURRENT AND AVAILABLE FOR INSPECTION AT ALL TIMES
          </p>
          <p className="pl202-statute"><strong>LVMC 6.86 Employers - Duties</strong></p>
          <p className="pl202-statute-body">
            (A) Maintain and make available for inspection, during normal business hours, by Metro or the Department a list of all employees who have been employed in capacities that require a work card.
          </p>

          <p className="pl202-warn-small">
            Non-compliance may result in a criminal misdemeanor citation being issued.<br/>
            Misdemeanor violations may be punishable by a fine of $1,000.00 and/or 6 months in jail for each offense.
          </p>

          {/* Signature */}
          <div className="pl202-sig">
            <div className="pl202-sig-line"></div>
            <div className="pl202-sig-label">Signature of Manager Completing Form</div>
          </div>

          <div className="pl202-foot">
            <span>Employee Listing - Form PL202</span>
            <span>revised 08-08-22</span>
          </div>
        </div>
      )}

      {uploadFor && (
        <DocumentUploadModal
          emp={uploadFor.emp}
          initialCategory={uploadFor.category}
          onClose={() => setUploadFor(null)}
          onUploaded={() => { setUploadFor(null); reload(); }}
        />
      )}

      {retag && (
        <DocumentEditModal
          emp={retag.emp}
          doc={retag.doc}
          initialCategory="Work Card"
          onClose={() => setRetag(null)}
          onSaved={() => { setRetag(null); reload(); }}
        />
      )}
    </div>
  );
}

function QuizEditorView({ user, questions, onChanged }) {
  const RIDES_FOR_TAGS = [
    { key: "slide", label: "Slide" },
    { key: "tornado", label: "Tornado" },
    { key: "dragon", label: "Dragon" },
    { key: "kiddieKart", label: "Kiddie Kart" },
    { key: "road", label: "Road" },
    { key: "sprint", label: "Sprint" },
    { key: "euro", label: "Euro" },
  ];
  const [editing, setEditing] = useState(null); // null | {} new | existing
  const [filter, setFilter] = useState("all"); // 'all' | ride key | 'universal' | 'untagged'
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const isUniversal = (q) => RIDES_FOR_TAGS.every((r) => (q.tags || []).includes(r.key));
  const filtered = (questions || []).filter((q) => {
    if (search && !q.text.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === "all") return true;
    if (filter === "universal") return isUniversal(q);
    if (filter === "untagged") return !q.tags || q.tags.length === 0;
    return (q.tags || []).includes(filter);
  });

  // Counts per filter
  const counts = {
    all: questions.length,
    universal: questions.filter(isUniversal).length,
    untagged: questions.filter((q) => !q.tags || q.tags.length === 0).length,
  };
  RIDES_FOR_TAGS.forEach((r) => {
    counts[r.key] = questions.filter((q) => (q.tags || []).includes(r.key)).length;
  });

  const startNew = () => setEditing({ text: "", choices: ["", "", "", ""], correct: 0, tags: [] });
  const archive = async (id) => {
    if (!confirm("Archive this question? It will no longer appear in new quizzes.")) return;
    setBusy(true);
    const res = await db.archiveQuizQuestion(id);
    setBusy(false);
    if (!res.ok) setErr(res.message);
    else { setErr(""); onChanged(); }
  };

  return (
    <div className="users-view">
      <div className="users-head">
        <div>
          <h2 className="page-title"><FileQuestion size={20} /> Quiz Editor</h2>
          <p className="page-sub">{questions.length} active questions. Click a question to edit. Tag each question to the rides it applies to.</p>
        </div>
        <button className="btn-primary" onClick={startNew}><Plus size={16} /> New question</button>
      </div>

      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <span style={{ fontSize: 11, textTransform: "uppercase", color: "var(--txt3)", letterSpacing: 0.5, marginRight: 4 }}>Filter:</span>
        {[
          { k: "all", l: `All (${counts.all})` },
          { k: "universal", l: `Universal (${counts.universal})` },
          ...RIDES_FOR_TAGS.map((r) => ({ k: r.key, l: `${r.label} (${counts[r.key]})` })),
          { k: "untagged", l: `Untagged (${counts.untagged})` },
        ].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)}
            className={filter === f.k ? "btn-primary" : "btn-ghost"}
            style={{ padding: "4px 10px", fontSize: 12 }}>{f.l}</button>
        ))}
      </div>

      <div className="search" style={{ marginBottom: 14, maxWidth: 360 }}>
        <Search size={16} />
        <input placeholder="Search question text…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {err && <div className="login-err" style={{ marginBottom: 10 }}><AlertCircle size={14} /> {err}</div>}

      {filtered.length === 0 ? (
        <Empty text={questions.length === 0 ? "No questions yet. Click 'New question' to add one." : "No questions match this filter."} />
      ) : (
        <div className="users-table" style={{ gridTemplateColumns: "1fr auto" }}>
          {filtered.map((q) => (
            <React.Fragment key={q.id}>
              <div className="users-row" style={{ gridTemplateColumns: "1fr auto", alignItems: "start", padding: "12px 14px" }}>
                <div>
                  <div style={{ fontSize: 13, color: "var(--txt)", marginBottom: 6 }}>{q.text}</div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 4 }}>
                    {q.mandatory && <span className="tag tag-mandatory" style={{ fontSize: 10 }}>★ MANDATORY</span>}
                    {(q.tags || []).length === 0 && <span className="tag tag-muted" style={{ fontSize: 10 }}>UNTAGGED</span>}
                    {isUniversal(q) ? (
                      <span className="tag tag-info" style={{ fontSize: 10 }}>UNIVERSAL (all 7)</span>
                    ) : (q.tags || []).map((t) => {
                      const r = RIDES_FOR_TAGS.find((x) => x.key === t);
                      return r ? <span key={t} className="tag tag-info" style={{ fontSize: 10 }}>{r.label}</span> : null;
                    })}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--txt3)" }}>
                    Correct: <b style={{ color: "var(--accent)" }}>{q.choices[q.correct]}</b>
                  </div>
                </div>
                <div className="u-actions" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <button className="btn-ghost-sm" onClick={() => setEditing(q)}>Edit</button>
                  <button className="btn-ghost-sm danger" disabled={busy} onClick={() => archive(q.id)}><Trash2 size={12} /> Archive</button>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {editing && (
        <QuestionEditModal
          question={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged(); }}
        />
      )}
    </div>
  );
}

function QuestionEditModal({ question, onClose, onSaved }) {
  const RIDES_FOR_TAGS = [
    { key: "slide", label: "Slide" },
    { key: "tornado", label: "Tornado" },
    { key: "dragon", label: "Dragon" },
    { key: "kiddieKart", label: "Kiddie Kart" },
    { key: "road", label: "Road" },
    { key: "sprint", label: "Sprint" },
    { key: "euro", label: "Euro" },
  ];
  const isNew = !question.id;
  const [text, setText] = useState(question.text || "");
  const [choices, setChoices] = useState(question.choices || ["", "", "", ""]);
  const [correct, setCorrect] = useState(question.correct ?? 0);
  const [tags, setTags] = useState(question.tags || []);
  const [mandatory, setMandatory] = useState(!!question.mandatory);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggleTag = (key) => {
    setTags((t) => t.includes(key) ? t.filter((x) => x !== key) : [...t, key]);
  };
  const setAllRides = () => setTags(RIDES_FOR_TAGS.map((r) => r.key));
  const clearTags = () => setTags([]);

  const save = async () => {
    setErr("");
    if (!text.trim()) { setErr("Question text is required."); return; }
    if (choices.some((c) => !c.trim())) { setErr("All 4 choices must be filled in."); return; }
    if (tags.length === 0) { setErr("Tag the question to at least one ride."); return; }
    setBusy(true);
    const payload = { text: text.trim(), choices: choices.map((c) => c.trim()), correct, tags, mandatory };
    const res = isNew ? await db.createQuizQuestion(payload) : await db.updateQuizQuestion(question.id, payload);
    setBusy(false);
    if (!res.ok) { setErr(res.message); return; }
    onSaved();
  };

  return (
    <Modal onClose={onClose} wide>
      <div className="modal-head">
        <h3><FileQuestion size={18} /> {isNew ? "New question" : "Edit question"}</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <label className="field"><span>Question text</span>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
            placeholder="e.g. The minimum height for a Sprint passenger is:"
            style={{ width: "100%", padding: 8, background: "var(--panel2)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 6, fontSize: 13 }} />
        </label>
        <div className="field">
          <span>Answer choices (mark the correct one)</span>
          {choices.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
              <input type="radio" name="correct" checked={correct === i} onChange={() => setCorrect(i)} />
              <input value={c} onChange={(e) => {
                const nc = [...choices]; nc[i] = e.target.value; setChoices(nc);
              }} placeholder={`Choice ${i + 1}`} style={{ flex: 1 }} />
            </div>
          ))}
        </div>
        <div className="field">
          <span>Applies to which rides? (tag at least one)</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
            {RIDES_FOR_TAGS.map((r) => (
              <label key={r.key} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={tags.includes(r.key)} onChange={() => toggleTag(r.key)} />
                <span>{r.label}</span>
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button type="button" className="btn-ghost" style={{ padding: "3px 8px", fontSize: 11 }} onClick={setAllRides}>Tag all 7 (universal)</button>
            <button type="button" className="btn-ghost" style={{ padding: "3px 8px", fontSize: 11 }} onClick={clearTags}>Clear</button>
          </div>
        </div>
        <div className="field">
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "8px 10px", background: "var(--panel2)", borderRadius: 6, border: "1px solid var(--line)" }}>
            <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} />
            <span>
              <strong style={{ fontSize: 13 }}>Always include in quizzes</strong>
              <span style={{ display: "block", fontSize: 11, color: "var(--txt3)", marginTop: 2 }}>
                Mandatory questions appear in every quiz for the rides they're tagged to. Use sparingly — too many mandatory questions reduce randomization.
              </span>
            </span>
          </label>
        </div>
        <p className="sched-target">Tip: questions tagged to all 7 rides are "universal" — they appear in every quiz (capped at 3 per quiz).</p>
        {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy} onClick={save}>{busy ? "Saving…" : (isNew ? "Create" : "Save")}</button>
      </div>
    </Modal>
  );
}

/* ============================================================
   L2 OBSERVATION MODAL (T→L2 advancement check, Manager+)
   ============================================================ */
function L2ObservationModal({ emp, ride, progression, quizRef, checklists, user, state, onClose, onSubmit }) {
  // progression: 't_to_l2' | 'l2_to_l3'. Defaults to t_to_l2 for backward compat.
  const prog = progression || "t_to_l2";
  const isL2toL3 = prog === "l2_to_l3";
  const targetLevel = isL2toL3 ? "L3" : "L2";

  // Pull the checklist for this ride + progression, grouped by category, sorted
  const rideItems = (checklists || []).filter((c) => c.ride === ride && (c.progression || "t_to_l2") === prog);
  const grouped = React.useMemo(() => {
    const by = {};
    rideItems.forEach((it) => {
      const cat = it.category || "General";
      if (!by[cat]) by[cat] = [];
      by[cat].push(it);
    });
    return by;
  }, [rideItems]);
  const categories = Object.keys(grouped);

  // Per-item state: {item_id => {result: 'pass'|'needs_work', note: ''}}
  const [results, setResults] = useState({});
  const [overallNote, setOverallNote] = useState("");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Date guards differ by progression:
  //   T→L2: must be ≥1 day after the trainer sign-off
  //   L2→L3: must be ≥1 day after the most recent L2→L3 observation on this ride for this emp
  //          (the trainer signoff was long ago for L2 employees, so we use the prior obs)
  let dateBlocked = false;
  let blockReason = null;
  if (isL2toL3) {
    const myPriorObs = (state.l2_observations || [])
      .filter((o) => o.employee_id === emp.id && o.ride === ride && (o.progression || "t_to_l2") === "l2_to_l3")
      .sort((a, b) => new Date(b.obs_date || b.created_at) - new Date(a.obs_date || a.created_at));
    if (myPriorObs.length > 0) {
      const last = myPriorObs[0];
      const lastDate = (last.obs_date || last.created_at || "").slice(0, 10);
      if (lastDate) {
        const diffDays = Math.floor((new Date(todayISO()) - new Date(lastDate)) / (1000 * 60 * 60 * 24));
        if (diffDays < 1) {
          dateBlocked = true;
          blockReason = `The previous L2→L3 observation was ${fmtDate(lastDate)}. The next must be at least 1 day later.`;
        }
      }
    }
  } else {
    const trainerSignoff = (state.signoffs || []).find((so) =>
      so.empId === emp.id && so.ride === ride && so.kind === "train_gate" && so.status === "complete"
    );
    if (trainerSignoff?.date) {
      const diffDays = Math.floor((new Date(todayISO()) - new Date(trainerSignoff.date)) / (1000 * 60 * 60 * 24));
      if (diffDays < 1) {
        dateBlocked = true;
        blockReason = `This observation must be at least 1 calendar day after the trainer sign-off (${fmtDate(trainerSignoff.date)}). Please return tomorrow.`;
      }
    }
  }

  // For L2→L3: compute progress (X of 4 passes, by N observers)
  const priorL2toL3 = (state.l2_observations || []).filter(
    (o) => o.employee_id === emp.id && o.ride === ride && (o.progression || "t_to_l2") === "l2_to_l3"
  );
  const priorPasses = priorL2toL3.filter((o) => o.outcome === "pass");
  const priorObservers = new Set(priorPasses.map((o) => o.observer_id));
  const sameObserverAlready = priorPasses.some((o) => o.observer_id === user.id);

  const setResult = (id, item, val) => {
    setResults((s) => ({ ...s, [id]: { ...(s[id] || {}), item_id: id, item_text: item, result: val } }));
  };
  const setItemNote = (id, note) => {
    setResults((s) => ({ ...s, [id]: { ...(s[id] || {}), item_id: id, note } }));
  };

  const totalItems = rideItems.length;
  const answered = Object.values(results).filter((r) => r.result === "pass" || r.result === "needs_work").length;
  const needsWorkCount = Object.values(results).filter((r) => r.result === "needs_work").length;
  const allAnswered = answered === totalItems;
  const passOverall = allAnswered && needsWorkCount === 0;
  const outcome = passOverall ? "pass" : "needs_work";

  const allowed = roleRank(user.role) >= roleRank("manager");

  // L2→L3 outcome label: needs to convey what this pass means for the count
  const willAdvance = isL2toL3 && passOverall && (priorPasses.length + 1 >= 4) && (priorObservers.size + (sameObserverAlready ? 0 : 1) >= 2);
  const outcomeLabel = isL2toL3
    ? (allAnswered
        ? (passOverall
            ? (willAdvance
                ? `Outcome: PASS → advance to L3 (4 passes by ≥2 observers met)`
                : `Outcome: PASS (${priorPasses.length + 1}/4, ${priorObservers.size + (sameObserverAlready ? 0 : 1)}/2 observers)`)
            : `Outcome: NEEDS WORK (${needsWorkCount} flagged)`)
        : "Outcome: incomplete")
    : (allAnswered
        ? (passOverall ? `Outcome: PASS → advance to L2` : `Outcome: NEEDS WORK (${needsWorkCount} flagged)`)
        : "Outcome: incomplete");

  const submit = async () => {
    setErr("");
    if (!allowed) { setErr("Only Manager+ can complete L2 observations."); return; }
    if (dateBlocked) { setErr(blockReason); return; }
    if (!allAnswered) { setErr(`Please mark each of the ${totalItems} checklist items (currently ${answered}/${totalItems}).`); return; }
    if (!sig) { setErr("Signature is required."); return; }
    setBusy(true);
    const resultArray = Object.values(results).map((r) => ({
      item_id: r.item_id, item_text: r.item_text, result: r.result, note: r.note || "",
    }));
    await onSubmit({
      results: resultArray,
      notes: overallNote.trim(),
      observer_sig: sig,
      outcome,
      progression: prog,
    });
    setBusy(false);
  };

  const titleSuffix = isL2toL3 ? "L2→L3 Observation" : "T→L2 Observation";

  if (totalItems === 0) {
    return (
      <Modal onClose={onClose}>
        <div className="modal-head">
          <h3><Eye size={18} /> {titleSuffix} — {emp.name}</h3>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <Empty text={`No ${isL2toL3 ? "L2→L3" : "T→L2"} checklist items have been defined for ${RIDE_MAP[ride].label} yet. An AGM/GM can add them via the Checklist Editor.`} />
        </div>
        <div className="modal-foot">
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} wide>
      <div className="modal-head">
        <h3><Eye size={18} /> {titleSuffix} — {emp.name} · {RIDE_MAP[ride].label}</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {!allowed && <div className="login-err"><Lock size={14} /> Only Manager+ can complete observations.</div>}
        {dateBlocked && <div className="login-err"><AlertCircle size={14} /> {blockReason}</div>}

        {(() => {
          // Topics missed on the passed quiz (and the exact questions, when saved),
          // so the observer goes over them with the employee.
          const rq = quizRef?.missedTopics ? quizRef : (state?.quizzes || [])
            .filter((q) => q.empId === emp.id && q.ride === ride && (q.status === "passed_gaps" || q.status === "passed_full"))
            .sort((a, b) => String(b.completedDate || "").localeCompare(String(a.completedDate || "")))[0];
          if (isL2toL3 || !rq || !(rq.missedTopics || []).length) return null;
          const last = (rq.attemptHistory || [])[rq.attemptHistory.length - 1];
          return (
            <div className="obs-review">
              <strong><FileQuestion size={14} /> Review with {emp.name.split(" ")[0]} during this observation</strong>
              <div className="ri-sub">They passed the quiz at {rq.score}% but missed:</div>
              <div className="qr-topics" style={{ marginTop: 6 }}>
                {rq.missedTopics.map((t) => <span className="qr-topic" key={t}>{topicLabel(ride, t)}</span>)}
              </div>
              {last?.missed?.length > 0 && (
                <ol className="qr-missed" style={{ marginTop: 8 }}>
                  {last.missed.map((m, i) => (
                    <li key={i}><div className="qm-q">{m.q}</div><div className="qm-a good"><Check size={12} /> Correct: {m.correct ?? "—"}</div></li>
                  ))}
                </ol>
              )}
            </div>
          );
        })()}

        <div className="signoff-note">
          <p><Eye size={14} />{" "}
            {isL2toL3
              ? <>Observe {emp.name} operating the {RIDE_MAP[ride].label} ride at L2 → L3 standard. To advance to L3, need <strong>4 passing observations from at least 2 different observers</strong>. Currently: <strong>{priorPasses.length}/4 passes</strong>, <strong>{priorObservers.size}/2 observers</strong>{sameObserverAlready ? " (you have already passed this trainee on this ride)" : ""}.</>
              : <>Observe {emp.name} operating the {RIDE_MAP[ride].label} ride with confidence and no mistakes. Mark each item as <strong>Pass</strong> or <strong>Needs Work</strong>. Any "Needs Work" keeps the trainee at T and flags those items for additional training.</>}
          </p>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "12px 0 4px" }}>
          <div style={{ fontSize: 12, color: "var(--txt2)" }}>{answered}/{totalItems} items marked{needsWorkCount > 0 ? ` · ${needsWorkCount} need work` : ""}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: passOverall ? "var(--accent)" : needsWorkCount > 0 ? "#d99a2b" : "var(--txt3)" }}>
            {outcomeLabel}
          </div>
        </div>

        {categories.map((cat) => (
          <div key={cat} style={{ marginTop: 14 }}>
            <h4 style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: "var(--txt3)", margin: "0 0 8px", borderBottom: "1px solid var(--line)", paddingBottom: 4 }}>{cat}</h4>
            {grouped[cat].map((item) => {
              const r = results[item.id] || {};
              return (
                <div key={item.id} style={{ padding: "10px 8px", borderBottom: "1px solid var(--line)" }}>
                  <div style={{ fontSize: 13, marginBottom: 6 }}>{item.item}</div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                    <button type="button"
                      onClick={() => setResult(item.id, item.item, "pass")}
                      style={{
                        padding: "4px 10px", fontSize: 11, borderRadius: 4, cursor: "pointer",
                        background: r.result === "pass" ? "rgba(76,175,80,0.20)" : "var(--panel2)",
                        border: r.result === "pass" ? "1px solid #4caf50" : "1px solid var(--line)",
                        color: r.result === "pass" ? "#4caf50" : "var(--txt2)", fontWeight: 600,
                      }}>✓ Pass</button>
                    <button type="button"
                      onClick={() => setResult(item.id, item.item, "needs_work")}
                      style={{
                        padding: "4px 10px", fontSize: 11, borderRadius: 4, cursor: "pointer",
                        background: r.result === "needs_work" ? "rgba(217,154,43,0.20)" : "var(--panel2)",
                        border: r.result === "needs_work" ? "1px solid #d99a2b" : "1px solid var(--line)",
                        color: r.result === "needs_work" ? "#d99a2b" : "var(--txt2)", fontWeight: 600,
                      }}>⚠ Needs Work</button>
                  </div>
                  {r.result === "needs_work" && (
                    <input
                      placeholder="What needs improvement?"
                      value={r.note || ""}
                      onChange={(e) => setItemNote(item.id, e.target.value)}
                      style={{ width: "100%", padding: 6, fontSize: 12, background: "var(--panel2)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 4 }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}

        <label className="field" style={{ marginTop: 16 }}>
          <span>Overall notes (optional)</span>
          <textarea value={overallNote} onChange={(e) => setOverallNote(e.target.value)} rows={3}
            placeholder="Any additional observations about this trainee's performance..."
            style={{ width: "100%", padding: 8, background: "var(--panel2)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 6, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
        </label>

        <div className="signer-box">
          <div className="signer-id"><ShieldCheck size={15} /> Signing as <strong>{user.name}</strong> — {user.title || ROLES[user.role]?.label}</div>
          <SignatureCapture onChange={setSig} />
        </div>

        {err && <div className="login-err" style={{ marginTop: 10 }}><AlertCircle size={14} /> {err}</div>}
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy || !allowed || dateBlocked || !allAnswered || !sig} onClick={submit}>
          {busy ? "Saving…" : passOverall
            ? (isL2toL3
                ? (willAdvance ? "Submit & advance to L3" : "Submit pass")
                : "Submit & advance to L2")
            : "Submit (needs work)"}
        </button>
      </div>
    </Modal>
  );
}

/* ============================================================
   CHECKLIST EDITOR (AGM/GM+)
   ============================================================ */
function ChecklistEditorView({ user, items, onChanged }) {
  const RIDES_FOR_TAGS = [
    { key: "slide", label: "Slide" },
    { key: "tornado", label: "Tornado" },
    { key: "dragon", label: "Dragon" },
    { key: "kiddieKart", label: "Kiddie Kart" },
    { key: "road", label: "Road" },
    { key: "sprint", label: "Sprint" },
    { key: "euro", label: "Euro" },
  ];
  const [selectedRide, setSelectedRide] = useState("kiddieKart");
  const [selectedProg, setSelectedProg] = useState("t_to_l2");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const filtered = (items || []).filter((i) => i.ride === selectedRide && (i.progression || "t_to_l2") === selectedProg);
  // Group by category for display
  const grouped = React.useMemo(() => {
    const by = {};
    filtered.forEach((it) => {
      const cat = it.category || "General";
      if (!by[cat]) by[cat] = [];
      by[cat].push(it);
    });
    return by;
  }, [filtered]);

  // Counts per ride for currently-selected progression
  const counts = {};
  RIDES_FOR_TAGS.forEach((r) => {
    counts[r.key] = (items || []).filter((i) => i.ride === r.key && (i.progression || "t_to_l2") === selectedProg).length;
  });

  const startNew = () => setEditing({ ride: selectedRide, category: "", item: "", sort_order: 100, progression: selectedProg });
  const archive = async (id) => {
    if (!confirm("Archive this checklist item? It will no longer appear in new observations (existing observation records keep it).")) return;
    setBusy(true);
    const res = await db.archiveObsChecklistItem(id);
    setBusy(false);
    if (!res.ok) setErr(res.message);
    else { setErr(""); onChanged(); }
  };

  return (
    <div className="users-view">
      <div className="users-head">
        <div>
          <h2 className="page-title"><Eye size={20} /> L2 Observation Checklist Editor</h2>
          <p className="page-sub">Define the checklist items Manager+ uses when observing a trainee for level advancement. {(items || []).length} active items total.</p>
        </div>
        <button className="btn-primary" onClick={startNew}><Plus size={16} /> New item</button>
      </div>

      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 11, textTransform: "uppercase", color: "var(--txt3)", letterSpacing: 0.5, marginRight: 4 }}>Progression:</span>
        <button onClick={() => setSelectedProg("t_to_l2")}
          className={selectedProg === "t_to_l2" ? "btn-primary" : "btn-ghost"}
          style={{ padding: "4px 10px", fontSize: 12 }}>T → L2</button>
        <button onClick={() => setSelectedProg("l2_to_l3")}
          className={selectedProg === "l2_to_l3" ? "btn-primary" : "btn-ghost"}
          style={{ padding: "4px 10px", fontSize: 12 }}>L2 → L3</button>
      </div>

      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <span style={{ fontSize: 11, textTransform: "uppercase", color: "var(--txt3)", letterSpacing: 0.5, marginRight: 4 }}>Ride:</span>
        {RIDES_FOR_TAGS.map((r) => (
          <button key={r.key} onClick={() => setSelectedRide(r.key)}
            className={selectedRide === r.key ? "btn-primary" : "btn-ghost"}
            style={{ padding: "4px 10px", fontSize: 12 }}>{r.label} ({counts[r.key]})</button>
        ))}
      </div>

      {err && <div className="login-err" style={{ marginBottom: 10 }}><AlertCircle size={14} /> {err}</div>}

      {filtered.length === 0 ? (
        <Empty text={`No ${selectedProg === "l2_to_l3" ? "L2→L3" : "T→L2"} checklist items yet for ${RIDE_MAP[selectedRide]?.label}. Click 'New item' to add one.`} />
      ) : (
        Object.keys(grouped).map((cat) => (
          <div key={cat} style={{ marginBottom: 18 }}>
            <h4 style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: "var(--accent)", margin: "0 0 8px" }}>{cat}</h4>
            <div className="users-table" style={{ gridTemplateColumns: "1fr auto" }}>
              {grouped[cat].map((it) => (
                <div className="users-row" key={it.id} style={{ gridTemplateColumns: "1fr auto", alignItems: "start", padding: "10px 12px" }}>
                  <div>
                    <div style={{ fontSize: 13, color: "var(--txt)", marginBottom: 4 }}>{it.item}</div>
                    <div style={{ fontSize: 10, color: "var(--txt3)" }}>order: {it.sort_order}</div>
                  </div>
                  <div className="u-actions" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button className="btn-ghost-sm" onClick={() => setEditing(it)}>Edit</button>
                    <button className="btn-ghost-sm danger" disabled={busy} onClick={() => archive(it.id)}><Trash2 size={12} /> Archive</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {editing && (
        <ChecklistItemEditModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged(); }}
        />
      )}
    </div>
  );
}

function ChecklistItemEditModal({ item, onClose, onSaved }) {
  const RIDES_FOR_TAGS = [
    { key: "slide", label: "Slide" }, { key: "tornado", label: "Tornado" }, { key: "dragon", label: "Dragon" },
    { key: "kiddieKart", label: "Kiddie Kart" }, { key: "road", label: "Road" },
    { key: "sprint", label: "Sprint" }, { key: "euro", label: "Euro" },
  ];
  const isNew = !item.id;
  const [ride, setRide] = useState(item.ride || "kiddieKart");
  const [progression, setProgression] = useState(item.progression || "t_to_l2");
  const [category, setCategory] = useState(item.category || "");
  const [text, setText] = useState(item.item || "");
  const [sortOrder, setSortOrder] = useState(item.sort_order || 100);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    if (!text.trim()) { setErr("Item text is required."); return; }
    setBusy(true);
    const payload = { ride, progression, category: category.trim(), item: text.trim(), sort_order: parseInt(sortOrder) || 0 };
    const res = isNew ? await db.createObsChecklistItem(payload) : await db.updateObsChecklistItem(item.id, payload);
    setBusy(false);
    if (!res.ok) { setErr(res.message); return; }
    onSaved();
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-head">
        <h3><Eye size={18} /> {isNew ? "New checklist item" : "Edit checklist item"}</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <label className="field"><span>Ride</span>
          <select value={ride} onChange={(e) => setRide(e.target.value)}>
            {RIDES_FOR_TAGS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
        <label className="field"><span>Progression</span>
          <select value={progression} onChange={(e) => setProgression(e.target.value)}>
            <option value="t_to_l2">T → L2</option>
            <option value="l2_to_l3">L2 → L3</option>
          </select>
        </label>
        <label className="field"><span>Category</span>
          <input value={category} onChange={(e) => setCategory(e.target.value)}
            placeholder='e.g., "Pre-ride setup", "During race"' />
        </label>
        <label className="field"><span>Item text</span>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
            placeholder="What specifically should the observer check?"
            style={{ width: "100%", padding: 8, background: "var(--panel2)", color: "var(--txt)", border: "1px solid var(--line)", borderRadius: 6, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
        </label>
        <label className="field"><span>Sort order (lower = shown first)</span>
          <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
        </label>
        {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy} onClick={save}>{busy ? "Saving…" : (isNew ? "Create" : "Save")}</button>
      </div>
    </Modal>
  );
}

function UsersView({ user, employees }) {
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [editing, setEditing] = useState(null);   // user obj or null
  const [adding, setAdding] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pwLink, setPwLink] = useState(null);     // {email, link}
  const [confirmDel, setConfirmDel] = useState(null);

  const refresh = useCallback(async () => {
    setErr("");
    const res = await db.listUsers();
    if (!res.ok) { setErr(res.message); setUsers([]); return; }
    setUsers(res.users || []);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // A user is deactivated if banned_until is in the future, or is_active is explicitly false.
  // The Supabase admin API uses banned_until; we also check is_active in case the edge fn returns it.
  const isDeactivated = (u) => {
    if (u.is_active === false) return true;
    if (u.banned_until) {
      const t = new Date(u.banned_until).getTime();
      if (!isNaN(t) && t > Date.now()) return true;
    }
    return false;
  };
  const deactivatedCount = (users || []).filter(isDeactivated).length;

  const filtered = (users || []).filter((u) => {
    if (!showDeactivated && isDeactivated(u)) return false;
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (filter.trim()) {
      const q = filter.toLowerCase();
      if (!(u.email?.toLowerCase().includes(q) || u.full_name?.toLowerCase().includes(q) || u.employee_name?.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";

  const doDelete = async (u) => {
    const res = await db.deleteUserAdmin(u.id);
    setConfirmDel(null);
    if (!res.ok) { alert(res.message); return; }
    refresh();
  };
  const doResetLink = async (u) => {
    const res = await db.passwordResetLink(u.email);
    if (!res.ok) { alert(res.message); return; }
    setPwLink({ email: u.email, link: res.link });
  };

  return (
    <div className="users-view">
      <div className="users-head">
        <div>
          <h2 className="page-title"><ShieldCheck size={20} /> Users & Logins</h2>
          <p className="page-sub">{user.role === "admin" ? "Manage who can sign in and what they can do." : `You can manage users with roles below ${ROLES[user.role].label}.`}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {user.role === "admin" && <button className="btn-ghost" onClick={() => setBulkOpen(true)}><Upload size={15} /> Import CSV</button>}
          <button className="btn-primary" onClick={() => setAdding(true)}><UserPlus size={16} /> Add login</button>
        </div>
      </div>

      <div className="users-filters">
        <div className="search">
          <Search size={16} />
          <input placeholder="Search email, name, linked employee…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="all">All roles</option>
          <option value="admin">Admin</option>
          <option value="agm_gm">AGM / GM</option>
          <option value="manager">Manager</option>
          <option value="trainer">Trainer</option>
          <option value="trainee">Trainee</option>
        </select>
        <label className="report-checkbox" style={{ marginLeft: 4 }}>
          <input type="checkbox" checked={showDeactivated} onChange={(e) => setShowDeactivated(e.target.checked)} />
          <span>Show deactivated{deactivatedCount > 0 ? ` (${deactivatedCount})` : ""}</span>
        </label>
        <button className="btn-ghost" onClick={refresh}><RotateCcw size={14} /> Refresh</button>
      </div>

      {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
      {users === null && !err && <div className="empty">Loading users…</div>}
      {users !== null && filtered.length === 0 && <div className="empty">No users match.</div>}

      {filtered.length > 0 && (
        <div className="users-table">
          <div className="users-row users-row-head">
            <div>Sign-in</div><div>Name / title</div><div>Role</div><div>Linked roster</div><div>Last sign-in</div><div></div>
          </div>
          {filtered.map((u) => {
            const deactivated = isDeactivated(u);
            return (
            <div key={u.id} className={`users-row ${deactivated ? "deactivated" : ""}`}>
              <div className="u-email">
                {u.is_username_only && u.username ? (
                  <>
                    <span style={{ color: "var(--accent)" }}>{u.username}</span>
                    <div className="u-sub">username</div>
                  </>
                ) : (
                  <>
                    {u.email}
                    {u.username && <div className="u-sub">also: {u.username}</div>}
                  </>
                )}
                {u.id === user.id && <span className="u-self"> (you)</span>}
                {deactivated && <span className="mini-flag arch" style={{ marginLeft: 6 }}>Deactivated</span>}
              </div>
              <div>
                <div>{u.full_name || <span className="u-dim">—</span>}</div>
                {u.title && <div className="u-sub">{u.title}</div>}
              </div>
              <div><span className={`role-pill role-${u.role}`}>{ROLES[u.role]?.label || u.role}</span></div>
              <div>{u.employee_name || <span className="u-dim">—</span>}</div>
              <div>{fmtDate(u.last_sign_in_at)}</div>
              <div className="u-actions">
                {(user.role === "admin" || roleRank(u.role) < roleRank(user.role) || u.id === user.id) ? (
                  <>
                    <button className="btn-ghost-sm" onClick={() => setEditing(u)}>Edit</button>
                    <button className="btn-ghost-sm" onClick={() => doResetLink(u)}><Lock size={12} /> Reset</button>
                    {u.id !== user.id && user.role === "admin" &&
                      <button className="btn-ghost-sm danger" onClick={() => setConfirmDel(u)}><Trash2 size={12} /> Delete</button>}
                  </>
                ) : (
                  <span className="u-dim" style={{ fontSize: 11 }}>above your level</span>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {adding && (
        <AddLoginModal user={user} employees={employees} users={users || []} onClose={() => { setAdding(false); refresh(); }} />
      )}
      {bulkOpen && (
        <BulkImportModal onClose={() => { setBulkOpen(false); refresh(); }} />
      )}
      {editing && (
        <EditUserModal target={editing} employees={employees} user={user} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
      )}
      {pwLink && (
        <Modal onClose={() => setPwLink(null)}>
          <div className="modal-head"><h3><Lock size={18} /> Password reset link</h3>
            <button className="icon-btn" onClick={() => setPwLink(null)}><X size={18} /></button></div>
          <div className="modal-body">
            <p>Copy this link and send it to <b>{pwLink.email}</b>. They click it to set a new password.</p>
            <textarea readOnly value={pwLink.link || ""} rows={4} style={{ width: "100%", fontFamily: "monospace", fontSize: 11 }} onClick={(e) => e.target.select()} />
            <p className="sched-target">Links expire after a short time. If they don't use it soon, generate a fresh one.</p>
          </div>
          <div className="modal-foot"><button className="btn-primary" onClick={() => { navigator.clipboard?.writeText(pwLink.link || ""); }}>Copy link</button></div>
        </Modal>
      )}
      {confirmDel && (
        <Modal onClose={() => setConfirmDel(null)}>
          <div className="modal-head"><h3><AlertCircle size={18} /> Permanently delete login?</h3>
            <button className="icon-btn" onClick={() => setConfirmDel(null)}><X size={18} /></button></div>
          <div className="modal-body">
            <p>Permanently delete the login for <b>{confirmDel.email}</b>?</p>
            <p className="sched-target"><b>For normal use, deactivate the employee from the Roster instead — that disables their login and preserves all records and history.</b></p>
            <p className="sched-target">This permanent-delete option is for cleanup of test accounts or duplicates. Their employee record on the Roster is <b>not</b> deleted — only the login. This action cannot be undone.</p>
          </div>
          <div className="modal-foot">
            <button className="btn-ghost" onClick={() => setConfirmDel(null)}>Cancel</button>
            <button className="btn-primary" style={{ background: "#c0492f" }} onClick={() => doDelete(confirmDel)}>Permanently delete login</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function EditUserModal({ target, employees, user, onClose, onSaved }) {
  const isAdmin = user.role === "admin";
  const ALL_ROLES = [
    { key: "trainee", label: "Trainee" },
    { key: "trainer", label: "Trainer" },
    { key: "manager", label: "Manager" },
    { key: "agm_gm",  label: "AGM / GM" },
    { key: "admin",   label: "Admin" },
  ];
  // Admins can assign any role; non-admins can only assign roles below their own.
  // The current target role stays in the list (even if at/above caller) so it
  // displays correctly but the dropdown only OFFERS valid choices.
  const assignable = isAdmin
    ? ALL_ROLES
    : ALL_ROLES.filter((r) => roleRank(r.key) < roleRank(user.role));
  const showCurrent = !assignable.find((r) => r.key === target.role);

  const [fullName, setFullName] = useState(target.full_name || "");
  const [title, setTitle] = useState(target.title || "");
  const [role, setRole] = useState(target.role);
  const [employeeName, setEmployeeName] = useState(target.employee_name || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Linked roster row (if any) — used to expose Start Date editing
  const linkedEmp = React.useMemo(
    () => (employees || []).find((e) => e.name === employeeName) || null,
    [employees, employeeName]
  );
  const [startDate, setStartDate] = useState(linkedEmp?.startDate || "");
  // Keep the field in sync if the user picks a different linked roster record
  React.useEffect(() => {
    setStartDate(linkedEmp?.startDate || "");
  }, [linkedEmp?.id]);

  const roster = (employees || []).filter((e) => !e.archived).map((e) => e.name).sort();

  const save = async () => {
    setErr(""); setBusy(true);
    const res = await db.updateUserAdmin({
      user_id: target.id, full_name: fullName.trim(), title: title.trim(),
      role, employee_name: employeeName || null,
    });
    if (!res.ok) { setBusy(false); setErr(res.message); return; }
    // If a linked roster row exists and start date changed, update employees directly
    if (linkedEmp && (startDate || null) !== (linkedEmp.startDate || null)) {
      const upd = await supabase.from("employees")
        .update({ start_date: startDate || null })
        .eq("id", linkedEmp.id);
      if (upd.error) {
        setBusy(false);
        setErr("User saved but start date update failed: " + upd.error.message);
        return;
      }
    }
    setBusy(false);
    onSaved();
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-head"><h3><ShieldCheck size={18} /> Edit user</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button></div>
      <div className="modal-body">
        <p className="sched-target"><b>{target.email}</b></p>
        <label className="field"><span>Full name</span><input value={fullName} onChange={(e) => setFullName(e.target.value)} /></label>
        <label className="field"><span>Title</span><input value={title} onChange={(e) => setTitle(e.target.value)} /></label>
        <label className="field"><span>Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value)} disabled={!isAdmin && roleRank(target.role) >= roleRank(user.role) && target.id !== user.id}>
            {showCurrent && <option value={target.role}>{ROLES[target.role]?.label || target.role} (current — you can't change this)</option>}
            {assignable.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
        <label className="field"><span>Linked roster record</span>
          <select value={employeeName} onChange={(e) => setEmployeeName(e.target.value)}>
            <option value="">— none —</option>
            {roster.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        {linkedEmp && (
          <label className="field"><span>Start date <span className="dim" style={{ fontWeight: 400 }}>(roster record)</span></span>
            <input
              type="date"
              value={startDate || ""}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <p className="sched-target" style={{ marginTop: 4 }}>Writes directly to the roster record. Format: YYYY-MM-DD.</p>
          </label>
        )}
        {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
      </div>
    </Modal>
  );
}

function BulkImportModal({ onClose }) {
  const [step, setStep] = useState("upload");
  const [rows, setRows] = useState([]);
  const [tempPassword, setTempPassword] = useState("lvmgp2026");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const RIDES_FOR_TEMPLATE = ["slide", "tornado", "dragon", "kiddieKart", "road", "sprint", "euro"];
  const TEMPLATE_HEADER = [
    "name", "start_date", "department", "username", "real_email", "role", "title",
    ...RIDES_FOR_TEMPLATE.map((r) => `level_${r}`),
    ...RIDES_FOR_TEMPLATE.map((r) => `rt_${r}`),
  ];
  const KNOWN = new Set(TEMPLATE_HEADER);

  const downloadTemplate = () => {
    const header = TEMPLATE_HEADER.join(",");
    const examples = [
      "Asia Torres,2024-06-15,Attractions,,,trainee,,L3,L2,L2,L3,L2,0,0,,,,,,,",
      "Jordan Smith,2024-01-10,Attractions,jordan.smith,,trainer,Crew Lead,L3,L3,L3,L3,L3,L2,T,,,,,,RT-L2,",
      "Maria Lopez,2023-03-01,Attractions,,maria@lvmgp.com,manager,Shift Manager,L3,L3,L3,L3,L3,L3,L3,,,,,,,",
      "Sam Park,2025-04-20,Attractions,,,trainee,,0,0,0,T,0,0,0,,,,,,,",
      "Pat Garcia,2024-09-10,Restaurant,pat.garcia,,trainee,Server,0,0,0,0,0,0,0,,,,,,,",
      "Riley Chen,2024-08-01,Kart & Ride Service Tech,riley.chen,,trainer,Mechanic,L3,L3,L3,L3,L3,0,0,,,,,,,",
    ];
    const csv = header + "\n" + examples.join("\n") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "lvmgp_import_template.csv";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return { error: "CSV needs a header row and at least one data row." };
    const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
    if (!header.includes("name")) return { error: "CSV must include a 'name' column." };
    const out = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const cells = line.split(",").map((c) => c.replace(/^"|"$/g, "").trim());
      const obj = {};
      header.forEach((h, idx) => { if (KNOWN.has(h)) obj[h] = cells[idx] || ""; });
      if ((obj.name || "").trim()) out.push(obj);
    }
    if (out.length === 0) return { error: "No valid rows found." };
    return { rows: out };
  };

  const onFile = async (e) => {
    setErr("");
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const parsed = parseCSV(text);
    if (parsed.error) { setErr(parsed.error); return; }
    setRows(parsed.rows);
    setStep("preview");
  };

  // Validation summary before importing
  const previewIssues = rows.map((r) => {
    const issues = [];
    if (!(r.real_email || "").trim() && !(r.username || "").trim()) issues.push("no login");
    return issues;
  });
  const blockingCount = previewIssues.filter((i) => i.length > 0).length;

  const summarizeLevels = (r) => {
    const set = RIDES_FOR_TEMPLATE
      .map((ride) => ({ ride, lvl: (r[`level_${ride}`] || "").trim().toUpperCase() }))
      .filter((x) => x.lvl && x.lvl !== "0");
    const rt = RIDES_FOR_TEMPLATE
      .map((ride) => ({ ride, rt: (r[`rt_${ride}`] || "").trim().toUpperCase() }))
      .filter((x) => x.rt);
    const parts = [];
    if (set.length) parts.push(`${set.length} level${set.length === 1 ? "" : "s"}`);
    if (rt.length) parts.push(`${rt.length} RT`);
    return parts.join(" · ") || "—";
  };

  const runImport = async () => {
    if (blockingCount > 0) { setErr(`${blockingCount} row(s) are missing a username or email. Fix the CSV first.`); return; }
    setBusy(true); setErr("");
    const res = await db.bulkImport({ rows, temp_password: tempPassword });
    setBusy(false);
    if (!res.ok) { setErr(res.message); return; }
    setResult(res);
    setStep("result");
  };

  const copyAllLogins = () => {
    if (!result) return;
    const lines = result.results
      .filter((r) => r.status === "ok" && r.identifier)
      .map((r) => `${r.name}\t${r.identifier}\t${tempPassword}`);
    navigator.clipboard?.writeText("Name\tSign-in\tTemp password\n" + lines.join("\n"));
  };

  return (
    <Modal onClose={onClose} wide>
      <div className="modal-head">
        <h3><Upload size={18} /> Bulk import</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        {step === "upload" && (
          <>
            <p className="sched-target">Upload a CSV with your crew. Each row creates: a roster record, a login, and stamps any existing training levels you specify.</p>
            <ol style={{ paddingLeft: 18, margin: "8px 0", color: "var(--txt2)", fontSize: 13, lineHeight: 1.7 }}>
              <li>Download the template, open it in Excel.</li>
              <li>Fill in one row per person. Every row needs <b>either a username or a real_email</b>.</li>
              <li>For training already completed, fill in the <b>level_</b> columns (0, T, L2, L3, or L4).</li>
              <li>For rides currently in retrain, fill in the <b>rt_</b> columns with RT-L2 / RT-L3 / RT-L4.</li>
              <li>Save as CSV. Upload here. You'll see a preview before anything is created.</li>
            </ol>
            <div style={{ display: "flex", gap: 8, margin: "14px 0" }}>
              <button className="btn-ghost" onClick={downloadTemplate}><Download size={14} /> Download template</button>
              <label className="btn-primary" style={{ cursor: "pointer" }}>
                <Upload size={14} /> Choose CSV file
                <input type="file" accept=".csv" style={{ display: "none" }} onChange={onFile} />
              </label>
            </div>
            <p className="sched-target">
              <b>Required:</b> name, plus username OR real_email.<br />
              <b>Optional:</b> start_date, role (defaults to trainee), title, level_* and rt_* columns.<br />
              <b>Level values:</b> 0, T, L2, L3 — plus L4 only on road / sprint / euro.<br />
              <b>RT values:</b> RT-L2, RT-L3, or RT-L4.
            </p>
            {err && <div className="login-err"><AlertCircle size={14} /> {err}</div>}
          </>
        )}

        {step === "preview" && (
          <>
            <p className="sched-target">Parsed <b>{rows.length}</b> rows. Review and confirm before importing.</p>
            <label className="field"><span>Temporary password for all new logins</span>
              <input value={tempPassword} onChange={(e) => setTempPassword(e.target.value)} placeholder="lvmgp2026" />
            </label>
            <p className="sched-target">Leadership (manager+) will be forced to change this on first sign-in. Crew will be nudged.</p>
            {blockingCount > 0 && (
              <div className="login-err" style={{ marginTop: 10 }}>
                <AlertCircle size={14} /> {blockingCount} row(s) missing a username or email — fix the CSV before importing.
              </div>
            )}
            <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 8, marginTop: 10 }}>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead style={{ background: "var(--panel2)", color: "var(--txt3)", textTransform: "uppercase", fontSize: 10 }}>
                  <tr>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Name</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Dept</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Sign-in</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Role</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Training</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const hasEmail = (r.real_email || "").trim();
                    const hasUser = (r.username || "").trim();
                    const issues = previewIssues[i];
                    return (
                      <tr key={i} style={{ borderTop: "1px solid var(--line)", background: issues.length ? "rgba(200,71,47,0.06)" : undefined }}>
                        <td style={{ padding: "6px 10px" }}>{r.name}</td>
                        <td style={{ padding: "6px 10px", color: "var(--txt2)" }}>{r.department || "Attractions"}</td>
                        <td style={{ padding: "6px 10px", color: hasEmail || hasUser ? "var(--accent)" : "#e07a63" }}>
                          {hasEmail || (hasUser ? `${hasUser} (username)` : "(missing)")}
                        </td>
                        <td style={{ padding: "6px 10px" }}>{r.role || "trainee"}</td>
                        <td style={{ padding: "6px 10px", color: "var(--txt3)" }}>{summarizeLevels(r)}</td>
                        <td style={{ padding: "6px 10px", color: "#e07a63", fontSize: 11 }}>{issues.join(", ") || ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {err && <div className="login-err" style={{ marginTop: 10 }}><AlertCircle size={14} /> {err}</div>}
          </>
        )}

        {step === "result" && result && (
          <>
            <div style={{ display: "flex", gap: 16, marginBottom: 14 }}>
              <div><b style={{ fontSize: 22 }}>{result.succeeded}</b> <span style={{ color: "var(--txt3)" }}>succeeded</span></div>
              <div><b style={{ fontSize: 22, color: result.failed > 0 ? "#e07a63" : "var(--txt)" }}>{result.failed}</b> <span style={{ color: "var(--txt3)" }}>failed</span></div>
              <div><b style={{ fontSize: 22 }}>{result.total}</b> <span style={{ color: "var(--txt3)" }}>total</span></div>
            </div>
            <p className="sched-target">All new logins use the temp password: <b>{tempPassword}</b>. Distribute name + sign-in + password to each person.</p>
            <button className="btn-ghost" onClick={copyAllLogins} style={{ marginBottom: 10 }}><Copy size={14} /> Copy all logins (Name / Sign-in / Temp password)</button>
            <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 8 }}>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead style={{ background: "var(--panel2)", color: "var(--txt3)", textTransform: "uppercase", fontSize: 10 }}>
                  <tr>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Row</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Name</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Status</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((r, i) => (
                    <tr key={i} style={{ borderTop: "1px solid var(--line)" }}>
                      <td style={{ padding: "6px 10px", color: "var(--txt3)" }}>{r.row}</td>
                      <td style={{ padding: "6px 10px" }}>{r.name}</td>
                      <td style={{ padding: "6px 10px", color: r.status === "ok" ? "#5fbf87" : "#e07a63" }}>{r.status === "ok" ? "✓" : "✗"}</td>
                      <td style={{ padding: "6px 10px", color: "var(--txt2)" }}>
                        {r.message}
                        {r.identifier && <div style={{ color: "var(--accent)", fontSize: 11 }}>sign-in: {r.identifier}</div>}
                        {r.warnings && r.warnings.length > 0 && (
                          <div style={{ color: "#e0a23e", fontSize: 11, marginTop: 2 }}>
                            {r.warnings.map((w, j) => <div key={j}>⚠ {w}</div>)}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <div className="modal-foot">
        {step === "upload" && <button className="btn-ghost" onClick={onClose}>Cancel</button>}
        {step === "preview" && (
          <>
            <button className="btn-ghost" onClick={() => { setRows([]); setStep("upload"); }}>Back</button>
            <button className="btn-primary" disabled={busy || tempPassword.length < 6} onClick={runImport}>
              {busy ? "Importing…" : `Import ${rows.length} row${rows.length === 1 ? "" : "s"}`}
            </button>
          </>
        )}
        {step === "result" && <button className="btn-primary" onClick={onClose}>Close</button>}
      </div>
    </Modal>
  );
}

function Empty({ text }) { return <div className="empty">{text}</div>; }

function AddLoginModal({ user, employees, users, onClose }) {
  const ALL_ROLES = [
    { key: "trainee", label: "Trainee (sees only their own training)" },
    { key: "trainer", label: "Trainer" },
    { key: "manager", label: "Manager" },
    { key: "agm_gm", label: "AGM / GM" },
    { key: "admin",   label: "Admin (full control)" },
  ];
  const availableRoles = ALL_ROLES.filter((r) => roleRank(r.key) < roleRank(user.role));
  const defaultRole = availableRoles[0]?.key || "trainee";

  const [loginType, setLoginType] = useState("username"); // 'username' | 'email'
  const [username, setUsername] = useState("");
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("lvmgp2026");
  const [fullName, setFullName] = useState("");
  const [startDate, setStartDate] = useState(todayISO());
  const [title, setTitle] = useState("");
  const [role, setRole] = useState(defaultRole);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // linkChoice: 'link' | 'separate' | null (undecided when name matches)
  const [linkChoice, setLinkChoice] = useState(null);

  // Detect if a roster record with this name already exists
  const existingMatch = useMemo(() => {
    const n = fullName.trim().toLowerCase();
    if (!n) return null;
    return (employees || []).find((e) => !e.archived && e.name.toLowerCase() === n) || null;
  }, [fullName, employees]);

  // Does the matched employee already have a login? If yes, we must NOT auto-link — that would
  // mean two logins sharing one roster record, which is the bug that just happened.
  const existingLoginForMatch = useMemo(() => {
    if (!existingMatch) return null;
    return (users || []).find((u) => u.employee_id === existingMatch.id) || null;
  }, [existingMatch, users]);

  // Reset the user's link choice whenever the name (and thus the match) changes
  useEffect(() => { setLinkChoice(null); }, [existingMatch?.id]);

  const suggestUsername = (name) => {
    const cleaned = name.trim().toLowerCase().replace(/[^a-z0-9\s.-]/g, "");
    const parts = cleaned.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    return parts.length === 1 ? parts[0] : `${parts[0]}.${parts[parts.length - 1]}`;
  };
  const onFullNameChange = (v) => {
    setFullName(v);
    if (!usernameTouched && loginType === "username") setUsername(suggestUsername(v));
  };

  const submit = async () => {
    setMsg(null);
    if (!fullName.trim()) { setMsg({ ok: false, text: "Enter a full name." }); return; }
    if (loginType === "username" && !username.trim()) { setMsg({ ok: false, text: "Enter a username." }); return; }
    if (loginType === "email" && !email.trim()) { setMsg({ ok: false, text: "Enter an email." }); return; }
    if (password.length < 6) { setMsg({ ok: false, text: "Password must be at least 6 characters." }); return; }
    // Guard against silently linking two logins to the same roster row.
    // If a name match exists, the user must explicitly choose link vs separate.
    if (existingMatch && !linkChoice) {
      setMsg({ ok: false, text: "Pick whether to link to the existing person or create a new roster record." });
      return;
    }
    setBusy(true);

    const shouldLink = existingMatch && linkChoice === "link";
    const wantsSeparate = existingMatch && linkChoice === "separate";
    // If the user wants a separate roster row despite the name match, we can't tell the
    // edge function directly not to auto-link. So we send a slightly-suffixed name so the
    // edge function won't find a match, then rename the created employee back afterward.
    const dedupSuffix = wantsSeparate ? ` (${startDate || todayISO()})` : "";
    const nameToSend = fullName.trim() + dedupSuffix;

    const payload = {
      password, full_name: nameToSend, title: title.trim(), role,
      start_date: shouldLink ? null : (startDate || null),
    };
    if (loginType === "username") payload.username = username.trim().toLowerCase();
    else payload.real_email = email.trim().toLowerCase();

    const res = await db.createUserLogin(payload);
    if (!res.ok) {
      setBusy(false);
      setMsg({ ok: false, text: res.message || "Could not create the login." });
      return;
    }
    // If we sent a suffixed name to force a separate roster row, correct it back now.
    if (wantsSeparate) {
      let empIdToRename = res.employee_id || null;
      // Fallback: if the edge function response didn't include employee_id, look it up
      // by the suffixed name we just sent
      if (!empIdToRename) {
        const lookup = await supabase.from("employees").select("id").eq("name", nameToSend).order("created_at", { ascending: false }).limit(1);
        empIdToRename = lookup.data?.[0]?.id || null;
      }
      if (empIdToRename) {
        const nameFix = await supabase.from("employees").update({ name: fullName.trim() }).eq("id", empIdToRename);
        if (nameFix.error) {
          // Non-fatal — the login exists and works
          console.warn("Could not correct name after separate-create:", nameFix.error.message);
        }
      }
    }
    setBusy(false);
    const identifier = res.username || (loginType === "email" ? email.trim() : username.trim());
    const rosterNote = shouldLink
      ? "linked to existing roster record"
      : (existingMatch ? "new roster record created (separate from existing person with same name)" : "new roster record created");
    setMsg({ ok: true, text: `✓ Created — ${rosterNote}. Sign-in: ${identifier} · Password: ${password}. Closing…` });
    setUsername(""); setUsernameTouched(false); setEmail(""); setPassword("lvmgp2026");
    setFullName(""); setStartDate(todayISO()); setTitle(""); setRole(defaultRole);
    setLinkChoice(null);
    setTimeout(() => { onClose(); }, 2800);
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-head">
        <h3><UserPlus size={18} /> Add login</h3>
        <button className="icon-btn" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="modal-body">
        <label className="field"><span>Full name</span>
          <input value={fullName} onChange={(e) => onFullNameChange(e.target.value)} placeholder="e.g. Asia Torres" autoFocus />
        </label>

        {existingMatch && (
          <div style={{ padding: 12, background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 6, marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
              <AlertCircle size={14} style={{ color: "var(--warn)", marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 12, lineHeight: 1.4 }}>
                <strong>A roster record for "{existingMatch.name}" already exists</strong>
                {existingMatch.startDate && ` (started ${fmtDate(existingMatch.startDate)})`}
                {existingLoginForMatch && (
                  <div style={{ color: "var(--err)", marginTop: 4 }}>
                    ⚠ That person <strong>already has a login</strong> ({existingLoginForMatch.email || existingLoginForMatch.username}). You should NOT link a second login to them.
                  </div>
                )}
                <div style={{ marginTop: 6 }}>Is this the same person, or a different person with the same name?</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button
                className={linkChoice === "link" ? "btn-primary sm" : "btn-ghost sm"}
                onClick={() => setLinkChoice("link")}
                disabled={!!existingLoginForMatch}
                title={existingLoginForMatch ? "Already has a login — can't link a second one" : ""}
              >
                Same person — link to existing
              </button>
              <button
                className={linkChoice === "separate" ? "btn-primary sm" : "btn-ghost sm"}
                onClick={() => setLinkChoice("separate")}
              >
                Different person — create new roster
              </button>
            </div>
          </div>
        )}

        {(!existingMatch && fullName.trim()) && (
          <p className="sched-target" style={{ marginTop: -8, marginBottom: 12 }}>↳ Will create a new roster record</p>
        )}

        {(!existingMatch || linkChoice === "separate") && fullName.trim() && (
          <label className="field"><span>Start date</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
        )}

        <div className="field"><span style={{ display: "block", marginBottom: 6, fontSize: 11.5, textTransform: "uppercase", color: "var(--txt3)", letterSpacing: 0.4 }}>Sign-in type</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className={loginType === "username" ? "btn-primary" : "btn-ghost"} style={{ flex: 1 }}
              onClick={() => setLoginType("username")}>Username (no email)</button>
            <button type="button" className={loginType === "email" ? "btn-primary" : "btn-ghost"} style={{ flex: 1 }}
              onClick={() => setLoginType("email")}>Real email</button>
          </div>
        </div>

        {loginType === "username" ? (
          <label className="field"><span>Username</span>
            <input value={username}
              onChange={(e) => { setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, "")); setUsernameTouched(true); }}
              placeholder="e.g. asia.torres" />
            <p className="sched-target" style={{ marginTop: 4 }}>They sign in by typing just this — no @ or domain.</p>
          </label>
        ) : (
          <label className="field"><span>Email</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@lvmgp.com" />
            <p className="sched-target" style={{ marginTop: 4 }}>Use for leadership or anyone with a real email.</p>
          </label>
        )}

        <label className="field"><span>Temporary password</span>
          <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="at least 6 characters" />
        </label>
        <label className="field"><span>Title (optional)</span><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Shift Manager" /></label>
        <label className="field"><span>Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            {availableRoles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
        <p className="sched-target">Leadership (manager+) will be required to change the password on first sign-in.</p>
        {msg && <div className={msg.ok ? "login-ok" : "login-err"}>{msg.ok ? <Check size={14} /> : <AlertCircle size={14} />} {msg.text}</div>}
      </div>
      <div className="modal-foot">
        <button className="btn-ghost" onClick={onClose}>Close</button>
        <button className="btn-primary" disabled={busy || (existingMatch && !linkChoice)} onClick={submit}>{busy ? "Creating…" : "Create login + roster"}</button>
      </div>
    </Modal>
  );
}

/* ============================================================
   STYLES
   ============================================================ */
function Style() {
  return (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700;800;900&family=Spline+Sans+Mono:wght@400;500;600&display=swap');

    :root {
      --bg: #0f1419;
      --bg2: #161d26;
      --panel: #1b232e;
      --panel2: #212c39;
      --line: #2c3845;
      --line2: #38475a;
      --txt: #e8edf2;
      --txt2: #9fb0c0;
      --txt3: #6b7d8f;
      --accent: #ff6b35;
      --accent2: #ffa552;
      --l0: #2a3441;
      --lT: #4a6fa5;
      --l2: #2e9e6b;
      --l3: #d99a2b;
      --l4: #c8472f;
      --gold: #d99a2b;
      --green: #2e9e6b;
      --blue: #4a6fa5;
      --purple: #8b5fbf;
      --radius: 12px;
    }

    * { box-sizing: border-box; margin: 0; padding: 0; }
    .loading { color: var(--txt2); font-family: 'Archivo', sans-serif; display:grid; place-items:center; height:100vh; background:var(--bg); }

    .app {
      display: flex; min-height: 100vh;
      background:
        radial-gradient(900px 500px at 100% 0%, rgba(255,107,53,0.06), transparent 60%),
        radial-gradient(700px 400px at 0% 100%, rgba(74,111,165,0.08), transparent 55%),
        var(--bg);
      color: var(--txt);
      font-family: 'Archivo', sans-serif;
      font-size: 14px;
    }

    /* SIDEBAR */
    .sidebar {
      width: 232px; flex-shrink: 0; background: var(--bg2);
      border-right: 1px solid var(--line); padding: 22px 16px;
      display: flex; flex-direction: column; position: sticky; top: 0; height: 100vh;
    }
    .brand { display: flex; align-items: center; gap: 12px; margin-bottom: 34px; padding: 0 4px; }
    .brand-mark { width: 40px; height: 40px; border-radius: 11px; display: grid; place-items: center;
      background: linear-gradient(135deg, var(--accent), var(--accent2)); color: #1a1008; box-shadow: 0 4px 14px rgba(255,107,53,0.3); }
    .brand-name { font-weight: 900; letter-spacing: 1.5px; font-size: 15px; }
    .brand-sub { font-size: 11px; color: var(--txt3); letter-spacing: 0.5px; }
    nav { display: flex; flex-direction: column; gap: 4px; }
    .nav-item { display: flex; align-items: center; gap: 12px; padding: 11px 14px; border-radius: 10px;
      background: none; border: none; color: var(--txt2); cursor: pointer; font-family: inherit; font-size: 14px;
      font-weight: 500; text-align: left; transition: all 0.15s; }
    .nav-item:hover { background: var(--panel); color: var(--txt); }
    .nav-item.active { background: linear-gradient(135deg, rgba(255,107,53,0.18), rgba(255,107,53,0.06));
      color: var(--accent2); font-weight: 600; }
    .nav-badge { margin-left: auto; background: var(--accent); color: #1a1008; font-size: 11px; font-weight: 700;
      padding: 1px 7px; border-radius: 20px; }
    .sidebar-foot { margin-top: auto; }
    .reset-btn { display: flex; align-items: center; gap: 7px; width: 100%; justify-content: center;
      padding: 9px; background: none; border: 1px solid var(--line); border-radius: 9px; color: var(--txt3);
      cursor: pointer; font-family: inherit; font-size: 12px; transition: all 0.15s; }
    .reset-btn:hover { border-color: var(--l3); color: var(--l3); }

    /* MAIN */
    .main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
    /* ---- Dashboard To Do + Goals ---- */
    .todo-sub { display: flex; align-items: center; gap: 7px; margin: 14px 0 6px; font-size: 11.5px; font-weight: 700;
                text-transform: uppercase; letter-spacing: 0.6px; color: var(--txt3); }
    .panel-body > .todo-sub:first-child { margin-top: 2px; }
    .todo-sub .count-pill { font-size: 10.5px; padding: 1px 7px; }
    .trained-list { display: flex; flex-wrap: wrap; gap: 6px; }
    .trained-chip { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 999px; font-size: 12.5px;
                    background: rgba(46,158,107,0.14); color: #7fd6ac; border: 1px solid rgba(46,158,107,0.35); }
    .goal-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; }
    .goal-card { border: 1px solid var(--line2); border-radius: 12px; padding: 14px; background: var(--bg2); }
    .goal-card.complete { border-color: rgba(46,158,107,0.6); }
    .goal-head { display: flex; justify-content: space-between; gap: 8px; margin-bottom: 10px; }
    .goal-head strong { font-size: 15px; }
    .goal-bar { height: 7px; border-radius: 99px; background: var(--panel2); overflow: hidden; margin: 2px 0 6px; }
    .goal-bar span { display: block; height: 100%; background: linear-gradient(90deg, var(--accent), var(--accent2)); border-radius: 99px; }
    .goal-card.complete .goal-bar span { background: var(--green); }
    .goal-meta { font-size: 12px; color: var(--txt3); margin-bottom: 8px; display: flex; align-items: center; gap: 5px; }
    .goal-steps { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
    .goal-steps li { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: var(--txt); }
    .goal-steps li > svg { flex-shrink: 0; margin-top: 2px; color: var(--txt3); }
    .goal-steps li.done > svg { color: var(--green); }
    .goal-steps li.done .gs-main > span:first-child { color: var(--txt2); }
    .gs-main { flex: 1; display: flex; flex-direction: column; }
    .gs-hint { font-size: 11.5px; color: var(--txt3); }
    .goal-add { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-top: 14px; }
    .goal-add select { background: var(--bg2); border: 1px solid var(--line2); border-radius: 9px; color: var(--txt);
                       padding: 8px 10px; font-family: inherit; font-size: 13px; min-width: 220px; }
    .goal-req-row { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
    .goal-req-num { width: 22px; text-align: center; color: var(--txt3); font-size: 12px; font-weight: 700; }
    .goal-req-fields { flex: 1; min-width: 0; }
    .goal-req-fields select, .goal-req-fields input { width: 100%; }
    .viewas-bar { position: sticky; top: 0; z-index: 2000; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
                  padding: 10px 18px; background: linear-gradient(135deg, #5b2a86, #8b5fbf); color: #fff; font-size: 13.5px;
                  box-shadow: 0 2px 10px rgba(0,0,0,0.35); }
    .viewas-bar span { flex: 1; min-width: 200px; }
    .viewas-exit { display: inline-flex; align-items: center; gap: 6px; background: #fff; color: #5b2a86; border: none;
                   border-radius: 8px; padding: 7px 12px; font-weight: 700; font-size: 13px; cursor: pointer; }
    .viewas-exit:hover { filter: brightness(0.95); }
    .header { display: flex; align-items: center; justify-content: space-between; padding: 22px 32px;
      border-bottom: 1px solid var(--line); position: sticky; top: 0; background: rgba(15,20,25,0.82);
      backdrop-filter: blur(12px); z-index: 20; }
    .header h1 { font-size: 22px; font-weight: 800; letter-spacing: -0.3px; }
    .header-actions { display: flex; align-items: center; gap: 12px; }
    .search { display: flex; align-items: center; gap: 8px; background: var(--panel); border: 1px solid var(--line);
      border-radius: 9px; padding: 8px 12px; color: var(--txt3); }
    .search input { background: none; border: none; outline: none; color: var(--txt); font-family: inherit; width: 160px; font-size: 13px; }
    .content { padding: 26px 32px 60px; }

    /* BUTTONS */
    .btn-primary { display: flex; align-items: center; gap: 7px; background: linear-gradient(135deg, var(--accent), var(--accent2));
      color: #1a1008; border: none; padding: 9px 16px; border-radius: 9px; font-family: inherit; font-weight: 700;
      font-size: 13px; cursor: pointer; transition: all 0.15s; }
    .btn-primary:hover { filter: brightness(1.08); transform: translateY(-1px); }
    .btn-primary:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
    .btn-ghost { background: none; border: 1px solid var(--line2); color: var(--txt2); padding: 9px 16px;
      border-radius: 9px; font-family: inherit; font-weight: 500; cursor: pointer; font-size: 13px; }
    .btn-ghost:hover { background: var(--panel); color: var(--txt); }
    .btn-mini { display: inline-flex; align-items: center; gap: 5px; background: var(--accent); color: #1a1008;
      border: none; padding: 6px 11px; border-radius: 7px; font-family: inherit; font-weight: 600; font-size: 12px; cursor: pointer; transition: all 0.15s; }
    .btn-mini:hover { filter: brightness(1.08); }
    .btn-mini.ghost { background: var(--panel2); color: var(--txt2); }
    .btn-mini.ghost:hover { background: var(--line); color: var(--txt); }
    .icon-btn { background: none; border: none; color: var(--txt3); cursor: pointer; padding: 5px; border-radius: 6px; display: grid; place-items: center; }
    .icon-btn:hover { background: var(--panel2); color: var(--l3); }

    /* STATS */
    .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 22px; }
    .stat-card { background: var(--panel); border: 1px solid var(--line); border-radius: var(--radius);
      padding: 20px; display: flex; align-items: center; gap: 16px; position: relative; overflow: hidden; }
    .stat-card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; }
    .stat-card.tone-gold::before { background: var(--gold); } .stat-card.tone-green::before { background: var(--green); }
    .stat-card.tone-blue::before { background: var(--blue); } .stat-card.tone-purple::before { background: var(--purple); }
    .stat-icon { width: 48px; height: 48px; border-radius: 12px; display: grid; place-items: center; }
    .tone-gold .stat-icon { background: rgba(217,154,43,0.15); color: var(--gold); }
    .tone-green .stat-icon { background: rgba(46,158,107,0.15); color: var(--green); }
    .tone-blue .stat-icon { background: rgba(74,111,165,0.15); color: var(--blue); }
    .tone-purple .stat-icon { background: rgba(139,95,191,0.15); color: var(--purple); }
    .stat-value { font-size: 30px; font-weight: 900; line-height: 1; font-family: 'Spline Sans Mono', monospace; }
    .stat-label { font-size: 12px; color: var(--txt3); margin-top: 6px; }

    /* COVERAGE BY RIDE */
    .dash-toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
    .dash-toolbar .total-banner { flex: 1; margin-bottom: 0; }
    .dash-toolbar .btn-primary { white-space: nowrap; }
    .total-banner { display: flex; align-items: center; gap: 12px; background: var(--panel);
      border: 1px solid var(--line); border-radius: var(--radius); padding: 16px 22px; }
    .total-banner svg { color: var(--accent2); }
    .total-num { font-size: 26px; font-weight: 900; font-family: 'Spline Sans Mono', monospace; line-height: 1; }
    .total-label { font-size: 13px; color: var(--txt3); font-weight: 500; }
    .coverage-panel { margin-bottom: 0; }
    .cov-legend { margin-left: auto; display: flex; gap: 14px; }
    .cov-legend span { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--txt3); }
    .cov-legend .dot, .cov-num i { width: 9px; height: 9px; border-radius: 3px; display: inline-block; flex-shrink: 0; }
    .cov-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 12px; padding: 16px 20px; }
    .cov-card { background: var(--bg2); border: 1px solid var(--line); border-radius: 10px; padding: 13px 14px;
      cursor: pointer; text-align: left; font-family: inherit; color: var(--txt); transition: all 0.15s; position: relative; display: block; width: 100%; }
    .cov-card:hover { border-color: var(--accent); transform: translateY(-2px); }
    .cov-open { display: flex; align-items: center; gap: 3px; font-size: 11px; color: var(--accent2); font-weight: 600;
      margin-top: 10px; opacity: 0; transition: opacity 0.15s; }
    .cov-card:hover .cov-open { opacity: 1; }
    .cov-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 11px; }
    .cov-name { font-weight: 700; font-size: 13.5px; }
    .cov-flag { font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.4px; }
    .cov-flag.ok { background: rgba(46,158,107,0.18); color: #4ec78c; }
    .cov-flag.warn { background: rgba(217,154,43,0.18); color: var(--gold); }
    .cov-flag.danger { background: rgba(200,71,47,0.2); color: #e07a63; }
    .cov-bar { display: flex; height: 8px; border-radius: 5px; overflow: hidden; gap: 1.5px; margin-bottom: 12px; background: var(--l0); }
    .cov-bar span { transition: flex 0.3s; min-width: 0; }
    .cov-nums { display: flex; justify-content: space-between; }
    .cov-num { display: flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 600;
      font-family: 'Spline Sans Mono', monospace; color: var(--txt2); }
    .cov-na { font-style: normal; font-size: 9px; color: var(--txt3); font-family: 'Archivo', sans-serif; font-weight: 500; }

    /* RIDE DETAIL MODAL */
    .ride-ladder { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; padding: 12px 14px; background: var(--bg2);
      border-radius: 10px; margin-bottom: 16px; font-size: 12px; color: var(--txt3); }
    .ride-ladder > span { display: inline-flex; align-items: center; gap: 6px; }
    .ladder-arrow { color: var(--txt3); margin: 0 2px; }
    .ladder-tag { font-style: normal; font-size: 10px; color: var(--l4); font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
    .ride-list { display: flex; flex-direction: column; }
    .ride-row { display: flex; align-items: center; gap: 12px; padding: 9px 6px; border-bottom: 1px solid var(--line); }
    .ride-row.muted { opacity: 0.55; }
    .ride-name { font-weight: 600; font-size: 14px; flex: 1; }
    .ride-meta { font-size: 11.5px; color: var(--txt3); width: 80px; text-align: right; }
    .ride-divider { font-size: 11px; text-transform: uppercase; letter-spacing: 0.6px; color: var(--txt3);
      padding: 14px 6px 8px; font-weight: 700; }

    /* PANELS */
    .dash { display: flex; flex-direction: column; gap: 20px; }
    .dash-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
    .panel { background: var(--panel); border: 1px solid var(--line); border-radius: var(--radius); overflow: hidden; }
    .panel-head { display: flex; align-items: center; gap: 10px; padding: 16px 20px; border-bottom: 1px solid var(--line); }
    .panel-head h2 { font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 8px; color: var(--txt); }
    .panel-head h2 svg { color: var(--accent2); }
    .count-pill { margin-left: auto; background: var(--panel2); color: var(--txt2); font-size: 12px; font-weight: 700;
      padding: 2px 9px; border-radius: 20px; font-family: 'Spline Sans Mono', monospace; }
    .panel-body { padding: 8px; max-height: 380px; overflow-y: auto; }

    .row-item { display: flex; align-items: center; gap: 12px; padding: 11px 12px; border-radius: 9px; transition: background 0.12s; }
    .row-item:hover { background: var(--panel2); }
    .ri-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
    .ri-main strong { font-weight: 600; font-size: 13.5px; }
    .ri-sub { font-size: 12px; color: var(--txt3); }
    .ri-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

    .tag { font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 6px; white-space: nowrap; }
    .tag-info { background: rgba(74,111,165,0.18); color: #7ba3d6; }
    .tag-warn { background: rgba(217,154,43,0.18); color: var(--gold); }
    .tag-fail { background: rgba(200,71,47,0.18); color: #e07a63; }
    .tag-pass { background: rgba(46,158,107,0.18); color: #4ec78c; }
    .tag-ok { background: rgba(46,158,107,0.18); color: #4ec78c; }
    .tag-muted { background: var(--panel2); color: var(--txt3); }
    .tag-mandatory { background: rgba(217,154,43,0.22); color: var(--gold); border: 1px solid rgba(217,154,43,0.4); }

    /* REPORTS */
    .reports-view { display: flex; flex-direction: column; gap: 14px; }
    .reports-nav { display: flex; gap: 4px; flex-wrap: wrap; border-bottom: 1px solid var(--line); padding-bottom: 2px; }
    .reports-tab { background: transparent; color: var(--txt3); border: none; padding: 8px 14px; cursor: pointer;
      font-size: 13px; font-weight: 500; border-bottom: 2px solid transparent; transition: all 0.12s;
      border-radius: 4px 4px 0 0; }
    .reports-tab:hover { color: var(--txt); background: var(--panel2); }
    .reports-tab.active { color: var(--accent2); border-bottom-color: var(--accent2); }
    /* PL202 City of LV Employee Listing form */
    .pl202-card { background: white; color: black; padding: 24px; }
    .pl202-form { color: black; font-family: 'Arial', sans-serif; max-width: 1000px; margin: 0 auto; }
    .pl202-header { display: flex; align-items: flex-start; gap: 16px; padding-bottom: 8px; border-bottom: 1px solid #000; }
    .pl202-header-left { flex: 0 0 80px; }
    .pl202-seal { width: 70px; height: 70px; border-radius: 50%; border: 2px solid #000;
      display: flex; align-items: center; justify-content: center; text-align: center;
      font-size: 8px; font-weight: 700; line-height: 1.2; color: #000; background: #f4f4f4; }
    .pl202-header-mid { flex: 1; }
    .pl202-agency { font-size: 13px; font-weight: 700; color: #000; line-height: 1.4; }
    .pl202-header-right { flex: 0 0 280px; font-size: 11px; color: #000; line-height: 1.5; text-align: right; }
    .pl202-email { color: #0066cc; font-weight: 600; }
    .pl202-title { text-align: center; font-size: 18px; font-weight: 700; color: #000; margin: 14px 0 16px; }
    .pl202-business { margin-bottom: 14px; }
    .pl202-business-row { display: flex; gap: 24px; margin-bottom: 12px; }
    .pl202-bf { flex: 1; }
    .pl202-label { font-size: 11px; color: #000; font-weight: 600; }
    .pl202-value { background: #e8eef8; border: 1px solid #999; padding: 4px 8px; margin-top: 2px; font-size: 12px;
      color: #000; min-height: 16px; }
    .pl202-licenses { font-size: 11px; line-height: 1.45; }
    .pl202-licenses div + div { margin-top: 2px; }
    .pl202-sub { font-size: 9px; color: #555; font-style: italic; margin-top: 2px; }
    .pl202-instr { font-size: 11px; color: #000; margin: 10px 0 6px; }
    .pl202-table { width: 100%; border-collapse: collapse; border: 1px solid #000; font-size: 11px; color: #000; }
    .pl202-table th { background: #e8eef8; border: 1px solid #000; padding: 6px 4px; font-weight: 700; text-align: center;
      vertical-align: middle; line-height: 1.2; }
    .pl202-table td { border: 1px solid #000; padding: 4px 6px; height: 22px; background: #e8eef8; color: #000; }
    .pl202-table tr:nth-child(even) td { background: #f0f4fb; }
    .pl202-warn { font-size: 11px; font-weight: 700; color: #c00; margin: 12px 0 6px; text-align: left; }
    .pl202-statute { font-size: 11px; color: #000; margin: 4px 0 2px; }
    .pl202-statute-body { font-size: 11px; color: #000; margin: 2px 0 8px 16px; }
    .pl202-warn-small { font-size: 10px; color: #c00; margin: 8px 0 16px; line-height: 1.4; }
    .pl202-sig { margin-top: 30px; display: flex; justify-content: flex-end; }
    .pl202-sig .pl202-sig-line { width: 320px; border-bottom: 1px solid #000; height: 28px; }
    .pl202-sig .pl202-sig-label { width: 320px; font-size: 10px; color: #000; text-align: center; margin-top: 2px; }
    .pl202-foot { display: flex; justify-content: space-between; margin-top: 20px; font-size: 10px; color: #000; }
    /* PL202 on-screen editing (hidden when printed) */
    .pl202-about { background: #f3f6fb; border: 1px solid #d5deec; color: #1d2733; padding: 10px 12px; border-radius: 6px;
      font-size: 12.5px; line-height: 1.55; }
    .pl202-issues { background: #fdf3f1; border: 1px solid #e7b9ae; color: #3b1a12; border-radius: 6px; padding: 8px 12px; font-size: 12px; }
    .pl202-issues summary { cursor: pointer; font-weight: 700; color: #9c2a12; display: flex; align-items: center; gap: 6px; }
    .pl202-issues ul { margin: 8px 0 2px 18px; padding: 0; }
    .pl202-issues li { margin: 3px 0; line-height: 1.45; }
    .pl202-issue-docs { margin: 3px 0 4px; }
    .pl202-issue-doc { display: flex; align-items: center; gap: 10px; font-size: 11.5px; }
    .pl202-issue-filename { max-width: 320px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #555; }
    .pl202-link { background: none; border: none; padding: 0; font: inherit; font-weight: 600; color: #1f4f8f;
      text-decoration: underline; cursor: pointer; }
    .pl202-link:hover { color: #0d3466; }
    .pl202-load-err { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; background: #fdf3f1; border: 1px solid #e7b9ae;
      color: #9c2a12; border-radius: 6px; padding: 10px 12px; font-size: 12.5px; margin: 10px 0; }
    .pl202-cell-btn { background: none; border: none; padding: 0; margin: 0; font: inherit; color: inherit; text-align: left;
      cursor: pointer; width: 100%; border-radius: 2px; }
    .pl202-cell-btn:hover, .pl202-cell-btn:focus-visible { background: #fff; outline: 1px dashed #1f4f8f; outline-offset: 1px; }
    .pl202-add { display: inline-flex; align-items: center; gap: 3px; background: #fff; border: 1px dashed #7f92b0; color: #1f4f8f;
      font: inherit; font-size: 10.5px; font-weight: 600; padding: 1px 6px; border-radius: 4px; cursor: pointer; white-space: nowrap; }
    .pl202-add.required { border-color: #c0392b; color: #a52714; }
    .pl202-add.optional { opacity: 0; }
    .pl202-table tr:hover .pl202-add.optional, .pl202-add.optional:focus-visible { opacity: 1; }
    .pl202-edit { display: flex; align-items: center; gap: 3px; }
    .pl202-edit input { flex: 1; min-width: 0; font: inherit; font-size: 11px; padding: 2px 4px; border: 1px solid #1f4f8f;
      border-radius: 3px; background: #fff; color: #000; }
    .pl202-edit-btn { background: #fff; border: 1px solid #999; border-radius: 3px; padding: 2px; cursor: pointer;
      display: grid; place-items: center; color: #000; }
    .pl202-edit-btn:disabled { opacity: 0.5; cursor: default; }
    .pl202-edit-err { color: #a52714; font-size: 10px; margin-top: 2px; }
    @media print {
      .pl202-card { padding: 0 !important; }
      .pl202-form { max-width: 100% !important; }
      .pl202-form * { color: black !important; }
      .pl202-table th { background: #e8eef8 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .pl202-table td { background: #e8eef8 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .pl202-table tr:nth-child(even) td { background: #f0f4fb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .pl202-value { background: #e8eef8 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .pl202-warn { color: #c00 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .pl202-warn-small { color: #c00 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .pl202-email { color: #0066cc !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    .report-card { background: var(--panel); padding: 22px 24px; border-radius: 8px; border: 1px solid var(--line); }
    .report-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 6px; }
    .report-head h2 { margin: 0; font-size: 18px; color: var(--txt); font-weight: 600; }
    .report-sub { font-size: 12.5px; color: var(--txt3); margin: 0 0 16px; }
    .report-export { display: flex; gap: 6px; }
    .report-controls { display: flex; gap: 10px; margin-bottom: 14px; align-items: flex-end; flex-wrap: wrap; }
    .report-h3 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--txt3);
      font-weight: 700; margin: 18px 0 10px; }
    .report-scroll { overflow-x: auto; }
    .report-table { width: 100%; border-collapse: collapse; font-size: 13px; }
    .report-table th { text-align: left; padding: 8px 10px; background: var(--panel2); color: var(--txt3);
      font-weight: 600; font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.4px;
      border-bottom: 1px solid var(--line); white-space: nowrap; }
    .report-table td { padding: 8px 10px; border-bottom: 1px solid var(--line); color: var(--txt); vertical-align: top; }
    .report-table tr:hover td { background: var(--panel2); }
    .report-table .dim { color: var(--txt3); }
    .report-table .mono { font-family: 'Spline Sans Mono', monospace; font-size: 12px; }
    .report-table .cell-cert { text-align: center; }
    .report-table .cell-warn { color: var(--gold); font-weight: 600; }
    .report-cert-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 6px; }
    .report-cert-row { display: flex; justify-content: space-between; padding: 6px 10px; background: var(--panel2);
      border-radius: 4px; font-size: 12.5px; }
    /* Per-employee ride history blocks */
    .ride-history-block { margin: 20px 0 16px; padding: 14px 16px; background: var(--panel2); border-radius: 6px;
      border-left: 3px solid var(--accent2); }
    .ride-history-block.muted { border-left-color: var(--line); padding: 8px 14px; margin: 14px 0; }
    .ride-history-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;
      padding-bottom: 8px; border-bottom: 1px solid var(--line); }
    .ride-history-title { margin: 0; font-size: 14px; color: var(--txt); font-weight: 700; }
    .ride-history-level { font-size: 12px; color: var(--txt3); display: inline-flex; align-items: center; gap: 6px; }
    .progression-table { width: 100%; }
    .progression-table td { padding: 6px 8px; font-size: 12.5px; border-bottom: 1px solid var(--line); vertical-align: top; }
    .progression-table tr:last-child td { border-bottom: none; }
    .progression-table tr.sev-good td { color: var(--txt); }
    .progression-table tr.sev-bad td { color: var(--txt); }
    .progression-table tr.sev-bad td:nth-child(3) { color: #e07a63; font-weight: 500; }
    .tag-evt { font-size: 10px; padding: 2px 7px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; }
    .tag-evt-quiz { background: rgba(74,111,165,0.18); color: #7ba3d6; }
    .tag-evt-observation { background: rgba(46,158,107,0.18); color: #4ec78c; }
    .tag-evt-signoff { background: rgba(180,140,200,0.18); color: #c8a6d8; }
    .tag-evt-event { background: var(--panel); color: var(--txt3); }
    .tag-evt-document { background: rgba(217,154,43,0.18); color: var(--gold); }
    /* Report filter panel */
    .report-filters { display: flex; gap: 14px; flex-wrap: wrap; padding: 12px 14px; background: var(--panel2);
      border-radius: 6px; margin-bottom: 14px; border: 1px solid var(--line); }
    .report-filter-group { flex: 1 1 320px; min-width: 280px; }
    .report-filter-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;
      padding-bottom: 6px; border-bottom: 1px solid var(--line); }
    .report-filter-label { font-size: 11px; color: var(--txt3); text-transform: uppercase; letter-spacing: 0.4px; font-weight: 600; }
    .report-filter-actions { display: flex; gap: 4px; }
    .report-checkbox-list { display: flex; flex-wrap: wrap; gap: 6px 14px; }
    .report-checkbox { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--txt);
      cursor: pointer; padding: 2px 0; user-select: none; }
    .report-checkbox input { margin: 0; cursor: pointer; }
    /* Mode toggle (Current vs Point-in-time) */
    .report-mode-toggle { display: inline-flex; background: var(--panel2); border-radius: 6px; padding: 3px;
      gap: 2px; margin-bottom: 12px; border: 1px solid var(--line); }
    .mode-btn { background: transparent; color: var(--txt3); border: none; padding: 6px 14px; cursor: pointer;
      font-size: 12.5px; font-weight: 500; border-radius: 4px; transition: all 0.12s; }
    .mode-btn:hover { color: var(--txt); }
    .mode-btn.active { background: var(--accent2); color: var(--bg); font-weight: 600; }
    /* Compliance document styling */
    .compliance-doc { font-family: 'Georgia', 'Times New Roman', serif; line-height: 1.5; }
    .compliance-doc .compliance-page-header { display: none; } /* shown only when printing */
    .compliance-title-block { text-align: center; padding: 20px 0 18px; border-bottom: 2px solid var(--gold);
      margin-bottom: 22px; }
    .compliance-org { font-size: 11px; letter-spacing: 2.5px; color: var(--txt3); font-weight: 700;
      text-transform: uppercase; margin-bottom: 8px; font-family: 'Archivo', sans-serif; }
    .compliance-title { font-size: 22px; font-weight: 700; color: var(--txt); margin-bottom: 4px;
      font-family: 'Georgia', 'Times New Roman', serif; }
    .compliance-subtitle { font-size: 13px; font-style: italic; color: var(--txt3); margin-bottom: 12px;
      font-family: 'Georgia', 'Times New Roman', serif; }
    .compliance-reportid { font-size: 11px; color: var(--txt3); font-family: 'Spline Sans Mono', monospace;
      letter-spacing: 0.5px; }
    /* Old compliance-header preserved for RideCoverage report */
    .compliance-header { background: var(--panel2); border-left: 4px solid var(--gold); padding: 16px 20px;
      margin-bottom: 20px; border-radius: 4px; }
    .compliance-attest { font-size: 13px; margin-bottom: 16px; border: 1px solid var(--line);
      background: var(--panel2); padding: 12px 18px; border-radius: 4px; }
    .compliance-attest th { text-align: left; padding: 5px 16px 5px 0; color: var(--txt3); font-weight: 600;
      font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.4px; vertical-align: top; white-space: nowrap; }
    .compliance-attest td { padding: 5px 0; color: var(--txt); font-family: 'Georgia', 'Times New Roman', serif; }
    .compliance-disclaimer { font-size: 11.5px; color: var(--txt2); margin: 8px 0 0; font-style: italic; line-height: 1.5; }
    /* Section titles within compliance doc */
    .compliance-section-title { font-size: 15px; font-weight: 700; color: var(--txt); margin: 20px 0 12px;
      padding-bottom: 6px; border-bottom: 1px solid var(--line);
      font-family: 'Georgia', 'Times New Roman', serif; }
    /* Prose paragraphs */
    .compliance-prose { font-size: 13.5px; line-height: 1.65; color: var(--txt); margin: 10px 0;
      font-family: 'Georgia', 'Times New Roman', serif; }
    /* Certification summary table */
    .compliance-cert-table { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: 13px; }
    .compliance-cert-table th { text-align: left; padding: 8px 12px; background: var(--panel2); color: var(--txt3);
      font-weight: 600; font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.4px;
      border-bottom: 2px solid var(--line); font-family: 'Archivo', sans-serif; }
    .compliance-cert-table td { padding: 9px 12px; border-bottom: 1px solid var(--line); color: var(--txt);
      font-family: 'Georgia', 'Times New Roman', serif; }
    .compliance-cert-table td:first-child { width: 30%; }
    .compliance-cert-table td:nth-child(2) { width: 20%; }
    /* Footer with signature block */
    .compliance-footer { margin-top: 36px; padding-top: 24px; border-top: 2px solid var(--gold); }
    .compliance-sig-block { display: flex; flex-direction: column; gap: 22px; margin: 18px 0; }
    .compliance-sig-row { display: flex; gap: 50px; align-items: flex-end; }
    .compliance-sig { flex: 1; }
    .compliance-sig-line { border-bottom: 1px solid var(--txt2); height: 28px; }
    .compliance-sig-label { font-size: 10.5px; color: var(--txt3); text-transform: uppercase; letter-spacing: 0.4px;
      margin-top: 4px; font-family: 'Archivo', sans-serif; }
    .compliance-page-footer { display: flex; justify-content: space-between; margin-top: 28px; padding-top: 12px;
      border-top: 1px solid var(--line); font-size: 10.5px; color: var(--txt3); letter-spacing: 0.3px;
      font-family: 'Spline Sans Mono', monospace; }
    /* Use serif inside ride sections too when in compliance mode */
    .compliance-mode .ride-history-block { font-family: 'Georgia', 'Times New Roman', serif; }
    .compliance-mode .ride-history-title { font-family: 'Georgia', 'Times New Roman', serif; }
    /* Doc filename as link inside report */
    .btn-link { background: none; border: none; color: var(--accent2); text-decoration: underline; cursor: pointer;
      padding: 0; font: inherit; font-size: inherit; }
    .btn-link:hover { color: var(--accent); }
    .print-only { display: none; }
    .expiry-expired { color: var(--err); font-weight: 700; }
    .expiry-soon { color: var(--gold); font-weight: 600; }
    .expiry-ok { color: var(--txt3); }
    .field-row { display: flex; gap: 10px; }
    /* PRINT STYLES — only show .print-target content, hide chrome */
    @media print {
      /* Force a clean white background by overriding all theme variables. */
      :root, html, body, .app {
        --bg: #ffffff !important;
        --bg2: #ffffff !important;
        --panel: #ffffff !important;
        --panel2: #f7f7f7 !important;
        --line: #c0c0c0 !important;
        --txt: #111111 !important;
        --txt2: #333333 !important;
        --txt3: #555555 !important;
        --accent: #b8410f !important;
        --accent2: #2a4b7a !important;
        --err: #b00000 !important;
        --ok: #1a6b3a !important;
        --gold: #8a6800 !important;
        --l3: #1a6b3a !important;
        color-scheme: light !important;
      }
      html, body, .app, .main, .content {
        background: white !important;
        background-image: none !important;
        color: black !important;
      }
      *, *::before, *::after {
        background-image: none !important;
        box-shadow: none !important;
        text-shadow: none !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
      }
      .header, .sidebar, .app, .main, .content { position: static !important; height: auto !important; min-height: 0 !important; }
      .sidebar, .topbar, .header, .reports-nav, .no-print, .modal-overlay,
      .nav, .nav-item, .sidebar-foot, .reports-tab, .report-mode-toggle,
      .report-filters, .report-controls, .report-export {
        display: none !important;
      }
      .app { display: block !important; }
      .main { display: block !important; padding: 0 !important; }
      .content, main { padding: 0 !important; max-width: 100% !important; margin: 0 !important; }
      .report-card { background: white !important; border: none !important; padding: 0 !important; margin: 0 !important; }
      .report-card h2, .report-card h3, .report-card td, .report-card th,
      .report-card span, .report-card div, .report-card p, .report-card strong, .report-card em {
        color: black !important;
      }
      .report-card .dim, .report-table .dim { color: #555 !important; }
      .report-table { width: 100% !important; border-collapse: collapse !important; }
      .report-table th { background: #f0f0f0 !important; border-bottom: 1px solid #aaa !important; color: #222 !important; }
      .report-table td { border-bottom: 1px solid #ddd !important; padding: 6px 8px !important; }
      .tag {
        border: 1px solid #555 !important;
        background: white !important;
        color: black !important;
        padding: 1px 6px !important;
      }
      .tag-mandatory { border-color: #b8860b !important; color: #5a4500 !important; }
      .expiry-expired { color: #b00 !important; font-weight: 700 !important; }
      .expiry-soon { color: #885000 !important; font-weight: 600 !important; }
      /* COMPLIANCE DOC: serif body, formal layout, running headers */
      .compliance-doc { font-family: 'Georgia', 'Times New Roman', serif !important; }
      .compliance-doc .compliance-page-header {
        display: flex !important; justify-content: space-between;
        padding-bottom: 4px; margin-bottom: 12px;
        border-bottom: 1px solid #888; font-size: 10px; color: #555;
        font-family: 'Archivo', Arial, sans-serif;
        letter-spacing: 0.5px; text-transform: uppercase; font-weight: 700;
      }
      .compliance-title-block { padding-top: 8px !important; border-bottom: 2px solid #8a6800 !important; }
      .compliance-org { color: #555 !important; font-family: 'Archivo', Arial, sans-serif !important; }
      .compliance-title { color: black !important; font-family: 'Georgia', 'Times New Roman', serif !important; }
      .compliance-subtitle { color: #555 !important; font-style: italic; font-family: 'Georgia', serif !important; }
      .compliance-reportid { color: #555 !important; }
      .compliance-attest { background: #fafafa !important; border: 1px solid #ccc !important; padding: 12px 16px !important; }
      .compliance-attest th { color: #555 !important; font-family: 'Archivo', Arial, sans-serif !important; }
      .compliance-attest td { color: black !important; font-family: 'Georgia', 'Times New Roman', serif !important; }
      .compliance-section-title { color: black !important; border-bottom: 1px solid #888 !important;
        page-break-after: avoid !important;
        font-family: 'Georgia', 'Times New Roman', serif !important; }
      .compliance-prose { color: black !important; font-family: 'Georgia', 'Times New Roman', serif !important; }
      .compliance-cert-table th { background: #f0f0f0 !important; }
      .compliance-cert-table td { font-family: 'Georgia', 'Times New Roman', serif !important; }
      .compliance-footer { border-top: 2px solid #8a6800 !important; page-break-before: auto; }
      .compliance-sig-line { border-bottom: 1px solid #222 !important; }
      .compliance-sig-label { color: #555 !important; font-family: 'Archivo', Arial, sans-serif !important; }
      .compliance-page-footer { color: #555 !important; border-top: 1px solid #888 !important;
        font-family: 'Spline Sans Mono', monospace !important; }
      /* In compliance mode, use serif for ride sections too */
      .compliance-mode .ride-history-block, .compliance-mode .ride-history-title,
      .compliance-mode .progression-table td { font-family: 'Georgia', 'Times New Roman', serif !important; }
      .ride-history-block {
        background: white !important;
        border-left: 2px solid #888 !important;
        padding: 12px 14px !important;
        margin: 14px 0 !important;
        page-break-inside: avoid;
      }
      .ride-history-block.muted { border-left: 1px dashed #ccc !important; }
      .ride-history-title { color: black !important; }
      .btn-link { display: none !important; }
      .print-only { display: inline !important; color: black !important; }
      button:not(.print-keep) { display: none !important; }
      .empty { display: block !important; color: #555 !important; }
      @page {
        size: letter;
        margin: 0.6in 0.5in 0.7in 0.5in;
      }
    }

    .empty { color: var(--txt3); font-size: 13px; padding: 20px 12px; text-align: center; font-style: italic; }

    /* LOG */
    .log { max-height: 320px; }
    .log-row { display: flex; align-items: center; gap: 12px; padding: 9px 12px; font-size: 13px; }
    .log-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--txt3); flex-shrink: 0; }
    .log-pass .log-dot { background: var(--green); } .log-fail .log-dot { background: var(--l3); }
    .log-quiz .log-dot { background: var(--accent); } .log-level .log-dot { background: var(--gold); }
    .log-new .log-dot { background: var(--blue); } .log-sched .log-dot { background: var(--purple); }
    .log-text { flex: 1; color: var(--txt2); }
    .log-date { color: var(--txt3); font-size: 12px; font-family: 'Spline Sans Mono', monospace; }

    /* ROSTER TABLE */
    .table-wrap { background: var(--panel); border: 1px solid var(--line); border-radius: var(--radius); overflow-x: auto; margin-bottom: 20px; }
    .cert-table { width: 100%; border-collapse: collapse; min-width: 760px; }
    .cert-table th { padding: 14px 10px; text-align: center; font-size: 11px; font-weight: 700; color: var(--txt);
      text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid var(--line); white-space: nowrap; }
    .cert-table th.ride-col span { display: inline-block; }
    .cert-table td { padding: 9px 10px; text-align: center; border-bottom: 1px solid var(--line); }
    .cert-table tr { cursor: pointer; transition: background 0.12s; }
    .cert-table tbody tr:hover { background: var(--panel2); }
    .cert-table tr.sel { background: rgba(255,107,53,0.08); }
    .sticky-col { position: sticky; left: 0; background: var(--panel); z-index: 2; text-align: left !important; }
    .cert-table tbody tr:hover .sticky-col { background: var(--panel2); }
    .cert-table tr.sel .sticky-col { background: #25201d; }
    .name-cell { font-weight: 600; font-size: 13.5px; white-space: nowrap; }
    .date-cell { font-size: 12px; color: var(--txt2); font-family: 'Spline Sans Mono', monospace; }
    .action-cell { white-space: nowrap; display: flex; gap: 6px; justify-content: flex-end; }

    .cell-badge { display: inline-grid; place-items: center; width: 28px; height: 28px; border-radius: 8px;
      font-weight: 800; font-size: 13px; color: #fff; font-family: 'Spline Sans Mono', monospace; }
    .cell-badge.sm { width: 22px; height: 22px; font-size: 11px; border-radius: 6px; }

    .legend { display: flex; flex-wrap: wrap; gap: 18px; padding: 16px 20px; background: var(--panel);
      border: 1px solid var(--line); border-radius: var(--radius); margin-top: 4px; }
    .legend-item { display: flex; align-items: center; gap: 9px; font-size: 12.5px; color: var(--txt2); }

    /* EMPLOYEE DETAIL */
    .emp-detail { background: var(--panel); border: 1px solid var(--line); border-radius: var(--radius); margin-bottom: 20px; overflow: hidden; }
    .ed-head { display: flex; align-items: center; justify-content: space-between; padding: 18px 22px; border-bottom: 1px solid var(--line);
      background: linear-gradient(135deg, rgba(255,107,53,0.08), transparent); }
    .ed-head h3 { font-size: 18px; font-weight: 800; }
    .ed-sub { font-size: 12px; color: var(--txt3); }
    .ed-actions { display: flex; gap: 8px; }
    .ed-grid { display: grid; grid-template-columns: 1.2fr 1fr 1.3fr; gap: 1px; background: var(--line); }
    .ed-block { background: var(--panel); padding: 18px 20px; }
    .ed-block h4 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--txt3); margin-bottom: 14px; font-weight: 700; }
    .ed-cert { display: flex; align-items: center; gap: 9px; padding: 6px 0; font-size: 13px; }
    .ed-ride { width: 78px; color: var(--txt2); flex-shrink: 0; }
    .ed-arrow { color: var(--txt3); flex-shrink: 0; }
    .ed-next { color: var(--txt2); font-size: 12px; }
    .ed-sched { display: flex; flex-direction: column; padding: 8px 0; font-size: 13px; color: var(--txt2); border-bottom: 1px solid var(--line); }
    .ed-sched-date { font-family: 'Spline Sans Mono', monospace; font-size: 12px; color: var(--txt3); }
    .ed-history { display: flex; flex-direction: column; gap: 10px; }
    .ed-hist-row { display: flex; gap: 10px; font-size: 12.5px; color: var(--txt2); }
    .ed-hist-date { font-family: 'Spline Sans Mono', monospace; color: var(--txt3); flex-shrink: 0; width: 48px; }
    /* Documents panel within employee detail */
    .ed-docs-block h4 { display: inline-flex; align-items: center; gap: 6px; }
    .ed-docs-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
    .ed-docs-head h4 { margin: 0; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--txt3); font-weight: 700; }
    .ed-docs-actions { display: flex; gap: 6px; }
    .ed-doc-list { display: flex; flex-direction: column; gap: 8px; }
    .ed-doc-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px;
      padding: 10px 12px; background: var(--panel2); border-radius: 6px; border: 1px solid var(--line); }
    .ed-doc-row.deleted { opacity: 0.55; }
    .ed-doc-meta { flex: 1; min-width: 0; }
    .ed-doc-line1 { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .ed-doc-filename { font-size: 13px; color: var(--txt); font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
    .ed-doc-desc { font-size: 11.5px; color: var(--txt2); margin-top: 3px; }
    .ed-doc-line3 { font-size: 11px; color: var(--txt3); margin-top: 4px; }
    .ed-doc-actions { display: flex; gap: 4px; flex-shrink: 0; }
    .tag-doc-cat { background: var(--accent2); color: var(--bg); padding: 2px 7px; font-size: 10px; font-weight: 600;
      text-transform: uppercase; letter-spacing: 0.3px; border-radius: 3px; }
    .tag-doc-ride { background: var(--panel); color: var(--accent2); padding: 2px 7px; font-size: 10px; font-weight: 600;
      letter-spacing: 0.3px; border-radius: 3px; border: 1px solid var(--accent2); }
    .ed-docs-filter { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; padding: 6px 10px;
      background: var(--panel2); border-radius: 6px; border: 1px solid var(--line); }
    .ed-docs-filter-label { font-size: 11px; color: var(--txt3); text-transform: uppercase; letter-spacing: 0.4px; }
    .ed-docs-filter select { background: var(--panel); color: var(--txt); border: 1px solid var(--line);
      border-radius: 4px; font-size: 12px; padding: 3px 6px; }
    .doc-ride-chips { display: flex; flex-wrap: wrap; gap: 6px; padding: 4px 0 2px; }
    .doc-ride-chip { background: var(--panel); color: var(--txt2); border: 1px solid var(--line);
      border-radius: 14px; padding: 4px 10px; font-size: 11.5px; cursor: pointer; transition: all 0.12s; }
    .doc-ride-chip:hover { border-color: var(--accent2); color: var(--txt); }
    .doc-ride-chip.on { background: var(--accent2); color: var(--bg); border-color: var(--accent2); font-weight: 600; }
    .ed-deleted-docs { margin-top: 12px; padding-top: 12px; border-top: 1px dashed var(--line); }
    .ed-deleted-head { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--txt3); margin-bottom: 8px; }

    /* MODAL */
    .modal-overlay { position: fixed; inset: 0; background: rgba(8,11,15,0.72); backdrop-filter: blur(4px);
      display: grid; place-items: center; z-index: 100; padding: 20px; animation: fade 0.15s; }
    @keyframes fade { from { opacity: 0; } }
    .modal { background: var(--panel); border: 1px solid var(--line2); border-radius: 16px; width: 480px; max-width: 100%;
      max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 30px 80px rgba(0,0,0,0.5); animation: pop 0.18s; }
    .modal.wide { width: 620px; }
    @keyframes pop { from { transform: scale(0.96) translateY(8px); opacity: 0; } }

    /* Course player fullscreen mode — works both via Fullscreen API
       AND as fallback for iOS Safari which won't grant fullscreen on a div */
    .course-player.is-fullscreen {
      position: fixed !important;
      inset: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      max-width: 100vw !important;
      max-height: 100vh !important;
      border-radius: 0 !important;
      z-index: 9999 !important;
      background: #0a0d12 !important;
    }
    .course-player.is-fullscreen .modal-body {
      flex: 1;
      max-height: none;
    }
    /* Make the slide image fill more of the viewport in fullscreen */
    .course-player.is-fullscreen .slide-image-wrap {
      flex: 1;
      min-height: 0 !important;
    }
    .course-player.is-fullscreen .slide-image-wrap img {
      max-height: calc(100vh - 280px);
      width: auto !important;
      max-width: 100%;
      object-fit: contain;
    }
    .modal-head { display: flex; align-items: center; justify-content: space-between; padding: 18px 22px; border-bottom: 1px solid var(--line); }
    .modal-head h3 { font-size: 16px; font-weight: 700; display: flex; align-items: center; gap: 10px; }
    .modal-head h3 svg { color: var(--accent2); }
    .modal-body { padding: 22px; overflow-y: auto; }
    .modal-foot { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 22px; border-top: 1px solid var(--line); }

    .field { display: flex; flex-direction: column; gap: 7px; margin-bottom: 16px; }
    .field span { font-size: 12px; color: var(--txt2); font-weight: 600; }
    .field input, .field select { background: var(--bg2); border: 1px solid var(--line2); border-radius: 9px;
      padding: 11px 13px; color: var(--txt); font-family: inherit; font-size: 14px; outline: none; }
    .field input:focus, .field select:focus { border-color: var(--accent); }
    .field select option { background: var(--bg2); }

    .signoff-info { display: flex; align-items: center; gap: 16px; justify-content: center; padding: 16px;
      background: var(--bg2); border-radius: 10px; margin-bottom: 16px; }
    .si-row { display: flex; flex-direction: column; align-items: center; gap: 7px; }
    .si-row span:first-child { font-size: 11px; color: var(--txt3); }
    .signoff-note { background: rgba(74,111,165,0.1); border: 1px solid rgba(74,111,165,0.25); border-radius: 10px; padding: 12px 14px; }
    .signoff-note p { font-size: 12.5px; color: var(--txt2); display: flex; align-items: flex-start; gap: 8px; line-height: 1.5; }
    .signoff-note svg { color: var(--accent2); flex-shrink: 0; margin-top: 2px; }
    .sched-target { font-size: 13px; color: var(--txt2); margin-bottom: 16px; padding: 10px 13px; background: var(--bg2); border-radius: 9px; }

    /* QUIZ */
    .quiz-body { display: flex; flex-direction: column; gap: 18px; }
    .quiz-intro { font-size: 13px; color: var(--txt2); padding-bottom: 4px; }
    .quiz-q-text { display: flex; gap: 10px; font-size: 14px; font-weight: 600; margin-bottom: 11px; line-height: 1.4; }
    .quiz-num { width: 22px; height: 22px; border-radius: 6px; background: var(--accent); color: #1a1008;
      display: grid; place-items: center; font-size: 12px; font-weight: 800; flex-shrink: 0; font-family: 'Spline Sans Mono', monospace; }
    .quiz-opts { display: flex; flex-direction: column; gap: 7px; padding-left: 32px; }
    .quiz-opt { display: flex; align-items: center; gap: 10px; padding: 10px 13px; border: 1px solid var(--line2);
      border-radius: 9px; cursor: pointer; transition: all 0.12s; font-size: 13.5px; }
    .quiz-opt:hover { border-color: var(--accent2); background: var(--panel2); }
    .quiz-opt.picked { border-color: var(--accent); background: rgba(255,107,53,0.1); }
    .quiz-opt input { accent-color: var(--accent); }

    .quiz-result { text-align: center; padding: 36px 22px; }
    .result-badge { width: 84px; height: 84px; border-radius: 50%; display: grid; place-items: center; margin: 0 auto 18px; }
    .result-badge.pass { background: rgba(46,158,107,0.15); color: var(--green); }
    .result-badge.fail { background: rgba(200,71,47,0.15); color: var(--l3); }
    .quiz-result h2 { font-size: 44px; font-weight: 900; font-family: 'Spline Sans Mono', monospace; margin-bottom: 12px; }
    .result-msg { font-size: 15px; margin-bottom: 6px; }
    .result-msg.pass { color: #4ec78c; } .result-msg.fail { color: #e07a63; }
    .result-sub { font-size: 13px; color: var(--txt3); margin-bottom: 24px; }

    /* LOGIN */
    .login-wrap { min-height: 100vh; display: grid; place-items: center; padding: 24px;
      background: radial-gradient(900px 500px at 100% 0%, rgba(255,107,53,0.08), transparent 60%),
        radial-gradient(700px 400px at 0% 100%, rgba(74,111,165,0.1), transparent 55%), var(--bg);
      font-family: 'Archivo', sans-serif; }
    .login-card { background: var(--panel); border: 1px solid var(--line2); border-radius: 16px; padding: 32px;
      width: 400px; max-width: 100%; box-shadow: 0 30px 80px rgba(0,0,0,0.5); }
    .login-brand { display: flex; align-items: center; gap: 12px; margin-bottom: 26px; }
    .brand-mark.lg { width: 48px; height: 48px; border-radius: 13px; }
    .login-title { font-size: 20px; font-weight: 800; margin-bottom: 20px; color: var(--txt); }
    .input-icon { display: flex; align-items: center; gap: 9px; background: var(--bg2); border: 1px solid var(--line2);
      border-radius: 9px; padding: 0 12px; color: var(--txt3); }
    .input-icon input { background: none; border: none; outline: none; color: var(--txt); font-family: inherit;
      font-size: 14px; padding: 11px 0; flex: 1; }
    .login-btn { width: 100%; justify-content: center; margin-top: 8px; padding: 12px; }
    .login-err { display: flex; align-items: center; gap: 8px; background: rgba(200,71,47,0.12);
      border: 1px solid rgba(200,71,47,0.3); color: #e07a63; font-size: 12.5px; padding: 9px 12px; border-radius: 8px; margin-bottom: 14px; }
    .login-ok { display: flex; align-items: center; gap: 8px; background: rgba(46,125,82,0.14);
      border: 1px solid rgba(46,125,82,0.4); color: #5fbf87; font-size: 12.5px; padding: 9px 12px; border-radius: 8px; margin-bottom: 14px; }
    .users-view { padding: 6px 2px 30px; }
    .users-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 14px; margin-bottom: 18px; flex-wrap: wrap; }
    .page-title { display: flex; align-items: center; gap: 10px; font-size: 22px; font-weight: 800; color: var(--txt); margin: 0 0 4px 0; letter-spacing: -0.2px; }
    .page-sub { color: var(--txt3); font-size: 13px; margin: 0; }
    .users-filters { display: flex; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
    .users-filters .search { flex: 1; min-width: 260px; }
    .users-filters select { background: var(--panel); color: var(--txt); border: 1px solid var(--line); border-radius: 8px; padding: 8px 10px; font-size: 13px; }
    .users-table { display: flex; flex-direction: column; gap: 1px; background: var(--line); border: 1px solid var(--line); border-radius: 10px; overflow: hidden; }
    .users-row { display: grid; grid-template-columns: 1.6fr 1.4fr 1fr 1.2fr 0.9fr 1.4fr; gap: 12px; padding: 12px 14px; background: var(--panel); align-items: center; font-size: 13px; }
    .users-row.deactivated { opacity: 0.55; }
    .users-row.deactivated .u-email { text-decoration: line-through; }
    .users-row-head { background: var(--panel2); color: var(--txt3); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700; }
    .u-email { color: var(--txt); font-weight: 600; word-break: break-all; }
    .u-self { color: var(--accent); font-weight: 500; }
    .u-sub { color: var(--txt3); font-size: 11.5px; margin-top: 2px; }
    .u-dim { color: var(--txt3); font-style: italic; }
    .u-actions { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
    .btn-ghost-sm { display: inline-flex; align-items: center; gap: 5px; background: transparent; color: var(--txt2); border: 1px solid var(--line2); border-radius: 6px; padding: 5px 9px; font-size: 11.5px; font-weight: 600; cursor: pointer; }
    .btn-ghost-sm:hover { color: var(--txt); border-color: var(--accent); }
    .btn-ghost-sm.danger:hover { color: #e07a63; border-color: #c0492f; }
    .role-pill { display: inline-block; padding: 3px 9px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: 0.3px; text-transform: uppercase; }
    .role-pill.role-admin    { background: rgba(255,107,53,0.18); color: #ff9a6f; border: 1px solid rgba(255,107,53,0.4); }
    .role-pill.role-agm_gm   { background: rgba(120,90,200,0.15); color: #b39ce0; border: 1px solid rgba(120,90,200,0.4); }
    .role-pill.role-manager  { background: rgba(70,140,200,0.15); color: #88baeb; border: 1px solid rgba(70,140,200,0.4); }
    .role-pill.role-trainer  { background: rgba(70,170,120,0.15); color: #8fd6ad; border: 1px solid rgba(70,170,120,0.4); }
    .role-pill.role-trainee  { background: rgba(140,140,160,0.15); color: #b8b8c8; border: 1px solid rgba(140,140,160,0.35); }
    @media (max-width: 920px) {
      .users-row { grid-template-columns: 1fr; gap: 6px; }
      .users-row-head { display: none; }
      .u-actions { justify-content: flex-start; margin-top: 6px; }
    }
    .login-demo { margin-top: 22px; border-top: 1px solid var(--line); padding-top: 16px; }
    .login-demo-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.6px; color: var(--txt3); margin-bottom: 10px; font-weight: 700; }
    .demo-row { display: flex; justify-content: space-between; align-items: center; width: 100%; background: var(--bg2);
      border: 1px solid var(--line); border-radius: 8px; padding: 8px 12px; margin-bottom: 6px; cursor: pointer; font-family: inherit; transition: all 0.12s; }
    .demo-row:hover { border-color: var(--accent); }
    .demo-role { font-size: 12.5px; font-weight: 600; color: var(--txt); }
    .demo-cred { font-size: 11.5px; color: var(--txt3); font-family: 'Spline Sans Mono', monospace; }
    .login-warn { font-size: 11px; color: var(--txt3); margin-top: 12px; font-style: italic; line-height: 1.5; }

    /* USER CARD */
    .user-card { display: flex; align-items: center; gap: 10px; padding: 10px; background: var(--panel);
      border: 1px solid var(--line); border-radius: 10px; margin-bottom: 10px; }
    .user-avatar { width: 34px; height: 34px; border-radius: 9px; background: linear-gradient(135deg, var(--accent), var(--accent2));
      color: #1a1008; display: grid; place-items: center; font-weight: 800; font-size: 12px; flex-shrink: 0; }
    .user-meta { flex: 1; min-width: 0; }
    .user-name { font-size: 12.5px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .user-role { font-size: 11px; color: var(--txt3); }

    /* SIGNATURE */
    .signer-box { background: var(--bg2); border: 1px solid var(--line); border-radius: 10px; padding: 14px; margin-top: 6px; }
    .signer-id { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--txt2); margin-bottom: 12px; }
    .signer-id svg { color: var(--accent2); }
    .sig-wrap { }
    .sig-canvas { width: 100%; height: 150px; background: #fff; border-radius: 8px; border: 1px solid var(--line2); touch-action: none; cursor: crosshair; display: block; }
    .sig-tools { display: flex; align-items: center; justify-content: space-between; margin-top: 8px; }
    .sig-hint { font-size: 11.5px; color: var(--txt3); }

    /* SIGN-OFF MODAL EXTRAS */
    .signoff-summary { background: var(--bg2); border-radius: 10px; padding: 14px; margin-bottom: 16px; }
    .ss-kind { font-size: 12.5px; color: var(--accent2); margin-top: 4px; font-weight: 600; }
    .ss-topics { font-size: 12px; color: var(--txt2); margin-top: 8px; padding-top: 8px; border-top: 1px solid var(--line); }
    .role-track { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
    .role-pill { display: flex; align-items: center; gap: 6px; font-size: 12px; padding: 6px 11px; border-radius: 20px;
      border: 1px solid var(--line2); color: var(--txt2); cursor: pointer; }
    .role-pill.active { border-color: var(--accent); color: var(--accent2); background: rgba(255,107,53,0.08); }
    .role-pill.done { border-color: var(--l2); color: #4ec78c; background: rgba(46,158,107,0.1); cursor: default; }
    .role-by { font-size: 10.5px; opacity: 0.7; }

    /* NOTIFICATIONS */
    .notif-panel .panel-head h2 svg { color: var(--gold); }
    .notif-row .notif-ic { color: var(--gold); flex-shrink: 0; }
    .notif-text { font-size: 13px; color: var(--txt); }

    /* MODULES */
    .modules-tab { display: grid; grid-template-columns: 220px 1fr; gap: 18px; }
    .mod-list { display: flex; flex-direction: column; gap: 4px; }
    .mod-tab { text-align: left; background: var(--panel); border: 1px solid var(--line); border-radius: 9px;
      padding: 11px 13px; color: var(--txt2); font-family: inherit; font-size: 13px; cursor: pointer; transition: all 0.12s; }
    .mod-tab:hover { border-color: var(--line2); color: var(--txt); }
    .mod-tab.active { background: linear-gradient(135deg, rgba(255,107,53,0.15), rgba(255,107,53,0.04)); color: var(--accent2);
      border-color: rgba(255,107,53,0.3); font-weight: 600; }
    .mod-body { max-height: none; }
    /* Markdown rendering (database-backed modules) */
    .md { font-size: 13.5px; color: var(--txt2); line-height: 1.65; padding: 6px; }
    /* ---- Policy documents (formatted HTML) ---- */
    .policy-doc { font-size: 14px; color: var(--txt); line-height: 1.6; }
    .policy-doc h1, .policy-doc h2 { font-size: 17px; color: var(--accent2); margin: 22px 0 8px; padding-bottom: 4px; border-bottom: 1px solid var(--line); }
    .policy-doc h3 { font-size: 15px; color: var(--txt); margin: 16px 0 6px; }
    .policy-doc h4 { font-size: 13.5px; color: var(--txt2); margin: 12px 0 4px; text-transform: none; }
    .policy-doc > :first-child { margin-top: 0; }
    .policy-doc p { margin: 0 0 10px; }
    .policy-doc ul, .policy-doc ol { margin: 0 0 10px; padding-left: 22px; }
    .policy-doc li { margin-bottom: 4px; }
    .policy-doc li > p { margin: 0; }
    .policy-doc strong { color: var(--txt); }
    .policy-doc table { width: 100%; border-collapse: collapse; margin: 8px 0 14px; font-size: 13px; display: block; overflow-x: auto; }
    .policy-doc th, .policy-doc td { border: 1px solid var(--line2); padding: 6px 9px; vertical-align: top; text-align: left; min-width: 80px; }
    .policy-doc th { background: var(--panel2); color: var(--txt); font-weight: 700; }
    .policy-doc th p, .policy-doc td p { margin: 0 0 4px; }
    .policy-doc blockquote { margin: 12px 0; padding: 12px 16px; border-left: 4px solid var(--accent); background: var(--panel2); border-radius: 0 8px 8px 0; }
    .policy-doc hr { border: 0; border-top: 1px solid var(--line2); margin: 20px 0; }
    .pe-wrap { border: 1px solid var(--line2); border-radius: 10px; background: var(--bg2); margin-top: 6px; }
    .pe-toolbar { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; padding: 8px; border-bottom: 1px solid var(--line); position: sticky; top: 0; background: var(--panel); z-index: 2; border-radius: 10px 10px 0 0; }
    .pe-btn { background: transparent; border: 1px solid transparent; color: var(--txt2); border-radius: 6px; padding: 5px 9px; font-size: 12.5px; cursor: pointer; line-height: 1.2; }
    .pe-btn:hover { background: var(--panel2); color: var(--txt); border-color: var(--line2); }
    .pe-btn.on { background: var(--accent); color: #fff; border-color: var(--accent); }
    .pe-btn:disabled { opacity: 0.35; cursor: not-allowed; }
    .pe-sep { width: 1px; height: 20px; background: var(--line2); margin: 0 4px; }
    .pe-msg { padding: 8px 12px; font-size: 12px; color: var(--accent2); border-bottom: 1px solid var(--line); }
    .pe-content .ProseMirror { min-height: 320px; padding: 16px 18px; outline: none; }
    .pe-content .ProseMirror table { display: table; }
    .pe-content .ProseMirror .selectedCell { background: rgba(255,107,53,0.18); }
    .pe-content .ProseMirror p.is-editor-empty:first-child::before { content: "Type here, or use Import Word to bring in a .docx"; color: var(--txt3); float: left; height: 0; pointer-events: none; }
    .md p { margin: 0 0 12px; }
    .md .md-h { color: var(--accent2); margin: 18px 0 10px; font-weight: 700; }
    .md .md-h1 { font-size: 17px; }
    .md .md-h2 { font-size: 15px; }
    .md .md-h3 { font-size: 14px; }
    .md ul, .md ol { margin: 0 0 12px; padding-left: 22px; }
    .md ol.md-ol-alpha { list-style-type: lower-alpha; }
    .md li { margin-bottom: 5px; }
    .md .md-li-children { margin-top: 5px; }
    .md .md-li-children ul, .md .md-li-children ol { margin-bottom: 4px; }
    .md strong { color: var(--txt); }
    .md em { color: var(--txt2); }
    .md blockquote { border-left: 3px solid var(--accent2); margin: 10px 0; padding: 8px 14px;
      background: rgba(255, 107, 53, 0.06); border-radius: 0 6px 6px 0; }
    .md blockquote p:last-child { margin-bottom: 0; }
    .md code { font-family: 'Spline Sans Mono', monospace; font-size: 12px; padding: 1px 5px;
      background: var(--panel2); border-radius: 3px; }
    .mod-intro { font-size: 13.5px; color: var(--txt2); font-style: italic; line-height: 1.6; padding: 6px 6px 16px; border-bottom: 1px solid var(--line); margin-bottom: 16px; }
    .mod-section { margin-bottom: 22px; padding: 0 6px; }
    .mod-section h4 { font-size: 14px; font-weight: 700; color: var(--accent2); margin-bottom: 10px; }
    .mod-section ul { margin: 0; padding-left: 20px; }
    .mod-section li { font-size: 13.5px; color: var(--txt2); line-height: 1.6; margin-bottom: 7px; }
    .mod-points { margin-bottom: 14px; }
    .mod-toggle { margin-left: auto; display: flex; background: var(--bg2); border: 1px solid var(--line); border-radius: 8px; overflow: hidden; }
    .mod-toggle button { background: none; border: none; color: var(--txt3); font-family: inherit; font-size: 12px; font-weight: 600;
      padding: 6px 13px; cursor: pointer; }
    .mod-toggle button.active { background: var(--accent); color: #1a1008; }

    /* MOBILE / TABLET full-screen module reader */
    .mod-mobile-list { display: flex; flex-direction: column; gap: 10px; }
    .mod-mobile-item { display: flex; align-items: center; justify-content: space-between; gap: 12px;
      background: var(--panel); border: 1px solid var(--line); border-radius: 12px; padding: 18px 18px;
      font-family: inherit; cursor: pointer; color: var(--txt2); transition: all 0.12s; text-align: left; }
    .mod-mobile-item:active { background: var(--panel2); }
    .mod-mobile-item svg { color: var(--accent2); flex-shrink: 0; }
    .mmi-text { display: flex; flex-direction: column; gap: 3px; }
    .mmi-title { font-size: 16px; font-weight: 700; color: var(--txt); }
    .mmi-sub { font-size: 12px; color: var(--txt3); }
    .mod-reader { display: flex; flex-direction: column; }
    .mod-reader-bar { position: sticky; top: 0; z-index: 5; display: flex; align-items: center; justify-content: space-between;
      gap: 12px; padding: 8px 0 14px; background: var(--bg); margin-bottom: 4px; }
    .mod-back { display: flex; align-items: center; gap: 6px; background: var(--panel); border: 1px solid var(--line);
      border-radius: 9px; padding: 9px 14px; color: var(--accent2); font-family: inherit; font-weight: 600; font-size: 14px; cursor: pointer; }
    .mod-reader-title { display: flex; align-items: center; gap: 10px; font-size: 22px; font-weight: 800; margin-bottom: 16px; }
    .mod-reader-title svg { color: var(--accent2); flex-shrink: 0; }
    .mod-reader .mod-body { max-height: none; }
    .mod-reader .mod-section { padding: 0; }
    .mod-reader .mod-section h4 { font-size: 16px; }
    .mod-reader .mod-section li, .mod-reader .db-prose, .mod-reader .db-steps li, .mod-reader .db-check li, .mod-reader .db-line { font-size: 15px; }

    .db-prose { font-size: 13px; color: var(--txt2); line-height: 1.65; margin: 8px 0 12px; }
    .db-box { background: var(--bg2); border: 1px solid var(--line); border-radius: 9px; padding: 12px 15px; margin: 8px 0 14px; }
    .db-title { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--accent2);
      margin-bottom: 9px; display: flex; align-items: center; gap: 6px; }
    .db-steps { margin: 0; padding-left: 20px; counter-reset: none; }
    .db-steps li { font-size: 13px; color: var(--txt2); line-height: 1.55; margin-bottom: 7px; }
    .db-check { margin: 0; padding: 0; list-style: none; }
    .db-check li { display: flex; gap: 9px; align-items: flex-start; font-size: 13px; color: var(--txt2); line-height: 1.5; margin-bottom: 7px; }
    .db-check li svg { color: var(--l2); flex-shrink: 0; margin-top: 3px; }
    .db-script { border-left: 3px solid var(--blue); }
    .db-toolkit { border-left: 3px solid var(--accent); }
    .db-line { font-size: 13px; color: var(--txt2); font-style: italic; line-height: 1.6; margin-bottom: 8px; }
    .db-warn { display: flex; gap: 10px; align-items: flex-start; background: rgba(200,71,47,0.1); border: 1px solid rgba(200,71,47,0.3);
      border-radius: 9px; padding: 11px 14px; margin: 8px 0 14px; }
    .db-warn svg { color: #e07a63; flex-shrink: 0; margin-top: 2px; }
    .db-warn span { font-size: 12.5px; color: #e8a596; line-height: 1.55; font-weight: 500; }

    .result-badge.gaps { background: rgba(217,154,43,0.15); color: var(--gold); }
    .result-msg.gaps { color: var(--gold); }

    /* MANAGEMENT + OBSERVATION + RT */
    .manage-perm { font-size: 12px; color: var(--txt3); margin-bottom: 14px; padding-bottom: 12px; border-bottom: 1px solid var(--line); }
    .manage-block { margin-bottom: 18px; }
    .override-block { border: 1px solid var(--gold); border-radius: 6px; padding: 14px 16px; background: rgba(217,154,43,0.04); }
    .override-form { margin-top: 10px; display: flex; flex-direction: column; gap: 10px; }
    /* PDF build progress */
    .pdf-progress { margin: 12px 0 16px; padding: 12px 14px; border: 1px solid var(--gold); border-radius: 6px;
      background: rgba(217,154,43,0.06); }
    .pdf-progress-bar { height: 4px; background: rgba(217,154,43,0.18); border-radius: 2px; overflow: hidden;
      position: relative; }
    .pdf-progress-indeterminate { position: absolute; left: -30%; top: 0; bottom: 0; width: 30%; background: var(--gold);
      border-radius: 2px; animation: pdf-prog 1.2s ease-in-out infinite; }
    @keyframes pdf-prog { 0% { left: -30%; } 100% { left: 100%; } }
    .pdf-progress-label { font-size: 12px; color: var(--txt2); margin-top: 8px; font-family: monospace; }
    .mb-title { font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--accent2); font-weight: 700; margin-bottom: 10px; }
    .mb-note { font-size: 12px; color: var(--txt3); line-height: 1.5; margin-top: 8px; display: flex; align-items: center; gap: 6px; }
    .level-btns { display: flex; flex-wrap: wrap; gap: 8px; }
    .lvl-btn { background: var(--bg2); border: 1.5px solid var(--line2); border-radius: 8px; padding: 8px 14px; color: var(--txt);
      font-family: 'Spline Sans Mono', monospace; font-weight: 700; cursor: pointer; font-size: 13px; }
    .lvl-btn:hover:not(:disabled) { filter: brightness(1.2); }
    .lvl-btn.active { background: var(--accent); color: #1a1008; border-color: var(--accent); }
    .lvl-btn:disabled { opacity: 0.4; cursor: default; }
    .lvl-btn.rt { border-color: var(--l4); color: #e07a63; font-family: 'Archivo', sans-serif; font-size: 12px; }
    .manage-actions { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
    .warn-btn { border-color: var(--gold); color: var(--gold); }
    .btn-danger { display: flex; align-items: center; gap: 7px; background: rgba(200,71,47,0.15); border: 1px solid var(--l4);
      color: #e07a63; padding: 9px 14px; border-radius: 9px; font-family: inherit; font-weight: 600; font-size: 13px; cursor: pointer; }
    .btn-danger:hover { background: rgba(200,71,47,0.25); }
    .obs-context { background: rgba(255,107,53,0.1); border: 1px solid rgba(255,107,53,0.25); border-radius: 8px;
      padding: 9px 12px; font-size: 12.5px; color: var(--accent2); font-weight: 600; margin-bottom: 16px; }
    .result-toggle { display: flex; gap: 8px; }
    .result-toggle button { flex: 1; display: flex; align-items: center; justify-content: center; gap: 7px; padding: 11px;
      background: var(--bg2); border: 1.5px solid var(--line2); border-radius: 9px; color: var(--txt2); font-family: inherit;
      font-weight: 600; cursor: pointer; font-size: 14px; }
    .result-toggle button.active.pass { background: rgba(46,158,107,0.18); border-color: var(--l2); color: #4ec78c; }
    .result-toggle button.active.fail { background: rgba(200,71,47,0.18); border-color: var(--l4); color: #e07a63; }
    .check-row { display: flex; align-items: center; gap: 9px; font-size: 13px; color: var(--txt2); margin-bottom: 14px; cursor: pointer; }
    .check-row input { accent-color: var(--accent); width: 16px; height: 16px; }
    .check-row.sm { font-size: 12.5px; margin-bottom: 0; }
    .cell-badge.rt { background: var(--l4); color: #fff; font-size: 11px; }
    .mini-flag { font-size: 9.5px; font-weight: 700; padding: 1px 6px; border-radius: 10px; margin-left: 8px; text-transform: uppercase; letter-spacing: 0.4px; }
    .mini-flag.arch { background: var(--panel2); color: var(--txt3); border: 1px solid var(--line2); }
    .mini-flag.dept { background: rgba(70,140,200,0.14); color: #88baeb; border: 1px solid rgba(70,140,200,0.35); }
    .roster-bar { display: flex; justify-content: flex-end; margin-bottom: 12px; }
    .cert-table tr.archived { opacity: 0.5; }

    /* OBSERVATIONS VIEW */
    .obs-tab { display: flex; flex-direction: column; gap: 18px; }
    .l4-panel { border: 1px solid rgba(212,160,23,0.35); }
    .obs-help { font-size: 12.5px; color: var(--txt3); line-height: 1.5; margin-bottom: 14px; }
    .obs-set { background: var(--bg2); border: 1px solid var(--line); border-radius: 11px; padding: 14px 16px; margin-bottom: 12px; }
    .obs-set.rt-set { border-color: var(--l4); }
    .obs-set-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
    .obs-slots { display: grid; grid-template-columns: repeat(2, 1fr); gap: 9px; }
    .obs-slot { display: flex; align-items: center; gap: 10px; padding: 9px 11px; background: var(--panel); border: 1px solid var(--line); border-radius: 9px; }
    .obs-slot.done { border-color: var(--l2); background: rgba(46,158,107,0.08); }
    .os-icon { width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
      background: var(--bg2); color: var(--txt3); flex-shrink: 0; }
    .obs-slot.done .os-icon { background: var(--l2); color: #fff; }
    .os-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    .os-label { font-size: 12.5px; font-weight: 600; color: var(--txt); }
    .os-by { font-size: 11px; color: var(--txt3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .obs-fail-note { display: flex; align-items: center; gap: 6px; font-size: 11.5px; color: #e07a63; margin-top: 10px; }
    .ttt-dots { display: flex; gap: 8px; }
    .ttt-dot { width: 28px; height: 8px; border-radius: 4px; background: var(--line2); }
    .ttt-dot.on { background: var(--l2); }
    .btn-mini.ok { color: #4ec78c; border-color: rgba(46,158,107,0.4); }

    /* TRAINEE DASHBOARD */
    .trainee-hero { display: flex; align-items: center; gap: 16px; background: linear-gradient(135deg, var(--panel), var(--panel2));
      border: 1px solid var(--line); border-radius: 14px; padding: 20px 22px; margin-bottom: 18px; }
    .th-avatar { width: 56px; height: 56px; border-radius: 50%; background: var(--accent); color: #1a1008; font-weight: 800;
      font-size: 20px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .trainee-hero h2 { font-size: 22px; font-weight: 800; }
    .th-sub { font-size: 13px; color: var(--txt3); }
    .outstanding-panel { border: 1px solid rgba(255,107,53,0.35); }
    .btn-primary.sm { padding: 8px 14px; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; }
    .trainee-await { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--gold);
      background: rgba(212,160,23,0.1); border-radius: 8px; padding: 10px 12px; margin-top: 10px; }
    .path-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 12px; }
    .path-card { background: var(--bg2); border: 1px solid var(--line); border-radius: 11px; padding: 14px; }
    .path-card.locked { opacity: 0.55; }
    .pc-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
    .pc-name { font-weight: 700; font-size: 13.5px; }
    .pc-ladder { display: flex; gap: 5px; margin-bottom: 10px; }
    .pc-step { flex: 1; text-align: center; font-family: 'Spline Sans Mono', monospace; font-size: 11px; font-weight: 600;
      padding: 4px 0; border-radius: 6px; background: var(--panel); color: var(--txt3); border: 1px solid var(--line); }
    .pc-step.reached { background: rgba(46,158,107,0.15); color: #4ec78c; border-color: transparent; }
    .pc-step.current { background: var(--accent); color: #1a1008; border-color: var(--accent); }
    .pc-next { font-size: 11.5px; color: var(--txt2); line-height: 1.4; }
    .pc-muted { color: var(--txt3); }
    .pc-done { color: #4ec78c; display: inline-flex; align-items: center; gap: 4px; }
    .path-chain { list-style: none; margin: 0; padding: 0; }
    .path-chain li { display: flex; align-items: flex-start; gap: 10px; font-size: 13px; color: var(--txt3); line-height: 1.5;
      padding: 9px 0; border-bottom: 1px solid var(--line); }
    .path-chain li svg { flex-shrink: 0; margin-top: 2px; }
    .path-chain li.done { color: var(--txt2); }
    .path-chain li.done svg { color: var(--l2); }
    .path-chain li.active { color: var(--txt); font-weight: 500; }
    .path-chain li.active svg { color: var(--accent); }
    .path-chain li.optional { color: var(--txt3); }
    .path-chain li.optional svg { color: var(--gold); }
    .path-foot { font-size: 12.5px; color: var(--txt3); margin-top: 12px; }
    .quiz-hist { background: var(--bg2); border: 1px solid var(--line); border-radius: 10px; padding: 12px 14px; margin-bottom: 9px; }
    .qh-head { display: flex; align-items: center; justify-content: space-between; }
    .qh-missed { font-size: 12px; color: #e0a05f; margin-top: 8px; }
    .qh-perfect { font-size: 12px; color: #4ec78c; margin-top: 8px; }
    .myrec-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
    .myrec-cell { background: var(--bg2); border: 1px solid var(--line); border-radius: 10px; padding: 13px; display: flex;
      flex-direction: column; gap: 7px; align-items: flex-start; }
    .mrc-ride { font-weight: 700; font-size: 13px; }
    .mrc-desc { font-size: 11px; color: var(--txt3); }

    /* DASHBOARD GROUPED TASK SECTIONS */
    .dash-sections { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 18px; }
    .task-section .panel-head { align-items: center; }
    .ts-head-right { display: flex; align-items: center; gap: 10px; }
    .ts-viewall { display: inline-flex; align-items: center; gap: 3px; background: none; border: none; color: var(--accent2);
      font-family: inherit; font-size: 12px; font-weight: 600; cursor: pointer; white-space: nowrap; }
    .ts-emp-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%;
      background: var(--bg2); border: 1px solid var(--line); border-radius: 9px; padding: 11px 13px; margin-bottom: 8px;
      cursor: pointer; font-family: inherit; text-align: left; transition: all 0.12s; }
    .ts-emp-row:hover { border-color: var(--accent); background: var(--panel2); }
    .ts-emp-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    .ts-emp-main strong { font-size: 13.5px; color: var(--txt); }
    .ts-emp-right { display: flex; align-items: center; gap: 8px; color: var(--txt3); flex-shrink: 0; }
    .ts-count { background: var(--accent); color: #1a1008; font-weight: 800; font-size: 12px; border-radius: 20px; min-width: 22px;
      height: 22px; display: inline-flex; align-items: center; justify-content: center; padding: 0 7px; }
    .rc-sub { font-size: 11px; color: var(--txt3); font-weight: 400; margin-left: 6px; }

    /* SIDE DRAWER */
    .drawer-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 100; display: flex; justify-content: flex-end; }
    .drawer { width: 420px; max-width: 92vw; height: 100%; background: var(--panel); border-left: 1px solid var(--line2);
      display: flex; flex-direction: column; animation: slideIn 0.18s ease; box-shadow: -10px 0 40px rgba(0,0,0,0.4); }
    @keyframes slideIn { from { transform: translateX(40px); opacity: 0.4; } to { transform: translateX(0); opacity: 1; } }
    .drawer-head { display: flex; align-items: center; justify-content: space-between; padding: 20px; border-bottom: 1px solid var(--line); }
    .drawer-head h3 { font-size: 19px; font-weight: 800; }
    .drawer-sub { font-size: 12.5px; color: var(--txt3); }
    .drawer-body { padding: 18px 20px; overflow-y: auto; flex: 1; }
    .drawer-task { display: flex; align-items: center; justify-content: space-between; gap: 12px;
      background: var(--bg2); border: 1px solid var(--line); border-radius: 10px; padding: 13px 14px; margin-bottom: 10px; }
    .dt-main { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
    .dt-label { font-size: 13px; font-weight: 600; color: var(--txt); }
    .dt-sub { font-size: 11.5px; color: var(--txt3); }
    .drawer-task .btn-mini:disabled { opacity: 0.4; cursor: not-allowed; display: inline-flex; align-items: center; gap: 5px; }
    .drawer-note { font-size: 11.5px; color: var(--txt3); line-height: 1.5; margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--line); }

    @media (max-width: 880px) {
      .dash-sections { grid-template-columns: 1fr; }      .stat-grid { grid-template-columns: repeat(2, 1fr); }
      .dash-cols { grid-template-columns: 1fr; }
      .ed-grid { grid-template-columns: 1fr; }
      .sidebar { width: 70px; }
      .brand-name, .brand-sub, .nav-item span, .reset-btn span { display: none; }
      .nav-item { justify-content: center; }
      .user-meta { display: none; }
      .user-card { justify-content: center; padding: 8px; }
      .content { padding: 16px 14px 50px; }
      .header { padding: 16px 14px; }
      .header h1 { font-size: 18px; }
    }
    @media (max-width: 600px) {
      .modal { width: 100%; }
      .mod-reader-title { font-size: 19px; }
    }

    /* ================= HOME / TEAM / EMPLOYEE PAGE ================= */
    .home { display: flex; flex-direction: column; gap: 16px; }
    .home .panel-body { max-height: none; }
    .home-hello { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
    .home-hello h2 { font-size: 20px; font-weight: 800; margin: 0 0 2px; }
    .home-tiles { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; }
    .home-tile { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; padding: 14px; border-radius: 12px;
      background: var(--panel); border: 1px solid var(--line); cursor: pointer; font-family: inherit; color: var(--txt); text-align: left; }
    .home-tile:hover { border-color: var(--line2); }
    .home-tile.sel { outline: 2px solid var(--accent); outline-offset: -1px; }
    .ht-n { font-size: 26px; font-weight: 800; line-height: 1; }
    .ht-l { font-size: 12px; color: var(--txt2); }
    .home-tile.tone-danger .ht-n { color: #f07a63; }
    .home-tile.tone-warn .ht-n { color: var(--accent2); }
    .home-tile.tone-ok .ht-n { color: var(--green); }
    .chip-row { display: flex; flex-wrap: wrap; gap: 6px; padding: 10px 14px 0; align-items: center; }
    .chip { display: inline-flex; align-items: center; gap: 6px; padding: 6px 11px; border-radius: 999px; border: 1px solid var(--line2);
      background: transparent; color: var(--txt2); font-family: inherit; font-size: 12.5px; cursor: pointer; }
    .chip.on { background: var(--accent); border-color: var(--accent); color: #1a1008; font-weight: 700; }
    .chip-n { font-size: 11px; opacity: 0.75; font-weight: 700; }
    .panel-head.as-btn { width: 100%; background: none; border: none; border-bottom: 1px solid var(--line); color: var(--txt);
      font-family: inherit; cursor: pointer; text-align: left; }
    .panel-head.as-btn h2 { flex: 1; }
    .att-list { display: flex; flex-direction: column; }
    .att-row { display: flex; align-items: center; gap: 12px; padding: 10px 8px; border-bottom: 1px solid var(--line); }
    .att-row:last-child { border-bottom: none; }
    .att-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; background: var(--blue); }
    .att-row.tone-danger .att-dot { background: #e5624a; }
    .att-row.tone-warn .att-dot { background: var(--accent2); }
    .att-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
    .att-emp { background: none; border: none; padding: 0; color: var(--accent2); font-weight: 700; font-family: inherit; font-size: 13.5px;
      cursor: pointer; text-align: left; align-self: flex-start; }
    .att-emp:hover { text-decoration: underline; }
    .att-label { font-size: 13.5px; color: var(--txt); }
    .att-sub { font-size: 12px; color: var(--txt3); }
    .attp-list { display: flex; flex-direction: column; gap: 6px; }
    .attp-card { border: 1px solid var(--line); border-left: 4px solid var(--blue); border-radius: 11px; background: var(--bg2); overflow: hidden; }
    .attp-card.tone-danger { border-left-color: #e5624a; }
    .attp-card.tone-warn { border-left-color: var(--accent2); }
    .attp-head { display: flex; align-items: center; gap: 12px; width: 100%; padding: 10px 12px; background: none; border: none;
      color: var(--txt); font-family: inherit; cursor: pointer; text-align: left; }
    .attp-head:hover { background: var(--panel2); }
    .attp-who { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
    .attp-preview { font-size: 12px; color: var(--txt2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .attp-counts { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
    .attp-body { padding: 0 12px 12px 62px; display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
    .attp-body .att-list { width: 100%; }
    .home-cols { display: grid; grid-template-columns: minmax(0, 1fr) minmax(260px, 340px); gap: 16px; align-items: start; }
    .home-cov { position: sticky; top: 84px; }
    .cmr-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
    .cmr-bar { display: flex; height: 6px; border-radius: 99px; overflow: hidden; background: var(--panel2); }
    .cov-mini { display: flex; flex-direction: column; }
    .cov-mini-row { display: flex; align-items: center; gap: 10px; padding: 9px 8px; background: none; border: none; border-bottom: 1px solid var(--line);
      color: var(--txt); font-family: inherit; cursor: pointer; text-align: left; }
    .cmr-name { flex: 1; font-weight: 600; }
    .cmr-sub { font-size: 12px; }

    .team-bar { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-bottom: 10px; }
    .team-search { flex: 1; min-width: 220px; }
    .team-search input { width: 100%; }
    .seg { display: inline-flex; border: 1px solid var(--line2); border-radius: 9px; overflow: hidden; }
    .seg button { background: none; border: none; color: var(--txt2); padding: 8px 14px; font-family: inherit; font-size: 13px; cursor: pointer; }
    .seg button.on { background: var(--panel2); color: var(--txt); font-weight: 700; }
    .team-sort { background: var(--bg2); border: 1px solid var(--line2); border-radius: 8px; color: var(--txt); padding: 6px 8px; font-family: inherit; font-size: 12.5px; }
    .team-list { display: flex; flex-direction: column; gap: 6px; }
    .team-head { display: grid; grid-template-columns: 40px minmax(170px, 1.3fr) minmax(170px, 1fr) minmax(170px, 1fr) minmax(150px, auto) 18px; gap: 12px;
      padding: 0 12px 2px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--txt3); font-weight: 700; }
    .team-row { display: grid; grid-template-columns: 40px minmax(170px, 1.3fr) minmax(170px, 1fr) minmax(170px, 1fr) minmax(150px, auto) 18px; align-items: center; gap: 12px;
      padding: 10px 12px; background: var(--panel); border: 1px solid var(--line); border-radius: 11px; cursor: pointer;
      font-family: inherit; color: var(--txt); text-align: left; width: 100%; }
    .team-row:hover { border-color: var(--line2); background: var(--panel2); }
    .team-row.archived { opacity: 0.55; }
    .tr-avatar, .emp-avatar { width: 38px; height: 38px; border-radius: 50%; display: grid; place-items: center; font-weight: 800; font-size: 13px;
      background: linear-gradient(135deg, var(--accent), var(--accent2)); color: #1a1008; }
    .tr-who { display: flex; flex-direction: column; min-width: 0; }
    .tr-who strong { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .tr-trained { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
    .tr-rides { display: inline-flex; align-items: center; gap: 3px; }
    .tr-rides i { width: 14px; height: 14px; border-radius: 3px; display: inline-block; }
    .tr-rides i.rt { outline: 2px solid #e5624a; outline-offset: 1px; }
    .tr-cap { font-size: 12px; color: var(--txt2); margin-left: 4px; }
    .tr-flags { display: flex; gap: 6px; justify-content: flex-end; flex-wrap: wrap; }
    .rc-actions { display: flex; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
    .ga-row { border: 1px solid var(--line); border-radius: 10px; margin-bottom: 10px; background: var(--panel); }
    .team-key { display: flex; flex-wrap: wrap; gap: 4px 14px; font-size: 11.5px; color: var(--txt3); padding: 0 4px 6px; }
    .team-key span { display: inline-flex; align-items: center; gap: 5px; }
    .team-key i { width: 11px; height: 11px; border-radius: 3px; display: inline-block; }
    .tr-col { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
    .tr-col-label { display: none; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--txt3); font-weight: 700; }
    .tr-rides { flex-wrap: wrap; row-gap: 3px; }
    .train-area { margin-bottom: 6px; }
    .train-area h3 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.6px; color: var(--txt3); margin: 12px 6px 4px; display: flex; gap: 8px; align-items: center; }
    .tr-chev { color: var(--txt3); }

    .emp-page { display: flex; flex-direction: column; gap: 14px; }
    .content.profile-open > *:not(.emp-page) { display: none; }
    .emp-back { align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; background: none; border: none; color: var(--txt2);
      font-family: inherit; font-size: 13.5px; cursor: pointer; padding: 4px 0; }
    .emp-back:hover { color: var(--txt); }
    .emp-hero { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; padding: 16px; background: var(--panel);
      border: 1px solid var(--line); border-radius: 14px; }
    .emp-avatar { width: 54px; height: 54px; font-size: 18px; }
    .emp-id { flex: 1; min-width: 200px; }
    .emp-id h2 { font-size: 21px; font-weight: 800; margin: 0 0 3px; }
    .emp-chips { display: flex; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
    .emp-actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .emp-tabs { display: flex; gap: 4px; overflow-x: auto; border-bottom: 1px solid var(--line); scrollbar-width: none; }
    .emp-tabs::-webkit-scrollbar { display: none; }
    .emp-tabs button { flex-shrink: 0; background: none; border: none; border-bottom: 2px solid transparent; color: var(--txt2);
      padding: 10px 12px; font-family: inherit; font-size: 13.5px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
    .emp-tabs button.on { color: var(--txt); border-bottom-color: var(--accent); font-weight: 700; }
    .et-n { background: var(--accent); color: #1a1008; font-size: 10.5px; font-weight: 800; border-radius: 99px; padding: 0 6px; }
    .emp-body { display: flex; flex-direction: column; gap: 14px; }
    .emp-body .panel-body { max-height: none; }
    .emp-stats { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; }
    .emp-stat { display: flex; flex-direction: column; gap: 4px; padding: 14px; border-radius: 12px; background: var(--panel); border: 1px solid var(--line);
      color: var(--txt); font-family: inherit; text-align: left; cursor: pointer; }
    .emp-stat:hover { border-color: var(--line2); }
    .es-n { font-size: 24px; font-weight: 800; }
    .es-n small { font-size: 14px; color: var(--txt3); font-weight: 600; }
    .es-l { font-size: 12px; color: var(--txt2); }
    .ride-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 10px; }
    .ride-card { padding: 12px; border-radius: 12px; background: var(--panel); border: 1px solid var(--line); display: flex; flex-direction: column; gap: 8px; }
    .ride-card.locked { opacity: 0.6; }
    .rc-top { display: flex; justify-content: space-between; align-items: center; }

    .qr-list { display: flex; flex-direction: column; gap: 10px; }
    .qr-card { padding: 14px; border-radius: 12px; background: var(--panel); border: 1px solid var(--line); display: flex; flex-direction: column; gap: 8px; }
    .qr-card.bad { border-left: 4px solid #e5624a; }
    .qr-card.warn { border-left: 4px solid var(--accent2); }
    .qr-head { display: flex; justify-content: space-between; gap: 10px; align-items: flex-start; }
    .qr-flag { font-size: 12.5px; color: #f07a63; display: flex; align-items: center; gap: 6px; }
    .qr-topics { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
    .qr-k { font-size: 12px; color: var(--txt3); font-weight: 700; }
    .qr-topic { font-size: 12.5px; padding: 3px 9px; border-radius: 999px; background: rgba(255,165,82,0.14); color: var(--accent2); border: 1px solid rgba(255,165,82,0.35); }
    .qr-next { font-size: 12.5px; color: var(--txt2); }
    .qr-missed { margin: 0; padding-left: 20px; display: flex; flex-direction: column; gap: 10px; }
    .qm-q { font-size: 13.5px; margin-bottom: 3px; }
    .qm-a { font-size: 12.5px; display: flex; align-items: center; gap: 5px; }
    .qm-a.bad { color: #f07a63; }
    .qm-a.good { color: #7fd6ac; }
    .qr-attempts { display: flex; flex-wrap: wrap; gap: 6px; }
    .obs-review { margin: 4px 0 12px; padding: 12px; border-radius: 10px; background: rgba(255,165,82,0.08); border: 1px solid rgba(255,165,82,0.35); }
    .obs-review strong { display: flex; align-items: center; gap: 6px; margin-bottom: 2px; }
    .qr-note { font-size: 12.5px; color: var(--txt2); background: var(--panel2); border-radius: 8px; padding: 8px 10px; }

    .mobile-nav, .mn-sheet-overlay { display: none; }

    /* ================= PHONE LAYOUT ================= */
    @media (max-width: 760px) {
      .app { display: block; }
      .sidebar { display: none; }
      .mobile-nav { display: flex; flex-direction: row; gap: 0; position: fixed; left: 0; right: 0; bottom: 0; z-index: 1500; background: var(--bg2);
        border-top: 1px solid var(--line); padding: 4px 4px calc(4px + env(safe-area-inset-bottom)); }
      .mn-item { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 2px; background: none; border: none;
        color: var(--txt3); font-family: inherit; padding: 6px 2px; cursor: pointer; min-width: 0; }
      .mn-item.active { color: var(--accent2); }
      .mn-ic { position: relative; display: inline-flex; }
      .mn-badge { position: absolute; top: -6px; right: -10px; background: var(--accent); color: #1a1008; font-size: 10px; font-weight: 800;
        border-radius: 99px; padding: 0 5px; line-height: 15px; }
      .mn-label { font-size: 10.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
      .mn-sheet-overlay { display: block; position: fixed; inset: 0; z-index: 1490; background: rgba(8,11,15,0.6); }
      .mn-sheet { position: absolute; left: 0; right: 0; bottom: calc(62px + env(safe-area-inset-bottom)); background: var(--bg2);
        border-top: 1px solid var(--line2); border-radius: 16px 16px 0 0; padding: 10px; max-height: 70vh; overflow-y: auto; }
      .mn-sheet-item { display: flex; align-items: center; gap: 12px; width: 100%; padding: 13px 12px; background: none; border: none;
        color: var(--txt); font-family: inherit; font-size: 15px; border-radius: 10px; cursor: pointer; text-align: left; }
      .mn-sheet-item.active { background: var(--panel2); color: var(--accent2); }
      .mn-sheet-foot { display: flex; align-items: center; gap: 8px; border-top: 1px solid var(--line); margin-top: 8px; padding-top: 10px; flex-wrap: wrap; }
      .mn-user { display: flex; flex-direction: column; flex: 1; min-width: 140px; }

      .header { padding: 12px 14px; }
      .header h1 { font-size: 18px; }
      .content { padding: 12px 12px calc(84px + env(safe-area-inset-bottom)); }
      .viewas-bar { padding: 8px 12px; font-size: 12.5px; }

      /* Panels: no nested scroll boxes on a phone */
      .panel-body { max-height: none; }
      .panel-head { padding: 13px 14px; }
      .row-item { flex-wrap: wrap; row-gap: 6px; padding: 10px 8px; }
      .row-item .ri-main { flex-basis: 100%; }
      .users-head { flex-direction: column; align-items: stretch; }

      /* Bigger tap targets; 16px inputs stop iPhone zoom */
      input, select, textarea { font-size: 16px !important; }
      .btn-primary, .btn-ghost { min-height: 40px; }
      .btn-primary.sm, .btn-ghost.sm { min-height: 36px; }

      /* Every pop-up becomes a full screen sheet */
      .modal-overlay { padding: 0 !important; z-index: 1600; align-items: stretch; }
      .modal, .modal.wide { width: 100vw !important; max-width: 100vw !important; height: 100dvh !important; max-height: 100dvh !important;
        border-radius: 0 !important; display: flex; flex-direction: column; }
      .modal-body { flex: 1; max-height: none !important; padding: 16px; }
      .modal-foot { flex-wrap: wrap; padding: 12px 16px calc(12px + env(safe-area-inset-bottom)); }
      .drawer { width: 100vw !important; }

      /* Home / Team / Employee */
      .home-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .home-cols { grid-template-columns: 1fr; }
      .home-cov { position: static; }
      .home-tile { padding: 12px; }
      .att-row { flex-wrap: wrap; }
      .att-row .att-main { flex-basis: calc(100% - 24px); }
      .att-row > .btn-primary, .att-row > .btn-ghost { margin-left: 20px; }
      .attp-head { flex-wrap: wrap; }
      .attp-who { flex-basis: calc(100% - 80px); }
      .attp-counts { justify-content: flex-start; padding-left: 50px; }
      .attp-preview { white-space: normal; }
      .attp-body { padding: 0 10px 10px 10px; }
      .team-head { display: none; }
      .team-row { grid-template-columns: 38px 1fr 16px; grid-template-areas: "av who chev" "av rides chev" "av rest chev" "av flags chev"; row-gap: 6px; }
      .tr-avatar { grid-area: av; align-self: start; }
      .tr-who { grid-area: who; }
      .tr-col-rides { grid-area: rides; }
      .tr-col-rest { grid-area: rest; }
      .tr-col-label { display: block; }
      .tr-flags { grid-area: flags; justify-content: flex-start; }
      .tr-chev { grid-area: chev; }
      .team-search { min-width: 0; flex-basis: 100%; }
      .emp-hero { padding: 12px; }
      .emp-actions { width: 100%; }
      .emp-actions .btn-ghost { flex: 1 1 auto; justify-content: center; }
      .emp-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .goal-grid { grid-template-columns: 1fr; }
      .trainee-hero { padding: 14px; }
      .path-grid { grid-template-columns: 1fr 1fr !important; }
    }
    `}</style>
  );
}
